import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Users, 
  DollarSign, 
  FileText, 
  Settings, 
  BarChart3,
  ArrowLeft,
  Search,
  MoreVertical,
  TrendingUp,
  Globe,
  CreditCard,
  Receipt,
  Calendar,
  Download,
  RefreshCw,
  RefreshCcw,
  X,
  Mail,
  Clock,
  Share2,
  Eye,
  EyeOff,
  Fingerprint,
  Shield,
  Sparkles,
  LogOut,
  Tag,
  Bell,
  Megaphone,
  ToggleLeft,
  ToggleRight,
  Edit,
  Trash2,
  Plus,
  Check,
  AlertTriangle,
  Lock,
  Gift,
  UserCog,
  Database,
  ShieldCheck,
  Loader2,
  LifeBuoy,
  Send,
  MessageSquare,
  Bug,
  ChevronRight,
  ChevronDown,
  Star,
  Building2,
  Copy,
  CheckCircle,
  XCircle,
  Banknote,
  Activity,
  Terminal,
  Wifi,
  WifiOff,
  Server,
  Zap,
  AlertOctagon,
  PlayCircle,
  PauseCircle,
  Link2,
  Filter,
  SlidersHorizontal,
  Award,
  Video,
  Save,
  Bot,
  Brain,
  Target,
  ThumbsUp,
  ThumbsDown,
  History,
  Facebook,
  Instagram,
  Music2,
  Image,
  HelpCircle,
  Upload,
  BookOpen,
  Briefcase,
  Building,
  Handshake,
  Key,
  Edit3,
  ExternalLink,
  User
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/9fe16284-c883-46c5-9976-fb4189ef86a2/images/4414b575521af6b937b0721d2c61060a2fec8b4fac41d65ab6fa293761654c61.png";

// Canadian Provincial Tax Rates (mirror of backend)
const CANADIAN_TAX_RATES = {
  "ON": { name: "Ontario", rate: 13, type: "HST" },
  "BC": { name: "British Columbia", rate: 12, type: "GST+PST" },
  "AB": { name: "Alberta", rate: 5, type: "GST" },
  "SK": { name: "Saskatchewan", rate: 11, type: "GST+PST" },
  "MB": { name: "Manitoba", rate: 12, type: "GST+PST" },
  "QC": { name: "Quebec", rate: 14.975, type: "GST+QST" },
  "NB": { name: "New Brunswick", rate: 15, type: "HST" },
  "NS": { name: "Nova Scotia", rate: 15, type: "HST" },
  "PE": { name: "Prince Edward Island", rate: 15, type: "HST" },
  "NL": { name: "Newfoundland", rate: 15, type: "HST" },
  "NT": { name: "Northwest Territories", rate: 5, type: "GST" },
  "YT": { name: "Yukon", rate: 5, type: "GST" },
  "NU": { name: "Nunavut", rate: 5, type: "GST" }
};

// Helper function to calculate time ago
const getTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

// Helper to format timestamp with multiple timezones
const formatMultiTimezone = (dateStr) => {
  const date = new Date(dateStr);
  const options = { hour: '2-digit', minute: '2-digit', hour12: true };
  
  const localTime = date.toLocaleTimeString('en-US', { ...options, timeZoneName: 'short' });
  const istTime = date.toLocaleTimeString('en-US', { ...options, timeZone: 'Asia/Kolkata' });
  const estTime = date.toLocaleTimeString('en-US', { ...options, timeZone: 'America/New_York' });
  const gmtTime = date.toLocaleTimeString('en-US', { ...options, timeZone: 'UTC' });
  
  return {
    local: localTime,
    ist: `${istTime} IST`,
    est: `${estTime} EST`,
    gmt: `${gmtTime} GMT`
  };
};

// Import Admin Help Modal
import AdminHelpModal from "@/components/AdminHelpModal";

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [revenueStats, setRevenueStats] = useState(null);
  const [engagementStats, setEngagementStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  
  // Sidebar collapsed sections state (all expanded by default)
  const [expandedSections, setExpandedSections] = useState({
    dashboard: true,
    userManagement: true,
    aiAutomation: true,
    financials: true,
    analytics: true,
    b2bEnterprise: true,
    system: true
  });
  
  // User Action Modals
  const [suspendUserModal, setSuspendUserModal] = useState(null);
  const [editUserModal, setEditUserModal] = useState(null);
  const [changePlanModal, setChangePlanModal] = useState(null);
  const [impersonationLoading, setImpersonationLoading] = useState(false);
  
  // Audit Log State
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogPage, setAuditLogPage] = useState(1);
  const [auditLogTotal, setAuditLogTotal] = useState(0);
  const [auditLogSearch, setAuditLogSearch] = useState("");
  
  // Feature Toggles State
  const [featureToggles, setFeatureToggles] = useState({});
  const [featureTogglesLoading, setFeatureTogglesLoading] = useState(false);
  const [featureToggleAnnouncement, setFeatureToggleAnnouncement] = useState(null);
  
  // Health Check History State
  const [healthCheckHistory, setHealthCheckHistory] = useState([]);
  const [healthCheckHistoryPage, setHealthCheckHistoryPage] = useState(1);
  const [healthCheckHistoryTotal, setHealthCheckHistoryTotal] = useState(0);
  const [healthCheckHistoryLoading, setHealthCheckHistoryLoading] = useState(false);
  
  // Transaction Detail Modal
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [settings, setSettings] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [analyticsDetail, setAnalyticsDetail] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [apiBalances, setApiBalances] = useState(null);
  const [apiBalanceLoading, setApiBalanceLoading] = useState(false);
  const [apiCostTracking, setApiCostTracking] = useState(null);
  
  // Help Modal State
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [helpSection, setHelpSection] = useState(null);
  
  // Content Moderation State
  const [flaggedContent, setFlaggedContent] = useState([]);
  const [moderationStats, setModerationStats] = useState({ pending: 0, approved: 0, rejected: 0, auto_flagged: 0 });
  const [moderationLoading, setModerationLoading] = useState(false);
  
  // Team & Roles State
  const [roles, setRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [teamMembersData, setTeamMembersData] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [creatingRole, setCreatingRole] = useState(false);
  const [creatingTeamMember, setCreatingTeamMember] = useState(false);
  const [newRoleData, setNewRoleData] = useState({ name: "", description: "", permissions: {} });
  const [newTeamMemberData, setNewTeamMemberData] = useState({ email: "", name: "", role_id: "support_agent", password: "" });
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [suspendTeamMemberModal, setSuspendTeamMemberModal] = useState(null);
  
  // Email Template Creation State
  const [createEmailTemplateModal, setCreateEmailTemplateModal] = useState(false);
  const [newEmailTemplateData, setNewEmailTemplateData] = useState({ name: "", topic: "", subject: "", body: "", status: "active" });
  const [generatingEmailContent, setGeneratingEmailContent] = useState(false);
  
  // Data & Compliance Settings State
  const [complianceSettings, setComplianceSettings] = useState({
    two_factor_admin: true,
    data_encryption: true,
    ssl_encryption: true,
    cookie_consent: true,
    ip_logging: true,
    auto_logout: false,
    session_timeout_minutes: 60
  });
  const [complianceLoading, setComplianceLoading] = useState(false);
  
  // Document Generation State
  const [documentHistory, setDocumentHistory] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [generatingDocument, setGeneratingDocument] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState("privacy_policy");
  const [documentContent, setDocumentContent] = useState("");
  
  // AI Document Chat State
  const [docChatMessage, setDocChatMessage] = useState("");
  const [docChatHistory, setDocChatHistory] = useState([]);
  const [docChatLoading, setDocChatLoading] = useState(false);
  const [uploadedDocContent, setUploadedDocContent] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  
  // Platform Configuration State (admin-only)
  const [platformConfig, setPlatformConfig] = useState({
    video_credit_costs: {},
    regen_credit_costs: {},
    profile_limits: {},
    plan_video_access: {},
    tax_rates: {}
  });
  const [platformConfigLoading, setPlatformConfigLoading] = useState(false);
  const [editPlatformConfig, setEditPlatformConfig] = useState(null);
  
  const [referralSettings, setReferralSettings] = useState({
    referrer_reward_posts: 3,
    referee_discount_percent: 10
  });
  // Influencer Management State
  const [influencerSettings, setInfluencerSettings] = useState({
    enabled: true,
    min_referees_for_upgrade: 10,
    min_purchase_per_referee: 29.0,
    commission_percent: 10.0,
    payout_threshold: 29.0,
    payout_methods: ["paypal"]
  });
  const [influencers, setInfluencers] = useState([]);
  const [influencerCandidates, setInfluencerCandidates] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [payoutSummary, setPayoutSummary] = useState({});
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [payoutAdminNotes, setPayoutAdminNotes] = useState("");
  const [payoutTxRef, setPayoutTxRef] = useState("");
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState({ open: 0, pending: 0, resolved: 0, total: 0 });
  const [ticketAnalytics, setTicketAnalytics] = useState(null);
  const [showTicketAnalytics, setShowTicketAnalytics] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [resolutionMessage, setResolutionMessage] = useState("");
  const [compensatePostCredits, setCompensatePostCredits] = useState(0);
  const [compensateVideoCredits, setCompensateVideoCredits] = useState(0);
  const [compensateRegenCredits, setCompensateRegenCredits] = useState(0);
  const [internalNote, setInternalNote] = useState("");
  // E-Transfer states
  const [pendingEtransfers, setPendingEtransfers] = useState([]);
  const [etransferStats, setEtransferStats] = useState({ pending: 0, verified: 0, rejected: 0, total_pending_amount: 0 });
  const [selectedEtransfer, setSelectedEtransfer] = useState(null);
  const [etransferLoading, setEtransferLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  
  // Email Analytics state
  const [emailStats, setEmailStats] = useState({ total_sent: 0, total_opens: 0, total_clicks: 0, unique_opens: 0, open_rate: 0, click_rate: 0 });
  const [emailActivity, setEmailActivity] = useState([]);
  
  // Gift Packs state
  const [giftStats, setGiftStats] = useState({ total_gifts: 0, redeemed: 0, pending: 0, revenue: 0 });
  const [giftActivity, setGiftActivity] = useState([]);
  
  // Service Alert Settings state (for Health Monitor tab)
  const [serviceAlertSettings, setServiceAlertSettings] = useState({
    auto_notify_on_outage: true,
    auto_notify_on_recovery: true,
    notify_self_healing_fixes: false,
    critical_services: ["content_generation", "video_generation", "social_posting", "payments", "image_generation", "analytics"],
    admin_alert_emails: [],
    admin_alert_enabled: true
  });
  const [serviceAlertLoading, setServiceAlertLoading] = useState(false);
  const [serviceAlertSaving, setServiceAlertSaving] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  
  // Health Monitor state
  const [healthStatus, setHealthStatus] = useState(null);
  const [healthHistory, setHealthHistory] = useState([]);
  const [runningHealthCheck, setRunningHealthCheck] = useState(false);
  
  // B2B Enterprise state
  const [b2bStats, setB2bStats] = useState({ partners: { total: 0, active: 0 }, enterprises: { total: 0, active: 0 }, merchants: { total: 0, active: 0 }, inquiries: { pending: 0 }, support: { pending_escalations: 0 } });
  const [b2bPartners, setB2bPartners] = useState([]);
  const [b2bEnterprises, setB2bEnterprises] = useState([]);
  const [b2bInquiries, setB2bInquiries] = useState([]);
  const [b2bPlans, setB2bPlans] = useState({});
  const [b2bLoading, setB2bLoading] = useState(false);
  const [b2bOnboardingSummary, setB2bOnboardingSummary] = useState(null);
  const [selectedOnboardingEnterprise, setSelectedOnboardingEnterprise] = useState(null);
  const [onboardingActionLoading, setOnboardingActionLoading] = useState(false);
  const [showAddPartnerModal, setShowAddPartnerModal] = useState(false);
  const [showAddEnterpriseModal, setShowAddEnterpriseModal] = useState(false);
  const [newPartnerData, setNewPartnerData] = useState({ company_name: "", contact_name: "", email: "", phone: "", country: "CA", territory: [], notes: "" });
  const [newEnterpriseData, setNewEnterpriseData] = useState({ company_name: "", contact_name: "", email: "", phone: "", country: "CA", partner_id: "", plan: "starter", notes: "" });
  const [selectedB2bPartner, setSelectedB2bPartner] = useState(null);
  const [selectedB2bEnterprise, setSelectedB2bEnterprise] = useState(null);
  
  // B2B Plan & Tier Editing state
  const [partnerTiers, setPartnerTiers] = useState({});
  const [editingEnterprisePlan, setEditingEnterprisePlan] = useState(null);
  const [editingPartnerTier, setEditingPartnerTier] = useState(null);
  
  // Partner Payout Management
  const [partnerPayouts, setPartnerPayouts] = useState([]);
  const [partnerPayoutSummary, setPartnerPayoutSummary] = useState({});
  const [selectedPartnerPayout, setSelectedPartnerPayout] = useState(null);
  const [partnerPayoutTxRef, setPartnerPayoutTxRef] = useState("");
  const [partnerPayoutNotes, setPartnerPayoutNotes] = useState("");
  
  // B2B Tech Support & API Logs state
  const [b2bEscalations, setB2bEscalations] = useState([]);
  const [b2bApiLogs, setB2bApiLogs] = useState([]);
  const [b2bApiLogStats, setB2bApiLogStats] = useState({ total: { sandbox: 0, production: 0, errors: 0 }, last_24h: { sandbox: 0, production: 0 } });
  const [b2bConversations, setB2bConversations] = useState([]);
  const [selectedEscalation, setSelectedEscalation] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  
  // B2B Settings state
  const [b2bSettings, setB2bSettings] = useState({
    inquiry_notification_email: "care@viraly.ca",
    inquiry_notification_emails: ["care@viraly.ca"],
    auto_approve_enterprises: false,
    sandbox_daily_limit: 100,
    sandbox_calls_for_production: 10,
    send_welcome_emails: true
  });
  const [b2bSettingsSaving, setB2bSettingsSaving] = useState(false);
  const [newB2bNotificationEmail, setNewB2bNotificationEmail] = useState("");
  
  // Publish Retry Settings state (for auto-posting retry configuration)
  const [publishRetrySettings, setPublishRetrySettings] = useState({
    max_retries: 3,
    retry_intervals_minutes: [5, 30, 120],
    notification_type: "push", // "push", "email", "both"
    tiktok_reminder_hours: [24, 6],
    auto_reschedule_on_failure: true
  });
  const [publishRetrySettingsSaving, setPublishRetrySettingsSaving] = useState(false);
  const [publishFailures, setPublishFailures] = useState({ failures: [], stats: {} });
  
  const [healthThresholds, setHealthThresholds] = useState({
    enabled: true,
    alert_email: "",
    alert_cooldown_minutes: 30,
    thresholds: {
      api_failure_rate: { enabled: true, threshold: 10, window_minutes: 60, description: "API failure rate exceeded" },
      db_latency_ms: { enabled: true, threshold: 500, window_minutes: 15, description: "Database latency high" },
      content_generation_failures: { enabled: true, threshold: 5, window_minutes: 60, description: "Content generation failures" },
      email_delivery_failures: { enabled: true, threshold: 3, window_minutes: 60, description: "Email delivery failures" },
      video_generation_failures: { enabled: true, threshold: 3, window_minutes: 60, description: "Video generation failures" },
      queue_backlog: { enabled: true, threshold: 50, window_minutes: 30, description: "Scheduled posts queue backlog" }
    }
  });
  const [healthAlerts, setHealthAlerts] = useState([]);
  const [savingHealthThresholds, setSavingHealthThresholds] = useState(false);
  const [testingHealthAlert, setTestingHealthAlert] = useState(false);
  
  // System Learning state (NEW)
  const [systemLearningInsights, setSystemLearningInsights] = useState(null);
  const [recurringIssues, setRecurringIssues] = useState([]);
  const [latencyInsights, setLatencyInsights] = useState(null);
  const [systemLearningAlerts, setSystemLearningAlerts] = useState([]);
  const [activeBreakdowns, setActiveBreakdowns] = useState([]);
  const [loadingSystemLearning, setLoadingSystemLearning] = useState(false);
  
  // Advanced System Learning state (NEW)
  const [featureUsageInsights, setFeatureUsageInsights] = useState(null);
  const [externalApiInsights, setExternalApiInsights] = useState(null);
  const [perUserLatencyInsights, setPerUserLatencyInsights] = useState(null);
  const [userImpactReport, setUserImpactReport] = useState(null);
  
  // System Logs state
  const [systemLogs, setSystemLogs] = useState([]);
  const [logFilter, setLogFilter] = useState("all"); // all, error, warning, info
  const [selectedLog, setSelectedLog] = useState(null);
  
  // Admin Purchase Notifications state
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  
  // User Notification Thresholds state
  const [notificationThresholds, setNotificationThresholds] = useState({
    low_credits_threshold: 5,
    low_credits_email_enabled: true,
    low_credits_push_enabled: true,
    low_credits_cooldown_days: 7,
    out_of_credits_email_enabled: true,
    out_of_credits_push_enabled: true,
    expiring_credits_days_before: 7,
    expiring_credits_email_enabled: true,
    expiring_credits_cooldown_days: 3,
    weekly_digest_enabled: true,
    scheduled_post_reminder_enabled: true
  });
  // Free Access Accounts (Whitelist) state
  const [freeAccessAccounts, setFreeAccessAccounts] = useState([]);
  const [newFreeAccessEmail, setNewFreeAccessEmail] = useState("");
  const [newFreeAccessReason, setNewFreeAccessReason] = useState("");
  const [newFreeAccessGenerateLink, setNewFreeAccessGenerateLink] = useState(false);
  const [newFreeAccessPostsLimit, setNewFreeAccessPostsLimit] = useState("");
  const [newFreeAccessVideosLimit, setNewFreeAccessVideosLimit] = useState("");
  const [addingFreeAccess, setAddingFreeAccess] = useState(false);
  // Filter states for various tabs
  const [userFilter, setUserFilter] = useState({ plan: "all", status: "all", dateRange: "all" });
  const [ticketFilter, setTicketFilter] = useState({ status: "all", priority: "all", category: "all" });
  const [etransferFilter, setEtransferFilter] = useState({ status: "all", dateRange: "all" });
  const [transactionFilter, setTransactionFilter] = useState({ status: "all", province: "all", product: "all", dateRange: "all" });
  // Modal states for editing
  const [editPromoModal, setEditPromoModal] = useState(null);
  const [editEmailTemplateModal, setEditEmailTemplateModal] = useState(null);
  const [editLoyaltyTierModal, setEditLoyaltyTierModal] = useState(null);
  const [revenueDetailModal, setRevenueDetailModal] = useState(null);
  const [createPromoModal, setCreatePromoModal] = useState(false);
  const [createAnnouncementModal, setCreateAnnouncementModal] = useState(false);
  const [createCouponModal, setCreateCouponModal] = useState(false);
  const [editCouponModal, setEditCouponModal] = useState(null);
  const [editAnnouncementModal, setEditAnnouncementModal] = useState(null);
  // Coupons state
  const [coupons, setCoupons] = useState([]);
  // Promotions sub-tab
  const [promoSubTab, setPromoSubTab] = useState("promotions");
  // AI-suggested promotions (from backend)
  const [suggestedPromotions, setSuggestedPromotions] = useState([]);
  // Editable promotions state
  const [editablePromotions, setEditablePromotions] = useState([
    { id: "1", name: "LAUNCH20", discount: 20, type: "percentage", uses: 145, maxUses: 500, status: "active", expires: "2025-12-31" },
    { id: "2", name: "WELCOME10", discount: 10, type: "percentage", uses: 892, maxUses: null, status: "active", expires: null },
    { id: "3", name: "HOLIDAY30", discount: 30, type: "fixed", uses: 50, maxUses: 100, status: "scheduled", expires: "2025-12-25" }
  ]);
  // Editable email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [notificationTemplatesByCategory, setNotificationTemplatesByCategory] = useState({});
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateSaving, setTemplateSaving] = useState(false);
  
  // Viraly Social Accounts State (for BOT auto-reply and content posting)
  const [viralySocialAccounts, setViralySocialAccounts] = useState({
    accounts: {
      facebook: { handle: "", page_id: "", connected: false, auto_reply_enabled: false },
      instagram: { handle: "", account_id: "", connected: false, auto_reply_enabled: false },
      tiktok: { handle: "", account_id: "", connected: false, auto_reply_enabled: false },
      pinterest: { handle: "", account_id: "", connected: false, auto_reply_enabled: false },
      linkedin: { handle: "", account_id: "", connected: false, auto_reply_enabled: false },
      youtube: { handle: "", channel_id: "", connected: false, auto_reply_enabled: false },
      twitter: { handle: "", account_id: "", connected: false, auto_reply_enabled: false }
    },
    settings: {
      auto_reply_enabled: false,
      auto_post_enabled: false,
      response_tone: "friendly",
      custom_instructions: ""
    }
  });
  const [savingViralySocial, setSavingViralySocial] = useState(false);
  
  // Viraly Internal Social Accounts (Official Viraly handles for posting)
  const [viralySocialConfig, setViralySocialConfig] = useState(null);
  const [viralySocialAccts, setViralySocialAccts] = useState([]);
  const [viralySocialDrafts, setViralySocialDrafts] = useState([]);
  const [viralySocialPosts, setViralySocialPosts] = useState([]);
  const [viralySocialLoading, setViralySocialLoading] = useState(false);
  const [viralySocialSubTab, setViralySocialSubTab] = useState("accounts");
  const [viralySocialGenerating, setViralySocialGenerating] = useState(false);
  const [viralySocialPublishing, setViralySocialPublishing] = useState(false);
  const [contentTopic, setContentTopic] = useState("");
  const [contentType, setContentType] = useState("promotional");
  const [contentPlatforms, setContentPlatforms] = useState(["instagram", "linkedin", "twitter"]);
  const [includeImage, setIncludeImage] = useState(true);
  
  // Auto-Reply Bot State (Full Feature)
  const [autoReplySettings, setAutoReplySettings] = useState({
    enabled: false,
    platforms: {
      facebook: { comments: false, dms: false },
      instagram: { comments: false, dms: false },
      tiktok: { comments: false, dms: false },
      pinterest: { comments: false, dms: false },
      linkedin: { comments: false, dms: false },
      youtube: { comments: false, dms: false },
      twitter: { comments: false, dms: false }
    },
    response_tone: 'friendly',
    response_delay_seconds: 30,
    blacklist_words: [],
    review_negative: true,
    review_complaints: true,
    review_all: false,
    custom_instructions: '',
    daily_limit: 100
  });
  const [autoReplyHistory, setAutoReplyHistory] = useState([]);
  const [autoReplyStats, setAutoReplyStats] = useState(null);
  const [autoReplyLoading, setAutoReplyLoading] = useState(false);
  const [autoReplySaving, setAutoReplySaving] = useState(false);
  const [autoReplyTestMessage, setAutoReplyTestMessage] = useState('');
  const [autoReplyTestPlatform, setAutoReplyTestPlatform] = useState('instagram');
  const [autoReplyTestResult, setAutoReplyTestResult] = useState(null);
  const [autoReplyTesting, setAutoReplyTesting] = useState(false);
  const [autoReplySubTab, setAutoReplySubTab] = useState('accounts');
  const [autoReplySimulationResults, setAutoReplySimulationResults] = useState([]);
  const [autoReplyRunningSimulation, setAutoReplyRunningSimulation] = useState(false);
  const [autoReplyCurrentSimIndex, setAutoReplyCurrentSimIndex] = useState(-1);
  const [autoReplyNewBlacklistWord, setAutoReplyNewBlacklistWord] = useState('');
  
  // Content Playbook State
  const [playbookStats, setPlaybookStats] = useState({
    active_users: 0,
    posts_today: 0,
    posts_scheduled: 3,
    viraly_config: {
      posts_per_day: 3,
      platforms: ['Instagram', 'Facebook', 'TikTok'],
      approval_mode: 'review_first'
    }
  });
  const [playbookPostsQueue, setPlaybookPostsQueue] = useState([]);
  
  // Auto-Reply Platform Config
  const AUTO_REPLY_PLATFORMS = [
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600', recommendedTone: 'helpful' },
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-r from-purple-500 to-pink-500', recommendedTone: 'friendly' },
    { id: 'tiktok', name: 'TikTok', icon: Music2, color: 'bg-black', recommendedTone: 'witty' },
    { id: 'pinterest', name: 'Pinterest', icon: Image, color: 'bg-red-600', recommendedTone: 'professional' },
    { id: 'linkedin', name: 'LinkedIn', icon: Briefcase, color: 'bg-blue-700', recommendedTone: 'professional' },
    { id: 'youtube', name: 'YouTube', icon: PlayCircle, color: 'bg-red-500', recommendedTone: 'engaging' },
    { id: 'twitter', name: 'X (Twitter)', icon: Share2, color: 'bg-black', recommendedTone: 'witty' }
  ];
  
  const AUTO_REPLY_TONES = [
    { id: 'friendly', name: 'Friendly', desc: 'Warm and approachable', emoji: '😊' },
    { id: 'professional', name: 'Professional', desc: 'Polished and clear', emoji: '💼' },
    { id: 'enthusiastic', name: 'Enthusiastic', desc: 'Energetic and excited', emoji: '🎉' },
    { id: 'helpful', name: 'Helpful', desc: 'Solution-oriented', emoji: '🤝' },
    { id: 'witty', name: 'Witty', desc: 'Clever with light humor', emoji: '😏' }
  ];
  
  const AUTO_REPLY_SIMULATION_MESSAGES = [
    { id: 1, message: "Your competitor XYZ has way better features and it's cheaper too. Why should I use Viraly?", category: "Competition", platform: "facebook", expectedIntent: "question" },
    { id: 2, message: "This is absolutely TERRIBLE! I paid for premium and nothing works. I want my money back NOW!", category: "Angry Complaint", platform: "instagram", expectedIntent: "complaint" },
    { id: 3, message: "omg I LOVE this app so much!! 😍 Best decision ever to switch to you guys", category: "Compliment", platform: "tiktok", expectedIntent: "compliment" },
    { id: 4, message: "How much does the pro plan cost? Also what's included?", category: "Pricing", platform: "instagram", expectedIntent: "question" },
    { id: 5, message: "Is there a free trial? I want to test before committing", category: "Sales", platform: "facebook", expectedIntent: "question" },
    { id: 6, message: "Can I schedule posts to multiple platforms at once?", category: "Features", platform: "tiktok", expectedIntent: "question" },
    { id: 7, message: "This is spam and I'm reporting your account", category: "Hostile", platform: "instagram", expectedIntent: "spam" },
    { id: 8, message: "Do you have an affiliate program? I'm an influencer with 50k followers", category: "Partnership", platform: "instagram", expectedIntent: "question" },
    { id: 9, message: "Your app crashed and I lost all my scheduled posts! Fix this immediately!", category: "Bug Report", platform: "facebook", expectedIntent: "complaint" },
    { id: 10, message: "Just bought the Growth plan - excited to start creating content! Any tips?", category: "New Customer", platform: "tiktok", expectedIntent: "compliment" }
  ];
  
  // Editable loyalty tiers state - loaded from backend
  const [loyaltyTiers, setLoyaltyTiers] = useState([]);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  // Revenue breakdown by province
  const [revenueByProvince, setRevenueByProvince] = useState({
    "ON": { total: 12500, monthly: 4200, daily: 145, ytd: 48000, transactions: 156 },
    "BC": { total: 8200, monthly: 2800, daily: 95, ytd: 32000, transactions: 98 },
    "AB": { total: 5600, monthly: 1900, daily: 65, ytd: 22000, transactions: 67 },
    "QC": { total: 4800, monthly: 1600, daily: 55, ytd: 19000, transactions: 58 },
    "INTL": { total: 3200, monthly: 1100, daily: 38, ytd: 12500, transactions: 42 }
  });
  const navigate = useNavigate();

  // Check admin auth on mount - also handle returning from impersonation
  useEffect(() => {
    // Check if returning from impersonation
    const impersonationActive = localStorage.getItem("viraly_impersonation_active");
    const originalAdminToken = localStorage.getItem("viraly_admin_original_token");
    
    if (impersonationActive === "true" && originalAdminToken) {
      // Clear impersonation state and restore admin token
      localStorage.removeItem("viraly_impersonation_active");
      localStorage.removeItem("viraly_impersonation_id");
      localStorage.removeItem("viraly_impersonation_user");
      localStorage.removeItem("viraly_token");
      localStorage.removeItem("viraly_user");
      
      // Restore the admin token
      localStorage.setItem("viraly_admin_token", originalAdminToken);
      localStorage.removeItem("viraly_admin_original_token");
      
      toast.info("Returned from user view mode");
    }
    
    const token = localStorage.getItem("viraly_admin_token");
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAdminData();
    }
  }, [activeTab, isAuthenticated]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const response = await apiClient.post("/auth/admin/login", {
        email: adminEmail,
        password: adminPassword
      });
      localStorage.setItem("viraly_admin_token", response.data.access_token);
      setIsAuthenticated(true);
      toast.success("Welcome, Admin!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAdminLogout = () => {
    localStorage.removeItem("viraly_admin_token");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
  };

  const handleAdminForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error("Please enter your admin email");
      return;
    }
    setForgotPasswordLoading(true);
    try {
      await apiClient.post("/auth/admin/forgot-password", {
        email: forgotPasswordEmail
      });
      setForgotPasswordSent(true);
      toast.success("If an admin account exists, a reset link has been sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Fetch admin purchase notifications
  const fetchAdminNotifications = async () => {
    try {
      const adminToken = localStorage.getItem("viraly_admin_token");
      if (!adminToken) return;
      
      const headers = { Authorization: `Bearer ${adminToken}` };
      const response = await apiClient.get("/admin/notifications?limit=20", { headers });
      setAdminNotifications(response.data?.notifications || []);
      setUnreadNotificationCount(response.data?.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const adminToken = localStorage.getItem("viraly_admin_token");
      if (!adminToken) return;
      
      const headers = { Authorization: `Bearer ${adminToken}` };
      await apiClient.post(`/admin/notifications/${notificationId}/read`, {}, { headers });
      
      // Update local state
      setAdminNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadNotificationCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification read:", error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsRead = async () => {
    try {
      const adminToken = localStorage.getItem("viraly_admin_token");
      if (!adminToken) return;
      
      const headers = { Authorization: `Bearer ${adminToken}` };
      await apiClient.post("/admin/notifications/mark-all-read", {}, { headers });
      
      // Update local state
      setAdminNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadNotificationCount(0);
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark notifications as read");
    }
  };

  // Poll for new notifications every 30 seconds when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchAdminNotifications();
      const interval = setInterval(fetchAdminNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationDropdownOpen && !event.target.closest('[data-testid="admin-notification-bell"]') && !event.target.closest('[data-testid="notification-dropdown"]')) {
        setNotificationDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notificationDropdownOpen]);

  const fetchAnalyticsDetail = async (type) => {
    setAnalyticsLoading(true);
    try {
      const adminToken = localStorage.getItem("viraly_admin_token");
      const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
      
      let endpoint = "/admin/analytics/overview";
      if (type === "top-content") endpoint = "/admin/analytics/top-content";
      else if (type === "revenue") endpoint = "/admin/analytics/revenue-breakdown";
      else if (type === "engagement") endpoint = "/admin/analytics/user-engagement";
      else if (type === "platform") endpoint = "/admin/analytics/platform-performance";
      
      const response = await apiClient.get(endpoint, { headers });
      setAnalyticsDetail({ type, data: response.data });
    } catch (error) {
      console.error("Analytics fetch error:", error);
      toast.error("Failed to load analytics details");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Fetch detailed user info when user is selected
  const fetchUserDetail = async (userId) => {
    setUserDetailLoading(true);
    try {
      const adminToken = localStorage.getItem("viraly_admin_token");
      const response = await apiClient.get(`/admin/users/${userId}/detailed`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUserDetail(response.data);
    } catch (error) {
      console.error("Error fetching user detail:", error);
      // Use the basic user info we already have - NO FAKE STATS
      const basicUser = users.find(u => u.id === userId);
      setUserDetail({
        user: basicUser || { id: userId, name: "Unknown", email: "unknown", plan: "free" },
        stats: {
          total_posts: basicUser?.posts_count || 0,
          published_posts: 0,
          total_spent: 0,
          referred_users: 0
        },
        posts: [],
        transactions: [],
        social_accounts: [],
        referrer: null,
        _data_limited: true // Flag to show data is limited
      });
    } finally {
      setUserDetailLoading(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    fetchUserDetail(user.id);
  };

  const closeUserDetail = () => {
    setSelectedUser(null);
    setUserDetail(null);
  };

  const handleImpersonateUser = async (user) => {
    if (!user?.id) return;
    
    setImpersonationLoading(true);
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    
    try {
      const res = await apiClient.post(`/admin/users/${user.id}/impersonate`, {}, { headers });
      
      if (res.data?.impersonation_token) {
        // Store original admin token for return
        localStorage.setItem("viraly_admin_original_token", adminToken);
        localStorage.setItem("viraly_impersonation_active", "true");
        localStorage.setItem("viraly_impersonation_id", res.data.impersonation_id);
        localStorage.setItem("viraly_impersonation_user", JSON.stringify(res.data.target_user));
        
        // Set the impersonation token as the user token
        localStorage.setItem("viraly_token", res.data.impersonation_token);
        
        // Set the user data for AuthProvider to recognize the session
        localStorage.setItem("viraly_user", JSON.stringify(res.data.target_user));
        
        toast.success(`Now viewing as ${res.data.target_user.name || res.data.target_user.email}`, {
          description: "You'll see exactly what this user sees. Session expires in 1 hour."
        });
        
        // Redirect to dashboard
        window.location.href = "/dashboard";
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to impersonate user");
    } finally {
      setImpersonationLoading(false);
    }
  };

  // Fetch referral settings when promotions tab is active
  useEffect(() => {
    const fetchReferralSettings = async () => {
      const adminToken = localStorage.getItem("viraly_admin_token");
      const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
      try {
        const res = await apiClient.get("/admin/referral-settings/v2", { headers });
        setReferralSettings(res.data);
      } catch (err) {
        // Use defaults
      }
    };
    if (isAuthenticated && activeTab === "promotions") {
      fetchReferralSettings();
    }
  }, [isAuthenticated, activeTab]);

  const handleSaveReferralSettings = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.put("/admin/referral-settings/v2", referralSettings, { headers });
      toast.success("Referral settings saved!");
    } catch (err) {
      toast.error("Failed to save referral settings");
    }
  };

  // Fetch loyalty settings when promotions tab is active
  const fetchLoyaltySettings = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    setLoyaltyLoading(true);
    try {
      const res = await apiClient.get("/admin/loyalty-settings", { headers });
      if (res.data?.tiers) {
        // Convert from backend format to frontend format
        const tierColors = { bronze: "amber", silver: "slate", gold: "yellow", platinum: "purple", diamond: "cyan" };
        const tiersArray = Object.entries(res.data.tiers).map(([name, config], index) => ({
          id: String(index + 1),
          tier: name.charAt(0).toUpperCase() + name.slice(1),
          tierKey: name,
          threshold: config.min_spend || 0,
          discount: config.discount_percent || 0,
          color: tierColors[name] || "slate"
        }));
        // Sort by threshold
        tiersArray.sort((a, b) => a.threshold - b.threshold);
        setLoyaltyTiers(tiersArray);
      }
    } catch (err) {
      // Use defaults if API fails
      setLoyaltyTiers([
        { id: "1", tier: "Bronze", tierKey: "bronze", threshold: 100, discount: 5, color: "amber" },
        { id: "2", tier: "Silver", tierKey: "silver", threshold: 300, discount: 10, color: "slate" },
        { id: "3", tier: "Gold", tierKey: "gold", threshold: 750, discount: 15, color: "yellow" },
        { id: "4", tier: "Platinum", tierKey: "platinum", threshold: 1500, discount: 20, color: "purple" },
        { id: "5", tier: "Diamond", tierKey: "diamond", threshold: 3000, discount: 25, color: "cyan" }
      ]);
    } finally {
      setLoyaltyLoading(false);
    }
  };

  const handleSaveLoyaltyTier = async (tier) => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      // First get current settings
      const currentRes = await apiClient.get("/admin/loyalty-settings", { headers });
      const currentSettings = currentRes.data || { enabled: true, tiers: {} };
      
      // Update the specific tier
      const tierKey = tier.tierKey || tier.tier.toLowerCase();
      currentSettings.tiers = currentSettings.tiers || {};
      currentSettings.tiers[tierKey] = {
        min_spend: tier.threshold,
        discount_percent: tier.discount
      };
      
      // Save back
      await apiClient.put("/admin/loyalty-settings", currentSettings, { headers });
      
      // Update local state
      setLoyaltyTiers(prev => prev.map(t => t.id === tier.id ? { ...tier, tierKey } : t));
      toast.success(`${tier.tier} tier updated!`);
      setEditLoyaltyTierModal(null);
    } catch (err) {
      toast.error("Failed to save loyalty tier");
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === "promotions") {
      fetchLoyaltySettings();
    }
  }, [isAuthenticated, activeTab]);

  // Platform Configuration Functions (admin-only settings for credits/taxes/limits)
  const fetchPlatformConfig = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    setPlatformConfigLoading(true);
    try {
      const res = await apiClient.get("/admin/platform-config/all", { headers });
      setPlatformConfig(res.data);
    } catch (err) {
      console.log("Failed to fetch platform config");
    } finally {
      setPlatformConfigLoading(false);
    }
  };

  const savePlatformConfig = async (configType, data) => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.put(`/admin/platform-config/${configType}`, data, { headers });
      toast.success(`${configType.replace(/-/g, ' ')} updated!`);
      fetchPlatformConfig();
      setEditPlatformConfig(null);
    } catch (err) {
      toast.error(`Failed to save ${configType}`);
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === "settings") {
      fetchPlatformConfig();
    }
  }, [isAuthenticated, activeTab]);

  // Influencer Management Functions
  const fetchInfluencerData = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      const [settingsRes, influencersRes, candidatesRes, payoutsRes] = await Promise.all([
        apiClient.get("/admin/influencer-settings", { headers }).catch(() => null),
        apiClient.get("/admin/influencers", { headers }).catch(() => null),
        apiClient.get("/admin/influencer-candidates", { headers }).catch(() => null),
        apiClient.get("/admin/influencer-payouts", { headers }).catch(() => null)
      ]);
      
      if (settingsRes?.data) setInfluencerSettings(settingsRes.data);
      if (influencersRes?.data) setInfluencers(influencersRes.data.influencers || []);
      if (candidatesRes?.data) setInfluencerCandidates(candidatesRes.data.candidates || []);
      if (payoutsRes?.data) {
        setPayoutRequests(payoutsRes.data.payouts || []);
        setPayoutSummary(payoutsRes.data.summary || {});
      }
    } catch (err) {
      console.log("Failed to fetch influencer data");
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === "promotions") {
      fetchInfluencerData();
      fetchCoupons();
      fetchAnnouncements();
      fetchSuggestedPromotions();
    }
  }, [isAuthenticated, activeTab]);

  // Fetch AI-Suggested Promotions
  const fetchSuggestedPromotions = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      const res = await apiClient.get("/admin/promotions/suggestions", { headers });
      setSuggestedPromotions(res.data.suggestions || []);
    } catch (err) {
      console.log("Failed to fetch promotion suggestions");
    }
  };

  // Create promotion from suggestion
  const createPromotionFromSuggestion = async (suggestion) => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.post("/admin/promotions/v2", {
        name: suggestion.event_name.toUpperCase().replace(/\s+/g, ""),
        discount_type: "percentage",
        discount_value: suggestion.suggested_discount,
        display_type: "banner",
        display_location: "all_pages",
        start_date: suggestion.suggested_start,
        end_date: suggestion.suggested_end,
        target_audience: "all",
        target_locales: suggestion.target_locales,
        locale_event_name: suggestion.event_name,
        applies_to: ["subscription", "payg", "video_addon", "regen_pack"],
        send_email: true,
        priority: "normal",
        message: suggestion.suggested_message,
        is_active: true,
        is_suggested: true
      }, { headers });
      toast.success(`Promotion created for ${suggestion.event_name}!`);
      fetchSuggestedPromotions(); // Refresh list
    } catch (err) {
      toast.error("Failed to create promotion");
    }
  };

  // Fetch Coupons
  const fetchCoupons = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      const res = await apiClient.get("/admin/coupons", { headers });
      setCoupons(res.data.coupons || []);
    } catch (err) {
      console.log("Failed to fetch coupons");
    }
  };

  // Fetch Announcements
  const fetchAnnouncements = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      const res = await apiClient.get("/admin/announcements", { headers });
      setAnnouncements(res.data.announcements || []);
    } catch (err) {
      console.log("Failed to fetch announcements");
    }
  };

  // Fetch Service Alert Settings
  const fetchServiceAlertSettings = async () => {
    setServiceAlertLoading(true);
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      const res = await apiClient.get("/admin/service-alert-settings", { headers });
      setServiceAlertSettings(res.data);
    } catch (err) {
      console.log("Failed to fetch service alert settings");
    } finally {
      setServiceAlertLoading(false);
    }
  };

  // Save Service Alert Settings
  const saveServiceAlertSettings = async () => {
    setServiceAlertSaving(true);
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.put("/admin/service-alert-settings", serviceAlertSettings, { headers });
      toast.success("Service alert settings saved!");
    } catch (err) {
      toast.error("Failed to save service alert settings");
    } finally {
      setServiceAlertSaving(false);
    }
  };

  // Fetch Viraly Social Accounts (for BOT auto-reply and content posting)
  const fetchViralySocialAccounts = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      const res = await apiClient.get("/admin/viraly-social-accounts", { headers });
      setViralySocialAccounts(res.data);
    } catch (err) {
      console.log("Failed to fetch Viraly social accounts");
    }
  };

  // Save Viraly Social Accounts
  const saveViralySocialAccounts = async () => {
    setSavingViralySocial(true);
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.put("/admin/viraly-social-accounts", viralySocialAccounts, { headers });
      toast.success("Viraly social accounts saved");
    } catch (err) {
      toast.error("Failed to save Viraly social accounts");
    } finally {
      setSavingViralySocial(false);
    }
  };

  // Update a specific Viraly social account handle
  const updateViralySocialHandle = (platform, field, value) => {
    setViralySocialAccounts(prev => ({
      ...prev,
      accounts: {
        ...prev.accounts,
        [platform]: {
          ...prev.accounts[platform],
          [field]: value
        }
      }
    }));
  };

  // Toggle auto-reply for a specific platform
  const toggleViralySocialAutoReply = (platform) => {
    setViralySocialAccounts(prev => ({
      ...prev,
      accounts: {
        ...prev.accounts,
        [platform]: {
          ...prev.accounts[platform],
          auto_reply_enabled: !prev.accounts[platform]?.auto_reply_enabled
        }
      }
    }));
  };

  // ======== AUTO-REPLY BOT FUNCTIONS ========
  
  // Fetch Auto-Reply Settings
  const fetchAutoReplySettings = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      const res = await apiClient.get("/auto-reply/settings", { headers });
      if (res.data) {
        setAutoReplySettings(res.data);
      }
    } catch (err) {
      console.log("Failed to fetch auto-reply settings");
    }
  };

  // Save Auto-Reply Settings
  const saveAutoReplySettings = async () => {
    setAutoReplySaving(true);
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.put("/auto-reply/settings", autoReplySettings, { headers });
      toast.success("Auto-Reply settings saved!");
    } catch (err) {
      toast.error("Failed to save auto-reply settings");
    } finally {
      setAutoReplySaving(false);
    }
  };

  // Toggle Auto-Reply Platform
  const toggleAutoReplyPlatform = (platform, type) => {
    setAutoReplySettings(prev => ({
      ...prev,
      platforms: {
        ...prev.platforms,
        [platform]: {
          ...prev.platforms[platform],
          [type]: !prev.platforms[platform][type]
        }
      }
    }));
  };

  // Test Auto-Reply
  const testAutoReply = async () => {
    if (!autoReplyTestMessage.trim()) {
      toast.error("Please enter a test message");
      return;
    }
    setAutoReplyTesting(true);
    setAutoReplyTestResult(null);
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      const res = await apiClient.post("/auto-reply/test", {
        message: autoReplyTestMessage,
        platform: autoReplyTestPlatform,
        tone: autoReplySettings.response_tone
      }, { headers });
      // Backend returns generated_response, not response
      setAutoReplyTestResult({
        response: res.data.generated_response,
        sentiment: res.data.sentiment,
        intent: res.data.intent,
        confidence: res.data.confidence
      });
      toast.success("Response generated!");
    } catch (err) {
      toast.error("Failed to generate response");
    } finally {
      setAutoReplyTesting(false);
    }
  };

  // Run Auto-Reply Simulation
  const runAutoReplySimulation = async () => {
    setAutoReplyRunningSimulation(true);
    setAutoReplySimulationResults([]);
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    
    for (let i = 0; i < AUTO_REPLY_SIMULATION_MESSAGES.length; i++) {
      setAutoReplyCurrentSimIndex(i);
      const sim = AUTO_REPLY_SIMULATION_MESSAGES[i];
      try {
        const res = await apiClient.post("/auto-reply/test", {
          message: sim.message,
          platform: sim.platform,
          tone: autoReplySettings.response_tone
        }, { headers });
        setAutoReplySimulationResults(prev => [...prev, {
          ...sim,
          response: res.data.generated_response,
          sentiment: res.data.sentiment,
          intent: res.data.intent,
          confidence: res.data.confidence,
          success: true
        }]);
      } catch (err) {
        setAutoReplySimulationResults(prev => [...prev, {
          ...sim,
          response: "Error generating response",
          success: false
        }]);
      }
      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    setAutoReplyCurrentSimIndex(-1);
    setAutoReplyRunningSimulation(false);
    toast.success("Simulation complete!");
  };

  // Add blacklist word
  const addAutoReplyBlacklistWord = () => {
    if (autoReplyNewBlacklistWord.trim() && !autoReplySettings.blacklist_words.includes(autoReplyNewBlacklistWord.trim().toLowerCase())) {
      setAutoReplySettings(prev => ({
        ...prev,
        blacklist_words: [...prev.blacklist_words, autoReplyNewBlacklistWord.trim().toLowerCase()]
      }));
      setAutoReplyNewBlacklistWord('');
    }
  };

  // Remove blacklist word
  const removeAutoReplyBlacklistWord = (word) => {
    setAutoReplySettings(prev => ({
      ...prev,
      blacklist_words: prev.blacklist_words.filter(w => w !== word)
    }));
  };

  // ======== END AUTO-REPLY BOT FUNCTIONS ========

  const handleSaveInfluencerSettings = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.put("/admin/influencer-settings", influencerSettings, { headers });
      toast.success("Influencer settings saved!");
    } catch (err) {
      toast.error("Failed to save influencer settings");
    }
  };

  const handleUpgradeToInfluencer = async (userId) => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.post(`/admin/influencers/${userId}/upgrade`, {}, { headers });
      toast.success("User upgraded to influencer!");
      fetchInfluencerData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upgrade user");
    }
  };

  const handlePayoutAction = async (payoutId, action, notes = "", txRef = "") => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.put(`/admin/influencer-payouts/${payoutId}`, {
        status: action,
        admin_notes: notes,
        transaction_reference: txRef
      }, { headers });
      toast.success(`Payout ${action}!`);
      setSelectedPayout(null);
      fetchInfluencerData();
    } catch (err) {
      toast.error("Failed to update payout");
    }
  };

  // Coupon CRUD handlers
  const handleCreateCoupon = async (couponData) => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.post("/admin/coupons", couponData, { headers });
      toast.success("Coupon created!");
      setCreateCouponModal(false);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create coupon");
    }
  };

  const handleUpdateCoupon = async (couponId, couponData) => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.put(`/admin/coupons/${couponId}`, couponData, { headers });
      toast.success("Coupon updated!");
      setEditCouponModal(null);
      fetchCoupons();
    } catch (err) {
      toast.error("Failed to update coupon");
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.delete(`/admin/coupons/${couponId}`, { headers });
      toast.success("Coupon deleted!");
      fetchCoupons();
    } catch (err) {
      toast.error("Failed to delete coupon");
    }
  };

  // Announcement CRUD handlers
  const handleCreateAnnouncement = async (data) => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.post("/admin/announcements", data, { headers });
      toast.success("Announcement created!");
      setCreateAnnouncementModal(false);
      fetchAnnouncements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create announcement");
    }
  };

  const handleUpdateAnnouncement = async (annId, data) => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.put(`/admin/announcements/${annId}`, data, { headers });
      toast.success("Announcement updated!");
      setEditAnnouncementModal(null);
      fetchAnnouncements();
    } catch (err) {
      toast.error("Failed to update announcement");
    }
  };

  const handleSaveNotificationThresholds = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.put("/admin/user-notification-settings", notificationThresholds, { headers });
      toast.success("Notification thresholds saved!");
    } catch (err) {
      toast.error("Failed to save notification settings");
    }
  };

  // Publish Retry Settings handlers
  const handleSavePublishRetrySettings = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    setPublishRetrySettingsSaving(true);
    try {
      await apiClient.put("/admin/publish-retry-settings", publishRetrySettings, { headers });
      toast.success("Publish retry settings saved!");
    } catch (err) {
      toast.error("Failed to save publish retry settings");
    } finally {
      setPublishRetrySettingsSaving(false);
    }
  };

  const handleTriggerPublishRetry = async () => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      const res = await apiClient.post("/admin/publish-retry/trigger-now", {}, { headers });
      toast.success(`Retry triggered: ${res.data.stats?.succeeded || 0} succeeded, ${res.data.stats?.failed || 0} failed`);
      // Refresh failures list
      const failuresRes = await apiClient.get("/admin/publish-failures?limit=50", { headers });
      setPublishFailures(failuresRes.data);
    } catch (err) {
      toast.error("Failed to trigger retry service");
    }
  };

  const handleResolvePublishFailure = async (failureId, resolution) => {
    const adminToken = localStorage.getItem("viraly_admin_token");
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    try {
      await apiClient.post(`/admin/publish-failures/${failureId}/resolve`, { resolution }, { headers });
      toast.success("Failure marked as resolved");
      // Refresh failures list
      const failuresRes = await apiClient.get("/admin/publish-failures?limit=50", { headers });
      setPublishFailures(failuresRes.data);
    } catch (err) {
      toast.error("Failed to resolve failure");
    }
  };

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const adminToken = localStorage.getItem("viraly_admin_token");
      const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
      
      if (activeTab === "overview") {
        // Try to fetch real stats
        try {
          const response = await apiClient.get("/admin/stats", { headers });
          setStats(response.data);
        } catch {
          // Set empty stats if API fails - no mock data
          setStats({
            total_users: 0,
            active_users: 0,
            total_posts: 0,
            published_posts: 0,
            total_revenue: 0
          });
        }
      } else if (activeTab === "users") {
        try {
          const response = await apiClient.get("/admin/users", { headers });
          // API returns {users: [...], total: N}
          setUsers(response.data?.users || response.data || []);
        } catch {
          setUsers([]);
        }
      } else if (activeTab === "support-tickets") {
        try {
          const response = await apiClient.get("/admin/support/tickets", { headers });
          setSupportTickets(response.data.tickets || []);
          setTicketStats(response.data.counts || { open: 0, pending: 0, resolved: 0, total: 0 });
        } catch {
          // Mock data
          setSupportTickets([
            { id: "ticket-1", user_email: "john@example.com", user_name: "John Doe", category: "bug", message: "Video generation failed after 50%", priority: "medium", status: "open", created_at: "2024-03-10T10:00:00Z" },
            { id: "ticket-2", user_email: "jane@example.com", user_name: "Jane Smith", category: "billing", message: "I was charged twice for my subscription", priority: "high", status: "open", created_at: "2024-03-09T15:30:00Z" }
          ]);
          setTicketStats({ open: 2, pending: 1, resolved: 5, total: 8 });
        }
      } else if (activeTab === "etransfers") {
        try {
          const response = await apiClient.get("/admin/etransfers/pending", { headers });
          setPendingEtransfers(response.data.etransfers || []);
          setEtransferStats(response.data.stats || { pending: 0, verified: 0, rejected: 0, total_pending_amount: 0 });
        } catch {
          // Mock data for demo
          setPendingEtransfers([
            { 
              id: "etransfer_20240310_abc123", 
              user_email: "john@example.com", 
              user_name: "John Doe",
              product_name: "Growth Plan",
              base_amount: 49,
              total_amount: 55.37,
              tax_amount: 6.37,
              province: "ON",
              reference: "VIR-ABC123XYZ",
              status: "pending_verification",
              created_at: "2024-03-10T14:30:00Z"
            },
            { 
              id: "etransfer_20240309_def456", 
              user_email: "jane@example.com", 
              user_name: "Jane Smith",
              product_name: "Video Pack (10)",
              base_amount: 12,
              total_amount: 13.56,
              tax_amount: 1.56,
              province: "BC",
              reference: "VIR-DEF456ABC",
              status: "pending_verification",
              created_at: "2024-03-09T09:15:00Z"
            }
          ]);
          setEtransferStats({ pending: 2, verified: 15, rejected: 1, total_pending_amount: 68.93 });
        }
      } else if (activeTab === "health-monitor") {
        // Fetch health status, history, thresholds, alerts, and service alert settings
        try {
          const [statusRes, thresholdsRes, alertsRes] = await Promise.all([
            apiClient.get("/admin/health/status", { headers }),
            apiClient.get("/admin/health/thresholds", { headers }),
            apiClient.get("/admin/health/alerts", { headers })
          ]);
          setHealthStatus(statusRes.data.current);
          setHealthHistory(statusRes.data.history || []);
          setHealthThresholds(thresholdsRes.data);
          setHealthAlerts(alertsRes.data.alerts || []);
        } catch {
          // Keep defaults
          setHealthStatus({ overall: "unknown", last_check: new Date().toISOString(), checks: [] });
          setHealthHistory([]);
        }
        // Fetch service alert settings for customer notifications
        fetchServiceAlertSettings();
        
        // Fetch complete health check history (paginated)
        setHealthCheckHistoryLoading(true);
        try {
          const historyRes = await apiClient.get(`/admin/health/history?page=${healthCheckHistoryPage}&limit=20`, { headers });
          setHealthCheckHistory(historyRes.data.checks || []);
          setHealthCheckHistoryTotal(historyRes.data.total || 0);
        } catch {
          setHealthCheckHistory([]);
        } finally {
          setHealthCheckHistoryLoading(false);
        }
        
        // Fetch System Learning data
        setLoadingSystemLearning(true);
        try {
          const [insightsRes, recurringRes, latencyRes, alertsRes, breakdownsRes, featureRes, externalApiRes, perUserRes, impactRes] = await Promise.all([
            apiClient.get("/admin/health/system-learning/insights", { headers }),
            apiClient.get("/admin/health/system-learning/recurring-issues", { headers }),
            apiClient.get("/admin/health/system-learning/latency?hours=24", { headers }),
            apiClient.get("/admin/health/system-learning/alerts?acknowledged=false", { headers }),
            apiClient.get("/admin/health/system-learning/breakdowns?include_resolved=false", { headers }),
            apiClient.get("/admin/health/system-learning/feature-usage?days=7", { headers }),
            apiClient.get("/admin/health/system-learning/external-apis?days=7", { headers }),
            apiClient.get("/admin/health/system-learning/per-user-latency?hours=24", { headers }),
            apiClient.get("/admin/health/system-learning/user-impact?days=30", { headers })
          ]);
          setSystemLearningInsights(insightsRes.data.insights);
          setRecurringIssues(recurringRes.data.recurring_issues || []);
          setLatencyInsights(latencyRes.data.latency_insights);
          setSystemLearningAlerts(alertsRes.data.alerts || []);
          setActiveBreakdowns(breakdownsRes.data.breakdowns || []);
          setFeatureUsageInsights(featureRes.data.insights);
          setExternalApiInsights(externalApiRes.data.insights);
          setPerUserLatencyInsights(perUserRes.data.insights);
          setUserImpactReport(impactRes.data.report);
        } catch (error) {
          console.error("System learning fetch error:", error);
          // Keep defaults
        } finally {
          setLoadingSystemLearning(false);
        }
      } else if (activeTab === "system-logs") {
        // Fetch system logs
        try {
          const response = await apiClient.get("/admin/logs", { headers });
          setSystemLogs(response.data.logs || []);
        } catch {
          // Mock data
          setSystemLogs([
            { id: 1, timestamp: new Date().toISOString(), level: "info", message: "User logged in", user: "john@example.com", endpoint: "/api/auth/login" },
            { id: 2, timestamp: new Date(Date.now() - 60000).toISOString(), level: "info", message: "Content generated successfully", user: "jane@example.com", endpoint: "/api/content/generate-single" },
            { id: 3, timestamp: new Date(Date.now() - 120000).toISOString(), level: "warning", message: "Rate limit approaching", user: "system", endpoint: "/api/content/video/generate" },
            { id: 4, timestamp: new Date(Date.now() - 180000).toISOString(), level: "error", message: "Video generation failed: Runway API timeout", user: "mike@example.com", endpoint: "/api/content/video/generate" },
            { id: 5, timestamp: new Date(Date.now() - 240000).toISOString(), level: "info", message: "Payment processed via PayPal", user: "sarah@example.com", endpoint: "/api/payments/paypal/complete" }
          ]);
        }
      } else if (activeTab === "pricing") {
        // Fetch pricing plans and bundles
        try {
          const [plansRes, bundlesRes] = await Promise.all([
            apiClient.get("/settings/plans"),
            apiClient.get("/settings/bundles")
          ]);
          // Add interval field if not present
          const plans = (plansRes.data || []).map(p => ({...p, interval: 'month'}));
          setPricingPlans(plans);
          setBundles(bundlesRes.data || []);
        } catch {
          setPricingPlans([
            { id: "starter", name: "Starter", price: 29, posts: 50, videos: 0, regen_credits: 5, radar_urls: 3, is_active: true, interval: 'month' },
            { id: "growth", name: "Growth", price: 49, posts: 100, videos: 5, regen_credits: 10, radar_urls: 5, is_active: true, interval: 'month' },
            { id: "pro", name: "Pro", price: 99, posts: 200, videos: 10, regen_credits: 20, radar_urls: 10, is_active: true, interval: 'month' }
          ]);
          setBundles([
            { id: "mini", name: "Mini Bundle", price: 5, posts: 5, type: "payg" },
            { id: "small", name: "Small Bundle", price: 12, posts: 15, type: "payg" },
            { id: "medium", name: "Medium Bundle", price: 22, posts: 30, type: "payg" },
            { id: "video_basic", name: "Video Basic", price: 5, videos: 3, type: "video_addon" },
            { id: "video_standard", name: "Video Standard", price: 7, videos: 5, type: "video_addon" },
            { id: "video_bulk", name: "Video Bulk", price: 12, videos: 10, type: "video_addon" }
          ]);
        }
      } else if (activeTab === "revenue") {
        // Fetch real revenue data from backend
        try {
          const response = await apiClient.get("/admin/revenue", { headers });
          setRevenueStats(response.data);
        } catch {
          // Empty state if no data
          setRevenueStats({
            total_revenue: 0,
            total_tax_collected: 0,
            total_gateway_fees: 0,
            net_revenue: 0,
            transactions_count: 0,
            avg_transaction: 0,
            by_province: {},
            by_product: {},
            monthly_trend: []
          });
        }
      } else if (activeTab === "engagement") {
        // Fetch real engagement data from backend
        try {
          const response = await apiClient.get("/admin/engagement", { headers });
          setEngagementStats(response.data);
        } catch {
          // Empty state if no data
          setEngagementStats({
            overview: { total_users: 0, active_users: 0, engagement_rate: 0, total_posts: 0, posts_this_week: 0, posts_today: 0 },
            content_stats: { total_generations: 0, video_generations: 0, published_posts: 0, scheduled_posts: 0 },
            daily_trend: [],
            platform_distribution: {}
          });
        }
      } else if (activeTab === "email-analytics") {
        // Fetch email analytics
        try {
          const response = await apiClient.get("/admin/email-analytics", { headers });
          setEmailStats(response.data.stats || { total_sent: 0, total_opens: 0, total_clicks: 0, unique_opens: 0, open_rate: 0, click_rate: 0 });
          setEmailActivity(response.data.recent_activity || []);
        } catch {
          // Use placeholder data
          setEmailStats({ total_sent: 0, total_opens: 0, total_clicks: 0, unique_opens: 0, open_rate: 0, click_rate: 0 });
          setEmailActivity([]);
        }
      } else if (activeTab === "gifts") {
        // Fetch gift packs data
        try {
          const response = await apiClient.get("/admin/gift-stats", { headers });
          setGiftStats(response.data.stats || { total_gifts: 0, redeemed: 0, pending: 0, revenue: 0 });
          setGiftActivity(response.data.recent_gifts || []);
        } catch {
          // Fetch directly from gift packs collection
          try {
            const allGifts = await apiClient.get("/admin/gifts", { headers });
            const gifts = allGifts.data || [];
            const stats = {
              total_gifts: gifts.length,
              redeemed: gifts.filter(g => g.status === "redeemed").length,
              pending: gifts.filter(g => g.status === "paid").length,
              revenue: gifts.filter(g => g.status !== "pending_payment").reduce((sum, g) => sum + (g.pack_price || 0), 0)
            };
            setGiftStats(stats);
            setGiftActivity(gifts.slice(0, 10));
          } catch {
            setGiftStats({ total_gifts: 0, redeemed: 0, pending: 0, revenue: 0 });
            setGiftActivity([]);
          }
        }
      } else if (activeTab === "transactions") {
        try {
          const response = await apiClient.get("/admin/transactions", { headers });
          // API returns {transactions: [...], total: N}
          setTransactions(response.data?.transactions || response.data || []);
        } catch {
          setTransactions([
            { id: "1", user_email: "john@example.com", product: "Growth Plan", subtotal: 49, tax: 6.37, total: 57.18, province: "ON", status: "completed", date: "2024-03-10" },
            { id: "2", user_email: "jane@example.com", product: "Pro Plan", subtotal: 99, tax: 14.85, total: 116.94, province: "NS", status: "completed", date: "2024-03-10" },
            { id: "3", user_email: "bob@intl.com", product: "Small Bundle", subtotal: 12, tax: 0, total: 12.35, province: null, status: "completed", date: "2024-03-09" }
          ]);
        }
      } else if (activeTab === "settings") {
        try {
          const response = await apiClient.get("/admin/settings", { headers });
          setSettings(response.data || {});
        } catch {
          setSettings({
            trial_days: 7,
            trial_posts: 5,
            watermark_enabled: true,
            watermark_text: "Made with Viraly",
            email_notifications: true,
            maintenance_mode: false
          });
        }
        // Fetch notification thresholds
        try {
          const notifRes = await apiClient.get("/admin/user-notification-settings", { headers });
          setNotificationThresholds(notifRes.data);
        } catch {
          // Keep defaults
        }
        // Fetch free access accounts
        try {
          const freeAccessRes = await apiClient.get("/admin/free-access-accounts", { headers });
          setFreeAccessAccounts(freeAccessRes.data.accounts || []);
        } catch {
          // Keep empty
        }
        // Fetch Viraly social accounts for BOT
        try {
          const socialRes = await apiClient.get("/admin/viraly-social-accounts", { headers });
          setViralySocialAccounts(socialRes.data);
        } catch {
          // Keep defaults
        }
        // Fetch Feature Toggles
        setFeatureTogglesLoading(true);
        try {
          const togglesRes = await apiClient.get("/admin/feature-toggles", { headers });
          setFeatureToggles(togglesRes.data.toggles || {});
        } catch {
          // Set defaults
          setFeatureToggles({
            vira_ai_assistant: true,
            video_generation: true,
            post_generation: true,
            url_to_post: true,
            brand_learning: true,
            viral_simulator: true,
            auto_reply: true,
            scheduled_posting: true,
            multi_profile: true,
            push_notifications: false,
            webauthn_login: true,
            etransfer_payments: true,
            paypal_payments: true,
            referral_system: true,
            gift_packs: true,
            loyalty_program: true,
            competitor_monitoring: true,
            image_input: true,
            text_input: true,
            voice_input: true,
            video_input: true,
            content_radar: false,
            group_finder: false,
            quote_across: false
          });
        } finally {
          setFeatureTogglesLoading(false);
        }
      } else if (activeTab === "communications") {
        // Fetch announcements
        try {
          const annRes = await apiClient.get("/admin/announcements", { headers });
          setAnnouncements(annRes.data.announcements || []);
        } catch {
          setAnnouncements([]);
        }
        
        // Fetch notification templates from real backend
        try {
          const templatesRes = await apiClient.get("/admin/notification-templates", { headers });
          const templates = templatesRes.data.templates || {};
          const byCategory = templatesRes.data.by_category || {};
          
          // Convert to array format for display
          const templateArray = Object.entries(templates).map(([id, t]) => ({
            id,
            name: t.name,
            description: `${t.category} notification`,
            status: "active",
            subject: t.email_subject || t.push_title || "",
            body: t.email_body || t.push_body || "",
            push_title: t.push_title,
            push_body: t.push_body,
            email_subject: t.email_subject,
            email_body: t.email_body,
            category: t.category,
            editable: t.editable
          }));
          
          setEmailTemplates(templateArray);
          setNotificationTemplatesByCategory(byCategory);
        } catch {
          setEmailTemplates([]);
        }
      } else if (activeTab === "api-balance") {
        setApiBalanceLoading(true);
        try {
          const response = await apiClient.get("/admin/api-balances", { headers });
          setApiBalances(response.data);
        } catch (err) {
          // Mock data for demo
          setApiBalances({
            emergent_llm: {
              available: true,
              status: "configured",
              top_up_url: "https://app.emergentintegrations.ai/profile",
              message: "Key configured - check balance in Emergent dashboard"
            },
            runway_ml: {
              available: true,
              status: "active",
              top_up_url: "https://dev.runwayml.com/dashboard",
              message: "API key active - check credits in Runway dashboard"
            },
            usage_30d: {
              videos: { total_videos: 45, completed: 42, processing: 2, failed: 1 },
              posts: 1250,
              estimated_cost: { videos: 21.00, posts: 55.00 }
            },
            last_checked: new Date().toISOString()
          });
        }
        
        // Also fetch cost tracking
        try {
          const costResponse = await apiClient.get("/admin/api-cost-tracking", { headers });
          setApiCostTracking(costResponse.data);
        } catch {
          // Fallback mock data for cost tracking
          setApiCostTracking({
            period: "last_30_days",
            total_estimated: 76.00,
            total_actual: 78.50,
            total_delta: 2.50,
            delta_percent: 3.3,
            operations: [
              { type: "image_generation", count: 1250, estimated_cost: 55.00, actual_cost: 56.20, delta: 1.20, delta_percent: 2.2 },
              { type: "video_generation", count: 45, estimated_cost: 21.00, actual_cost: 22.30, delta: 1.30, delta_percent: 6.2 },
            ],
            cost_status: "normal"
          });
        } finally {
          setApiBalanceLoading(false);
        }
      } else if (activeTab === "auto-reply") {
        // Fetch Auto-Reply Settings
        setAutoReplyLoading(true);
        try {
          const response = await apiClient.get("/auto-reply/settings", { headers });
          if (response.data) {
            setAutoReplySettings(response.data);
          }
        } catch {
          // Keep defaults
        } finally {
          setAutoReplyLoading(false);
        }
      } else if (activeTab === "content-playbook") {
        // Fetch Content Playbook stats and queue
        try {
          const [statsRes, queueRes] = await Promise.all([
            apiClient.get("/admin/playbook/stats", { headers }).catch(() => null),
            apiClient.get("/admin/playbook/viraly-posts", { headers }).catch(() => null)
          ]);
          
          if (statsRes?.data) {
            setPlaybookStats(prev => ({
              ...prev,
              active_users: statsRes.data.active_users || 0,
              posts_today: statsRes.data.posts_today || 0
            }));
          }
          if (queueRes?.data) {
            setPlaybookPostsQueue(queueRes.data.posts || []);
          }
        } catch {
          // Keep defaults - tab will show mock UI
        }
      } else if (activeTab === "content") {
        // Fetch Content Moderation data
        setModerationLoading(true);
        try {
          const res = await apiClient.get("/admin/content-moderation", { headers });
          setFlaggedContent(res.data.flagged_content || []);
          setModerationStats(res.data.stats || { pending: 0, approved: 0, rejected: 0, auto_flagged: 0 });
        } catch {
          setFlaggedContent([]);
        } finally {
          setModerationLoading(false);
        }
      } else if (activeTab === "team") {
        // Fetch Team & Roles data
        setRolesLoading(true);
        try {
          const [rolesRes, teamRes] = await Promise.all([
            apiClient.get("/admin/roles", { headers }),
            apiClient.get("/admin/team-members", { headers })
          ]);
          setRoles(rolesRes.data.roles || []);
          setAvailablePermissions(rolesRes.data.available_permissions || {});
          setTeamMembersData(teamRes.data.team_members || []);
        } catch {
          setRoles([]);
          setTeamMembersData([]);
        } finally {
          setRolesLoading(false);
        }
      } else if (activeTab === "documents") {
        // Fetch Document Generation History
        setDocumentsLoading(true);
        try {
          const res = await apiClient.get("/admin/documents/history", { headers });
          setDocumentHistory(res.data.documents || []);
        } catch {
          setDocumentHistory([]);
        } finally {
          setDocumentsLoading(false);
        }
      } else if (activeTab === "compliance") {
        // Fetch Compliance Settings
        setComplianceLoading(true);
        try {
          const res = await apiClient.get("/admin/compliance-settings", { headers });
          setComplianceSettings(res.data.settings || {
            two_factor_admin: true,
            data_encryption: true,
            ssl_encryption: true,
            cookie_consent: true,
            ip_logging: true,
            auto_logout: false,
            session_timeout_minutes: 60
          });
        } catch {
          // Keep defaults
        } finally {
          setComplianceLoading(false);
        }
        
        // Fetch Audit Logs with pagination
        setAuditLogsLoading(true);
        try {
          const logsRes = await apiClient.get(`/admin/audit-logs?page=${auditLogPage}&limit=20&search=${auditLogSearch}`, { headers });
          setAuditLogs(logsRes.data.logs || []);
          setAuditLogTotal(logsRes.data.total || 0);
        } catch {
          setAuditLogs([]);
        } finally {
          setAuditLogsLoading(false);
        }
      } else if (activeTab === "b2b-overview") {
        // Fetch B2B Overview Stats
        setB2bLoading(true);
        try {
          const statsRes = await apiClient.get("/b2b/stats", { headers });
          setB2bStats(statsRes.data);
        } catch {
          setB2bStats({ partners: { total: 0, active: 0 }, enterprises: { total: 0, active: 0 }, merchants: { total: 0, active: 0 }, inquiries: { pending: 0 } });
        }
        try {
          const plansRes = await apiClient.get("/b2b/plans", { headers });
          setB2bPlans(plansRes.data.plans || {});
        } catch {
          setB2bPlans({});
        }
        try {
          const tiersRes = await apiClient.get("/b2b/partner-tiers", { headers });
          setPartnerTiers(tiersRes.data.tiers || {});
        } catch {
          setPartnerTiers({});
        }
        try {
          const payoutsRes = await apiClient.get("/b2b/partner-payouts", { headers });
          setPartnerPayouts(payoutsRes.data.payouts || []);
          setPartnerPayoutSummary(payoutsRes.data.summary || {});
        } catch {
          setPartnerPayouts([]);
          setPartnerPayoutSummary({});
        }
        setB2bLoading(false);
      } else if (activeTab === "b2b-partners") {
        setB2bLoading(true);
        try {
          const res = await apiClient.get("/b2b/partners", { headers });
          setB2bPartners(res.data.partners || []);
        } catch {
          setB2bPartners([]);
        }
        setB2bLoading(false);
      } else if (activeTab === "b2b-enterprises") {
        setB2bLoading(true);
        try {
          const [enterprisesRes, onboardingRes] = await Promise.all([
            apiClient.get("/b2b/enterprises", { headers }),
            apiClient.get("/b2b/enterprises/onboarding-summary", { headers })
          ]);
          setB2bEnterprises(enterprisesRes.data.enterprises || []);
          setB2bOnboardingSummary(onboardingRes.data);
        } catch {
          setB2bEnterprises([]);
          setB2bOnboardingSummary(null);
        }
        setB2bLoading(false);
      } else if (activeTab === "b2b-inquiries") {
        setB2bLoading(true);
        try {
          const res = await apiClient.get("/b2b/inquiries", { headers });
          setB2bInquiries(res.data.inquiries || []);
        } catch {
          setB2bInquiries([]);
        }
        setB2bLoading(false);
      } else if (activeTab === "b2b-tech-support") {
        setB2bLoading(true);
        try {
          const [escalationsRes, conversationsRes] = await Promise.all([
            apiClient.get("/b2b/support/escalations", { headers }),
            apiClient.get("/b2b/support/conversations", { headers })
          ]);
          setB2bEscalations(escalationsRes.data.escalations || []);
          setB2bConversations(conversationsRes.data.conversations || []);
        } catch {
          setB2bEscalations([]);
          setB2bConversations([]);
        }
        setB2bLoading(false);
      } else if (activeTab === "b2b-api-logs") {
        setB2bLoading(true);
        try {
          const [logsRes, statsRes] = await Promise.all([
            apiClient.get("/b2b/api-logs?limit=50", { headers }),
            apiClient.get("/b2b/api-logs/stats", { headers })
          ]);
          setB2bApiLogs(logsRes.data.logs || []);
          setB2bApiLogStats(statsRes.data);
        } catch {
          setB2bApiLogs([]);
        }
        setB2bLoading(false);
      } else if (activeTab === "b2b-settings") {
        setB2bLoading(true);
        try {
          const res = await apiClient.get("/b2b/settings", { headers });
          setB2bSettings(res.data);
        } catch {
          // Keep defaults
        }
        setB2bLoading(false);
      } else if (activeTab === "publish-retry") {
        // Fetch publish retry settings and failures
        try {
          const [settingsRes, failuresRes] = await Promise.all([
            apiClient.get("/admin/publish-retry-settings", { headers }),
            apiClient.get("/admin/publish-failures?limit=50", { headers })
          ]);
          setPublishRetrySettings(settingsRes.data);
          setPublishFailures(failuresRes.data);
        } catch (error) {
          console.error("Failed to fetch publish retry data:", error);
        }
      } else if (activeTab === "viraly-social") {
        // Fetch Viraly Internal Social Accounts data
        setViralySocialLoading(true);
        try {
          const [configRes, acctsRes, draftsRes, postsRes] = await Promise.all([
            apiClient.get("/admin/viraly-social", { headers }),
            apiClient.get("/admin/viraly-social/accounts", { headers }),
            apiClient.get("/admin/viraly-social/drafts", { headers }),
            apiClient.get("/admin/viraly-social/posts", { headers })
          ]);
          setViralySocialConfig(configRes.data.config || null);
          setViralySocialAccts(acctsRes.data.accounts || []);
          setViralySocialDrafts(draftsRes.data.drafts || []);
          setViralySocialPosts(postsRes.data.posts || []);
        } catch (error) {
          console.error("Failed to fetch Viraly social data:", error);
        } finally {
          setViralySocialLoading(false);
        }
      }
    } catch (error) {
      console.error("Admin data fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Categorized sidebar sections for better organization
  const sidebarSections = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: BarChart3,
      items: [
        { id: "overview", label: "Overview", icon: BarChart3 }
      ]
    },
    {
      id: "userManagement",
      label: "User Management",
      icon: Users,
      items: [
        { id: "users", label: "Users", icon: Users },
        { id: "support-tickets", label: "Support Tickets", icon: LifeBuoy },
        { id: "team", label: "Team & Roles", icon: UserCog }
      ]
    },
    {
      id: "aiAutomation",
      label: "AI & Automation",
      icon: Bot,
      items: [
        { id: "content-playbook", label: "Content Playbook", icon: Zap, isHighlighted: true },
        { id: "auto-reply", label: "VIRA (Bot)", icon: Bot, isHighlighted: true },
        { id: "viraly-social", label: "Viraly Social", icon: Share2, isHighlighted: true },
        { id: "content", label: "Content Moderation", icon: FileText }
      ]
    },
    {
      id: "financials",
      label: "Financials",
      icon: DollarSign,
      items: [
        { id: "revenue", label: "Revenue", icon: DollarSign },
        { id: "transactions", label: "Transactions", icon: Receipt },
        { id: "pricing", label: "Pricing Plans", icon: Tag },
        { id: "etransfers", label: "E-Transfers", icon: Building2 },
        { id: "gifts", label: "Gift Packs", icon: Gift },
        { id: "promotions", label: "Promotions", icon: Megaphone }
      ]
    },
    {
      id: "analytics",
      label: "Analytics",
      icon: TrendingUp,
      items: [
        { id: "engagement", label: "Engagement", icon: TrendingUp },
        { id: "email-analytics", label: "Email Analytics", icon: Mail },
        { id: "api-balance", label: "API Balance", icon: Database }
      ]
    },
    {
      id: "b2bEnterprise",
      label: "B2B Enterprise",
      icon: Building,
      items: [
        { id: "b2b-overview", label: "B2B Overview", icon: Building },
        { id: "b2b-partners", label: "Partners", icon: Handshake },
        { id: "b2b-enterprises", label: "Enterprises", icon: Briefcase },
        { id: "b2b-inquiries", label: "Inquiries", icon: MessageSquare },
        { id: "b2b-tech-support", label: "Tech Support", icon: LifeBuoy, isHighlighted: true },
        { id: "b2b-api-logs", label: "API Logs", icon: Terminal },
        { id: "b2b-settings", label: "B2B Settings", icon: Settings }
      ]
    },
    {
      id: "system",
      label: "System",
      icon: Settings,
      items: [
        { id: "health-monitor", label: "Health Monitor", icon: Activity },
        { id: "system-logs", label: "System Logs", icon: Terminal },
        { id: "publish-retry", label: "Publish Retry", icon: RefreshCcw },
        { id: "communications", label: "Communications", icon: Mail },
        { id: "compliance", label: "Data & Compliance", icon: ShieldCheck },
        { id: "documents", label: "Documents", icon: Download },
        { id: "settings", label: "Settings", icon: Settings, isHighlighted: true }
      ]
    }
  ];
  
  // Legacy flat tabs for backward compatibility with help section mapping
  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "users", label: "Users", icon: Users },
    { id: "content-playbook", label: "Content Playbook", icon: Zap, isHighlighted: true },
    { id: "support-tickets", label: "Support Tickets", icon: LifeBuoy },
    { id: "etransfers", label: "E-Transfers", icon: Building2 },
    { id: "pricing", label: "Pricing Plans", icon: Tag },
    { id: "engagement", label: "Engagement", icon: TrendingUp },
    { id: "auto-reply", label: "VIRA (Bot)", icon: Bot, isHighlighted: true },
    { id: "viraly-social", label: "Viraly Social", icon: Share2, isHighlighted: true },
    { id: "email-analytics", label: "Email Analytics", icon: Mail },
    { id: "revenue", label: "Revenue", icon: DollarSign },
    { id: "transactions", label: "Transactions", icon: Receipt },
    { id: "gifts", label: "Gift Packs", icon: Gift },
    { id: "promotions", label: "Promotions", icon: Megaphone },
    { id: "api-balance", label: "API Balance", icon: Database },
    { id: "health-monitor", label: "Health Monitor", icon: Activity },
    { id: "system-logs", label: "System Logs", icon: Terminal },
    { id: "content", label: "Content", icon: FileText },
    { id: "communications", label: "Communications", icon: Megaphone },
    { id: "team", label: "Team & Roles", icon: UserCog },
    { id: "compliance", label: "Data & Compliance", icon: ShieldCheck },
    { id: "documents", label: "Documents", icon: Download },
    { id: "b2b-overview", label: "B2B Overview", icon: Building },
    { id: "b2b-partners", label: "Partners", icon: Handshake },
    { id: "b2b-enterprises", label: "Enterprises", icon: Briefcase },
    { id: "b2b-inquiries", label: "Inquiries", icon: MessageSquare },
    { id: "b2b-tech-support", label: "Tech Support", icon: LifeBuoy, isHighlighted: true },
    { id: "b2b-api-logs", label: "API Logs", icon: Terminal },
    { id: "b2b-settings", label: "B2B Settings", icon: Settings },
    { id: "publish-retry", label: "Publish Retry", icon: RefreshCcw },
    { id: "settings", label: "Settings", icon: Settings, isHighlighted: true }
  ];
  
  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };
  
  // Check if any item in a section is active
  const isSectionActive = (section) => {
    return section.items.some(item => item.id === activeTab);
  };

  return (
    <div className="min-h-screen bg-viraly-dark flex">
      {/* Admin Login Screen */}
      {!isAuthenticated && (
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md p-8"
          >
            <div className="text-center mb-8">
              <img src={LOGO_URL} alt="Viraly" className="h-16 w-16 mx-auto mb-4" />
              <h1 className="font-heading text-3xl font-bold gradient-text">Admin Login</h1>
              <p className="text-viraly-muted mt-2">Access the Viraly admin panel</p>
            </div>
            
            <form onSubmit={handleAdminLogin} className="card-base p-6 space-y-4">
              <div>
                <label className="text-sm text-viraly-light mb-2 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                  <Input
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@viraly.ai"
                    className="input-base pl-10"
                    required
                    data-testid="admin-email-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-viraly-light mb-2 block">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input-base pl-10"
                    required
                    data-testid="admin-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={authLoading}
                className="btn-primary w-full"
                data-testid="admin-login-btn"
              >
                {authLoading ? "Signing in..." : "Sign In to Admin"}
              </Button>
              
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setForgotPasswordEmail(adminEmail);
                }}
                className="w-full text-sm text-viraly-muted hover:text-viraly-orange transition-colors"
                data-testid="admin-forgot-password-link"
              >
                Forgot your password?
              </button>
              
              <p className="text-xs text-viraly-muted text-center mt-4">
                Only authorized administrators can access this panel
              </p>
            </form>
            
            {/* Forgot Password Modal */}
            <AnimatePresence>
              {showForgotPassword && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
                  onClick={() => setShowForgotPassword(false)}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-viraly-card border border-white/10 rounded-2xl p-6 w-full max-w-md mx-4"
                  >
                    {!forgotPasswordSent ? (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold text-viraly-light">Reset Admin Password</h3>
                          <button
                            onClick={() => setShowForgotPassword(false)}
                            className="text-viraly-muted hover:text-white"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        
                        <p className="text-viraly-muted text-sm mb-4">
                          Enter your admin email address and we'll send you a link to reset your password.
                        </p>
                        
                        <form onSubmit={handleAdminForgotPassword}>
                          <div className="mb-4">
                            <label className="text-sm text-viraly-light mb-2 block">Admin Email</label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                              <Input
                                type="email"
                                value={forgotPasswordEmail}
                                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                                placeholder="admin@viraly.ai"
                                className="input-base pl-10"
                                required
                                data-testid="admin-forgot-email-input"
                              />
                            </div>
                          </div>
                          
                          <div className="flex gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowForgotPassword(false)}
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              disabled={forgotPasswordLoading}
                              className="flex-1 btn-primary"
                              data-testid="admin-forgot-submit-btn"
                            >
                              {forgotPasswordLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                "Send Reset Link"
                              )}
                            </Button>
                          </div>
                        </form>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                          <Mail className="w-8 h-8 text-green-400" />
                        </div>
                        <h3 className="text-xl font-bold text-viraly-light mb-2">Check Your Email</h3>
                        <p className="text-viraly-muted text-sm mb-4">
                          If an admin account exists with this email, you will receive a password reset link shortly.
                        </p>
                        <Button
                          onClick={() => {
                            setShowForgotPassword(false);
                            setForgotPasswordSent(false);
                            setForgotPasswordEmail("");
                          }}
                          className="btn-primary"
                        >
                          Back to Login
                        </Button>
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
            
            <div className="text-center mt-6">
              <Link to="/" className="text-viraly-muted hover:text-white text-sm flex items-center justify-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Viraly
              </Link>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Admin Panel (after authentication) */}
      {isAuthenticated && (
        <>
          {/* Sidebar */}
          <aside className="w-64 bg-viraly-card border-r border-white/5 fixed h-screen flex flex-col">
            <div className="p-6 flex-shrink-0">
              <Link to="/" className="flex items-center gap-3">
                <img src={LOGO_URL} alt="Viraly" className="h-10 w-10" />
                <div>
                  <span className="font-heading font-bold text-xl gradient-text">Viraly</span>
                  <p className="text-xs text-viraly-muted">Admin Panel</p>
                </div>
              </Link>
            </div>
            
            <nav className="px-3 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-1 pb-4">
                {sidebarSections.map((section) => {
                  const isExpanded = expandedSections[section.id];
                  const sectionActive = isSectionActive(section);
                  
                  // Map tab IDs to help sections
                  const helpSectionMap = {
                    "overview": "overview",
                    "users": "users",
                    "content-playbook": "content_playbook",
                    "support-tickets": "support_tickets",
                    "etransfers": "etransfers",
                    "pricing": "pricing",
                    "engagement": "engagement",
                    "auto-reply": "vira_bot",
                    "viraly-social": "viraly_social",
                    "email-analytics": "email_analytics",
                    "revenue": "revenue",
                    "transactions": "transactions",
                    "gifts": "gift_packs",
                    "promotions": "promotions",
                    "api-balance": "api_balance",
                    "health-monitor": "health_monitor",
                    "system-logs": "system_logs",
                    "content": "content",
                    "communications": "communications",
                    "team": "team_roles",
                    "compliance": "data_compliance",
                    "documents": "documents",
                    "settings": "settings",
                    "b2b-overview": "b2b_overview",
                    "b2b-partners": "b2b_partners",
                    "b2b-enterprises": "b2b_enterprises",
                    "b2b-inquiries": "b2b_inquiries",
                    "b2b-tech-support": "b2b_tech_support",
                    "b2b-api-logs": "b2b_api_logs",
                    "b2b-settings": "b2b_settings",
                    "publish-retry": "publish_retry"
                  };
                  
                  return (
                    <Collapsible
                      key={section.id}
                      open={isExpanded}
                      onOpenChange={() => toggleSection(section.id)}
                    >
                      <CollapsibleTrigger asChild>
                        <div className="group relative">
                          <button
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-[13px] font-medium ${
                              sectionActive
                                ? "text-viraly-orange bg-viraly-orange/10"
                                : "text-viraly-light hover:text-white hover:bg-white/5"
                            }`}
                            data-testid={`admin-section-${section.id}`}
                          >
                            <section.icon className="w-4 h-4 flex-shrink-0" />
                            <span className="flex-1 text-left">{section.label}</span>
                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? "rotate-0" : "-rotate-90"}`} />
                          </button>
                          {/* Help Button for Section - appears on hover */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setHelpSection(helpSectionMap[section.items[0]?.id] || section.id);
                              setHelpModalOpen(true);
                            }}
                            className="absolute right-8 top-1/2 -translate-y-1/2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all"
                            title={`Help: ${section.label}`}
                          >
                            <HelpCircle className="w-3.5 h-3.5 text-viraly-muted hover:text-viraly-orange" />
                          </button>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
                        <ul className="mt-1 ml-3 space-y-0.5 border-l border-white/10 pl-2">
                          {section.items.map((item) => {
                            const isActive = activeTab === item.id;
                            return (
                              <li key={item.id} className="group relative">
                                <button
                                  onClick={() => setActiveTab(item.id)}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md transition-all text-[12px] ${
                                    isActive
                                      ? "bg-viraly-gradient text-white"
                                      : item.isHighlighted
                                        ? "text-viraly-orange hover:text-white hover:bg-viraly-orange/20 font-semibold"
                                        : "text-viraly-muted hover:text-white hover:bg-white/5"
                                  }`}
                                  data-testid={`admin-tab-${item.id}`}
                                >
                                  <item.icon className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span className="truncate flex-1 text-left">{item.label}</span>
                                </button>
                                {/* Help Button - appears on hover */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setHelpSection(helpSectionMap[item.id] || item.id);
                                    setHelpModalOpen(true);
                                  }}
                                  className="absolute right-0.5 top-1/2 -translate-y-1/2 p-0.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all"
                                  title={`Help: ${item.label}`}
                                >
                                  <HelpCircle className="w-3 h-3 text-viraly-muted hover:text-viraly-orange" />
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </div>
            </nav>
            
            <div className="p-4 space-y-2 border-t border-white/5 bg-viraly-card flex-shrink-0">
              <button 
                onClick={handleAdminLogout}
                className="w-full flex items-center gap-2 px-4 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-colors"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64">
        {/* Top Bar with Notifications */}
        <div className="sticky top-0 z-40 bg-viraly-dark/95 backdrop-blur-sm border-b border-white/5 px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-white capitalize">
              {activeTab === "overview" ? "Dashboard" : activeTab.replace(/-/g, " ")}
            </h2>
          </div>
          
          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
              className="relative p-2 rounded-lg hover:bg-white/5 transition-colors"
              data-testid="admin-notification-bell"
            >
              <Bell className="w-5 h-5 text-viraly-muted hover:text-white" />
              {unreadNotificationCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-viraly-orange text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadNotificationCount > 9 ? "9+" : unreadNotificationCount}
                </span>
              )}
            </button>
            
            {/* Notification Dropdown */}
            <AnimatePresence>
              {notificationDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 top-12 w-96 bg-viraly-card border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
                  data-testid="notification-dropdown"
                >
                  <div className="p-4 border-b border-white/5 flex items-center justify-between">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      Notifications
                    </h3>
                    {unreadNotificationCount > 0 && (
                      <button
                        onClick={markAllNotificationsRead}
                        className="text-xs text-viraly-orange hover:text-white transition-colors"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {adminNotifications.length === 0 ? (
                      <div className="p-8 text-center text-viraly-muted">
                        <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No notifications yet</p>
                        <p className="text-xs mt-1">Purchase alerts will appear here</p>
                      </div>
                    ) : (
                      adminNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => !notification.read && markNotificationRead(notification.id)}
                          className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${
                            !notification.read ? "bg-viraly-orange/5" : ""
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              notification.type === "purchase" ? "bg-green-500/20 text-green-400" :
                              notification.type === "gift" ? "bg-purple-500/20 text-purple-400" :
                              notification.type === "e-transfer" ? "bg-blue-500/20 text-blue-400" :
                              "bg-viraly-orange/20 text-viraly-orange"
                            }`}>
                              {notification.type === "gift" ? <Gift className="w-4 h-4" /> :
                               notification.type === "e-transfer" ? <Banknote className="w-4 h-4" /> :
                               <DollarSign className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">
                                {notification.title}
                                {!notification.read && (
                                  <span className="ml-2 w-2 h-2 bg-viraly-orange rounded-full inline-block" />
                                )}
                              </p>
                              <p className="text-xs text-viraly-muted mt-0.5 truncate">
                                {notification.message}
                              </p>
                              <p className="text-xs text-viraly-muted/60 mt-1">
                                {new Date(notification.created_at).toLocaleString()}
                              </p>
                            </div>
                            {notification.data?.amount && (
                              <span className="text-sm font-bold text-green-400 flex-shrink-0">
                                ${notification.data.amount.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {adminNotifications.length > 0 && (
                    <div className="p-3 border-t border-white/5 bg-viraly-dark/50">
                      <button
                        onClick={() => {
                          setActiveTab("transactions");
                          setNotificationDropdownOpen(false);
                        }}
                        className="w-full text-center text-sm text-viraly-orange hover:text-white transition-colors"
                      >
                        View all transactions →
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        
        {/* Page Content */}
        <div className="p-8">
        {activeTab === "overview" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-3xl font-bold mb-8">Dashboard Overview</h1>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="stat-card" data-testid="stat-total-users">
                  <p className="text-viraly-muted text-sm mb-1">Total Users</p>
                  <p className="text-3xl font-heading font-bold gradient-text">
                    {stats?.total_users?.toLocaleString()}
                  </p>
                </div>
                <div className="stat-card" data-testid="stat-active-users">
                  <p className="text-viraly-muted text-sm mb-1">Active Users</p>
                  <p className="text-3xl font-heading font-bold text-viraly-success">
                    {stats?.active_users?.toLocaleString()}
                  </p>
                </div>
                <div className="stat-card" data-testid="stat-total-posts">
                  <p className="text-viraly-muted text-sm mb-1">Total Posts</p>
                  <p className="text-3xl font-heading font-bold text-blue-400">
                    {stats?.total_posts?.toLocaleString()}
                  </p>
                </div>
                <div className="stat-card" data-testid="stat-revenue">
                  <p className="text-viraly-muted text-sm mb-1">Total Revenue</p>
                  <p className="text-3xl font-heading font-bold text-purple-400">
                    ${stats?.total_revenue?.toLocaleString()}
                  </p>
                </div>
              </div>
            )}
            
            <div className="card-base p-6">
              <h2 className="font-heading text-xl font-bold mb-4">Quick Stats</h2>
              <p className="text-viraly-muted">
                Revenue and user growth charts coming soon. This is a demo admin panel.
              </p>
            </div>
          </motion.div>
        )}

        {activeTab === "users" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-3xl font-bold">User Management</h1>
              <div className="flex items-center gap-3">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                  <Input
                    placeholder="Search users..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="input-base pl-10"
                    data-testid="search-users"
                  />
                </div>
              </div>
            </div>

            {/* Filters Row */}
            <div className="flex items-center gap-4 mb-6 p-4 rounded-xl bg-white/5">
              <div className="flex items-center gap-2 text-viraly-muted">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <select
                value={userFilter.plan}
                onChange={(e) => setUserFilter({...userFilter, plan: e.target.value})}
                className="input-base px-3 py-2 text-sm rounded-lg"
                data-testid="filter-users-plan"
              >
                <option value="all">All Plans</option>
                <option value="free">Free / Trial</option>
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="pro">Pro</option>
              </select>
              <select
                value={userFilter.status}
                onChange={(e) => setUserFilter({...userFilter, status: e.target.value})}
                className="input-base px-3 py-2 text-sm rounded-lg"
                data-testid="filter-users-status"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive (30+ days)</option>
                <option value="suspended">Suspended</option>
              </select>
              <select
                value={userFilter.dateRange}
                onChange={(e) => setUserFilter({...userFilter, dateRange: e.target.value})}
                className="input-base px-3 py-2 text-sm rounded-lg"
                data-testid="filter-users-date"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
              </select>
              {(userFilter.plan !== "all" || userFilter.status !== "all" || userFilter.dateRange !== "all") && (
                <button
                  onClick={() => setUserFilter({ plan: "all", status: "all", dateRange: "all" })}
                  className="text-xs text-viraly-orange hover:text-white transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="card-base overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left p-4 font-medium text-viraly-muted">User</th>
                      <th className="text-left p-4 font-medium text-viraly-muted">Plan</th>
                      <th className="text-left p-4 font-medium text-viraly-muted">Status</th>
                      <th className="text-left p-4 font-medium text-viraly-muted">Posts</th>
                      <th className="text-left p-4 font-medium text-viraly-muted">Joined</th>
                      <th className="text-left p-4 font-medium text-viraly-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(users || [])
                      .filter(u => 
                        (u.name?.toLowerCase().includes(search.toLowerCase()) ||
                        u.email?.toLowerCase().includes(search.toLowerCase())) &&
                        (userFilter.plan === "all" || u.plan === userFilter.plan || (userFilter.plan === "free" && (!u.plan || u.plan === "trial" || u.plan === "free"))) &&
                        (userFilter.status === "all" || 
                          (userFilter.status === "suspended" && (u.status === 'suspended' || u.is_suspended)) ||
                          (userFilter.status === "active" && u.status !== 'suspended' && !u.is_suspended))
                      )
                      .map((user) => (
                      <tr 
                        key={user.id} 
                        className={`border-t border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${user.status === 'suspended' || user.is_suspended ? 'opacity-60' : ''}`}
                        data-testid={`user-row-${user.id}`}
                        onClick={() => handleUserClick(user)}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.status === 'suspended' || user.is_suspended ? 'bg-red-500/20' : 'bg-viraly-gradient'}`}>
                              <span className={`font-bold ${user.status === 'suspended' || user.is_suspended ? 'text-red-400' : 'text-white'}`}>
                                {user.name?.charAt(0) || "U"}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">{user.name || "Unknown"}</p>
                              <p className="text-sm text-viraly-muted">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="px-3 py-1 rounded-full text-sm bg-viraly-orange/20 text-viraly-orange capitalize">
                            {user.plan || "free"}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.status === 'suspended' || user.is_suspended 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-viraly-success/20 text-viraly-success'
                          }`}>
                            {user.status === 'suspended' || user.is_suspended ? 'Suspended' : 'Active'}
                          </span>
                        </td>
                        <td className="p-4">{user.posts_remaining ?? 0}</td>
                        <td className="p-4 text-viraly-muted">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-2 hover:bg-white/5 rounded-lg" data-testid={`user-actions-${user.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-viraly-card border-white/10">
                              <DropdownMenuItem className="cursor-pointer" onClick={() => handleUserClick(user)}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer" onClick={() => setEditUserModal(user)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit User
                              </DropdownMenuItem>
                              <DropdownMenuItem className="cursor-pointer" onClick={() => setChangePlanModal(user)}>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Change Plan
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="cursor-pointer text-viraly-error" 
                                onClick={() => setSuspendUserModal(user)}
                              >
                                <PauseCircle className="w-4 h-4 mr-2" />
                                {user.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        {/* Content Playbook Tab - Viraly Auto-Pilot Admin */}
        {activeTab === "content-playbook" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Zap className="w-8 h-8 text-viraly-orange" />
                  Content Playbook
                </h1>
                <p className="text-viraly-muted mt-2">Manage Viraly's Auto-Pilot content generation</p>
              </div>
              <Button
                className="btn-primary flex items-center gap-2"
                onClick={() => fetchAdminData()}
                data-testid="refresh-playbook-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
            
            {/* Viraly Auto-Pilot Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-viraly-orange/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-viraly-orange" />
                  </div>
                  <span className="text-viraly-muted">Auto-Pilot Status</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-viraly-success">ACTIVE</span>
                  <span className="text-xs px-2 py-1 bg-viraly-success/20 text-viraly-success rounded-full">Running</span>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-viraly-muted">Posts Today</span>
                </div>
                <p className="text-2xl font-bold text-viraly-light">0 / 3</p>
              </div>
              
              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-viraly-muted">Users with Auto-Pilot</span>
                </div>
                <p className="text-2xl font-bold text-viraly-light">{playbookStats.active_users}</p>
              </div>
            </div>
            
            {/* Viraly Configuration */}
            <div className="card-base p-6 mb-8">
              <h2 className="text-xl font-bold text-viraly-light mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-viraly-orange" />
                Viraly Auto-Pilot Configuration
              </h2>
              <p className="text-viraly-muted mb-6">Configure automatic content generation for Viraly's own social accounts</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-viraly-muted mb-2 block">Posts Per Day</label>
                    <select className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-viraly-light">
                      <option value="1">1 post/day</option>
                      <option value="2">2 posts/day</option>
                      <option value="3" selected>3 posts/day</option>
                      <option value="5">5 posts/day</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm text-viraly-muted mb-2 block">Platforms</label>
                    <div className="flex flex-wrap gap-2">
                      {["Instagram", "Facebook", "TikTok"].map(p => (
                        <span key={p} className="px-3 py-1 bg-viraly-orange/20 text-viraly-orange rounded-full text-sm">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-viraly-muted mb-2 block">Approval Mode</label>
                    <select className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-viraly-light">
                      <option value="review_first" selected>Review First (Recommended)</option>
                      <option value="auto_approve">Auto-Approve</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm text-viraly-muted mb-2 block">Industries to Showcase</label>
                    <div className="flex flex-wrap gap-1">
                      {["Real Estate", "Restaurant", "Fitness", "Bakery", "Tech", "Beauty", "Dental", "Yoga"].map(i => (
                        <span key={i} className="px-2 py-0.5 bg-white/10 text-viraly-light rounded text-xs">
                          {i}
                        </span>
                      ))}
                      <span className="px-2 py-0.5 bg-white/5 text-viraly-muted rounded text-xs">+20 more</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-white/10 flex justify-end gap-3">
                <Button variant="ghost">Reset to Default</Button>
                <Button className="btn-primary">Save Configuration</Button>
              </div>
            </div>
            
            {/* Pending Posts Queue */}
            <div className="card-base p-6">
              <h2 className="text-xl font-bold text-viraly-light mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" />
                Viraly Posts Queue
              </h2>
              
              <div className="flex gap-2 mb-4">
                {["Pending Review", "Approved", "Posted", "Archived"].map(tab => (
                  <button
                    key={tab}
                    className="px-4 py-2 rounded-lg text-sm transition-all bg-white/5 text-viraly-muted hover:bg-white/10"
                  >
                    {tab}
                  </button>
                ))}
              </div>
              
              <div className="text-center py-12 text-viraly-muted">
                <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No posts in queue</p>
                <p className="text-sm mt-2">Auto-Pilot will generate content based on your schedule</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Support Tickets Tab */}
        {activeTab === "support-tickets" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <LifeBuoy className="w-8 h-8 text-viraly-orange" />
                  Support Tickets
                </h1>
                <p className="text-viraly-muted mt-2">Manage customer support requests and issues</p>
              </div>
              <Button
                className="btn-primary flex items-center gap-2"
                onClick={() => fetchAdminData()}
                data-testid="refresh-tickets-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>

            {/* Ticket Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div 
                className={`card-base p-4 text-center border-l-4 border-red-500 cursor-pointer hover:bg-white/5 transition-colors ${ticketFilter.status === 'open' ? 'bg-red-500/10' : ''}`}
                onClick={() => setTicketFilter({...ticketFilter, status: ticketFilter.status === 'open' ? 'all' : 'open'})}
                data-testid="filter-tickets-open"
              >
                <p className="text-viraly-muted text-sm">Open</p>
                <p className="text-2xl font-bold text-red-400">{ticketStats.open}</p>
              </div>
              <div 
                className={`card-base p-4 text-center border-l-4 border-yellow-500 cursor-pointer hover:bg-white/5 transition-colors ${ticketFilter.status === 'pending' ? 'bg-yellow-500/10' : ''}`}
                onClick={() => setTicketFilter({...ticketFilter, status: ticketFilter.status === 'pending' ? 'all' : 'pending'})}
                data-testid="filter-tickets-pending"
              >
                <p className="text-viraly-muted text-sm">Pending</p>
                <p className="text-2xl font-bold text-yellow-400">{ticketStats.pending}</p>
              </div>
              <div 
                className={`card-base p-4 text-center border-l-4 border-viraly-success cursor-pointer hover:bg-white/5 transition-colors ${ticketFilter.status === 'resolved' ? 'bg-green-500/10' : ''}`}
                onClick={() => setTicketFilter({...ticketFilter, status: ticketFilter.status === 'resolved' ? 'all' : 'resolved'})}
                data-testid="filter-tickets-resolved"
              >
                <p className="text-viraly-muted text-sm">Resolved</p>
                <p className="text-2xl font-bold text-viraly-success">{ticketStats.resolved}</p>
              </div>
              <div 
                className={`card-base p-4 text-center border-l-4 border-blue-500 cursor-pointer hover:bg-white/5 transition-colors ${ticketFilter.status === 'all' ? 'bg-blue-500/10' : ''}`}
                onClick={() => setTicketFilter({...ticketFilter, status: 'all'})}
                data-testid="filter-tickets-all"
              >
                <p className="text-viraly-muted text-sm">Total</p>
                <p className="text-2xl font-bold text-blue-400">{ticketStats.total}</p>
              </div>
            </div>

            {/* Filters Row */}
            <div className="flex items-center gap-4 mb-6 p-4 rounded-xl bg-white/5">
              <div className="flex items-center gap-2 text-viraly-muted">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <select
                value={ticketFilter.priority}
                onChange={(e) => setTicketFilter({...ticketFilter, priority: e.target.value})}
                className="input-base px-3 py-2 text-sm rounded-lg"
                data-testid="filter-tickets-priority"
              >
                <option value="all">All Priorities</option>
                <option value="high">High Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="low">Low Priority</option>
              </select>
              <select
                value={ticketFilter.category}
                onChange={(e) => setTicketFilter({...ticketFilter, category: e.target.value})}
                className="input-base px-3 py-2 text-sm rounded-lg"
                data-testid="filter-tickets-category"
              >
                <option value="all">All Categories</option>
                <option value="bug">Bug Reports</option>
                <option value="billing">Billing Issues</option>
                <option value="feature">Feature Requests</option>
                <option value="account">Account Issues</option>
                <option value="other">Other</option>
              </select>
              {(ticketFilter.status !== "all" || ticketFilter.priority !== "all" || ticketFilter.category !== "all") && (
                <button
                  onClick={() => setTicketFilter({ status: "all", priority: "all", category: "all" })}
                  className="text-xs text-viraly-orange hover:text-white transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>

            {/* Ticket List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : (supportTickets || []).filter(t => 
                (ticketFilter.status === 'all' || t.status === ticketFilter.status) &&
                (ticketFilter.priority === 'all' || t.priority === ticketFilter.priority) &&
                (ticketFilter.category === 'all' || t.category === ticketFilter.category)
              ).length === 0 ? (
              <div className="card-base p-12 text-center">
                <LifeBuoy className="w-16 h-16 text-viraly-success mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">No Tickets Match Filters</h3>
                <p className="text-viraly-muted">Try adjusting your filter criteria or clear all filters.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(supportTickets || [])
                  .filter(t => 
                    (ticketFilter.status === 'all' || t.status === ticketFilter.status) &&
                    (ticketFilter.priority === 'all' || t.priority === ticketFilter.priority) &&
                    (ticketFilter.category === 'all' || t.category === ticketFilter.category)
                  )
                  .map((ticket) => (
                  <div
                    key={ticket.id}
                    className={`card-base p-4 cursor-pointer hover:border-viraly-orange/50 transition-all ${
                      selectedTicket?.id === ticket.id ? 'border-viraly-orange' : ''
                    }`}
                    onClick={() => setSelectedTicket(selectedTicket?.id === ticket.id ? null : ticket)}
                    data-testid={`ticket-row-${ticket.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          ticket.category === 'bug' ? 'bg-red-500/20' : 'bg-yellow-500/20'
                        }`}>
                          {ticket.category === 'bug' ? (
                            <Bug className="w-5 h-5 text-red-400" />
                          ) : (
                            <CreditCard className="w-5 h-5 text-yellow-400" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <p className="font-medium">{ticket.user_name || ticket.user_email}</p>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              ticket.status === 'open' ? 'bg-red-500/20 text-red-400' :
                              ticket.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-viraly-success/20 text-viraly-success'
                            }`}>{ticket.status}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              ticket.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                              ticket.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-white/10 text-viraly-muted'
                            }`}>{ticket.priority}</span>
                          </div>
                          <p className="text-sm text-viraly-muted">{ticket.user_email}</p>
                          <p className="text-sm mt-2 line-clamp-2">{ticket.message}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-xs text-viraly-muted">
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </p>
                        <ChevronRight className={`w-5 h-5 text-viraly-muted transition-transform ${
                          selectedTicket?.id === ticket.id ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </div>

                    {/* Expanded Ticket Detail */}
                    <AnimatePresence>
                      {selectedTicket?.id === ticket.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-4 border-t border-white/10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Full Message */}
                          <div className="mb-4">
                            <label className="text-xs text-viraly-muted mb-2 block">Full Message</label>
                            <div className="p-3 rounded-xl bg-white/5">
                              <p className="text-sm">{ticket.message}</p>
                            </div>
                          </div>

                          {/* Internal Notes */}
                          <div className="mb-4">
                            <label className="text-xs text-viraly-muted mb-2 block">Add Internal Note (admin only)</label>
                            <Textarea
                              value={internalNote}
                              onChange={(e) => setInternalNote(e.target.value)}
                              placeholder="Add internal notes for this ticket..."
                              className="input-base text-sm resize-none h-20"
                            />
                          </div>

                          {/* Resolution Section */}
                          {ticket.status !== 'resolved' && (
                            <div className="space-y-4">
                              {/* Compensation Credits */}
                              <div className="p-4 rounded-xl bg-viraly-success/10 border border-viraly-success/20">
                                <h4 className="font-medium text-viraly-success mb-3 flex items-center gap-2">
                                  <Gift className="w-4 h-4" />
                                  Compensation Credits (Optional)
                                </h4>
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <label className="text-xs text-viraly-muted mb-1 block">Post Credits</label>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={compensatePostCredits}
                                      onChange={(e) => setCompensatePostCredits(parseInt(e.target.value) || 0)}
                                      className="input-base text-center"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-viraly-muted mb-1 block">Video Credits</label>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={compensateVideoCredits}
                                      onChange={(e) => setCompensateVideoCredits(parseInt(e.target.value) || 0)}
                                      className="input-base text-center"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-viraly-muted mb-1 block">Regen Credits</label>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={compensateRegenCredits}
                                      onChange={(e) => setCompensateRegenCredits(parseInt(e.target.value) || 0)}
                                      className="input-base text-center"
                                    />
                                  </div>
                                </div>
                                <p className="text-xs text-viraly-muted mt-2">
                                  Credits valid for 90 days (as per admin settings)
                                </p>
                              </div>

                              {/* Resolution Message */}
                              <div>
                                <label className="text-xs text-viraly-muted mb-2 block">Resolution Email to Customer</label>
                                <Textarea
                                  value={resolutionMessage}
                                  onChange={(e) => setResolutionMessage(e.target.value)}
                                  placeholder="Write a personalized resolution message to the customer..."
                                  className="input-base text-sm resize-none h-24"
                                />
                                <p className="text-xs text-viraly-muted mt-1">
                                  This will be sent to the customer's email ({ticket.user_email})
                                </p>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-3">
                                <Button
                                  onClick={async () => {
                                    setTicketLoading(true);
                                    try {
                                      const adminToken = localStorage.getItem("viraly_admin_token");
                                      const params = new URLSearchParams({
                                        resolution_message: resolutionMessage || "Your issue has been resolved.",
                                        send_email_flag: "true",
                                        compensate_post_credits: compensatePostCredits.toString(),
                                        compensate_video_credits: compensateVideoCredits.toString(),
                                        compensate_regen_credits: compensateRegenCredits.toString()
                                      });
                                      
                                      await apiClient.post(
                                        `/admin/support/tickets/${ticket.id}/resolve?${params.toString()}`,
                                        {},
                                        { headers: { Authorization: `Bearer ${adminToken}` }}
                                      );
                                      
                                      toast.success("Ticket resolved and customer notified!");
                                      setSelectedTicket(null);
                                      setResolutionMessage("");
                                      setCompensatePostCredits(0);
                                      setCompensateVideoCredits(0);
                                      setCompensateRegenCredits(0);
                                      fetchAdminData();
                                    } catch (error) {
                                      toast.error("Failed to resolve ticket");
                                      console.error(error);
                                    } finally {
                                      setTicketLoading(false);
                                    }
                                  }}
                                  disabled={ticketLoading}
                                  className="btn-primary flex items-center gap-2"
                                >
                                  {ticketLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Send className="w-4 h-4" />
                                  )}
                                  Resolve & Send Email
                                </Button>
                                <Button
                                  onClick={async () => {
                                    setTicketLoading(true);
                                    try {
                                      const adminToken = localStorage.getItem("viraly_admin_token");
                                      await apiClient.put(
                                        `/admin/support/tickets/${ticket.id}`,
                                        { status: "pending", internal_notes: internalNote },
                                        { headers: { Authorization: `Bearer ${adminToken}` }}
                                      );
                                      toast.success("Ticket marked as pending");
                                      setInternalNote("");
                                      fetchAdminData();
                                    } catch (error) {
                                      toast.error("Failed to update ticket");
                                    } finally {
                                      setTicketLoading(false);
                                    }
                                  }}
                                  disabled={ticketLoading}
                                  className="btn-secondary"
                                >
                                  Mark as Pending
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Resolved Ticket Info */}
                          {ticket.status === 'resolved' && (
                            <div className="p-4 rounded-xl bg-viraly-success/10 border border-viraly-success/20">
                              <div className="flex items-center gap-2 mb-2">
                                <Check className="w-5 h-5 text-viraly-success" />
                                <span className="font-medium text-viraly-success">Resolved</span>
                              </div>
                              {ticket.resolved_at && (
                                <p className="text-xs text-viraly-muted">
                                  Resolved on {new Date(ticket.resolved_at).toLocaleDateString()}
                                </p>
                              )}
                              {ticket.resolution_message && (
                                <p className="text-sm mt-2">{ticket.resolution_message}</p>
                              )}
                              {ticket.compensation_credits && (
                                <div className="mt-2 pt-2 border-t border-white/10">
                                  <p className="text-xs text-viraly-success">
                                    Compensation: {ticket.compensation_credits.post_credits || 0} posts, {ticket.compensation_credits.video_credits || 0} videos, {ticket.compensation_credits.regen_credits || 0} regen
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* E-Transfers Tab */}
        {activeTab === "etransfers" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Building2 className="w-8 h-8 text-viraly-orange" />
                  E-Transfer Payments
                </h1>
                <p className="text-viraly-muted mt-2">Verify and approve pending Interac e-Transfer payments</p>
              </div>
              <Button
                className="btn-primary flex items-center gap-2"
                onClick={() => fetchAdminData()}
                data-testid="refresh-etransfers-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>

            {/* E-Transfer Stats - Clickable Filters */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div 
                className={`card-base p-4 text-center border-l-4 border-yellow-500 cursor-pointer hover:bg-white/5 transition-colors ${etransferFilter.status === 'pending' ? 'bg-yellow-500/10' : ''}`}
                onClick={() => setEtransferFilter({...etransferFilter, status: etransferFilter.status === 'pending' ? 'all' : 'pending'})}
                data-testid="filter-etransfer-pending"
              >
                <p className="text-viraly-muted text-sm">Pending Verification</p>
                <p className="text-2xl font-bold text-yellow-400">{etransferStats.pending}</p>
              </div>
              <div 
                className={`card-base p-4 text-center border-l-4 border-viraly-success cursor-pointer hover:bg-white/5 transition-colors ${etransferFilter.status === 'verified' ? 'bg-green-500/10' : ''}`}
                onClick={() => setEtransferFilter({...etransferFilter, status: etransferFilter.status === 'verified' ? 'all' : 'verified'})}
                data-testid="filter-etransfer-verified"
              >
                <p className="text-viraly-muted text-sm">Verified (30 days)</p>
                <p className="text-2xl font-bold text-viraly-success">{etransferStats.verified}</p>
              </div>
              <div 
                className={`card-base p-4 text-center border-l-4 border-red-500 cursor-pointer hover:bg-white/5 transition-colors ${etransferFilter.status === 'rejected' ? 'bg-red-500/10' : ''}`}
                onClick={() => setEtransferFilter({...etransferFilter, status: etransferFilter.status === 'rejected' ? 'all' : 'rejected'})}
                data-testid="filter-etransfer-rejected"
              >
                <p className="text-viraly-muted text-sm">Rejected</p>
                <p className="text-2xl font-bold text-red-400">{etransferStats.rejected}</p>
              </div>
              <div 
                className={`card-base p-4 text-center border-l-4 border-viraly-orange cursor-pointer hover:bg-white/5 transition-colors ${etransferFilter.status === 'all' ? 'bg-viraly-orange/10' : ''}`}
                onClick={() => setEtransferFilter({...etransferFilter, status: 'all'})}
                data-testid="filter-etransfer-all"
              >
                <p className="text-viraly-muted text-sm">Pending Amount</p>
                <p className="text-2xl font-bold text-viraly-orange">${etransferStats.total_pending_amount?.toFixed(2) || '0.00'}</p>
              </div>
            </div>

            {/* Filters Row */}
            <div className="flex items-center gap-4 mb-6 p-4 rounded-xl bg-white/5">
              <div className="flex items-center gap-2 text-viraly-muted">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <select
                value={etransferFilter.dateRange}
                onChange={(e) => setEtransferFilter({...etransferFilter, dateRange: e.target.value})}
                className="input-base px-3 py-2 text-sm rounded-lg"
                data-testid="filter-etransfer-date"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
              {(etransferFilter.status !== "all" || etransferFilter.dateRange !== "all") && (
                <button
                  onClick={() => setEtransferFilter({ status: "all", dateRange: "all" })}
                  className="text-xs text-viraly-orange hover:text-white transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>

            {/* Instructions Banner */}
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 mb-6">
              <h4 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
                <Banknote className="w-5 h-5" />
                How to Verify E-Transfers
              </h4>
              <ol className="text-sm text-viraly-muted space-y-1 list-decimal list-inside">
                <li>Check your bank account (finance@viraly.ca) for incoming Interac e-Transfers</li>
                <li>Match the <strong>Reference Code</strong> and <strong>Amount</strong> with entries below</li>
                <li>Click <strong>"Verify & Activate"</strong> to credit the customer's account</li>
                <li>Customer receives confirmation email automatically</li>
              </ol>
            </div>

            {/* Pending E-Transfers List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : (pendingEtransfers || []).filter(et => 
                etransferFilter.status === 'all' || et.status === etransferFilter.status || 
                (etransferFilter.status === 'pending' && et.status === 'pending_verification')
              ).length === 0 ? (
              <div className="card-base p-12 text-center">
                <CheckCircle className="w-16 h-16 text-viraly-success mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">All Caught Up!</h3>
                <p className="text-viraly-muted">No e-Transfer verifications matching your filters.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(pendingEtransfers || [])
                  .filter(et => 
                    etransferFilter.status === 'all' || et.status === etransferFilter.status ||
                    (etransferFilter.status === 'pending' && et.status === 'pending_verification')
                  )
                  .map((etransfer) => (
                  <div
                    key={etransfer.id}
                    className={`card-base p-4 cursor-pointer hover:border-viraly-orange/50 transition-all ${
                      selectedEtransfer?.id === etransfer.id ? 'border-viraly-orange' : ''
                    }`}
                    onClick={() => setSelectedEtransfer(selectedEtransfer?.id === etransfer.id ? null : etransfer)}
                    data-testid={`etransfer-row-${etransfer.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-yellow-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <p className="font-medium">{etransfer.user_name || etransfer.user_email}</p>
                            <span className="px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400">
                              Pending
                            </span>
                          </div>
                          <p className="text-sm text-viraly-muted">{etransfer.user_email}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-viraly-light">{etransfer.product_name}</span>
                            <span className="font-mono text-viraly-orange font-bold">${etransfer.total_amount?.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-viraly-muted">
                          {new Date(etransfer.created_at).toLocaleDateString()} {new Date(etransfer.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          <span className="text-viraly-muted">Ref:</span>
                          <code className="px-2 py-0.5 rounded bg-white/10 font-mono text-viraly-light">{etransfer.reference}</code>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-viraly-muted mt-2 ml-auto transition-transform ${
                          selectedEtransfer?.id === etransfer.id ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </div>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {selectedEtransfer?.id === etransfer.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-4 border-t border-white/10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Payment Details Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div className="p-3 rounded-lg bg-white/5">
                              <p className="text-xs text-viraly-muted mb-1">Base Amount</p>
                              <p className="font-bold">${etransfer.base_amount?.toFixed(2)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-white/5">
                              <p className="text-xs text-viraly-muted mb-1">Tax ({etransfer.province || 'N/A'})</p>
                              <p className="font-bold">${etransfer.tax_amount?.toFixed(2)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/30">
                              <p className="text-xs text-viraly-muted mb-1">Total to Match</p>
                              <p className="font-bold text-viraly-orange">${etransfer.total_amount?.toFixed(2)} CAD</p>
                            </div>
                            <div className="p-3 rounded-lg bg-white/5">
                              <p className="text-xs text-viraly-muted mb-1">Reference Code</p>
                              <p className="font-bold font-mono text-sm">{etransfer.reference}</p>
                            </div>
                          </div>

                          {/* Admin Notes */}
                          <div className="mb-4">
                            <label className="text-xs text-viraly-muted mb-2 block">Admin Notes (optional)</label>
                            <Textarea
                              value={adminNotes}
                              onChange={(e) => setAdminNotes(e.target.value)}
                              placeholder="Add notes about this verification (e.g., bank confirmation #)..."
                              className="input-base text-sm resize-none h-16"
                            />
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center gap-3">
                            <Button
                              onClick={async () => {
                                setEtransferLoading(true);
                                try {
                                  const adminToken = localStorage.getItem("viraly_admin_token");
                                  await apiClient.post(
                                    `/admin/etransfers/${etransfer.id}/verify`,
                                    { 
                                      verified: true, 
                                      admin_notes: adminNotes,
                                      send_email: true
                                    },
                                    { headers: { Authorization: `Bearer ${adminToken}` }}
                                  );
                                  toast.success(`✅ Payment verified! ${etransfer.user_email} has been credited.`);
                                  setSelectedEtransfer(null);
                                  setAdminNotes("");
                                  fetchAdminData();
                                } catch (error) {
                                  console.error(error);
                                  toast.error("Failed to verify payment. Please try again.");
                                } finally {
                                  setEtransferLoading(false);
                                }
                              }}
                              disabled={etransferLoading}
                              className="btn-primary flex items-center gap-2"
                              data-testid={`verify-etransfer-${etransfer.id}`}
                            >
                              {etransferLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                              Verify & Activate Credits
                            </Button>
                            <Button
                              onClick={async () => {
                                if (!confirm("Are you sure you want to reject this e-Transfer? The customer will be notified.")) return;
                                setEtransferLoading(true);
                                try {
                                  const adminToken = localStorage.getItem("viraly_admin_token");
                                  await apiClient.post(
                                    `/admin/etransfers/${etransfer.id}/verify`,
                                    { 
                                      verified: false, 
                                      admin_notes: adminNotes || "Payment not found in bank records",
                                      send_email: true
                                    },
                                    { headers: { Authorization: `Bearer ${adminToken}` }}
                                  );
                                  toast.info("Payment rejected. Customer has been notified.");
                                  setSelectedEtransfer(null);
                                  setAdminNotes("");
                                  fetchAdminData();
                                } catch (error) {
                                  console.error(error);
                                  toast.error("Failed to reject payment.");
                                } finally {
                                  setEtransferLoading(false);
                                }
                              }}
                              disabled={etransferLoading}
                              className="btn-secondary flex items-center gap-2 text-red-400 hover:text-red-300"
                            >
                              <XCircle className="w-4 h-4" />
                              Reject (Not Found)
                            </Button>
                          </div>

                          <p className="text-xs text-viraly-muted mt-3">
                            💡 <strong>Tip:</strong> Always verify the exact amount and reference code match your bank records before approving.
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Email Analytics Tab */}
        {activeTab === "email-analytics" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Mail className="w-8 h-8 text-viraly-orange" />
                  Email Analytics
                </h1>
                <p className="text-viraly-muted mt-2">Track email opens, clicks, and engagement across all campaigns</p>
              </div>
            </div>
            
            <div className="grid grid-cols-4 gap-4 mb-8">
              <div className="card-base p-4">
                <p className="text-viraly-muted text-sm mb-2">Total Emails Sent</p>
                <p className="text-2xl font-bold gradient-text">{emailStats.total_sent}</p>
              </div>
              <div className="card-base p-4">
                <p className="text-viraly-muted text-sm mb-2">Unique Opens</p>
                <p className="text-2xl font-bold text-viraly-success">{emailStats.unique_opens}</p>
              </div>
              <div className="card-base p-4">
                <p className="text-viraly-muted text-sm mb-2">Total Clicks</p>
                <p className="text-2xl font-bold text-blue-400">{emailStats.total_clicks}</p>
              </div>
              <div className="card-base p-4">
                <p className="text-viraly-muted text-sm mb-2">Open Rate</p>
                <p className="text-2xl font-bold text-purple-400">{emailStats.open_rate}%</p>
              </div>
            </div>
            
            <div className="card-base p-6">
              <h3 className="font-heading text-xl font-bold mb-4">Recent Email Activity</h3>
              {emailActivity.length > 0 ? (
                <div className="space-y-3">
                  {emailActivity.map((activity, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${activity.type === 'open' ? 'bg-viraly-success' : 'bg-blue-400'}`} />
                        <span className="text-sm">{activity.type === 'open' ? 'Email opened' : 'Link clicked'}</span>
                      </div>
                      <span className="text-xs text-viraly-muted">{new Date(activity.timestamp).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-viraly-muted text-center py-8">No email tracking data yet. Email tracking is enabled for all automated emails including gift notifications, referral invites, and system alerts.</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Gift Packs Tab */}
        {activeTab === "gifts" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Gift className="w-8 h-8 text-viraly-orange" />
                  Gift Packs
                </h1>
                <p className="text-viraly-muted mt-2">Manage and track gifted credit packs</p>
              </div>
            </div>
            
            <div className="grid grid-cols-4 gap-4 mb-8">
              <div className="card-base p-4">
                <p className="text-viraly-muted text-sm mb-2">Total Gifts Sent</p>
                <p className="text-2xl font-bold gradient-text">{giftStats.total_gifts}</p>
              </div>
              <div className="card-base p-4">
                <p className="text-viraly-muted text-sm mb-2">Gifts Redeemed</p>
                <p className="text-2xl font-bold text-viraly-success">{giftStats.redeemed}</p>
              </div>
              <div className="card-base p-4">
                <p className="text-viraly-muted text-sm mb-2">Pending Gifts</p>
                <p className="text-2xl font-bold text-yellow-400">{giftStats.pending}</p>
              </div>
              <div className="card-base p-4">
                <p className="text-viraly-muted text-sm mb-2">Gift Revenue</p>
                <p className="text-2xl font-bold text-purple-400">${giftStats.revenue.toFixed(2)}</p>
              </div>
            </div>
            
            <div className="card-base p-6">
              <h3 className="font-heading text-xl font-bold mb-4">Recent Gift Activity</h3>
              {giftActivity.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-viraly-muted text-sm border-b border-white/10">
                        <th className="pb-3">Gift Code</th>
                        <th className="pb-3">Sender</th>
                        <th className="pb-3">Recipient</th>
                        <th className="pb-3">Pack</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {giftActivity.map((gift, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="py-3 font-mono text-sm">{gift.gift_code}</td>
                          <td className="py-3 text-sm">{gift.sender_name || gift.sender_email}</td>
                          <td className="py-3 text-sm">{gift.recipient_name}</td>
                          <td className="py-3 text-sm">{gift.pack_name}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              gift.status === 'redeemed' ? 'bg-viraly-success/20 text-viraly-success' :
                              gift.status === 'paid' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-white/10 text-viraly-muted'
                            }`}>
                              {gift.status}
                            </span>
                          </td>
                          <td className="py-3 text-sm text-viraly-muted">{new Date(gift.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-viraly-muted text-center py-8">No gift packs have been purchased yet. Users can gift credit packs from the Pricing page.</p>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === "engagement" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 text-viraly-orange" />
                  Engagement Analytics
                </h1>
                <p className="text-viraly-muted mt-2">
                  Monitor platform performance and user engagement metrics
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  className="btn-secondary flex items-center gap-2"
                  onClick={() => toast.info("Generating engagement report...")}
                >
                  <Download className="w-4 h-4" />
                  Export Report
                </Button>
                <Button
                  className="btn-primary flex items-center gap-2"
                  onClick={() => fetchAdminData()}
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh Data
                </Button>
              </div>
            </div>

            {/* Engagement Overview Stats - Real Data */}
            <div className="grid grid-cols-5 gap-4 mb-8">
              <div className="card-base p-4 text-center">
                <p className="text-viraly-muted text-sm">Total Users</p>
                <p className="text-2xl font-bold text-viraly-orange">{engagementStats?.overview?.total_users?.toLocaleString() || 0}</p>
                <p className="text-xs text-viraly-muted">registered accounts</p>
              </div>
              <div className="card-base p-4 text-center">
                <p className="text-viraly-muted text-sm">Active Users</p>
                <p className="text-2xl font-bold text-viraly-success">{engagementStats?.overview?.active_users?.toLocaleString() || 0}</p>
                <p className="text-xs text-viraly-muted">last 30 days</p>
              </div>
              <div className="card-base p-4 text-center">
                <p className="text-viraly-muted text-sm">Engagement Rate</p>
                <p className="text-2xl font-bold text-purple-400">{engagementStats?.overview?.engagement_rate || 0}%</p>
                <p className="text-xs text-viraly-muted">active/total</p>
              </div>
              <div className="card-base p-4 text-center">
                <p className="text-viraly-muted text-sm">Total Posts</p>
                <p className="text-2xl font-bold text-blue-400">{engagementStats?.overview?.total_posts?.toLocaleString() || 0}</p>
                <p className="text-xs text-viraly-muted">all time</p>
              </div>
              <div className="card-base p-4 text-center">
                <p className="text-viraly-muted text-sm">Posts This Week</p>
                <p className="text-2xl font-bold">{engagementStats?.overview?.posts_this_week?.toLocaleString() || 0}</p>
                <p className="text-xs text-viraly-muted">{engagementStats?.overview?.posts_today || 0} today</p>
              </div>
            </div>

            {/* Platform Breakdown & Content Stats */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-viraly-orange" />
                  Posts by Platform
                </h3>
                <div className="space-y-4">
                  {engagementStats?.platform_distribution && Object.entries(engagementStats.platform_distribution).length > 0 ? (
                    Object.entries(engagementStats.platform_distribution)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 5)
                      .map(([platform, count]) => {
                        const total = Object.values(engagementStats.platform_distribution).reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? ((count / total) * 100).toFixed(0) : 0;
                        const platformColors = {
                          instagram: { bg: 'bg-pink-500/20', text: 'text-pink-400', label: 'IG' },
                          facebook: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'FB' },
                          tiktok: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'TT' },
                          pinterest: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'PI' },
                          linkedin: { bg: 'bg-blue-700/20', text: 'text-blue-300', label: 'LI' },
                          youtube: { bg: 'bg-red-600/20', text: 'text-red-400', label: 'YT' },
                          twitter: { bg: 'bg-gray-500/20', text: 'text-gray-300', label: 'X' },
                          twitter: { bg: 'bg-sky-500/20', text: 'text-sky-400', label: 'TW' },
                          linkedin: { bg: 'bg-blue-600/20', text: 'text-blue-500', label: 'LI' }
                        };
                        const colors = platformColors[platform.toLowerCase()] || { bg: 'bg-gray-500/20', text: 'text-gray-400', label: platform.substring(0,2).toUpperCase() };
                        return (
                          <div key={platform} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
                                <span className={`${colors.text} font-bold`}>{colors.label}</span>
                              </div>
                              <div>
                                <p className="font-medium capitalize">{platform}</p>
                                <p className="text-xs text-viraly-muted">{percentage}% of total</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${colors.text}`}>{count.toLocaleString()}</p>
                              <p className="text-xs text-viraly-muted">posts</p>
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <p className="text-viraly-muted text-center py-4">No platform data yet</p>
                  )}
                </div>
              </div>

              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-viraly-orange" />
                  Content Generation Stats
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Total Generations</p>
                      <p className="text-xs text-viraly-muted">AI content created</p>
                    </div>
                    <p className="font-bold text-viraly-orange">{engagementStats?.content_stats?.total_generations?.toLocaleString() || 0}</p>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Video Generations</p>
                      <p className="text-xs text-viraly-muted">AI videos created</p>
                    </div>
                    <p className="font-bold text-blue-400">{engagementStats?.content_stats?.video_generations?.toLocaleString() || 0}</p>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Published Posts</p>
                      <p className="text-xs text-viraly-muted">Successfully posted</p>
                    </div>
                    <p className="font-bold text-viraly-success">{engagementStats?.content_stats?.published_posts?.toLocaleString() || 0}</p>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Scheduled Posts</p>
                      <p className="text-xs text-viraly-muted">Pending publication</p>
                    </div>
                    <p className="font-bold text-yellow-400">{engagementStats?.content_stats?.scheduled_posts?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Activity Trend */}
            <div className="card-base p-6 mb-8">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-viraly-orange" />
                Daily Activity (Last 7 Days)
              </h3>
              <div className="flex items-end justify-between gap-2 h-32">
                {(engagementStats?.daily_trend || []).map((day, idx) => {
                  const maxPosts = Math.max(...(engagementStats?.daily_trend || []).map(d => d.posts || 0), 1);
                  const height = ((day.posts || 0) / maxPosts) * 100;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                      <div 
                        className="w-full bg-viraly-orange/80 rounded-t transition-all" 
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                      <p className="text-xs text-viraly-muted">{day.date}</p>
                      <p className="text-xs font-medium">{day.posts}</p>
                    </div>
                  );
                })}
                {(!engagementStats?.daily_trend || engagementStats.daily_trend.length === 0) && (
                  <p className="text-viraly-muted text-center w-full py-8">No activity data available</p>
                )}
              </div>
            </div>

            {/* Advanced Analytics Row */}
            <div className="grid grid-cols-3 gap-6 mb-8">
              {/* Viral Performance */}
              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Viral Performance
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Avg Viral Score</span>
                    <span className="font-bold text-yellow-400">{engagementStats?.viral_metrics?.avg_score || 0}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Best Score</span>
                    <span className="font-bold text-viraly-success">{engagementStats?.viral_metrics?.max_score || 0}%</span>
                  </div>
                  <div className="h-px bg-white/10 my-2"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">High Viral (75%+)</span>
                    <span className="font-bold text-viraly-success">{engagementStats?.viral_metrics?.high_viral_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Medium (50-74%)</span>
                    <span className="font-bold text-yellow-400">{engagementStats?.viral_metrics?.medium_viral_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Low (&lt;50%)</span>
                    <span className="font-bold text-red-400">{engagementStats?.viral_metrics?.low_viral_count || 0}</span>
                  </div>
                </div>
              </div>

              {/* Feature Usage */}
              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-400" />
                  Feature Usage
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Video Posts</span>
                    <span className="font-bold text-blue-400">{engagementStats?.feature_usage?.video_posts || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Template Posts</span>
                    <span className="font-bold text-purple-400">{engagementStats?.feature_usage?.template_posts || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Voice Posts</span>
                    <span className="font-bold text-green-400">{engagementStats?.feature_usage?.voice_posts || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">URL Posts</span>
                    <span className="font-bold text-pink-400">{engagementStats?.feature_usage?.url_posts || 0}</span>
                  </div>
                </div>
              </div>

              {/* User Retention */}
              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-viraly-orange" />
                  User Retention
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Trial Users</span>
                    <span className="font-bold">{engagementStats?.retention?.trial_users || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Paid Users</span>
                    <span className="font-bold text-viraly-success">{engagementStats?.retention?.paid_users || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Conversion Rate</span>
                    <span className="font-bold text-viraly-orange">{engagementStats?.retention?.conversion_rate || 0}%</span>
                  </div>
                  <div className="h-px bg-white/10 my-2"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-viraly-muted">Power Users (5+ posts)</span>
                    <span className="font-bold text-purple-400">{engagementStats?.retention?.power_users || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Last Updated */}
            {engagementStats?.calculated_at && (
              <p className="text-xs text-viraly-muted text-right">
                Last updated: {new Date(engagementStats.calculated_at).toLocaleString()}
              </p>
            )}
          </motion.div>
        )}

        {/* ======== VIRA (BOT) TAB ======== */}
        {activeTab === "auto-reply" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Bot className="w-8 h-8 text-viraly-orange" />
                  VIRA (Bot)
                </h1>
                <p className="text-viraly-muted mt-2">
                  AI-powered chatbot for auto-replies on your social accounts and customer engagement
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={async () => {
                    await saveAutoReplySettings();
                    await saveViralySocialAccounts();
                  }}
                  disabled={autoReplySaving || savingViralySocial}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-vira-settings-btn"
                >
                  {(autoReplySaving || savingViralySocial) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save All Settings
                </Button>
              </div>
            </div>

            {/* Sub-tabs for VIRA */}
            <div className="flex gap-2 mb-6">
              {['accounts', 'settings', 'test', 'history'].map((tab) => (
                <Button
                  key={tab}
                  onClick={() => setAutoReplySubTab(tab)}
                  className={autoReplySubTab === tab ? 'btn-primary' : 'btn-secondary'}
                  data-testid={`vira-tab-${tab}`}
                >
                  {tab === 'accounts' && <Globe className="w-4 h-4 mr-2" />}
                  {tab === 'settings' && <Settings className="w-4 h-4 mr-2" />}
                  {tab === 'test' && <Zap className="w-4 h-4 mr-2" />}
                  {tab === 'history' && <History className="w-4 h-4 mr-2" />}
                  {tab === 'accounts' ? 'Social Accounts' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </Button>
              ))}
            </div>

            {/* Social Accounts Sub-Tab */}
            {autoReplySubTab === 'accounts' && (
              <div className="space-y-6">
                {/* Viraly Social Media Handles */}
                <div className="card-base p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                      <Globe className="w-5 h-5 text-viraly-orange" />
                      Viraly Social Media Handles
                    </h3>
                  </div>
                  <p className="text-sm text-viraly-muted mb-4">
                    Connect your brand's social accounts for VIRA auto-replies on comments & DMs and content posting
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Facebook */}
                    <div className="p-4 rounded-lg bg-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#1877F2] flex items-center justify-center text-white font-bold">f</div>
                          <div>
                            <span className="font-medium">Facebook Page</span>
                            {viralySocialAccounts.accounts?.facebook?.connected && (
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Connected</span>
                            )}
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="text-xs text-viraly-muted">Auto-Reply</span>
                          <button 
                            onClick={() => toggleViralySocialAutoReply('facebook')}
                            className={`w-10 h-5 rounded-full transition-colors ${viralySocialAccounts.accounts?.facebook?.auto_reply_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}
                            data-testid="toggle-fb-autoreply"
                          >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${viralySocialAccounts.accounts?.facebook?.auto_reply_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </label>
                      </div>
                      <Input 
                        placeholder="e.g., @ViralySocial or Page ID"
                        value={viralySocialAccounts.accounts?.facebook?.handle || ""}
                        onChange={(e) => updateViralySocialHandle('facebook', 'handle', e.target.value)}
                        className="input-base"
                      />
                    </div>
                    
                    {/* Instagram */}
                    <div className="p-4 rounded-lg bg-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">ig</div>
                          <div>
                            <span className="font-medium">Instagram</span>
                            {viralySocialAccounts.accounts?.instagram?.connected && (
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Connected</span>
                            )}
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="text-xs text-viraly-muted">Auto-Reply</span>
                          <button 
                            onClick={() => toggleViralySocialAutoReply('instagram')}
                            className={`w-10 h-5 rounded-full transition-colors ${viralySocialAccounts.accounts?.instagram?.auto_reply_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}
                            data-testid="toggle-ig-autoreply"
                          >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${viralySocialAccounts.accounts?.instagram?.auto_reply_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </label>
                      </div>
                      <Input 
                        placeholder="e.g., @viraly_official"
                        value={viralySocialAccounts.accounts?.instagram?.handle || ""}
                        onChange={(e) => updateViralySocialHandle('instagram', 'handle', e.target.value)}
                        className="input-base"
                      />
                    </div>
                    
                    {/* TikTok */}
                    <div className="p-4 rounded-lg bg-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center text-white font-bold">tt</div>
                          <div>
                            <span className="font-medium">TikTok</span>
                            {viralySocialAccounts.accounts?.tiktok?.connected && (
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Connected</span>
                            )}
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="text-xs text-viraly-muted">Auto-Reply</span>
                          <button 
                            onClick={() => toggleViralySocialAutoReply('tiktok')}
                            className={`w-10 h-5 rounded-full transition-colors ${viralySocialAccounts.accounts?.tiktok?.auto_reply_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}
                            data-testid="toggle-tiktok-autoreply"
                          >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${viralySocialAccounts.accounts?.tiktok?.auto_reply_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </label>
                      </div>
                      <Input 
                        placeholder="e.g., @viraly"
                        value={viralySocialAccounts.accounts?.tiktok?.handle || ""}
                        onChange={(e) => updateViralySocialHandle('tiktok', 'handle', e.target.value)}
                        className="input-base"
                      />
                    </div>
                    
                    {/* Pinterest */}
                    <div className="p-4 rounded-lg bg-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center text-white font-bold">P</div>
                          <div>
                            <span className="font-medium">Pinterest</span>
                            {viralySocialAccounts.accounts?.pinterest?.connected && (
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Connected</span>
                            )}
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="text-xs text-viraly-muted">Auto-Reply</span>
                          <button 
                            onClick={() => toggleViralySocialAutoReply('pinterest')}
                            className={`w-10 h-5 rounded-full transition-colors ${viralySocialAccounts.accounts?.pinterest?.auto_reply_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}
                            data-testid="toggle-pinterest-autoreply"
                          >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${viralySocialAccounts.accounts?.pinterest?.auto_reply_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </label>
                      </div>
                      <Input 
                        placeholder="e.g., @viraly"
                        value={viralySocialAccounts.accounts?.pinterest?.handle || ""}
                        onChange={(e) => updateViralySocialHandle('pinterest', 'handle', e.target.value)}
                        className="input-base"
                      />
                    </div>
                    
                    {/* LinkedIn */}
                    <div className="p-4 rounded-lg bg-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#0A66C2] flex items-center justify-center text-white font-bold">in</div>
                          <div>
                            <span className="font-medium">LinkedIn</span>
                            {viralySocialAccounts.accounts?.linkedin?.connected && (
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Connected</span>
                            )}
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="text-xs text-viraly-muted">Auto-Reply</span>
                          <button 
                            onClick={() => toggleViralySocialAutoReply('linkedin')}
                            className={`w-10 h-5 rounded-full transition-colors ${viralySocialAccounts.accounts?.linkedin?.auto_reply_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}
                            data-testid="toggle-linkedin-autoreply"
                          >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${viralySocialAccounts.accounts?.linkedin?.auto_reply_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </label>
                      </div>
                      <Input 
                        placeholder="e.g., viraly-official"
                        value={viralySocialAccounts.accounts?.linkedin?.handle || ""}
                        onChange={(e) => updateViralySocialHandle('linkedin', 'handle', e.target.value)}
                        className="input-base"
                      />
                    </div>
                    
                    {/* YouTube */}
                    <div className="p-4 rounded-lg bg-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center text-white font-bold">YT</div>
                          <div>
                            <span className="font-medium">YouTube</span>
                            {viralySocialAccounts.accounts?.youtube?.connected && (
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Connected</span>
                            )}
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="text-xs text-viraly-muted">Auto-Reply</span>
                          <button 
                            onClick={() => toggleViralySocialAutoReply('youtube')}
                            className={`w-10 h-5 rounded-full transition-colors ${viralySocialAccounts.accounts?.youtube?.auto_reply_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}
                            data-testid="toggle-youtube-autoreply"
                          >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${viralySocialAccounts.accounts?.youtube?.auto_reply_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </label>
                      </div>
                      <Input 
                        placeholder="e.g., @ViralySocial"
                        value={viralySocialAccounts.accounts?.youtube?.handle || ""}
                        onChange={(e) => updateViralySocialHandle('youtube', 'handle', e.target.value)}
                        className="input-base"
                      />
                    </div>
                    
                    {/* Twitter/X */}
                    <div className="p-4 rounded-lg bg-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center text-white font-bold">X</div>
                          <div>
                            <span className="font-medium">X (Twitter)</span>
                            {viralySocialAccounts.accounts?.twitter?.connected && (
                              <span className="ml-2 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">Connected</span>
                            )}
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="text-xs text-viraly-muted">Auto-Reply</span>
                          <button 
                            onClick={() => toggleViralySocialAutoReply('twitter')}
                            className={`w-10 h-5 rounded-full transition-colors ${viralySocialAccounts.accounts?.twitter?.auto_reply_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}
                            data-testid="toggle-twitter-autoreply"
                          >
                            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${viralySocialAccounts.accounts?.twitter?.auto_reply_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </label>
                      </div>
                      <Input 
                        placeholder="e.g., @viraly"
                        value={viralySocialAccounts.accounts?.twitter?.handle || ""}
                        onChange={(e) => updateViralySocialHandle('twitter', 'handle', e.target.value)}
                        className="input-base"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-viraly-muted mt-4 p-3 rounded-lg bg-viraly-orange/10">
                    💡 Connect your accounts via bundle.social to enable VIRA auto-replies on comments and DMs.
                    Toggle "Auto-Reply" per platform to enable/disable. Configure response settings in the Settings tab.
                  </p>
                </div>
              </div>
            )}

            {/* Settings Sub-Tab */}
            {autoReplySubTab === 'settings' && (
              <div className="space-y-6">
                {/* Platform Settings */}
                <div className="card-base p-6">
                  <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-viraly-orange" />
                    Platform Settings
                  </h3>
                  <p className="text-sm text-viraly-muted mb-4">
                    Enable auto-replies for Comments and DMs on each platform
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {AUTO_REPLY_PLATFORMS.map((platform) => {
                      const Icon = platform.icon;
                      const platformSettings = autoReplySettings?.platforms?.[platform.id] || { comments: false, dms: false };
                      return (
                        <div key={platform.id} className="p-4 rounded-lg bg-white/5">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg ${platform.color} flex items-center justify-center`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <span className="font-medium">{platform.name}</span>
                            </div>
                          </div>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <button
                                onClick={() => toggleAutoReplyPlatform(platform.id, 'comments')}
                                className={`w-10 h-5 rounded-full transition-colors ${platformSettings.comments ? 'bg-viraly-orange' : 'bg-white/20'}`}
                                data-testid={`toggle-${platform.id}-comments`}
                              >
                                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${platformSettings.comments ? 'translate-x-5' : 'translate-x-0.5'}`} />
                              </button>
                              <span className="text-sm">Comments</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <button
                                onClick={() => toggleAutoReplyPlatform(platform.id, 'dms')}
                                className={`w-10 h-5 rounded-full transition-colors ${platformSettings.dms ? 'bg-viraly-orange' : 'bg-white/20'}`}
                                data-testid={`toggle-${platform.id}-dms`}
                              >
                                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${platformSettings.dms ? 'translate-x-5' : 'translate-x-0.5'}`} />
                              </button>
                              <span className="text-sm">DMs</span>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Response Tone */}
                <div className="card-base p-6">
                  <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                    <Brain className="w-5 h-5 text-viraly-orange" />
                    Response Tone
                  </h3>
                  <div className="grid grid-cols-5 gap-3">
                    {AUTO_REPLY_TONES.map((tone) => (
                      <button
                        key={tone.id}
                        onClick={() => setAutoReplySettings(prev => ({ ...prev, response_tone: tone.id }))}
                        className={`p-4 rounded-lg text-center transition-all ${autoReplySettings.response_tone === tone.id ? 'bg-viraly-orange/20 border-2 border-viraly-orange' : 'bg-white/5 border-2 border-transparent hover:bg-white/10'}`}
                        data-testid={`tone-${tone.id}`}
                      >
                        <span className="text-2xl block mb-2">{tone.emoji}</span>
                        <span className="font-medium text-sm">{tone.name}</span>
                        <p className="text-xs text-viraly-muted mt-1">{tone.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Advanced Settings */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="card-base p-6">
                    <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-viraly-orange" />
                      Review Settings
                    </h3>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <span>Review negative sentiment</span>
                        <button
                          onClick={() => setAutoReplySettings(prev => ({ ...prev, review_negative: !prev.review_negative }))}
                          className={`w-10 h-5 rounded-full transition-colors ${autoReplySettings.review_negative ? 'bg-viraly-orange' : 'bg-white/20'}`}
                        >
                          <div className={`w-4 h-4 rounded-full bg-white transition-transform ${autoReplySettings.review_negative ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </label>
                      <label className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <span>Review complaints</span>
                        <button
                          onClick={() => setAutoReplySettings(prev => ({ ...prev, review_complaints: !prev.review_complaints }))}
                          className={`w-10 h-5 rounded-full transition-colors ${autoReplySettings.review_complaints ? 'bg-viraly-orange' : 'bg-white/20'}`}
                        >
                          <div className={`w-4 h-4 rounded-full bg-white transition-transform ${autoReplySettings.review_complaints ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </label>
                      <label className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <span>Review all before sending</span>
                        <button
                          onClick={() => setAutoReplySettings(prev => ({ ...prev, review_all: !prev.review_all }))}
                          className={`w-10 h-5 rounded-full transition-colors ${autoReplySettings.review_all ? 'bg-viraly-orange' : 'bg-white/20'}`}
                        >
                          <div className={`w-4 h-4 rounded-full bg-white transition-transform ${autoReplySettings.review_all ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                      </label>
                    </div>
                  </div>

                  <div className="card-base p-6">
                    <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-viraly-orange" />
                      Limits & Delays
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-viraly-muted block mb-2">Response Delay (seconds)</label>
                        <Input
                          type="number"
                          value={autoReplySettings.response_delay_seconds}
                          onChange={(e) => setAutoReplySettings(prev => ({ ...prev, response_delay_seconds: parseInt(e.target.value) || 30 }))}
                          className="input-base"
                          min={5}
                          max={300}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-viraly-muted block mb-2">Daily Response Limit</label>
                        <Input
                          type="number"
                          value={autoReplySettings.daily_limit}
                          onChange={(e) => setAutoReplySettings(prev => ({ ...prev, daily_limit: parseInt(e.target.value) || 100 }))}
                          className="input-base"
                          min={10}
                          max={1000}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Blacklist Words */}
                <div className="card-base p-6">
                  <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    Blacklist Words
                  </h3>
                  <p className="text-sm text-viraly-muted mb-4">Messages containing these words will not receive auto-replies</p>
                  <div className="flex gap-2 mb-4">
                    <Input
                      value={autoReplyNewBlacklistWord}
                      onChange={(e) => setAutoReplyNewBlacklistWord(e.target.value)}
                      placeholder="Add word to blacklist..."
                      className="input-base flex-1"
                      onKeyDown={(e) => e.key === 'Enter' && addAutoReplyBlacklistWord()}
                    />
                    <Button onClick={addAutoReplyBlacklistWord} className="btn-secondary">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(autoReplySettings?.blacklist_words || []).map((word) => (
                      <span key={word} className="px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-sm flex items-center gap-2">
                        {word}
                        <button onClick={() => removeAutoReplyBlacklistWord(word)} className="hover:text-red-300">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {(!autoReplySettings?.blacklist_words || autoReplySettings.blacklist_words.length === 0) && (
                      <span className="text-viraly-muted text-sm">No blacklist words added</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Test Sub-Tab */}
            {autoReplySubTab === 'test' && (
              <div className="space-y-6">
                {/* Single Test */}
                <div className="card-base p-6">
                  <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-viraly-orange" />
                    Test Auto-Reply
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm text-viraly-muted block mb-2">Platform</label>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {AUTO_REPLY_PLATFORMS.map((platform) => {
                          const Icon = platform.icon;
                          return (
                            <button
                              key={platform.id}
                              onClick={() => setAutoReplyTestPlatform(platform.id)}
                              className={`p-2 rounded-lg flex items-center gap-1.5 text-sm transition-all ${autoReplyTestPlatform === platform.id ? 'bg-viraly-orange/20 border-2 border-viraly-orange' : 'bg-white/5 border-2 border-transparent hover:bg-white/10'}`}
                              data-testid={`test-platform-${platform.id}`}
                            >
                              <Icon className="w-4 h-4" />
                              <span className="hidden sm:inline">{platform.name}</span>
                            </button>
                          );
                        })}
                      </div>
                      <label className="text-sm text-viraly-muted block mb-2">Test Message</label>
                      <textarea
                        value={autoReplyTestMessage}
                        onChange={(e) => setAutoReplyTestMessage(e.target.value)}
                        placeholder="Enter a test comment or DM to see how VIRA responds..."
                        className="input-base w-full h-32 resize-none"
                        data-testid="test-message-input"
                      />
                      <Button
                        onClick={testAutoReply}
                        disabled={autoReplyTesting || !autoReplyTestMessage.trim()}
                        className="btn-primary mt-4 w-full"
                        data-testid="generate-response-btn"
                      >
                        {autoReplyTesting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate Response
                          </>
                        )}
                      </Button>
                    </div>
                    <div>
                      <label className="text-sm text-viraly-muted block mb-2">VIRA's Response</label>
                      <div className="p-4 rounded-lg bg-white/5 min-h-[200px]">
                        {autoReplyTestResult ? (
                          <div className="space-y-4">
                            <p className="text-viraly-light">{autoReplyTestResult.response}</p>
                            <div className="flex gap-4 pt-4 border-t border-white/10">
                              <div className="text-center">
                                <p className="text-xs text-viraly-muted">Sentiment</p>
                                <p className={`font-medium ${autoReplyTestResult.sentiment === 'positive' ? 'text-green-400' : autoReplyTestResult.sentiment === 'negative' ? 'text-red-400' : 'text-yellow-400'}`}>
                                  {autoReplyTestResult.sentiment}
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-viraly-muted">Intent</p>
                                <p className="font-medium text-viraly-light">{autoReplyTestResult.intent}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-viraly-muted">Confidence</p>
                                <p className="font-medium text-viraly-orange">{autoReplyTestResult.confidence}%</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-viraly-muted">
                            <Bot className="w-12 h-12 mb-2 opacity-50" />
                            <p>Response will appear here</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Simulation Lab */}
                <div className="card-base p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                        <Brain className="w-5 h-5 text-purple-400" />
                        Simulation Lab
                      </h3>
                      <p className="text-sm text-viraly-muted">Test VIRA with 10 challenging scenarios</p>
                    </div>
                    <Button
                      onClick={runAutoReplySimulation}
                      disabled={autoReplyRunningSimulation}
                      className="btn-primary"
                      data-testid="run-simulation-btn"
                    >
                      {autoReplyRunningSimulation ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Running ({autoReplyCurrentSimIndex + 1}/10)
                        </>
                      ) : (
                        <>
                          <PlayCircle className="w-4 h-4 mr-2" />
                          Run Full Simulation
                        </>
                      )}
                    </Button>
                  </div>

                  {autoReplySimulationResults.length > 0 && (
                    <div className="space-y-3 mt-4">
                      {autoReplySimulationResults.map((result, idx) => (
                        <div key={idx} className="p-4 rounded-lg bg-white/5">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 rounded text-xs bg-white/10">{result.category}</span>
                              <span className="px-2 py-0.5 rounded text-xs bg-white/10">{result.platform}</span>
                            </div>
                            {result.success ? (
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-400" />
                            )}
                          </div>
                          <p className="text-sm text-viraly-muted mb-2">"{result.message}"</p>
                          <p className="text-sm text-viraly-light">{result.response}</p>
                          {result.success && (
                            <div className="flex gap-4 mt-2 text-xs">
                              <span className={result.sentiment === 'positive' ? 'text-green-400' : result.sentiment === 'negative' ? 'text-red-400' : 'text-yellow-400'}>
                                {result.sentiment}
                              </span>
                              <span className="text-viraly-muted">{result.intent}</span>
                              <span className="text-viraly-orange">{result.confidence}%</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* History Sub-Tab */}
            {autoReplySubTab === 'history' && (
              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <History className="w-5 h-5 text-viraly-orange" />
                  Response History
                </h3>
                <p className="text-viraly-muted text-center py-12">
                  Response history will appear here once webhooks are connected and auto-replies are sent.
                </p>
              </div>
            )}
          </motion.div>
        )}
        {/* ======== END VIRA (BOT) TAB ======== */}

        {activeTab === "revenue" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <DollarSign className="w-8 h-8 text-viraly-orange" />
                  Revenue Dashboard
                </h1>
                <p className="text-viraly-muted mt-2">
                  Financial overview with tax breakdown and gateway fees
                </p>
              </div>
              <div className="flex gap-3">
                <Button className="btn-secondary flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export CSV
                </Button>
                <Button 
                  onClick={() => fetchAdminData()}
                  className="btn-primary flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Revenue Stats Cards */}
            {revenueStats && (
              <>
                <div className="grid grid-cols-4 gap-4 mb-8">
                  <div className="card-base p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-viraly-success/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-viraly-success" />
                      </div>
                      <p className="text-viraly-muted text-sm">Total Revenue</p>
                    </div>
                    <p className="text-2xl font-bold text-viraly-success">${revenueStats.total_revenue.toLocaleString()}</p>
                  </div>
                  <div className="card-base p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                        <Receipt className="w-5 h-5 text-blue-400" />
                      </div>
                      <p className="text-viraly-muted text-sm">Tax Collected</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-400">${revenueStats.total_tax_collected.toLocaleString()}</p>
                  </div>
                  <div className="card-base p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-yellow-400" />
                      </div>
                      <p className="text-viraly-muted text-sm">Gateway Fees</p>
                    </div>
                    <p className="text-2xl font-bold text-yellow-400">${revenueStats.total_gateway_fees.toLocaleString()}</p>
                  </div>
                  <div className="card-base p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-viraly-orange/20 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-viraly-orange" />
                      </div>
                      <p className="text-viraly-muted text-sm">Net Revenue</p>
                    </div>
                    <p className="text-2xl font-bold text-viraly-orange">${revenueStats.net_revenue.toLocaleString()}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-8">
                  {/* By Province - Clickable for drill-down */}
                  <div className="card-base p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                        <Globe className="w-5 h-5 text-viraly-orange" />
                        Revenue by Region
                      </h3>
                      <p className="text-xs text-viraly-muted">Click for details</p>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(revenueStats.by_province).map(([code, data]) => (
                        <div 
                          key={code} 
                          className="flex items-center justify-between p-3 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 hover:border-viraly-orange/50 border border-transparent transition-all"
                          onClick={() => setRevenueDetailModal({ 
                            code, 
                            name: code === "INTL" ? "International" : CANADIAN_TAX_RATES[code]?.name || code,
                            data: revenueByProvince[code] || { total: data.revenue, monthly: 0, daily: 0, ytd: 0, transactions: data.count },
                            apiData: data
                          })}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">{code === "INTL" ? "🌍" : "🇨🇦"}</span>
                            <div>
                              <p className="font-medium">{code === "INTL" ? "International" : CANADIAN_TAX_RATES[code]?.name || code}</p>
                              <p className="text-xs text-viraly-muted">{data.count} transactions</p>
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-3">
                            <div>
                              <p className="font-bold">${data.revenue.toLocaleString()}</p>
                              {data.tax > 0 && (
                                <p className="text-xs text-viraly-muted">Tax: ${data.tax.toFixed(2)}</p>
                              )}
                            </div>
                            <ChevronRight className="w-4 h-4 text-viraly-muted" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* By Product - Also clickable */}
                  <div className="card-base p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                        <FileText className="w-5 h-5 text-viraly-orange" />
                        Revenue by Product
                      </h3>
                      <p className="text-xs text-viraly-muted">Click for details</p>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(revenueStats.by_product).map(([product, data]) => (
                        <div 
                          key={product} 
                          className="flex items-center justify-between p-3 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 hover:border-viraly-orange/50 border border-transparent transition-all"
                          onClick={() => toast.info(`${product.replace("_", " ")}: ${data.count} purchases totaling $${data.revenue.toLocaleString()}`)}
                        >
                          <div>
                            <p className="font-medium capitalize">{product.replace("_", " ")}</p>
                            <p className="text-xs text-viraly-muted">{data.count} purchases</p>
                          </div>
                          <div className="text-right flex items-center gap-3">
                            <div>
                              <p className="font-bold">${data.revenue.toLocaleString()}</p>
                              <p className="text-xs text-viraly-muted">
                                Avg: ${(data.revenue / data.count).toFixed(2)}
                              </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-viraly-muted" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Recent Transactions */}
            <div className="card-base overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="font-heading text-lg font-bold">Recent Transactions</h3>
              </div>
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4 font-medium text-viraly-muted">Customer</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Product</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Region</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Subtotal</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Tax</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Total</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => (
                    <tr key={txn.id} className="border-t border-white/5">
                      <td className="p-4 text-sm">{txn.user_email}</td>
                      <td className="p-4 text-sm">{txn.product}</td>
                      <td className="p-4 text-sm">
                        {txn.province ? (
                          <span className="flex items-center gap-1">
                            🇨🇦 {txn.province}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            🌍 INTL
                          </span>
                        )}
                      </td>
                      <td className="p-4 text-sm">${txn.subtotal.toFixed(2)}</td>
                      <td className="p-4 text-sm text-viraly-muted">${txn.tax.toFixed(2)}</td>
                      <td className="p-4 text-sm font-medium">${txn.total.toFixed(2)}</td>
                      <td className="p-4 text-sm text-viraly-muted">{txn.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Tax Rates Reference */}
            <div className="mt-8 card-base p-6">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-viraly-orange" />
                Canadian Tax Rates Reference
              </h3>
              <div className="grid grid-cols-4 gap-3">
                {Object.entries(CANADIAN_TAX_RATES).map(([code, info]) => (
                  <div key={code} className="p-3 rounded-lg bg-white/5 text-center">
                    <p className="font-medium">{code}</p>
                    <p className="text-viraly-orange font-bold">{info.rate}%</p>
                    <p className="text-xs text-viraly-muted">{info.type}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Pricing Plans Tab */}
        {activeTab === "pricing" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Tag className="w-8 h-8 text-viraly-orange" />
                  Pricing Plans
                </h1>
                <p className="text-viraly-muted mt-2">Manage subscription plans and Pay As You Go bundles</p>
              </div>
              <Button 
                className="btn-primary flex items-center gap-2" 
                data-testid="add-plan-btn"
                onClick={() => {
                  setEditingPlan(null);
                  setShowPlanModal(true);
                }}
              >
                <Plus className="w-4 h-4" />
                Add Plan
              </Button>
            </div>

            {/* Subscription Plans */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4">Monthly Subscriptions</h2>
              <div className="grid grid-cols-3 gap-4">
                {(pricingPlans || []).filter(p => p.interval === 'month').map((plan) => (
                  <div key={plan.id} className="card-base p-6" data-testid={`plan-card-${plan.id}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading text-lg font-bold">{plan.name}</h3>
                      <div className="flex items-center gap-2">
                        <button 
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors" 
                          data-testid={`edit-plan-${plan.id}`}
                          onClick={() => {
                            setEditingPlan(plan);
                            setShowPlanModal(true);
                          }}
                        >
                          <Edit className="w-4 h-4 text-viraly-muted" />
                        </button>
                        {plan.is_active ? (
                          <span className="px-2 py-1 rounded text-xs bg-viraly-success/20 text-viraly-success">Active</span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400">Inactive</span>
                        )}
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-viraly-orange mb-4">${plan.price}<span className="text-sm text-viraly-muted font-normal">/mo</span></div>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center justify-between">
                        <span className="text-viraly-muted">Posts</span>
                        <span className="font-medium">{plan.posts}</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span className="text-viraly-muted">Videos</span>
                        <span className="font-medium">{plan.videos}</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span className="text-viraly-muted">Regen Credits</span>
                        <span className="font-medium">{plan.regen_credits}</span>
                      </li>
                      <li className="flex items-center justify-between">
                        <span className="text-viraly-muted">Radar URLs</span>
                        <span className="font-medium">{plan.radar_urls}</span>
                      </li>
                      <li className="flex items-center justify-between pt-2 border-t border-white/10">
                        <span className="text-viraly-muted">Yearly Discount</span>
                        <div className="flex items-center gap-2">
                          <Input 
                            type="number" 
                            defaultValue={plan.yearly_discount_percent || 35} 
                            className="w-16 input-base text-center text-sm h-7"
                            min="0"
                            max="100"
                            data-testid={`yearly-discount-${plan.id}`}
                          />
                          <span className="text-viraly-muted">%</span>
                        </div>
                      </li>
                    </ul>
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <p className="text-xs text-viraly-muted">
                        Yearly: ${((plan.price * 12) * (1 - (plan.yearly_discount_percent || 35) / 100)).toFixed(0)}/yr 
                        <span className="text-viraly-success ml-1">(${((plan.price * (1 - (plan.yearly_discount_percent || 35) / 100))).toFixed(0)}/mo)</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PAYG Bundles */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4">Pay-As-You-Go Bundles</h2>
              <div className="card-base overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="text-left p-4 font-medium text-viraly-muted">Bundle</th>
                      <th className="text-left p-4 font-medium text-viraly-muted">Type</th>
                      <th className="text-left p-4 font-medium text-viraly-muted">Posts/Videos</th>
                      <th className="text-left p-4 font-medium text-viraly-muted">Price</th>
                      <th className="text-left p-4 font-medium text-viraly-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bundles.map((bundle) => (
                      <tr key={bundle.id} className="border-t border-white/5">
                        <td className="p-4 font-medium">{bundle.name}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            bundle.type === 'payg' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                          }`}>{bundle.type}</span>
                        </td>
                        <td className="p-4">{bundle.posts || bundle.videos}</td>
                        <td className="p-4 font-bold text-viraly-orange">${bundle.price}</td>
                        <td className="p-4">
                          <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                            <Edit className="w-4 h-4 text-viraly-muted" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Regen Packs */}
            <div>
              <h2 className="font-heading text-xl font-bold mb-4">Regen Credit Packs</h2>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { name: "Basic", credits: 5, price: 1.99 },
                  { name: "Standard", credits: 15, price: 4.99 },
                  { name: "Bulk", credits: 30, price: 7.99 }
                ].map((pack) => (
                  <div key={pack.name} className="card-base p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{pack.name}</p>
                      <p className="text-sm text-viraly-muted">{pack.credits} credits</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-viraly-orange">${pack.price}</p>
                      <button className="text-xs text-viraly-muted hover:text-white">Edit</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Add/Edit Plan Modal */}
            {showPlanModal && (
              <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowPlanModal(false)}>
                <div className="bg-[#1a1a2e] rounded-xl p-6 max-w-2xl w-full border border-white/10 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-viraly-light">
                      {editingPlan ? `Edit Plan: ${editingPlan.name}` : 'Add New Plan'}
                    </h3>
                    <button onClick={() => setShowPlanModal(false)} className="text-viraly-muted hover:text-white text-xl">✕</button>
                  </div>
                  
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const planData = {
                      name: formData.get('name'),
                      price: parseFloat(formData.get('price')),
                      interval: formData.get('interval'),
                      posts: parseInt(formData.get('posts')),
                      videos: parseInt(formData.get('videos')),
                      regen_credits: parseInt(formData.get('regen_credits')),
                      radar_urls: parseInt(formData.get('radar_urls')),
                      profiles: parseInt(formData.get('profiles')),
                      yearly_discount_percent: parseInt(formData.get('yearly_discount')),
                      is_active: formData.get('is_active') === 'on'
                    };
                    
                    try {
                      if (editingPlan) {
                        await apiClient.put(`/admin/plans/${editingPlan.id}`, planData);
                        toast.success(`Plan "${planData.name}" updated successfully`);
                      } else {
                        await apiClient.post('/admin/plans', planData);
                        toast.success(`Plan "${planData.name}" created successfully`);
                      }
                      setShowPlanModal(false);
                      // Refetch plans
                      const res = await apiClient.get('/admin/plans');
                      setPricingPlans(res.data.plans || []);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to save plan');
                    }
                  }} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-viraly-muted">Plan Name</label>
                        <Input name="name" defaultValue={editingPlan?.name || ''} required className="input-base mt-1" placeholder="e.g., Starter" />
                      </div>
                      <div>
                        <label className="text-sm text-viraly-muted">Price ($/month)</label>
                        <Input name="price" type="number" step="0.01" defaultValue={editingPlan?.price || ''} required className="input-base mt-1" placeholder="29.00" />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-viraly-muted">Interval</label>
                        <select name="interval" defaultValue={editingPlan?.interval || 'month'} className="input-base mt-1 w-full">
                          <option value="month">Monthly</option>
                          <option value="year">Yearly</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm text-viraly-muted">Yearly Discount (%)</label>
                        <Input name="yearly_discount" type="number" min="0" max="100" defaultValue={editingPlan?.yearly_discount_percent || 35} className="input-base mt-1" />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <label className="text-sm text-viraly-muted">Posts</label>
                        <Input name="posts" type="number" defaultValue={editingPlan?.posts || 50} required className="input-base mt-1" />
                      </div>
                      <div>
                        <label className="text-sm text-viraly-muted">Videos</label>
                        <Input name="videos" type="number" defaultValue={editingPlan?.videos || 0} required className="input-base mt-1" />
                      </div>
                      <div>
                        <label className="text-sm text-viraly-muted">Regen Credits</label>
                        <Input name="regen_credits" type="number" defaultValue={editingPlan?.regen_credits || 5} required className="input-base mt-1" />
                      </div>
                      <div>
                        <label className="text-sm text-viraly-muted">Radar URLs</label>
                        <Input name="radar_urls" type="number" defaultValue={editingPlan?.radar_urls || 3} required className="input-base mt-1" />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-viraly-muted">Brand Profiles</label>
                        <Input name="profiles" type="number" defaultValue={editingPlan?.profiles || 2} required className="input-base mt-1" />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input type="checkbox" name="is_active" id="is_active" defaultChecked={editingPlan?.is_active ?? true} className="w-4 h-4" />
                        <label htmlFor="is_active" className="text-sm text-viraly-muted">Plan is Active</label>
                      </div>
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setShowPlanModal(false)} className="flex-1">
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1 btn-primary">
                        {editingPlan ? 'Update Plan' : 'Create Plan'}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Transactions Tab */}
        {activeTab === "transactions" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Receipt className="w-8 h-8 text-viraly-orange" />
                  Transactions
                </h1>
                <p className="text-viraly-muted mt-2">View all payment transactions</p>
              </div>
              <Button className="btn-secondary flex items-center gap-2" data-testid="export-transactions-btn">
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>

            {/* Filters Row */}
            <div className="flex items-center gap-4 mb-6 p-4 rounded-xl bg-white/5">
              <div className="flex items-center gap-2 text-viraly-muted">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <select
                value={transactionFilter.status}
                onChange={(e) => setTransactionFilter({...transactionFilter, status: e.target.value})}
                className="input-base px-3 py-2 text-sm rounded-lg"
                data-testid="filter-txn-status"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={transactionFilter.province}
                onChange={(e) => setTransactionFilter({...transactionFilter, province: e.target.value})}
                className="input-base px-3 py-2 text-sm rounded-lg"
                data-testid="filter-txn-province"
              >
                <option value="all">All Regions</option>
                <option value="ON">Ontario</option>
                <option value="BC">British Columbia</option>
                <option value="AB">Alberta</option>
                <option value="QC">Quebec</option>
                <option value="INTL">International</option>
              </select>
              <select
                value={transactionFilter.product}
                onChange={(e) => setTransactionFilter({...transactionFilter, product: e.target.value})}
                className="input-base px-3 py-2 text-sm rounded-lg"
                data-testid="filter-txn-product"
              >
                <option value="all">All Products</option>
                <option value="plan">Subscriptions</option>
                <option value="bundle">Post Bundles</option>
                <option value="video">Video Packs</option>
              </select>
              <select
                value={transactionFilter.dateRange}
                onChange={(e) => setTransactionFilter({...transactionFilter, dateRange: e.target.value})}
                className="input-base px-3 py-2 text-sm rounded-lg"
                data-testid="filter-txn-date"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
              </select>
              {(transactionFilter.status !== "all" || transactionFilter.province !== "all" || transactionFilter.product !== "all" || transactionFilter.dateRange !== "all") && (
                <button
                  onClick={() => setTransactionFilter({ status: "all", province: "all", product: "all", dateRange: "all" })}
                  className="text-xs text-viraly-orange hover:text-white transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>

            <div className="card-base overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left p-4 font-medium text-viraly-muted">ID</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Customer</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Product</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Region</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Subtotal</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Tax</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">PG Fee</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Total</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Status</th>
                    <th className="text-left p-4 font-medium text-viraly-muted">Gateway</th>
                  </tr>
                </thead>
                <tbody>
                  {(transactions || [])
                    .filter(txn => 
                      (transactionFilter.status === 'all' || txn.status === transactionFilter.status) &&
                      (transactionFilter.province === 'all' || txn.province === transactionFilter.province || (transactionFilter.province === 'INTL' && !txn.province))
                    )
                    .map((txn) => (
                    <tr 
                      key={txn.id} 
                      className="border-t border-white/5 hover:bg-white/10 cursor-pointer transition-colors" 
                      data-testid={`txn-row-${txn.id}`}
                      onClick={() => setSelectedTransaction(txn)}
                    >
                      <td className="p-4 text-sm text-viraly-orange font-mono">#{txn.id?.slice(0, 8)}</td>
                      <td className="p-4">
                        <p className="text-sm">{txn.user_name || 'N/A'}</p>
                        <p className="text-xs text-viraly-muted">{txn.user_email}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm capitalize">{txn.product_type || txn.type || txn.product || 'N/A'}</p>
                        <p className="text-xs text-viraly-muted">{txn.plan_id || ''}</p>
                      </td>
                      <td className="p-4 text-sm">
                        {txn.province ? `🇨🇦 ${txn.province}` : '🌍 INTL'}
                      </td>
                      <td className="p-4 text-sm">${(txn.subtotal || txn.amount || 0).toFixed(2)}</td>
                      <td className="p-4 text-sm text-viraly-muted">
                        ${(txn.tax_amount || txn.tax || 0).toFixed(2)}
                        {txn.tax_rate && <span className="text-xs ml-1">({txn.tax_rate}%)</span>}
                      </td>
                      <td className="p-4 text-sm text-blue-400">${(txn.gateway_fee || 0).toFixed(2)}</td>
                      <td className="p-4 text-sm font-medium text-viraly-orange">${(txn.total_amount || txn.total || txn.amount || 0).toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          txn.status === 'completed' || txn.status === 'succeeded' ? 'bg-viraly-success/20 text-viraly-success' :
                          txn.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>{txn.status}</span>
                      </td>
                      <td className="p-4">
                        <p className="text-sm">{txn.gateway || 'PayPal'}</p>
                        <p className="text-xs text-viraly-muted">{txn.created_at ? new Date(txn.created_at).toLocaleDateString() : txn.date}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Communications Tab */}
        {activeTab === "communications" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Mail className="w-8 h-8 text-viraly-orange" />
                  Communications
                </h1>
                <p className="text-viraly-muted mt-2">Manage email templates and notification settings</p>
              </div>
              <Button 
                className="btn-primary flex items-center gap-2" 
                data-testid="new-email-template-btn"
                onClick={() => setCreateEmailTemplateModal(true)}
              >
                <Plus className="w-4 h-4" />
                New Email Template
              </Button>
            </div>

            {/* Email Templates - Now Editable with Create */}
            <div className="card-base p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                  <Mail className="w-5 h-5 text-viraly-orange" />
                  Email Templates ({emailTemplates.length})
                </h3>
                <p className="text-viraly-muted text-sm">Click to edit template</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {emailTemplates.map((template) => (
                  <div 
                    key={template.id} 
                    className="p-4 rounded-xl bg-white/5 hover:bg-white/10 hover:border-viraly-orange/50 border border-transparent transition-all cursor-pointer" 
                    data-testid={`email-template-${template.name.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setEditEmailTemplateModal(template)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">{template.name}</p>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        template.status === 'active' ? 'bg-viraly-success/20 text-viraly-success' : 'bg-white/10 text-viraly-muted'
                      }`}>{template.status}</span>
                    </div>
                    <p className="text-sm text-viraly-muted mb-2">{template.description}</p>
                    <p className="text-xs text-viraly-orange truncate">Subject: {template.subject}</p>
                    <button className="mt-2 text-xs text-viraly-orange hover:text-white transition-colors flex items-center gap-1">
                      <Edit className="w-3 h-3" /> Edit Template
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Notification Settings Quick Access */}
            <div className="card-base p-6">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-viraly-orange" />
                Notification Settings
              </h3>
              <p className="text-sm text-viraly-muted mb-4">
                Configure push notification and email preferences for different user events.
                For announcements, use the <Link to="#" onClick={() => setActiveTab('promotions')} className="text-viraly-orange hover:underline">Promotions & Announcements</Link> tab.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(notificationTemplatesByCategory).slice(0, 4).map(([category, templates]) => (
                  <div key={category} className="p-4 rounded-xl bg-white/5">
                    <p className="font-medium text-sm capitalize mb-2">{category.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-viraly-muted">{templates.length} templates configured</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Health Monitor Tab */}
        {activeTab === "health-monitor" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Activity className="w-8 h-8 text-viraly-orange" />
                  System Health Monitor
                </h1>
                <p className="text-viraly-muted mt-2">Real-time monitoring of all system components</p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={async () => {
                    setRunningHealthCheck(true);
                    try {
                      const adminToken = localStorage.getItem("viraly_admin_token");
                      const response = await apiClient.post("/admin/health/run-check", {}, {
                        headers: { Authorization: `Bearer ${adminToken}` }
                      });
                      setHealthStatus(response.data.current);
                      setHealthHistory(prev => [response.data.current, ...prev].slice(0, 50));
                      toast.success("Health check completed!");
                    } catch (error) {
                      toast.error("Health check failed");
                    } finally {
                      setRunningHealthCheck(false);
                    }
                  }}
                  disabled={runningHealthCheck}
                  className="btn-primary flex items-center gap-2"
                  data-testid="run-health-check-btn"
                >
                  {runningHealthCheck ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <PlayCircle className="w-4 h-4" />
                  )}
                  Run Health Check
                </Button>
                <Button
                  onClick={() => fetchAdminData()}
                  className="btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Overall Status Banner */}
            <div className={`p-6 rounded-xl mb-6 ${
              healthStatus?.overall === "healthy" 
                ? "bg-green-500/10 border border-green-500/30" 
                : healthStatus?.overall === "degraded"
                ? "bg-yellow-500/10 border border-yellow-500/30"
                : "bg-red-500/10 border border-red-500/30"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {healthStatus?.overall === "healthy" ? (
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                  ) : healthStatus?.overall === "degraded" ? (
                    <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-8 h-8 text-yellow-500" />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                      <AlertOctagon className="w-8 h-8 text-red-500" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-bold capitalize">{healthStatus?.overall || "Unknown"}</h2>
                    <p className="text-viraly-muted">
                      Last checked: {healthStatus?.last_check ? new Date(healthStatus.last_check).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'short', timeStyle: 'medium' }) + ' EST' : "Never"}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-viraly-muted">Auto-check interval</p>
                  <p className="font-bold text-viraly-orange">Every {healthStatus?.auto_check_interval || 15} minutes</p>
                </div>
              </div>
            </div>

            {/* Individual Service Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {(healthStatus?.checks || []).map((check, idx) => (
                <div 
                  key={idx} 
                  className={`card-base p-4 ${
                    check.status === "healthy" && check.severity === "normal"
                      ? "border-l-4 border-green-500" 
                      : check.severity === "warning"
                      ? "border-l-4 border-yellow-500"
                      : check.status === "unhealthy" || check.severity === "critical"
                      ? "border-l-4 border-red-500"
                      : "border-l-4 border-green-500"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Server className="w-4 h-4 text-viraly-muted" />
                      <span className="font-medium">{check.name}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      check.status === "healthy" && check.severity === "normal" 
                        ? "bg-green-500/20 text-green-400" 
                        : check.severity === "warning"
                        ? "bg-yellow-500/20 text-yellow-400"
                        : "bg-red-500/20 text-red-400"
                    }`}>{check.severity || check.status}</span>
                  </div>
                  
                  {/* Latency with range indicator */}
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-viraly-muted">Latency</span>
                    <span className={`font-mono font-bold ${
                      check.severity === "normal" ? "text-green-400" : 
                      check.severity === "warning" ? "text-yellow-400" : "text-red-400"
                    }`}>{check.latency}ms</span>
                  </div>
                  
                  {/* Latency Range */}
                  {check.latency_range && (
                    <div className="text-xs text-viraly-muted mb-2">
                      Range: {check.latency_range}
                    </div>
                  )}
                  
                  {/* User Impact - ALWAYS SHOW */}
                  <div className={`p-2 rounded-lg mb-2 ${
                    check.severity === "normal" 
                      ? "bg-green-500/10 border border-green-500/20" 
                      : check.severity === "warning"
                      ? "bg-yellow-500/10 border border-yellow-500/20"
                      : "bg-red-500/10 border border-red-500/20"
                  }`}>
                    <p className="text-xs font-medium mb-1">User Impact:</p>
                    <p className={`text-xs ${
                      check.severity === "normal" ? "text-green-300" : 
                      check.severity === "warning" ? "text-yellow-300" : "text-red-300"
                    }`}>{check.user_impact || "Normal operation"}</p>
                    {check.user_activity && (
                      <p className="text-xs text-viraly-muted mt-1">Affects: {check.user_activity}</p>
                    )}
                  </div>
                  
                  {/* Suggested Action - Only show if action exists */}
                  {check.action && (
                    <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-300">{check.action}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Incident Thresholds Info */}
            {healthStatus?.incident_thresholds && (
              <div className="card-base p-4 mb-6">
                <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <AlertOctagon className="w-4 h-4 text-viraly-orange" />
                  Incident Escalation Thresholds
                  <span className="text-xs text-viraly-muted ml-2">(Click values to edit)</span>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="p-2 rounded-lg bg-white/5">
                    <p className="text-viraly-muted text-xs">Consecutive Warnings</p>
                    <div className="flex items-center gap-2">
                      <input 
                        type="number" 
                        min="1" 
                        max="10"
                        value={healthStatus.incident_thresholds.consecutive_warnings}
                        onChange={async (e) => {
                          const newVal = parseInt(e.target.value);
                          const updated = {...healthStatus.incident_thresholds, consecutive_warnings: newVal};
                          try {
                            await apiClient.put("/admin/health/incident-thresholds", updated);
                            setHealthStatus(prev => ({...prev, incident_thresholds: updated}));
                          } catch(err) {}
                        }}
                        className="w-12 bg-white/10 border border-white/20 rounded px-2 py-1 text-yellow-400 font-bold text-center"
                      />
                      <span className="text-yellow-400">= Alert</span>
                    </div>
                  </div>
                  <div className="p-2 rounded-lg bg-white/5">
                    <p className="text-viraly-muted text-xs">Consecutive Critical</p>
                    <div className="flex items-center gap-2">
                      <input 
                        type="number" 
                        min="1" 
                        max="10"
                        value={healthStatus.incident_thresholds.consecutive_critical}
                        onChange={async (e) => {
                          const newVal = parseInt(e.target.value);
                          const updated = {...healthStatus.incident_thresholds, consecutive_critical: newVal};
                          try {
                            await apiClient.put("/admin/health/incident-thresholds", updated);
                            setHealthStatus(prev => ({...prev, incident_thresholds: updated}));
                          } catch(err) {}
                        }}
                        className="w-12 bg-white/10 border border-white/20 rounded px-2 py-1 text-red-400 font-bold text-center"
                      />
                      <span className="text-red-400">= Action</span>
                    </div>
                  </div>
                  <div className="p-2 rounded-lg bg-white/5">
                    <p className="text-viraly-muted text-xs">Warnings in 1 Hour</p>
                    <div className="flex items-center gap-2">
                      <input 
                        type="number" 
                        min="1" 
                        max="20"
                        value={healthStatus.incident_thresholds.warnings_in_hour}
                        onChange={async (e) => {
                          const newVal = parseInt(e.target.value);
                          const updated = {...healthStatus.incident_thresholds, warnings_in_hour: newVal};
                          try {
                            await apiClient.put("/admin/health/incident-thresholds", updated);
                            setHealthStatus(prev => ({...prev, incident_thresholds: updated}));
                          } catch(err) {}
                        }}
                        className="w-12 bg-white/10 border border-white/20 rounded px-2 py-1 text-yellow-400 font-bold text-center"
                      />
                      <span className="text-yellow-400">= Investigate</span>
                    </div>
                  </div>
                  <div className="p-2 rounded-lg bg-white/5">
                    <p className="text-viraly-muted text-xs">Critical in 1 Hour</p>
                    <div className="flex items-center gap-2">
                      <input 
                        type="number" 
                        min="1" 
                        max="20"
                        value={healthStatus.incident_thresholds.critical_in_hour}
                        onChange={async (e) => {
                          const newVal = parseInt(e.target.value);
                          const updated = {...healthStatus.incident_thresholds, critical_in_hour: newVal};
                          try {
                            await apiClient.put("/admin/health/incident-thresholds", updated);
                            setHealthStatus(prev => ({...prev, incident_thresholds: updated}));
                          } catch(err) {}
                        }}
                        className="w-12 bg-white/10 border border-white/20 rounded px-2 py-1 text-red-400 font-bold text-center"
                      />
                      <span className="text-red-400">= Escalate</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Circuit Breakers */}
            <div className="mt-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <h4 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Circuit Breakers (Auto-Disable on Failure)
              </h4>
              <p className="text-xs text-viraly-muted mb-3">
                When a service fails {healthThresholds?.circuit_breaker_threshold || 3} consecutive checks, it's automatically disabled to prevent user errors.
                Users are notified when services go down and when they recover.
              </p>
              <div className="flex flex-wrap gap-4">
                {(healthStatus?.circuit_breakers ? Object.entries(healthStatus.circuit_breakers) : [
                  ["video_generation", {open: false, failures: 0}],
                  ["image_generation", {open: false, failures: 0}],
                  ["email_service", {open: false, failures: 0}],
                  ["paypal_api", {open: false, failures: 0}]
                ]).map(([service, state]) => (
                  <div key={service} className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                    <div className={`w-2 h-2 rounded-full ${state.open ? 'bg-red-500' : 'bg-green-500'}`} />
                    <span className="text-xs capitalize">{service.replace(/_/g, ' ')}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${state.open ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                      {state.open ? 'Open (Disabled)' : 'Active'}
                    </span>
                    {state.failures > 0 && !state.open && (
                      <span className="text-xs text-yellow-400">({state.failures} failures)</span>
                    )}
                    {state.open && (
                      <button
                        onClick={async () => {
                          try {
                            await apiClient.post(`/admin/health/circuit-breakers/${service}/reset`);
                            toast.success(`Circuit breaker for ${service.replace(/_/g, ' ')} reset`);
                            // Refresh health status
                            const res = await apiClient.get("/admin/health/status");
                            setHealthStatus(res.data.current);
                          } catch (err) {
                            toast.error("Failed to reset circuit breaker");
                          }
                        }}
                        className="text-xs text-blue-400 hover:text-blue-300 underline ml-1"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Proactive Health Thresholds */}
            <div className="mt-6 card-base p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                    <Bell className="w-5 h-5 text-viraly-orange" />
                    Proactive Alert Thresholds
                  </h3>
                  <p className="text-sm text-viraly-muted mt-1">
                    Get alerted BEFORE customers complain when metrics exceed thresholds
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-sm">Enabled</span>
                    <button
                      onClick={() => setHealthThresholds(prev => ({...prev, enabled: !prev.enabled}))}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        healthThresholds.enabled ? 'bg-green-500' : 'bg-white/20'
                      }`}
                    >
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        healthThresholds.enabled ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                  </label>
                </div>
              </div>

              {healthThresholds.enabled && (
                <>
                  {/* Alert Email & Cooldown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Alert Email</label>
                      <Input
                        type="email"
                        value={healthThresholds.alert_email || ""}
                        onChange={(e) => setHealthThresholds(prev => ({...prev, alert_email: e.target.value}))}
                        placeholder="admin@viraly.ca"
                        className="input-base"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Alert Cooldown (minutes)</label>
                      <Input
                        type="number"
                        value={healthThresholds.alert_cooldown_minutes || 30}
                        onChange={(e) => setHealthThresholds(prev => ({...prev, alert_cooldown_minutes: parseInt(e.target.value) || 30}))}
                        min={5}
                        max={1440}
                        className="input-base"
                      />
                      <p className="text-xs text-viraly-muted mt-1">Minimum time between alert emails</p>
                    </div>
                  </div>

                  {/* Threshold Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {Object.entries(healthThresholds.thresholds || {}).map(([key, config]) => (
                      <div key={key} className={`p-4 rounded-xl border ${config.enabled ? 'bg-white/5 border-white/10' : 'bg-white/2 border-white/5 opacity-60'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {key === "api_failure_rate" && <Server className="w-4 h-4 text-red-400" />}
                            {key === "db_latency_ms" && <Database className="w-4 h-4 text-blue-400" />}
                            {key === "content_generation_failures" && <FileText className="w-4 h-4 text-purple-400" />}
                            {key === "email_delivery_failures" && <Mail className="w-4 h-4 text-yellow-400" />}
                            {key === "video_generation_failures" && <Video className="w-4 h-4 text-pink-400" />}
                            {key === "queue_backlog" && <Clock className="w-4 h-4 text-orange-400" />}
                            <span className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                          </div>
                          <button
                            onClick={() => {
                              const newThresholds = {...healthThresholds};
                              newThresholds.thresholds[key].enabled = !config.enabled;
                              setHealthThresholds(newThresholds);
                            }}
                            className={`relative w-10 h-5 rounded-full transition-colors ${
                              config.enabled ? 'bg-green-500' : 'bg-white/20'
                            }`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                              config.enabled ? 'left-5' : 'left-0.5'
                            }`} />
                          </button>
                        </div>
                        <p className="text-xs text-viraly-muted mb-3">{config.description}</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-viraly-muted">Threshold</label>
                            <Input
                              type="number"
                              value={config.threshold}
                              onChange={(e) => {
                                const newThresholds = {...healthThresholds};
                                newThresholds.thresholds[key].threshold = parseInt(e.target.value) || 0;
                                setHealthThresholds(newThresholds);
                              }}
                              className="input-base text-sm h-8"
                              disabled={!config.enabled}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-viraly-muted">Window (min)</label>
                            <Input
                              type="number"
                              value={config.window_minutes}
                              onChange={(e) => {
                                const newThresholds = {...healthThresholds};
                                newThresholds.thresholds[key].window_minutes = parseInt(e.target.value) || 15;
                                setHealthThresholds(newThresholds);
                              }}
                              className="input-base text-sm h-8"
                              disabled={!config.enabled}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Save & Test Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={async () => {
                        setSavingHealthThresholds(true);
                        try {
                          const adminToken = localStorage.getItem("viraly_admin_token");
                          await apiClient.put("/admin/health/thresholds", healthThresholds, {
                            headers: { Authorization: `Bearer ${adminToken}` }
                          });
                          toast.success("Health thresholds saved!");
                        } catch (error) {
                          toast.error("Failed to save thresholds");
                        } finally {
                          setSavingHealthThresholds(false);
                        }
                      }}
                      disabled={savingHealthThresholds}
                      className="btn-primary flex items-center gap-2"
                      data-testid="save-health-thresholds-btn"
                    >
                      {savingHealthThresholds ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Thresholds
                    </Button>
                    <Button
                      onClick={async () => {
                        setTestingHealthAlert(true);
                        try {
                          const adminToken = localStorage.getItem("viraly_admin_token");
                          const response = await apiClient.post("/admin/health/test-alert", {}, {
                            headers: { Authorization: `Bearer ${adminToken}` }
                          });
                          if (response.data.error) {
                            toast.error(response.data.error);
                          } else {
                            toast.success(response.data.message || "Test alert sent!");
                          }
                        } catch (error) {
                          toast.error("Failed to send test alert");
                        } finally {
                          setTestingHealthAlert(false);
                        }
                      }}
                      disabled={testingHealthAlert || !healthThresholds.alert_email}
                      className="btn-secondary flex items-center gap-2"
                      data-testid="test-health-alert-btn"
                    >
                      {testingHealthAlert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Send Test Alert
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Recent Health Alerts */}
            {healthAlerts.length > 0 && (
              <div className="mt-6 card-base p-6">
                <h3 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Recent Alerts ({healthAlerts.filter(a => !a.acknowledged).length} unacknowledged)
                </h3>
                <div className="space-y-3">
                  {healthAlerts.slice(0, 10).map((alert, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center justify-between p-4 rounded-xl ${
                        alert.acknowledged ? 'bg-white/5' : 'bg-red-500/10 border border-red-500/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${alert.acknowledged ? 'bg-gray-500' : 'bg-red-500 animate-pulse'}`} />
                        <div>
                          <p className="text-sm font-medium">{alert.message}</p>
                          <p className="text-xs text-viraly-muted">
                            {new Date(alert.timestamp).toLocaleString()}
                            {alert.acknowledged && ` • Acknowledged by ${alert.acknowledged_by}`}
                          </p>
                        </div>
                      </div>
                      {!alert.acknowledged && (
                        <Button
                          onClick={async () => {
                            try {
                              const adminToken = localStorage.getItem("viraly_admin_token");
                              await apiClient.post(`/admin/health/alerts/${alert.id}/acknowledge`, {}, {
                                headers: { Authorization: `Bearer ${adminToken}` }
                              });
                              setHealthAlerts(prev => prev.map(a => 
                                a.id === alert.id ? {...a, acknowledged: true, acknowledged_by: "You"} : a
                              ));
                              toast.success("Alert acknowledged");
                            } catch {
                              toast.error("Failed to acknowledge alert");
                            }
                          }}
                          size="sm"
                          className="btn-secondary text-xs"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Acknowledge
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Customer Service Alert Notifications */}
            <div className="mt-6 card-base p-6" data-testid="service-alert-settings-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-viraly-orange/20 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-viraly-orange" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold">Customer Alert Notifications</h3>
                  <p className="text-sm text-viraly-muted">Automatically notify customers via push when critical services go down or recover</p>
                </div>
              </div>
              
              {serviceAlertLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                  <span className="ml-2 text-viraly-muted">Loading service alert settings...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Toggle: Auto-notify on Outage */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <WifiOff className="w-5 h-5 text-red-400" />
                      <div>
                        <p className="font-medium">Notify on Service Outage</p>
                        <p className="text-sm text-viraly-muted">Send push notification to all customers when a critical service goes down</p>
                      </div>
                    </div>
                    <button
                      data-testid="toggle-notify-outage"
                      onClick={() => setServiceAlertSettings(prev => ({ ...prev, auto_notify_on_outage: !prev.auto_notify_on_outage }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${serviceAlertSettings.auto_notify_on_outage ? 'bg-viraly-orange' : 'bg-white/20'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${serviceAlertSettings.auto_notify_on_outage ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  {/* Toggle: Auto-notify on Recovery */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <Wifi className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="font-medium">Notify on Service Recovery</p>
                        <p className="text-sm text-viraly-muted">Send push notification to all customers when a service is restored</p>
                      </div>
                    </div>
                    <button
                      data-testid="toggle-notify-recovery"
                      onClick={() => setServiceAlertSettings(prev => ({ ...prev, auto_notify_on_recovery: !prev.auto_notify_on_recovery }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${serviceAlertSettings.auto_notify_on_recovery ? 'bg-viraly-orange' : 'bg-white/20'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${serviceAlertSettings.auto_notify_on_recovery ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  {/* Toggle: Notify for Self-Healing Fixes */}
                  <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="font-medium">Notify on Auto-Fixed Issues</p>
                        <p className="text-sm text-viraly-muted">Send notification when the system automatically fixes an issue (may be noisy)</p>
                      </div>
                    </div>
                    <button
                      data-testid="toggle-notify-self-healing"
                      onClick={() => setServiceAlertSettings(prev => ({ ...prev, notify_self_healing_fixes: !prev.notify_self_healing_fixes }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${serviceAlertSettings.notify_self_healing_fixes ? 'bg-viraly-orange' : 'bg-white/20'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${serviceAlertSettings.notify_self_healing_fixes ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  {/* Admin Email Alerts Section */}
                  <div className="p-4 rounded-xl bg-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-viraly-orange" />
                        <p className="font-medium text-sm">Admin Team Email Alerts</p>
                      </div>
                      <button
                        onClick={() => setServiceAlertSettings(prev => ({ ...prev, admin_alert_enabled: !prev.admin_alert_enabled }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${serviceAlertSettings.admin_alert_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${serviceAlertSettings.admin_alert_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    <p className="text-xs text-viraly-muted mb-3">
                      Receive email alerts when services go down or recover. Great for off-hours monitoring.
                    </p>
                    
                    {/* Email List */}
                    <div className="space-y-2 mb-3">
                      {serviceAlertSettings.admin_alert_emails?.map((email, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                          <span className="text-sm">{email}</span>
                          <button
                            onClick={() => setServiceAlertSettings(prev => ({
                              ...prev,
                              admin_alert_emails: prev.admin_alert_emails.filter((_, i) => i !== idx)
                            }))}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Add Email Input */}
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder="Enter admin email..."
                        value={newAdminEmail}
                        onChange={(e) => setNewAdminEmail(e.target.value)}
                        className="input-base flex-1 text-sm"
                        data-testid="admin-alert-email-input"
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          if (newAdminEmail && newAdminEmail.includes('@')) {
                            setServiceAlertSettings(prev => ({
                              ...prev,
                              admin_alert_emails: [...(prev.admin_alert_emails || []), newAdminEmail]
                            }));
                            setNewAdminEmail("");
                          } else {
                            toast.error("Please enter a valid email");
                          }
                        }}
                        className="btn-secondary"
                        data-testid="add-admin-email-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Critical Services List */}
                  <div className="p-4 rounded-xl bg-white/5">
                    <div className="flex items-center gap-2 mb-3">
                      <Server className="w-4 h-4 text-viraly-orange" />
                      <p className="font-medium text-sm">Monitored Critical Services</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {serviceAlertSettings.critical_services?.map((service) => (
                        <span key={service} className="px-3 py-1 rounded-full bg-viraly-orange/10 text-viraly-orange text-sm">
                          {service.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-viraly-muted mt-3">
                      When status changes for any of these services, customers will be notified based on your settings above.
                    </p>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end pt-2">
                    <Button
                      data-testid="save-service-alert-settings"
                      onClick={saveServiceAlertSettings}
                      disabled={serviceAlertSaving}
                      className="btn-primary flex items-center gap-2"
                    >
                      {serviceAlertSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Save Alert Settings
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Complete Health Check History - All Time */}
            <div className="mt-6 card-base p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                    <History className="w-5 h-5 text-viraly-orange" />
                    Health Check History
                  </h3>
                  <p className="text-sm text-viraly-muted mt-1">
                    Complete history since production launch ({healthCheckHistoryTotal.toLocaleString()} total checks)
                  </p>
                </div>
                <Button
                  className="btn-secondary text-sm"
                  onClick={() => fetchAdminData()}
                  disabled={healthCheckHistoryLoading}
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${healthCheckHistoryLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              
              {healthCheckHistoryLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                </div>
              ) : healthCheckHistory.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium text-viraly-muted">Timestamp</th>
                          <th className="text-left p-3 text-sm font-medium text-viraly-muted">Status</th>
                          <th className="text-left p-3 text-sm font-medium text-viraly-muted">Services</th>
                          <th className="text-left p-3 text-sm font-medium text-viraly-muted">Triggered By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {healthCheckHistory.map((check, idx) => {
                          const checkTime = new Date(check.timestamp);
                          const gmtTime = checkTime.toLocaleString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', hour12: true });
                          const estTime = checkTime.toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: true });
                          return (
                            <tr key={idx} className="border-t border-white/5 hover:bg-white/5">
                              <td className="p-3">
                                <p className="text-sm">{checkTime.toLocaleString()}</p>
                                <p className="text-xs text-viraly-muted">{gmtTime} GMT | {estTime} EST</p>
                              </td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  check.overall === 'healthy' ? 'bg-green-500/20 text-green-400' :
                                  check.overall === 'degraded' ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-red-500/20 text-red-400'
                                }`}>{check.overall || 'unknown'}</span>
                              </td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                  {check.services ? Object.entries(check.services).slice(0, 4).map(([name, svc]) => (
                                    <span key={name} className={`px-1 py-0.5 rounded text-[10px] ${
                                      svc.status === 'healthy' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                                    }`}>{name.split('_')[0]}</span>
                                  )) : (
                                    <span className="text-xs text-viraly-muted">N/A</span>
                                  )}
                                </div>
                              </td>
                              <td className="p-3 text-sm text-viraly-muted">
                                {check.triggered_by || 'Auto'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between border-t border-white/10 pt-4 mt-4">
                    <p className="text-sm text-viraly-muted">
                      Page {healthCheckHistoryPage} of {Math.ceil(healthCheckHistoryTotal / 20)}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={healthCheckHistoryPage <= 1}
                        onClick={() => { setHealthCheckHistoryPage(p => p - 1); fetchAdminData(); }}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={healthCheckHistoryPage >= Math.ceil(healthCheckHistoryTotal / 20)}
                        onClick={() => { setHealthCheckHistoryPage(p => p + 1); fetchAdminData(); }}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-viraly-muted text-center py-8">No health check history available yet</p>
              )}
            </div>
            
            {/* ================================================================== */}
            {/* SYSTEM LEARNING SECTION - Pattern Analysis & Actionable Insights  */}
            {/* ================================================================== */}
            <div className="card-dark p-6 mt-6" data-testid="system-learning-section">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <Brain className="w-5 h-5 text-viraly-orange" />
                    System Learning & Pattern Analysis
                  </h3>
                  <p className="text-viraly-muted text-sm">AI-powered analysis of system behavior for proactive issue prevention</p>
                </div>
                {loadingSystemLearning && <Loader2 className="w-5 h-5 animate-spin text-viraly-orange" />}
              </div>
              
              {/* Insights Overview Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <p className="text-xs text-viraly-muted uppercase tracking-wide">Active Issues</p>
                  <p className={`text-2xl font-bold ${(systemLearningInsights?.active_breakdowns || 0) > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {systemLearningInsights?.active_breakdowns || 0}
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <p className="text-xs text-viraly-muted uppercase tracking-wide">Recurring (7d)</p>
                  <p className={`text-2xl font-bold ${(systemLearningInsights?.recurring_issues_7d || 0) > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                    {systemLearningInsights?.recurring_issues_7d || 0}
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <p className="text-xs text-viraly-muted uppercase tracking-wide">Auto-Recovery Rate</p>
                  <p className="text-2xl font-bold text-viraly-orange">
                    {systemLearningInsights?.auto_recovery_rate || 0}%
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <p className="text-xs text-viraly-muted uppercase tracking-wide">Avg Resolution</p>
                  <p className="text-2xl font-bold">
                    {systemLearningInsights?.avg_resolution_time_minutes || 0}<span className="text-sm text-viraly-muted ml-1">min</span>
                  </p>
                </div>
              </div>
              
              {/* Active Breakdowns with Actionable Suggestions */}
              {activeBreakdowns.length > 0 && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <h4 className="font-bold text-red-400 flex items-center gap-2 mb-3">
                    <AlertOctagon className="w-4 h-4" />
                    Active Issues Requiring Attention
                  </h4>
                  {activeBreakdowns.map((breakdown, idx) => (
                    <div key={idx} className="bg-black/30 rounded-lg p-4 mb-2 last:mb-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-red-300">{breakdown.service_name}: {breakdown.error_type}</p>
                          <p className="text-sm text-viraly-muted mt-1">{breakdown.error_message?.slice(0, 150)}</p>
                          <p className="text-xs text-viraly-muted mt-2">
                            Started: {new Date(breakdown.started_at).toLocaleString('en-US', { timeZone: 'America/New_York' })} EST
                          </p>
                        </div>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={async () => {
                            const method = prompt("Enter resolution method (e.g., restart_service, clear_cache, manual_fix):");
                            if (!method) return;
                            try {
                              const adminToken = localStorage.getItem("viraly_admin_token");
                              await apiClient.post(`/admin/health/system-learning/breakdowns/${breakdown._id || breakdown.id}/resolve`, 
                                { resolution_method: method },
                                { headers: { Authorization: `Bearer ${adminToken}` } }
                              );
                              toast.success("Breakdown marked as resolved!");
                              fetchAdminData();
                            } catch (e) {
                              toast.error("Failed to resolve breakdown");
                            }
                          }}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Mark Resolved
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* System Learning Alerts with Actionable Suggestions */}
              {systemLearningAlerts.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-yellow-400 flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4" />
                    Learning Alerts - Suggested Actions
                  </h4>
                  <div className="space-y-2">
                    {systemLearningAlerts.slice(0, 5).map((alert, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border flex items-start justify-between ${
                        alert.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                        alert.severity === 'high' ? 'bg-orange-500/10 border-orange-500/30' :
                        'bg-yellow-500/10 border-yellow-500/30'
                      }`}>
                        <div>
                          <p className="font-medium text-sm">
                            {alert.alert_type === 'latency_anomaly' && (
                              <>⚡ Latency Anomaly: <code className="bg-black/30 px-1 rounded">{alert.endpoint}</code> is {alert.deviation_factor?.toFixed(1)}x slower than baseline</>
                            )}
                            {alert.alert_type === 'recurring_issue' && (
                              <>🔄 Recurring Issue detected ({alert.occurrence_count}+ occurrences)</>
                            )}
                          </p>
                          <p className="text-xs text-viraly-muted mt-1">
                            {alert.alert_type === 'latency_anomaly' && `Current: ${alert.current_value}ms | Baseline: ${alert.baseline_avg?.toFixed(0)}ms`}
                            {alert.suggested_resolution && (
                              <span className="ml-2 text-green-400">
                                💡 Suggested: {alert.suggested_resolution.method} (worked {alert.suggested_resolution.success_count} times)
                              </span>
                            )}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            try {
                              const adminToken = localStorage.getItem("viraly_admin_token");
                              await apiClient.post(`/admin/health/system-learning/alerts/${alert._id || alert.id}/acknowledge`, {}, 
                                { headers: { Authorization: `Bearer ${adminToken}` } }
                              );
                              toast.success("Alert acknowledged");
                              setSystemLearningAlerts(prev => prev.filter(a => (a._id || a.id) !== (alert._id || alert.id)));
                            } catch {
                              toast.error("Failed to acknowledge");
                            }
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recurring Issues with Resolution Playbooks */}
              {recurringIssues.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                    <RefreshCw className="w-4 h-4 text-yellow-400" />
                    Recurring Issues - Resolution Playbooks
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-viraly-muted border-b border-white/10">
                          <th className="pb-2">Service</th>
                          <th className="pb-2">Error Type</th>
                          <th className="pb-2">Occurrences</th>
                          <th className="pb-2">Resolution Rate</th>
                          <th className="pb-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recurringIssues.slice(0, 5).map((issue, idx) => (
                          <tr key={idx} className="border-b border-white/5">
                            <td className="py-2">{issue.service_name}</td>
                            <td className="py-2 text-yellow-400">{issue.error_type}</td>
                            <td className="py-2">{issue.occurrence_count}x</td>
                            <td className="py-2">
                              <span className={issue.resolution_rate > 80 ? 'text-green-400' : issue.resolution_rate > 50 ? 'text-yellow-400' : 'text-red-400'}>
                                {issue.resolution_rate}%
                              </span>
                            </td>
                            <td className="py-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    const adminToken = localStorage.getItem("viraly_admin_token");
                                    const res = await apiClient.get(`/admin/health/system-learning/playbook/${issue.issue_signature}`, 
                                      { headers: { Authorization: `Bearer ${adminToken}` } }
                                    );
                                    const playbook = res.data.playbook;
                                    if (playbook?.playbook?.length > 0) {
                                      const steps = playbook.playbook.map((p, i) => 
                                        `${i+1}. ${p.method} (${p.success_count} successes, ~${p.avg_resolution_time_minutes || '?'}min)`
                                      ).join('\n');
                                      alert(`Resolution Playbook for ${issue.service_name}:\n\n${steps}`);
                                    } else {
                                      alert("No resolution playbook available yet. Resolve this issue and log the method to build the playbook.");
                                    }
                                  } catch {
                                    toast.error("Failed to load playbook");
                                  }
                                }}
                              >
                                <BookOpen className="w-3 h-3 mr-1" /> View Playbook
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Latency Insights */}
              {latencyInsights?.endpoints?.length > 0 && (
                <div>
                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-blue-400" />
                    API Latency Trends (24h)
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-viraly-muted border-b border-white/10">
                          <th className="pb-2">Endpoint</th>
                          <th className="pb-2">Avg</th>
                          <th className="pb-2">Max</th>
                          <th className="pb-2">Requests</th>
                          <th className="pb-2">Error Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latencyInsights.endpoints.slice(0, 8).map((ep, idx) => (
                          <tr key={idx} className="border-b border-white/5">
                            <td className="py-2 font-mono text-xs">{ep.endpoint}</td>
                            <td className={`py-2 ${ep.avg_latency_ms > 500 ? 'text-red-400' : ep.avg_latency_ms > 200 ? 'text-yellow-400' : 'text-green-400'}`}>
                              {ep.avg_latency_ms}ms
                            </td>
                            <td className="py-2 text-viraly-muted">{ep.max_latency_ms}ms</td>
                            <td className="py-2">{ep.request_count}</td>
                            <td className={`py-2 ${ep.error_rate > 5 ? 'text-red-400' : 'text-green-400'}`}>
                              {ep.error_rate}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* ================================================================== */}
              {/* ADVANCED ANALYTICS - Feature Usage, External APIs, Per-User, Impact */}
              {/* ================================================================== */}
              
              {/* Feature Usage & Failure Rates */}
              {featureUsageInsights?.features?.length > 0 && (
                <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4 text-purple-400" />
                    Feature Usage & Failure Rates (7 days)
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-viraly-muted border-b border-white/10">
                          <th className="pb-2">Feature</th>
                          <th className="pb-2">Uses</th>
                          <th className="pb-2">Failure Rate</th>
                          <th className="pb-2">Avg Time</th>
                          <th className="pb-2">Top Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {featureUsageInsights.features.slice(0, 10).map((f, idx) => (
                          <tr key={idx} className="border-b border-white/5">
                            <td className="py-2 font-medium">{f.feature}</td>
                            <td className="py-2">{f.total_uses}</td>
                            <td className={`py-2 ${f.failure_rate > 10 ? 'text-red-400' : f.failure_rate > 5 ? 'text-yellow-400' : 'text-green-400'}`}>
                              {f.failure_rate?.toFixed(1)}%
                            </td>
                            <td className="py-2 text-viraly-muted">{f.avg_execution_time_ms}ms</td>
                            <td className="py-2 text-xs text-red-300">
                              {f.top_errors?.slice(0, 2).map(e => e[0]).join(', ') || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* External API Costs & Performance */}
              {externalApiInsights?.by_api?.length > 0 && (
                <div className="mt-6 p-4 bg-white/5 rounded-lg border border-white/10">
                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-blue-400" />
                    External API Usage & Costs (7 days)
                    <span className="ml-auto text-lg font-bold text-green-400">
                      ${externalApiInsights.total_cost_usd?.toFixed(2) || '0.00'}
                    </span>
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-viraly-muted border-b border-white/10">
                          <th className="pb-2">API</th>
                          <th className="pb-2">Calls</th>
                          <th className="pb-2">Success Rate</th>
                          <th className="pb-2">Avg Latency</th>
                          <th className="pb-2">Cost (USD)</th>
                          <th className="pb-2">Tokens</th>
                        </tr>
                      </thead>
                      <tbody>
                        {externalApiInsights.by_api.map((api, idx) => (
                          <tr key={idx} className="border-b border-white/5">
                            <td className="py-2 font-medium capitalize">{api.api_name}</td>
                            <td className="py-2">{api.total_calls}</td>
                            <td className={`py-2 ${api.success_rate < 95 ? 'text-red-400' : 'text-green-400'}`}>
                              {api.success_rate?.toFixed(1)}%
                            </td>
                            <td className="py-2 text-viraly-muted">{api.avg_latency_ms}ms</td>
                            <td className="py-2 text-green-400">${api.total_cost_usd?.toFixed(4)}</td>
                            <td className="py-2 text-viraly-muted">{api.total_tokens?.toLocaleString() || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Daily cost trend mini-chart */}
                  {externalApiInsights.daily_cost_trend?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="text-xs text-viraly-muted mb-2">Daily Cost Trend</p>
                      <div className="flex items-end gap-1 h-16">
                        {externalApiInsights.daily_cost_trend.slice(-7).map((day, idx) => {
                          const maxCost = Math.max(...externalApiInsights.daily_cost_trend.map(d => d.cost_usd));
                          const height = maxCost > 0 ? (day.cost_usd / maxCost) * 100 : 0;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center">
                              <div 
                                className="w-full bg-green-500/60 rounded-t" 
                                style={{ height: `${Math.max(height, 5)}%` }}
                                title={`${day.date}: $${day.cost_usd.toFixed(4)}`}
                              />
                              <span className="text-[8px] text-viraly-muted mt-1">{day.date.slice(-2)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Per-User Latency by Plan & Region */}
              {(perUserLatencyInsights?.by_plan?.length > 0 || perUserLatencyInsights?.by_region?.length > 0) && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* By Plan */}
                  {perUserLatencyInsights.by_plan?.length > 0 && (
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <h4 className="font-semibold flex items-center gap-2 mb-3">
                        <CreditCard className="w-4 h-4 text-yellow-400" />
                        Latency by User Plan
                      </h4>
                      <div className="space-y-2">
                        {perUserLatencyInsights.by_plan.map((p, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-black/20 rounded">
                            <span className="font-medium capitalize">{p.plan || 'Unknown'}</span>
                            <span className={`${p.avg_latency > 500 ? 'text-red-400' : 'text-green-400'}`}>
                              {p.avg_latency?.toFixed(0)}ms avg
                            </span>
                            <span className="text-viraly-muted text-sm">{p.unique_user_count} users</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* By Region */}
                  {perUserLatencyInsights.by_region?.length > 0 && (
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                      <h4 className="font-semibold flex items-center gap-2 mb-3">
                        <Globe className="w-4 h-4 text-cyan-400" />
                        Latency by Region
                      </h4>
                      <div className="space-y-2">
                        {perUserLatencyInsights.by_region.slice(0, 5).map((r, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-black/20 rounded">
                            <span className="font-medium">{r.region || 'Unknown'}</span>
                            <span className={`${r.avg_latency_ms > 500 ? 'text-red-400' : 'text-green-400'}`}>
                              {r.avg_latency_ms}ms avg
                            </span>
                            <span className="text-viraly-muted text-sm">{r.requests} requests</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* User Impact Report */}
              {(userImpactReport?.unresolved_incidents > 0 || Object.keys(userImpactReport?.by_severity || {}).length > 0) && (
                <div className="mt-6 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                  <h4 className="font-semibold flex items-center gap-2 mb-3 text-red-400">
                    <AlertOctagon className="w-4 h-4" />
                    User Impact Report (30 days)
                    {userImpactReport.unresolved_incidents > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-red-600 text-white text-xs rounded">
                        {userImpactReport.unresolved_incidents} Unresolved
                      </span>
                    )}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['critical', 'high', 'medium', 'low'].map(severity => {
                      const data = userImpactReport.by_severity?.[severity];
                      if (!data) return null;
                      const colors = {
                        critical: 'bg-red-600',
                        high: 'bg-orange-500',
                        medium: 'bg-yellow-500',
                        low: 'bg-blue-500'
                      };
                      return (
                        <div key={severity} className={`p-3 rounded ${colors[severity]}/20 border ${colors[severity]}/30`}>
                          <p className="text-xs uppercase tracking-wide text-viraly-muted">{severity}</p>
                          <p className="text-xl font-bold">{data.incidents} incidents</p>
                          <p className="text-sm text-viraly-muted">{data.users_affected} users affected</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Empty State */}
              {!loadingSystemLearning && !activeBreakdowns.length && !systemLearningAlerts.length && !recurringIssues.length && (
                <div className="text-center py-8 text-viraly-muted">
                  <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No issues detected. System is learning from normal operations.</p>
                  <p className="text-sm mt-2">Patterns will appear here as the system collects more data.</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* System Logs Tab */}
        {activeTab === "system-logs" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Terminal className="w-8 h-8 text-viraly-orange" />
                  System Logs
                </h1>
                <p className="text-viraly-muted mt-2">Real-time system activity and error tracking</p>
              </div>
              <div className="flex gap-3">
                <select
                  value={logFilter}
                  onChange={(e) => setLogFilter(e.target.value)}
                  className="input-base px-4 py-2"
                >
                  <option value="all">All Logs</option>
                  <option value="error">Errors Only</option>
                  <option value="warning">Warnings</option>
                  <option value="info">Info</option>
                </select>
                <Button
                  onClick={() => fetchAdminData()}
                  className="btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Log Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="card-base p-4 text-center border-l-4 border-red-500">
                <p className="text-viraly-muted text-sm">Errors (24h)</p>
                <p className="text-2xl font-bold text-red-400">
                  {(systemLogs || []).filter(l => l.level === "error").length}
                </p>
              </div>
              <div className="card-base p-4 text-center border-l-4 border-yellow-500">
                <p className="text-viraly-muted text-sm">Warnings (24h)</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {(systemLogs || []).filter(l => l.level === "warning").length}
                </p>
              </div>
              <div className="card-base p-4 text-center border-l-4 border-blue-500">
                <p className="text-viraly-muted text-sm">Info (24h)</p>
                <p className="text-2xl font-bold text-blue-400">
                  {(systemLogs || []).filter(l => l.level === "info").length}
                </p>
              </div>
              <div className="card-base p-4 text-center border-l-4 border-viraly-orange">
                <p className="text-viraly-muted text-sm">Total Logs</p>
                <p className="text-2xl font-bold text-viraly-orange">{(systemLogs || []).length}</p>
              </div>
            </div>

            {/* Logs Table */}
            <div className="card-base overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-viraly-muted uppercase">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-viraly-muted uppercase">Level</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-viraly-muted uppercase">Message</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-viraly-muted uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-viraly-muted uppercase">Endpoint</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {(systemLogs || [])
                      .filter(log => logFilter === "all" || log.level === logFilter)
                      .map((log) => (
                        <tr 
                          key={log.id} 
                          className="hover:bg-white/5 cursor-pointer"
                          onClick={() => setSelectedLog(log)}
                        >
                          <td className="px-4 py-3 text-sm text-viraly-muted whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              log.level === "error" ? "bg-red-500/20 text-red-400" :
                              log.level === "warning" ? "bg-yellow-500/20 text-yellow-400" :
                              "bg-blue-500/20 text-blue-400"
                            }`}>
                              {log.level.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm max-w-md truncate">{log.message}</td>
                          <td className="px-4 py-3 text-sm text-viraly-muted">{log.user}</td>
                          <td className="px-4 py-3 text-sm font-mono text-viraly-muted">{log.endpoint}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              
              {/* Log Detail Modal */}
              {selectedLog && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setSelectedLog(null)}>
                  <div className="bg-[#1a1a2e] rounded-xl p-6 max-w-2xl w-full border border-white/10 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-viraly-light flex items-center gap-2">
                        <Terminal className="w-5 h-5" />
                        Log Entry Details
                      </h3>
                      <button onClick={() => setSelectedLog(null)} className="text-viraly-muted hover:text-white text-xl">✕</button>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-viraly-muted uppercase">Timestamp</label>
                          <p className="text-sm mt-1">{new Date(selectedLog.timestamp).toLocaleString('en-US', { timeZone: 'America/New_York' })} EST</p>
                        </div>
                        <div>
                          <label className="text-xs text-viraly-muted uppercase">Level</label>
                          <p className="mt-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              selectedLog.level === "error" ? "bg-red-500/20 text-red-400" :
                              selectedLog.level === "warning" ? "bg-yellow-500/20 text-yellow-400" :
                              "bg-blue-500/20 text-blue-400"
                            }`}>
                              {selectedLog.level?.toUpperCase()}
                            </span>
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-viraly-muted uppercase">User</label>
                        <p className="text-sm mt-1">{selectedLog.user || "System"}</p>
                      </div>
                      
                      <div>
                        <label className="text-xs text-viraly-muted uppercase">Endpoint</label>
                        <p className="text-sm mt-1 font-mono text-viraly-orange">{selectedLog.endpoint || "N/A"}</p>
                      </div>
                      
                      <div>
                        <label className="text-xs text-viraly-muted uppercase">Message</label>
                        <p className="text-sm mt-1 p-3 bg-white/5 rounded-lg whitespace-pre-wrap">{selectedLog.message}</p>
                      </div>
                      
                      {selectedLog.stack_trace && (
                        <div>
                          <label className="text-xs text-viraly-muted uppercase">Stack Trace</label>
                          <pre className="text-xs mt-1 p-3 bg-red-500/10 rounded-lg overflow-x-auto text-red-400 whitespace-pre-wrap">{selectedLog.stack_trace}</pre>
                        </div>
                      )}
                      
                      {selectedLog.ip_address && (
                        <div>
                          <label className="text-xs text-viraly-muted uppercase">IP Address</label>
                          <p className="text-sm mt-1 font-mono">{selectedLog.ip_address}</p>
                        </div>
                      )}
                      
                      {selectedLog.request_body && (
                        <div>
                          <label className="text-xs text-viraly-muted uppercase">Request Body</label>
                          <pre className="text-xs mt-1 p-3 bg-white/5 rounded-lg overflow-x-auto">{JSON.stringify(selectedLog.request_body, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                    
                    <Button className="w-full mt-4" onClick={() => setSelectedLog(null)}>Close</Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Content Tab */}
        {activeTab === "content" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <FileText className="w-8 h-8 text-viraly-orange" />
                  Content Moderation
                </h1>
                <p className="text-viraly-muted mt-2">Review and moderate user-generated content</p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => fetchAdminData()}
                  className="btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Moderation Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="card-base p-4">
                <p className="text-sm text-viraly-muted">Pending Review</p>
                <p className="text-2xl font-bold text-amber-400">{moderationStats.pending}</p>
              </div>
              <div className="card-base p-4">
                <p className="text-sm text-viraly-muted">Approved</p>
                <p className="text-2xl font-bold text-green-400">{moderationStats.approved}</p>
              </div>
              <div className="card-base p-4">
                <p className="text-sm text-viraly-muted">Rejected</p>
                <p className="text-2xl font-bold text-red-400">{moderationStats.rejected}</p>
              </div>
              <div className="card-base p-4">
                <p className="text-sm text-viraly-muted">Auto-Flagged</p>
                <p className="text-2xl font-bold text-purple-400">{moderationStats.auto_flagged}</p>
              </div>
            </div>

            {moderationLoading ? (
              <div className="card-base p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange mx-auto mb-4" />
                <p className="text-viraly-muted">Loading flagged content...</p>
              </div>
            ) : flaggedContent.length === 0 ? (
              <div className="card-base p-8 text-center">
                <Check className="w-16 h-16 text-viraly-success mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold mb-2">No Flagged Content</h3>
                <p className="text-viraly-muted">All user content is currently compliant with community guidelines.</p>
              </div>
            ) : (
              <div className="card-base overflow-hidden">
                <div className="p-4 border-b border-white/10">
                  <h3 className="font-bold">Flagged Content ({flaggedContent.length})</h3>
                </div>
                <div className="divide-y divide-white/5">
                  {flaggedContent.map((content) => (
                    <div key={content.id} className="p-4 hover:bg-white/5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium mb-1">{content.content?.substring(0, 100)}...</p>
                          <p className="text-sm text-viraly-muted mb-2">
                            User: {content.user_email || content.user_id} | Flagged: {content.flag_reason || "Unknown"}
                          </p>
                          <p className="text-xs text-viraly-muted">
                            Created: {new Date(content.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="btn-secondary text-green-400"
                            onClick={async () => {
                              try {
                                const adminToken = localStorage.getItem("viraly_admin_token");
                                await apiClient.post(`/admin/content-moderation/${content.id}/approve`, {}, {
                                  headers: { Authorization: `Bearer ${adminToken}` }
                                });
                                toast.success("Content approved");
                                fetchAdminData();
                              } catch {
                                toast.error("Failed to approve");
                              }
                            }}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            className="btn-secondary text-red-400"
                            onClick={async () => {
                              try {
                                const adminToken = localStorage.getItem("viraly_admin_token");
                                await apiClient.post(`/admin/content-moderation/${content.id}/reject`, "Policy violation", {
                                  headers: { Authorization: `Bearer ${adminToken}`, "Content-Type": "application/json" }
                                });
                                toast.success("Content rejected");
                                fetchAdminData();
                              } catch {
                                toast.error("Failed to reject");
                              }
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Download className="w-8 h-8 text-viraly-orange" />
                  Business Documents
                </h1>
                <p className="text-viraly-muted mt-2">Generate and manage legal business documents with AI</p>
              </div>
              <Button
                onClick={() => fetchAdminData()}
                className="btn-secondary flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>

            {/* AI Document Generator */}
            <div className="card-base p-6 mb-6">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-viraly-orange" />
                AI Document Generator
              </h3>
              
              {/* Quick Generate from Templates */}
              <div className="flex gap-4 mb-4">
                <select
                  value={selectedDocType}
                  onChange={(e) => setSelectedDocType(e.target.value)}
                  className="input-base px-4 py-2 rounded-xl flex-1"
                >
                  <optgroup label="Legal Documents">
                    <option value="privacy_policy">Privacy Policy</option>
                    <option value="terms_of_service">Terms of Service</option>
                    <option value="refund_policy">Refund Policy</option>
                    <option value="data_processing">Data Processing Agreement (DPA)</option>
                    <option value="cookie_policy">Cookie Policy</option>
                    <option value="acceptable_use">Acceptable Use Policy</option>
                    <option value="dmca_policy">DMCA Policy</option>
                    <option value="disclaimer">Website Disclaimer</option>
                    <option value="eula">End User License Agreement (EULA)</option>
                    <option value="affiliate_terms">Affiliate Program Terms</option>
                  </optgroup>
                  <optgroup label="Business Documents">
                    <option value="business_plan">Business Plan</option>
                    <option value="investor_pitch">Investor Pitch Deck Outline</option>
                    <option value="partnership_proposal">Partnership Proposal</option>
                    <option value="service_agreement">Service Agreement Template</option>
                    <option value="nda">Non-Disclosure Agreement (NDA)</option>
                    <option value="sow">Statement of Work (SOW)</option>
                    <option value="sla">Service Level Agreement (SLA)</option>
                    <option value="invoice_template">Invoice Template</option>
                    <option value="proposal_template">Proposal Template</option>
                  </optgroup>
                  <optgroup label="Marketing Documents">
                    <option value="marketing_plan">Marketing Plan</option>
                    <option value="content_calendar">Content Calendar Template</option>
                    <option value="brand_guidelines">Brand Guidelines</option>
                    <option value="social_media_policy">Social Media Policy</option>
                    <option value="press_release">Press Release Template</option>
                    <option value="case_study">Case Study Template</option>
                    <option value="email_sequences">Email Marketing Sequences</option>
                    <option value="ad_copy_guide">Ad Copy Guidelines</option>
                  </optgroup>
                  <optgroup label="HR & Internal Documents">
                    <option value="employee_handbook">Employee Handbook</option>
                    <option value="remote_work_policy">Remote Work Policy</option>
                    <option value="code_of_conduct">Code of Conduct</option>
                    <option value="onboarding_guide">Onboarding Guide</option>
                    <option value="performance_review">Performance Review Template</option>
                    <option value="job_description">Job Description Template</option>
                    <option value="expense_policy">Expense Reimbursement Policy</option>
                    <option value="vacation_policy">Vacation Policy</option>
                  </optgroup>
                  <optgroup label="Technical Documents">
                    <option value="api_documentation">API Documentation</option>
                    <option value="user_manual">User Manual</option>
                    <option value="faq_document">FAQ Document</option>
                    <option value="troubleshooting_guide">Troubleshooting Guide</option>
                    <option value="system_requirements">System Requirements</option>
                    <option value="changelog">Changelog Template</option>
                    <option value="release_notes">Release Notes Template</option>
                    <option value="security_policy">Security Policy</option>
                  </optgroup>
                  <optgroup label="Customer Support">
                    <option value="support_scripts">Support Response Scripts</option>
                    <option value="refund_sop">Refund SOP</option>
                    <option value="escalation_procedure">Escalation Procedure</option>
                    <option value="customer_survey">Customer Survey Questions</option>
                    <option value="feedback_form">Feedback Collection Form</option>
                    <option value="knowledge_base">Knowledge Base Article Template</option>
                  </optgroup>
                  <optgroup label="Financial Documents">
                    <option value="financial_report">Financial Report Template</option>
                    <option value="budget_template">Budget Template</option>
                    <option value="pricing_strategy">Pricing Strategy Document</option>
                    <option value="revenue_forecast">Revenue Forecast Template</option>
                    <option value="expense_report">Expense Report Template</option>
                  </optgroup>
                </select>
                <Button
                  onClick={async () => {
                    setGeneratingDocument(true);
                    try {
                      const adminToken = localStorage.getItem("viraly_admin_token");
                      const res = await apiClient.post("/admin/documents/generate", 
                        { doc_type: selectedDocType, parameters: {} },
                        { headers: { Authorization: `Bearer ${adminToken}` } }
                      );
                      setDocumentContent(res.data.document.content);
                      toast.success("Document generated!");
                      fetchAdminData();
                    } catch (err) {
                      toast.error("Failed to generate document");
                    } finally {
                      setGeneratingDocument(false);
                    }
                  }}
                  disabled={generatingDocument}
                  className="btn-primary flex items-center gap-2"
                >
                  {generatingDocument ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Quick Generate
                </Button>
              </div>
            </div>
            
            {/* AI Document Chat - Generate or Update ANY Document */}
            <div className="card-base p-6 mb-6">
              <h3 className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-viraly-orange" />
                AI Document Chat
              </h3>
              <p className="text-sm text-viraly-muted mb-4">
                Generate ANY custom document or upload an existing document to update it. 
                AI will give you the COMPLETE updated document, not just changes.
              </p>
              
              {/* Upload Existing Document */}
              <div className="p-4 rounded-xl bg-white/5 mb-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="text-sm text-viraly-muted mb-2 block">Upload existing document to update (optional)</label>
                    <input
                      type="file"
                      accept=".txt,.md,.docx"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setUploadedFileName(file.name);
                          const text = await file.text();
                          setUploadedDocContent(text);
                          toast.success(`Loaded: ${file.name}`);
                        }
                      }}
                      className="input-base w-full text-sm"
                    />
                  </div>
                  {uploadedFileName && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-green-400">{uploadedFileName}</span>
                      <button
                        onClick={() => { setUploadedDocContent(""); setUploadedFileName(""); }}
                        className="p-1 hover:bg-white/10 rounded"
                      >
                        <X className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Chat Interface */}
              <div className="border border-white/10 rounded-xl overflow-hidden mb-4">
                {/* Chat History */}
                <div className="max-h-64 overflow-y-auto p-4 space-y-3 bg-white/5">
                  {docChatHistory.length === 0 ? (
                    <div className="text-center py-8 text-viraly-muted">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Start a conversation to generate or update documents</p>
                      <p className="text-xs mt-2">Examples: "Create a marketing plan for Q1" or "Update this document with our new features"</p>
                    </div>
                  ) : (
                    docChatHistory.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] p-3 rounded-xl ${
                          msg.role === 'user' 
                            ? 'bg-viraly-orange/20 text-viraly-light' 
                            : 'bg-white/10 text-viraly-light'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content.substring(0, 500)}{msg.content.length > 500 ? '...' : ''}</p>
                          {msg.role === 'assistant' && msg.content.length > 500 && (
                            <button
                              onClick={() => setDocumentContent(msg.content)}
                              className="text-xs text-viraly-orange mt-2 hover:underline"
                            >
                              View full document →
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  {docChatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white/10 p-3 rounded-xl">
                        <Loader2 className="w-4 h-4 animate-spin text-viraly-orange" />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Chat Input */}
                <div className="p-3 border-t border-white/10 flex gap-2">
                  <Input
                    value={docChatMessage}
                    onChange={(e) => setDocChatMessage(e.target.value)}
                    placeholder={uploadedDocContent 
                      ? "What changes would you like? (e.g., 'Add new video generation feature section')" 
                      : "What document do you need? (e.g., 'Create investor pitch deck outline')"
                    }
                    className="input-base flex-1"
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && !e.shiftKey && docChatMessage.trim()) {
                        e.preventDefault();
                        // Send message
                        setDocChatLoading(true);
                        const userMsg = docChatMessage;
                        setDocChatMessage("");
                        setDocChatHistory(prev => [...prev, { role: 'user', content: userMsg }]);
                        
                        try {
                          const adminToken = localStorage.getItem("viraly_admin_token");
                          const res = await apiClient.post("/admin/documents/chat", {
                            message: userMsg,
                            uploaded_document: uploadedDocContent || null,
                            conversation_history: docChatHistory
                          }, { headers: { Authorization: `Bearer ${adminToken}` } });
                          
                          setDocChatHistory(prev => [...prev, { role: 'assistant', content: res.data.response }]);
                          setDocumentContent(res.data.response);
                          toast.success("Document generated!");
                          fetchAdminData();
                        } catch (err) {
                          toast.error("Failed to generate");
                          setDocChatHistory(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
                        } finally {
                          setDocChatLoading(false);
                        }
                      }
                    }}
                  />
                  <Button
                    onClick={async () => {
                      if (!docChatMessage.trim()) return;
                      setDocChatLoading(true);
                      const userMsg = docChatMessage;
                      setDocChatMessage("");
                      setDocChatHistory(prev => [...prev, { role: 'user', content: userMsg }]);
                      
                      try {
                        const adminToken = localStorage.getItem("viraly_admin_token");
                        const res = await apiClient.post("/admin/documents/chat", {
                          message: userMsg,
                          uploaded_document: uploadedDocContent || null,
                          conversation_history: docChatHistory
                        }, { headers: { Authorization: `Bearer ${adminToken}` } });
                        
                        setDocChatHistory(prev => [...prev, { role: 'assistant', content: res.data.response }]);
                        setDocumentContent(res.data.response);
                        toast.success("Document generated!");
                        fetchAdminData();
                      } catch (err) {
                        toast.error("Failed to generate");
                      } finally {
                        setDocChatLoading(false);
                      }
                    }}
                    disabled={docChatLoading || !docChatMessage.trim()}
                    className="btn-primary"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Clear Chat */}
              {docChatHistory.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setDocChatHistory([]); setUploadedDocContent(""); setUploadedFileName(""); }}
                  className="text-xs"
                >
                  Clear Chat & Start New
                </Button>
              )}
              
              {/* Generated Document Preview */}
              {documentContent && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-sm">Generated Document</h4>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(documentContent);
                          toast.success("Copied to clipboard!");
                        }}
                        className="btn-secondary text-xs"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          const blob = new Blob([documentContent], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `viraly_document_${Date.now()}.txt`;
                          a.click();
                        }}
                        className="btn-secondary text-xs"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 max-h-96 overflow-y-auto border border-white/10">
                    <pre className="whitespace-pre-wrap text-sm text-viraly-light">{documentContent}</pre>
                  </div>
                </div>
              )}
            </div>

            {/* Document Generation History */}
            <div className="card-base p-6 mb-6">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-viraly-orange" />
                Generation History
              </h3>
              
              {documentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                </div>
              ) : documentHistory.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-viraly-muted mx-auto mb-3" />
                  <p className="text-viraly-muted">No documents generated yet. Use the AI generator above.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-sm text-viraly-muted">Document Type</th>
                        <th className="text-left py-3 px-4 text-sm text-viraly-muted">Generated By</th>
                        <th className="text-left py-3 px-4 text-sm text-viraly-muted">Date</th>
                        <th className="text-left py-3 px-4 text-sm text-viraly-muted">Version</th>
                        <th className="text-right py-3 px-4 text-sm text-viraly-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentHistory.map((doc) => (
                        <tr key={doc.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-4">
                            <span className="font-medium">{doc.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-viraly-muted">
                            {doc.generated_by_name || doc.generated_by}
                          </td>
                          <td className="py-3 px-4 text-sm text-viraly-muted">
                            {new Date(doc.created_at).toLocaleString()}
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 rounded-full bg-viraly-orange/20 text-viraly-orange text-xs">
                              v{doc.version || 1}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              size="sm"
                              className="btn-secondary"
                              onClick={() => setDocumentContent(doc.content)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Static Download Documents */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Financial Analysis */}
              <div className="card-base p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold">Financial Analysis</h3>
                    <p className="text-xs text-viraly-muted">Version 3.0</p>
                  </div>
                </div>
                <p className="text-sm text-viraly-muted mb-4">
                  Complete pricing model, cost analysis, regen credits, user growth projections, and payment processing details.
                </p>
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/downloads/Viraly_Financial_Analysis_v3.docx`}
                  download
                  className="btn-primary w-full flex items-center justify-center gap-2 py-2"
                >
                  <Download className="w-4 h-4" />
                  Download .docx
                </a>
              </div>

              {/* Complete PRD */}
              <div className="card-base p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold">Complete PRD</h3>
                    <p className="text-xs text-viraly-muted">Version 2.0 - Training Guide</p>
                  </div>
                </div>
                <p className="text-sm text-viraly-muted mb-4">
                  Complete platform guide including customer journey, feature specs, technical architecture, and API reference.
                </p>
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/downloads/Viraly_Complete_PRD_v2.docx`}
                  download
                  className="btn-primary w-full flex items-center justify-center gap-2 py-2"
                >
                  <Download className="w-4 h-4" />
                  Download .docx
                </a>
              </div>

              {/* Agency Proposal */}
              <div className="card-base p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold">Agency Proposal</h3>
                    <p className="text-xs text-viraly-muted">Multi-Brand Support</p>
                  </div>
                </div>
                <p className="text-sm text-viraly-muted mb-4">
                  Marketing agency & multi-brand architecture proposal with implementation phases and pricing tiers.
                </p>
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/downloads/Viraly_Agency_MultiAgent_Proposal.docx`}
                  download
                  className="btn-primary w-full flex items-center justify-center gap-2 py-2"
                >
                  <Download className="w-4 h-4" />
                  Download .docx
                </a>
              </div>
            </div>
          </motion.div>
        )}


        {/* Viraly Social Tab - Internal Social Accounts */}
        {activeTab === "viraly-social" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Share2 className="w-8 h-8 text-viraly-orange" />
                  Viraly Social Accounts
                </h1>
                <p className="text-viraly-muted mt-2">Manage and post to Viraly's official social media handles</p>
              </div>
              
              {/* Quick Preview as Viraly User */}
              <Button
                onClick={async () => {
                  // Find Viraly's user account (look for viraly in email or name)
                  setImpersonationLoading(true);
                  const adminToken = localStorage.getItem("viraly_admin_token");
                  const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
                  
                  try {
                    // Search for Viraly user account
                    const usersRes = await apiClient.get("/admin/users?search=viraly&limit=10", { headers });
                    const viralUsers = usersRes.data?.users || [];
                    
                    // Find the official Viraly account (prioritize email containing viraly)
                    const viralyUser = viralUsers.find(u => 
                      u.email?.toLowerCase().includes('viraly') || 
                      u.name?.toLowerCase().includes('viraly')
                    );
                    
                    if (viralyUser) {
                      // Start impersonation
                      const res = await apiClient.post(`/admin/users/${viralyUser.id}/impersonate`, {}, { headers });
                      
                      if (res.data?.impersonation_token) {
                        localStorage.setItem("viraly_admin_original_token", adminToken);
                        localStorage.setItem("viraly_impersonation_active", "true");
                        localStorage.setItem("viraly_impersonation_id", res.data.impersonation_id);
                        localStorage.setItem("viraly_impersonation_user", JSON.stringify(res.data.target_user));
                        localStorage.setItem("viraly_token", res.data.impersonation_token);
                        localStorage.setItem("viraly_user", JSON.stringify({
                          id: res.data.target_user.id,
                          email: res.data.target_user.email,
                          name: res.data.target_user.name,
                          plan: res.data.target_user.plan
                        }));
                        
                        toast.success(`Viewing as ${res.data.target_user.name || res.data.target_user.email}`, {
                          description: "You'll see exactly what the Viraly user sees"
                        });
                        
                        window.location.href = "/dashboard";
                      }
                    } else {
                      toast.error("Viraly user account not found", {
                        description: "Create a user account with 'viraly' in the email or name first"
                      });
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to preview as Viraly user");
                  } finally {
                    setImpersonationLoading(false);
                  }
                }}
                disabled={impersonationLoading}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="preview-as-viraly-user-btn"
              >
                {impersonationLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Preview as Viraly User
              </Button>
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
              {[
                { id: "accounts", label: "Connected Accounts", icon: Globe },
                { id: "create", label: "Create Content", icon: Edit3 },
                { id: "drafts", label: "Drafts", icon: FileText },
                { id: "published", label: "Published", icon: CheckCircle }
              ].map(tab => (
                <Button
                  key={tab.id}
                  onClick={() => setViralySocialSubTab(tab.id)}
                  className={viralySocialSubTab === tab.id ? "btn-primary" : "bg-white/5 hover:bg-white/10"}
                  data-testid={`viraly-social-tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </Button>
              ))}
            </div>

            {viralySocialLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : (
              <>
                {/* Connected Accounts Sub-tab */}
                {viralySocialSubTab === "accounts" && (
                  <div className="space-y-6">
                    {/* Connect Button */}
                    <div className="card-base p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-bold text-viraly-light">Connect Viraly's Official Accounts</h3>
                          <p className="text-sm text-viraly-muted mt-1">Link your official social media accounts to post directly from this panel</p>
                        </div>
                        <Button
                          onClick={async () => {
                            try {
                              const res = await apiClient.post("/admin/viraly-social/connect", {}, {
                                headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
                              });
                              if (res.data.connect_url) {
                                window.open(res.data.connect_url, "_blank");
                                toast.success("Connection window opened", { description: "Complete the connection in the new window" });
                              }
                            } catch (error) {
                              toast.error("Failed to get connect URL", { description: error.response?.data?.detail || "Try again" });
                            }
                          }}
                          className="btn-primary"
                          data-testid="viraly-social-connect-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Connect Accounts
                        </Button>
                      </div>
                    </div>

                    {/* Connected Accounts Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {["instagram", "facebook", "tiktok", "pinterest", "linkedin", "youtube", "twitter"].map(platform => {
                        const connected = viralySocialAccts.find(a => a.platform === platform);
                        const platformIcons = {
                          instagram: Instagram,
                          facebook: Facebook,
                          tiktok: Music2,
                          pinterest: Target,
                          linkedin: Briefcase,
                          youtube: PlayCircle,
                          twitter: MessageSquare
                        };
                        const PlatformIcon = platformIcons[platform] || Globe;
                        
                        return (
                          <div
                            key={platform}
                            className={`card-base p-4 ${connected ? "border-green-500/30" : "border-white/10"}`}
                            data-testid={`viraly-social-account-${platform}`}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${connected ? "bg-green-500/20" : "bg-white/10"}`}>
                                <PlatformIcon className={`w-5 h-5 ${connected ? "text-green-400" : "text-viraly-muted"}`} />
                              </div>
                              <div>
                                <p className="font-medium text-viraly-light capitalize">{platform}</p>
                                {connected ? (
                                  <p className="text-xs text-green-400">@{connected.username}</p>
                                ) : (
                                  <p className="text-xs text-viraly-muted">Not connected</p>
                                )}
                              </div>
                            </div>
                            {connected && (
                              <div className="flex items-center gap-2 mt-2">
                                <CheckCircle className="w-4 h-4 text-green-400" />
                                <span className="text-xs text-green-400">Ready to post</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Create Content Sub-tab */}
                {viralySocialSubTab === "create" && (
                  <div className="space-y-6">
                    <div className="card-base p-6">
                      <h3 className="font-bold text-viraly-light mb-4">Generate Content for Viraly</h3>
                      
                      {/* Topic Input */}
                      <div className="mb-4">
                        <label className="block text-sm text-viraly-muted mb-2">Topic / Theme</label>
                        <Input
                          value={contentTopic}
                          onChange={(e) => setContentTopic(e.target.value)}
                          placeholder="e.g., New feature announcement, Social media tips..."
                          className="input-base"
                          data-testid="viraly-content-topic"
                        />
                      </div>

                      {/* Content Type */}
                      <div className="mb-4">
                        <label className="block text-sm text-viraly-muted mb-2">Content Type</label>
                        <select
                          value={contentType}
                          onChange={(e) => setContentType(e.target.value)}
                          className="input-base w-full"
                          data-testid="viraly-content-type"
                        >
                          <option value="promotional">Promotional</option>
                          <option value="educational">Educational</option>
                          <option value="announcement">Announcement</option>
                          <option value="engagement">Engagement</option>
                        </select>
                      </div>

                      {/* Platform Selection */}
                      <div className="mb-4">
                        <label className="block text-sm text-viraly-muted mb-2">Platforms</label>
                        <div className="flex flex-wrap gap-2">
                          {["instagram", "facebook", "tiktok", "pinterest", "linkedin", "youtube", "twitter"].map(p => (
                            <button
                              key={p}
                              onClick={() => {
                                if (contentPlatforms.includes(p)) {
                                  setContentPlatforms(contentPlatforms.filter(x => x !== p));
                                } else {
                                  setContentPlatforms([...contentPlatforms, p]);
                                }
                              }}
                              className={`px-3 py-1 rounded-full text-sm capitalize ${contentPlatforms.includes(p) ? "bg-viraly-orange text-white" : "bg-white/10 text-viraly-muted"}`}
                              data-testid={`viraly-platform-${p}`}
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Include Image */}
                      <div className="mb-6 flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={includeImage}
                          onChange={(e) => setIncludeImage(e.target.checked)}
                          className="rounded"
                          data-testid="viraly-include-image"
                        />
                        <label className="text-sm text-viraly-light">Generate AI image</label>
                      </div>

                      {/* Generate Button */}
                      <Button
                        onClick={async () => {
                          if (!contentTopic.trim()) {
                            toast.error("Please enter a topic");
                            return;
                          }
                          setViralySocialGenerating(true);
                          try {
                            const res = await apiClient.post("/admin/viraly-social/generate-content", {
                              topic: contentTopic,
                              content_type: contentType,
                              platforms: contentPlatforms,
                              include_image: includeImage
                            }, {
                              headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
                            });
                            toast.success("Content generated!", { description: "Check the Drafts tab" });
                            setViralySocialSubTab("drafts");
                            // Refresh drafts
                            const draftsRes = await apiClient.get("/admin/viraly-social/drafts", {
                              headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
                            });
                            setViralySocialDrafts(draftsRes.data.drafts || []);
                          } catch (error) {
                            toast.error("Generation failed", { description: error.response?.data?.detail || "Try again" });
                          } finally {
                            setViralySocialGenerating(false);
                          }
                        }}
                        disabled={viralySocialGenerating || !contentTopic.trim()}
                        className="btn-primary w-full"
                        data-testid="viraly-generate-content-btn"
                      >
                        {viralySocialGenerating ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" /> Generate Content</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Drafts Sub-tab */}
                {viralySocialSubTab === "drafts" && (
                  <div className="space-y-4">
                    {viralySocialDrafts.length === 0 ? (
                      <div className="card-base p-12 text-center">
                        <FileText className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                        <p className="text-viraly-muted">No drafts yet. Create content to get started.</p>
                      </div>
                    ) : (
                      viralySocialDrafts.map(draft => (
                        <div key={draft.id} className="card-base p-6" data-testid={`viraly-draft-${draft.id}`}>
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h4 className="font-bold text-viraly-light">{draft.topic}</h4>
                              <p className="text-sm text-viraly-muted">
                                {draft.content_type} • {draft.platforms?.join(", ")} • {new Date(draft.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs ${draft.status === "draft" ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400"}`}>
                              {draft.status}
                            </span>
                          </div>
                          
                          {draft.image_url && (
                            <img src={draft.image_url} alt="Generated" className="w-full max-w-md rounded-lg mb-4" />
                          )}
                          
                          <div className="bg-white/5 p-4 rounded-lg mb-4 max-h-40 overflow-y-auto">
                            <pre className="text-xs text-viraly-light whitespace-pre-wrap">
                              {typeof draft.generated_content === "object" 
                                ? JSON.stringify(draft.generated_content, null, 2)
                                : draft.generated_content}
                            </pre>
                          </div>
                          
                          {draft.status === "draft" && (
                            <div className="flex gap-2">
                              <Button
                                onClick={async () => {
                                  setViralySocialPublishing(true);
                                  try {
                                    const res = await apiClient.post("/admin/viraly-social/publish", {
                                      draft_id: draft.id,
                                      platforms: draft.platforms
                                    }, {
                                      headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
                                    });
                                    toast.success("Published!", { description: res.data.message });
                                    // Refresh data
                                    const [draftsRes, postsRes] = await Promise.all([
                                      apiClient.get("/admin/viraly-social/drafts", { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }),
                                      apiClient.get("/admin/viraly-social/posts", { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } })
                                    ]);
                                    setViralySocialDrafts(draftsRes.data.drafts || []);
                                    setViralySocialPosts(postsRes.data.posts || []);
                                  } catch (error) {
                                    toast.error("Publish failed", { description: error.response?.data?.detail || "Try again" });
                                  } finally {
                                    setViralySocialPublishing(false);
                                  }
                                }}
                                disabled={viralySocialPublishing}
                                className="btn-primary"
                                data-testid={`viraly-publish-${draft.id}`}
                              >
                                {viralySocialPublishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                                Publish Now
                              </Button>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Published Sub-tab */}
                {viralySocialSubTab === "published" && (
                  <div className="space-y-4">
                    {viralySocialPosts.length === 0 ? (
                      <div className="card-base p-12 text-center">
                        <CheckCircle className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                        <p className="text-viraly-muted">No published posts yet.</p>
                      </div>
                    ) : (
                      viralySocialPosts.map(post => (
                        <div key={post.id} className="card-base p-6" data-testid={`viraly-post-${post.id}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm text-viraly-light">
                                Published to: <span className="text-viraly-orange">{post.platforms?.join(", ")}</span>
                              </p>
                              <p className="text-xs text-viraly-muted mt-1">
                                {new Date(post.created_at).toLocaleString()}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs ${post.status === "published" ? "bg-green-500/20 text-green-400" : post.status === "scheduled" ? "bg-blue-500/20 text-blue-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                              {post.status}
                            </span>
                          </div>
                          {post.image_url && (
                            <img src={post.image_url} alt="Post" className="w-32 h-32 object-cover rounded-lg mt-3" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Settings className="w-8 h-8 text-viraly-orange" />
                  Platform Settings
                </h1>
                <p className="text-viraly-muted mt-2">Configure platform-wide settings</p>
              </div>
              <Button className="btn-primary">Save Changes</Button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* General Settings */}
              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4">Trial & Onboarding</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Trial Days</p>
                      <p className="text-sm text-viraly-muted">Free trial period for new users</p>
                    </div>
                    <Input type="number" defaultValue={settings.trial_days || 7} className="w-20 input-base text-center" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Trial Posts</p>
                      <p className="text-sm text-viraly-muted">Free posts for trial users</p>
                    </div>
                    <Input type="number" defaultValue={settings.trial_posts || 5} className="w-20 input-base text-center" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Trial Videos</p>
                      <p className="text-sm text-viraly-muted">Allow video creation in trial</p>
                    </div>
                    <button className={`w-12 h-6 rounded-full transition-colors ${settings.trial_videos_enabled ? 'bg-viraly-orange' : 'bg-white/20'}`}>
                      <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.trial_videos_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Pay As You Go Validity (Days)</p>
                      <p className="text-sm text-viraly-muted">Days until Pay As You Go/Extended Video credits expire</p>
                    </div>
                    <Input type="number" defaultValue={settings.payg_validity_days || 90} className="w-20 input-base text-center" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Yearly Discount %</p>
                      <p className="text-sm text-viraly-muted">Discount for annual plans</p>
                    </div>
                    <Input type="number" defaultValue={settings.yearly_discount_percent || 35} className="w-20 input-base text-center" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Maintenance Mode</p>
                      <p className="text-sm text-viraly-muted">Temporarily disable the platform</p>
                    </div>
                    <button className={`w-12 h-6 rounded-full transition-colors ${settings.maintenance_mode ? 'bg-viraly-orange' : 'bg-white/20'}`}>
                      <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.maintenance_mode ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Customer Levels REMOVED - Use Promotions → Loyalty instead */}

              {/* Watermark Settings */}
              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4">Watermark</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Watermark Enabled</p>
                      <p className="text-sm text-viraly-muted">Apply watermark to all content</p>
                    </div>
                    <button className={`w-12 h-6 rounded-full transition-colors ${settings.watermark_enabled !== false ? 'bg-viraly-orange' : 'bg-white/20'}`}>
                      <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.watermark_enabled !== false ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5">
                    <p className="font-medium mb-2">Watermark Text</p>
                    <Input defaultValue={settings.watermark_text || "Made with Viraly"} className="input-base" />
                  </div>
                </div>
              </div>

              {/* Email Settings */}
              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4">Email</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-viraly-muted">Send email notifications to users</p>
                    </div>
                    <button className={`w-12 h-6 rounded-full transition-colors ${settings.email_notifications !== false ? 'bg-viraly-orange' : 'bg-white/20'}`}>
                      <div className={`w-5 h-5 rounded-full bg-white transition-transform ${settings.email_notifications !== false ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="p-3 rounded-lg bg-viraly-success/10 border border-viraly-success/20">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-viraly-success" />
                      <span className="text-viraly-success font-medium">IONOS SMTP Configured</span>
                    </div>
                    <p className="text-sm text-viraly-muted mt-1">care@viraly.ca</p>
                  </div>
                </div>
              </div>

              {/* User Notification Thresholds */}
              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-lg font-bold">User Notification Thresholds</h3>
                    <p className="text-sm text-viraly-muted">Configure when to alert users about credits</p>
                  </div>
                  <button 
                    onClick={handleSaveNotificationThresholds}
                    className="px-4 py-2 bg-viraly-orange hover:bg-viraly-orange/80 rounded-lg text-sm font-medium transition-colors"
                  >
                    Save Thresholds
                  </button>
                </div>
                <div className="space-y-4">
                  {/* Low Credits Alert */}
                  <div className="p-4 rounded-lg bg-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium text-yellow-400">Low Credits Alert</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-viraly-muted">Threshold (posts remaining)</label>
                        <Input 
                          type="number" 
                          value={notificationThresholds.low_credits_threshold}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, low_credits_threshold: parseInt(e.target.value) || 5})}
                          className="input-base mt-1"
                          min={1}
                          max={50}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-viraly-muted">Cooldown (days)</label>
                        <Input 
                          type="number" 
                          value={notificationThresholds.low_credits_cooldown_days}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, low_credits_cooldown_days: parseInt(e.target.value) || 7})}
                          className="input-base mt-1"
                          min={1}
                          max={30}
                        />
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3">
                      <label className="flex items-center gap-2 text-sm">
                        <input 
                          type="checkbox" 
                          checked={notificationThresholds.low_credits_email_enabled}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, low_credits_email_enabled: e.target.checked})}
                          className="rounded border-white/20"
                        />
                        Email
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input 
                          type="checkbox" 
                          checked={notificationThresholds.low_credits_push_enabled}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, low_credits_push_enabled: e.target.checked})}
                          className="rounded border-white/20"
                        />
                        Push Notification
                      </label>
                    </div>
                  </div>

                  {/* Out of Credits Alert */}
                  <div className="p-4 rounded-lg bg-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium text-red-400">Out of Credits Alert</p>
                    </div>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input 
                          type="checkbox" 
                          checked={notificationThresholds.out_of_credits_email_enabled}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, out_of_credits_email_enabled: e.target.checked})}
                          className="rounded border-white/20"
                        />
                        Email
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input 
                          type="checkbox" 
                          checked={notificationThresholds.out_of_credits_push_enabled}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, out_of_credits_push_enabled: e.target.checked})}
                          className="rounded border-white/20"
                        />
                        Push Notification
                      </label>
                    </div>
                  </div>

                  {/* Expiring Credits Alert */}
                  <div className="p-4 rounded-lg bg-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium text-orange-400">Expiring Credits Alert</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-viraly-muted">Days before expiry</label>
                        <Input 
                          type="number" 
                          value={notificationThresholds.expiring_credits_days_before}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, expiring_credits_days_before: parseInt(e.target.value) || 7})}
                          className="input-base mt-1"
                          min={1}
                          max={30}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-viraly-muted">Cooldown (days)</label>
                        <Input 
                          type="number" 
                          value={notificationThresholds.expiring_credits_cooldown_days}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, expiring_credits_cooldown_days: parseInt(e.target.value) || 3})}
                          className="input-base mt-1"
                          min={1}
                          max={14}
                        />
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3">
                      <label className="flex items-center gap-2 text-sm">
                        <input 
                          type="checkbox" 
                          checked={notificationThresholds.expiring_credits_email_enabled}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, expiring_credits_email_enabled: e.target.checked})}
                          className="rounded border-white/20"
                        />
                        Email
                      </label>
                    </div>
                  </div>

                  {/* Scheduled Jobs */}
                  <div className="p-4 rounded-lg bg-white/5">
                    <p className="font-medium mb-3">Scheduled Notifications</p>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input 
                          type="checkbox" 
                          checked={notificationThresholds.weekly_digest_enabled}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, weekly_digest_enabled: e.target.checked})}
                          className="rounded border-white/20"
                        />
                        Weekly Activity Digest
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input 
                          type="checkbox" 
                          checked={notificationThresholds.scheduled_post_reminder_enabled}
                          onChange={(e) => setNotificationThresholds({...notificationThresholds, scheduled_post_reminder_enabled: e.target.checked})}
                          className="rounded border-white/20"
                        />
                        Scheduled Post Reminders
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Platform Configuration (Credits, Limits, Taxes) */}
              <div className="card-base p-6 col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                      <Settings className="w-5 h-5 text-viraly-orange" />
                      Platform Configuration
                    </h3>
                    <p className="text-sm text-viraly-muted mt-1">
                      Configure credit costs, profile limits, and tax rates (based on LLM/video provider costs)
                    </p>
                  </div>
                  {platformConfigLoading && <Loader2 className="w-5 h-5 animate-spin text-viraly-orange" />}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Video Credit Costs */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium">Video Credit Costs</p>
                      <button 
                        onClick={() => setEditPlatformConfig({ type: 'video-credit-costs', data: platformConfig.video_credit_costs })}
                        className="text-xs text-viraly-orange hover:text-white"
                      >
                        <Edit className="w-3 h-3 inline mr-1" />Edit
                      </button>
                    </div>
                    <div className="space-y-1 text-sm text-viraly-muted">
                      {Object.entries(platformConfig.video_credit_costs || {}).slice(0, 4).map(([key, val]) => (
                        <div key={key} className="flex justify-between">
                          <span>{key}</span>
                          <span className="text-white">{val} credits</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Regen Credit Costs */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium">Regen Credit Costs</p>
                      <button 
                        onClick={() => setEditPlatformConfig({ type: 'regen-credit-costs', data: platformConfig.regen_credit_costs })}
                        className="text-xs text-viraly-orange hover:text-white"
                      >
                        <Edit className="w-3 h-3 inline mr-1" />Edit
                      </button>
                    </div>
                    <div className="space-y-1 text-sm text-viraly-muted">
                      {Object.entries(platformConfig.regen_credit_costs || {}).map(([key, val]) => (
                        <div key={key} className="flex justify-between">
                          <span>{key.replace(/_/g, ' ')}</span>
                          <span className="text-white">{val} credits</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Profile Limits */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium">Profile Limits per Plan</p>
                      <button 
                        onClick={() => setEditPlatformConfig({ type: 'profile-limits', data: platformConfig.profile_limits })}
                        className="text-xs text-viraly-orange hover:text-white"
                      >
                        <Edit className="w-3 h-3 inline mr-1" />Edit
                      </button>
                    </div>
                    <div className="space-y-1 text-sm text-viraly-muted">
                      {Object.entries(platformConfig.profile_limits || {}).map(([plan, limit]) => (
                        <div key={plan} className="flex justify-between">
                          <span className="capitalize">{plan.replace(/_/g, ' ')}</span>
                          <span className="text-white">{limit} profiles</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Tax Rates */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-medium">Tax Rates (Canada)</p>
                      <button 
                        onClick={() => setEditPlatformConfig({ type: 'tax-rates', data: platformConfig.tax_rates })}
                        className="text-xs text-viraly-orange hover:text-white"
                      >
                        <Edit className="w-3 h-3 inline mr-1" />Edit
                      </button>
                    </div>
                    <div className="space-y-1 text-sm text-viraly-muted">
                      {Object.entries(platformConfig.tax_rates?.CA || {}).slice(0, 4).map(([prov, info]) => (
                        <div key={prov} className="flex justify-between">
                          <span>{prov}</span>
                          <span className="text-white">{(info.rate * 100).toFixed(1)}% ({info.type})</span>
                        </div>
                      ))}
                      {Object.keys(platformConfig.tax_rates?.CA || {}).length > 4 && (
                        <p className="text-xs text-viraly-muted">+{Object.keys(platformConfig.tax_rates?.CA || {}).length - 4} more provinces</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature Toggles - Connected to Backend */}
              <div className="card-base p-6 col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                    <ToggleRight className="w-5 h-5 text-viraly-orange" />
                    Feature Toggles
                  </h3>
                  <Button
                    className="btn-secondary text-sm"
                    onClick={() => fetchAdminData()}
                    disabled={featureTogglesLoading}
                  >
                    <RefreshCw className={`w-3 h-3 mr-1 ${featureTogglesLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                
                {featureTogglesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Active Features - Customer Facing */}
                    <div>
                      <h4 className="text-sm font-medium text-viraly-orange mb-3 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Active Features (Customer-Facing)
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { key: "vira_ai_assistant", name: "Vira (AI Assistant)", desc: "AI chatbot for content help" },
                          { key: "video_generation", name: "Video Generation", desc: "AI video creation with RunwayML" },
                          { key: "post_generation", name: "Post Generation", desc: "AI text + image generation" },
                          { key: "url_to_post", name: "URL to Post", desc: "Convert URL to social post" },
                          { key: "brand_learning", name: "Brand Learning", desc: "AI learns brand voice" },
                          { key: "viral_simulator", name: "Viral Simulator", desc: "Predict post virality" },
                          { key: "auto_reply", name: "Auto Reply", desc: "Auto-respond to comments" },
                          { key: "scheduled_posting", name: "Scheduled Posting", desc: "Queue posts for future" },
                          { key: "multi_profile", name: "Multi-Profile Support", desc: "Manage multiple brands" },
                          { key: "competitor_monitoring", name: "Competitor Monitoring", desc: "Track up to 5-10 competitors" },
                          { key: "loyalty_program", name: "Loyalty Program", desc: "Points & rewards system" },
                          { key: "referral_system", name: "Referral System", desc: "Refer friends & earn" },
                          { key: "gift_packs", name: "Gift Packs", desc: "Purchase & send credits" }
                        ].map((feature) => (
                          <div key={feature.key} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                            <div>
                              <p className="font-medium text-sm">{feature.name}</p>
                              <p className="text-xs text-viraly-muted">{feature.desc}</p>
                            </div>
                            <button 
                              onClick={async () => {
                                const newValue = !featureToggles[feature.key];
                                // If turning OFF, show announcement option
                                if (!newValue) {
                                  setFeatureToggleAnnouncement({ feature: feature, newValue });
                                  return;
                                }
                                // Turning ON - just save
                                setFeatureToggles(prev => ({ ...prev, [feature.key]: newValue }));
                                try {
                                  const adminToken = localStorage.getItem("viraly_admin_token");
                                  await apiClient.put("/admin/feature-toggles", {
                                    [feature.key]: newValue
                                  }, { headers: { Authorization: `Bearer ${adminToken}` }});
                                  toast.success(`${feature.name} enabled`);
                                } catch (err) {
                                  setFeatureToggles(prev => ({ ...prev, [feature.key]: !newValue }));
                                  toast.error("Failed to update feature");
                                }
                              }}
                              className={`w-12 h-6 rounded-full transition-colors ${featureToggles[feature.key] ? 'bg-viraly-success' : 'bg-red-500/50'}`}
                            >
                              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${featureToggles[feature.key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Input Types */}
                    <div>
                      <h4 className="text-sm font-medium text-blue-400 mb-3 flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Input Types
                      </h4>
                      <div className="grid grid-cols-4 gap-3">
                        {[
                          { key: "text_input", name: "Text Input", desc: "Paste/type text" },
                          { key: "image_input", name: "Image Input", desc: "Upload images" },
                          { key: "voice_input", name: "Voice Input", desc: "Voice to text" },
                          { key: "video_input", name: "Video Input", desc: "Upload videos" }
                        ].map((feature) => (
                          <div key={feature.key} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                            <div>
                              <p className="font-medium text-sm">{feature.name}</p>
                              <p className="text-xs text-viraly-muted">{feature.desc}</p>
                            </div>
                            <button 
                              onClick={async () => {
                                const newValue = !featureToggles[feature.key];
                                if (!newValue) {
                                  setFeatureToggleAnnouncement({ feature: feature, newValue });
                                  return;
                                }
                                setFeatureToggles(prev => ({ ...prev, [feature.key]: newValue }));
                                try {
                                  const adminToken = localStorage.getItem("viraly_admin_token");
                                  await apiClient.put("/admin/feature-toggles", { [feature.key]: newValue }, { headers: { Authorization: `Bearer ${adminToken}` }});
                                  toast.success(`${feature.name} enabled`);
                                } catch (err) {
                                  setFeatureToggles(prev => ({ ...prev, [feature.key]: !newValue }));
                                  toast.error("Failed to update feature");
                                }
                              }}
                              className={`w-12 h-6 rounded-full transition-colors ${featureToggles[feature.key] ? 'bg-viraly-success' : 'bg-red-500/50'}`}
                            >
                              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${featureToggles[feature.key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Payment & Auth Options */}
                    <div>
                      <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Payment & Authentication
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { key: "paypal_payments", name: "PayPal Payments", desc: "PayPal checkout" },
                          { key: "etransfer_payments", name: "E-Transfer Payments", desc: "Interac for Canada" },
                          { key: "webauthn_login", name: "WebAuthn Login", desc: "Passkey/biometric" },
                          { key: "push_notifications", name: "Push Notifications", desc: "Browser push" }
                        ].map((feature) => (
                          <div key={feature.key} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                            <div>
                              <p className="font-medium text-sm">{feature.name}</p>
                              <p className="text-xs text-viraly-muted">{feature.desc}</p>
                            </div>
                            <button 
                              onClick={async () => {
                                const newValue = !featureToggles[feature.key];
                                if (!newValue) {
                                  setFeatureToggleAnnouncement({ feature: feature, newValue });
                                  return;
                                }
                                setFeatureToggles(prev => ({ ...prev, [feature.key]: newValue }));
                                try {
                                  const adminToken = localStorage.getItem("viraly_admin_token");
                                  await apiClient.put("/admin/feature-toggles", { [feature.key]: newValue }, { headers: { Authorization: `Bearer ${adminToken}` }});
                                  toast.success(`${feature.name} enabled`);
                                } catch (err) {
                                  setFeatureToggles(prev => ({ ...prev, [feature.key]: !newValue }));
                                  toast.error("Failed to update feature");
                                }
                              }}
                              className={`w-12 h-6 rounded-full transition-colors ${featureToggles[feature.key] ? 'bg-viraly-success' : 'bg-red-500/50'}`}
                            >
                              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${featureToggles[feature.key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Hidden/Inactive Features */}
                    <div>
                      <h4 className="text-sm font-medium text-viraly-muted mb-3 flex items-center gap-2">
                        <EyeOff className="w-4 h-4" />
                        Hidden Features (Developed but Inactive)
                      </h4>
                      <div className="grid grid-cols-3 gap-3 opacity-70">
                        {[
                          { key: "content_radar", name: "Content Radar", desc: "Monitor competitor content (legacy)" },
                          { key: "group_finder", name: "Group Finder", desc: "Find relevant FB groups" },
                          { key: "quote_across", name: "Quote Across", desc: "Cross-post quotes" }
                        ].map((feature) => (
                          <div key={feature.key} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-dashed border-white/10">
                            <div>
                              <p className="font-medium text-sm">{feature.name}</p>
                              <p className="text-xs text-viraly-muted">{feature.desc}</p>
                            </div>
                            <button 
                              onClick={async () => {
                                const newValue = !featureToggles[feature.key];
                                setFeatureToggles(prev => ({ ...prev, [feature.key]: newValue }));
                                try {
                                  const adminToken = localStorage.getItem("viraly_admin_token");
                                  await apiClient.put("/admin/feature-toggles", { [feature.key]: newValue }, { headers: { Authorization: `Bearer ${adminToken}` }});
                                  toast.success(`${feature.name} ${newValue ? 'enabled' : 'disabled'}`);
                                } catch (err) {
                                  setFeatureToggles(prev => ({ ...prev, [feature.key]: !newValue }));
                                  toast.error("Failed to update feature");
                                }
                              }}
                              className={`w-12 h-6 rounded-full transition-colors ${featureToggles[feature.key] ? 'bg-yellow-500' : 'bg-white/20'}`}
                            >
                              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${featureToggles[feature.key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-viraly-muted mt-2">These features are developed but not launched. Keep inactive unless testing.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Note: Social Media Handles moved to VIRA (Bot) tab */}

              {/* Free Access Accounts (Whitelist) */}
              <div className="card-base p-6 col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                      <Gift className="w-5 h-5 text-viraly-orange" />
                      Free Access Accounts
                    </h3>
                    <p className="text-sm text-viraly-muted mt-1">
                      Friends, family, partners, or compensation accounts that get free access with optional daily limits
                    </p>
                  </div>
                </div>
                
                {/* Add New Free Access Account */}
                <div className="p-4 rounded-xl bg-viraly-orange/10 border border-viraly-orange/20 mb-4">
                  <p className="font-medium text-viraly-orange mb-3">Add Free Access Account</p>
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <Input
                        type="email"
                        placeholder="Email they'll register with"
                        value={newFreeAccessEmail}
                        onChange={(e) => setNewFreeAccessEmail(e.target.value)}
                        className="input-base flex-1"
                        data-testid="free-access-email-input"
                      />
                      <Input
                        placeholder="Name/Reason (e.g., Mom, Partner, Compensation)"
                        value={newFreeAccessReason}
                        onChange={(e) => setNewFreeAccessReason(e.target.value)}
                        className="input-base flex-1"
                        data-testid="free-access-reason-input"
                      />
                    </div>
                    <div className="flex gap-3 items-center">
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          type="number"
                          min="0"
                          placeholder="Posts/day (0=unlimited)"
                          value={newFreeAccessPostsLimit}
                          onChange={(e) => setNewFreeAccessPostsLimit(e.target.value)}
                          className="input-base w-40"
                          data-testid="free-access-posts-limit"
                        />
                        <Input
                          type="number"
                          min="0"
                          placeholder="Videos/day (0=unlimited)"
                          value={newFreeAccessVideosLimit}
                          onChange={(e) => setNewFreeAccessVideosLimit(e.target.value)}
                          className="input-base w-40"
                          data-testid="free-access-videos-limit"
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                        <input
                          type="checkbox"
                          checked={newFreeAccessGenerateLink}
                          onChange={(e) => setNewFreeAccessGenerateLink(e.target.checked)}
                          className="w-4 h-4 accent-viraly-orange"
                          data-testid="free-access-generate-link"
                        />
                        <span className="text-sm">Generate Invite Link</span>
                        <Link2 className="w-4 h-4 text-viraly-muted" />
                      </label>
                      <Button
                        onClick={async () => {
                          if (!newFreeAccessEmail) return;
                          setAddingFreeAccess(true);
                          try {
                            const adminToken = localStorage.getItem("viraly_admin_token");
                            const res = await apiClient.post("/admin/free-access-accounts", 
                              { 
                                email: newFreeAccessEmail, 
                                reason: newFreeAccessReason,
                                generate_invite_link: newFreeAccessGenerateLink,
                                daily_posts_limit: parseInt(newFreeAccessPostsLimit) || 0,
                                daily_videos_limit: parseInt(newFreeAccessVideosLimit) || 0
                              },
                              { headers: { Authorization: `Bearer ${adminToken}` }}
                            );
                            if (res.data.invite_link) {
                              navigator.clipboard.writeText(res.data.invite_link);
                              toast.success(`Added! Invite link copied to clipboard`);
                            } else {
                              toast.success(`Added ${newFreeAccessEmail} to free access list`);
                            }
                            setNewFreeAccessEmail("");
                            setNewFreeAccessReason("");
                            setNewFreeAccessGenerateLink(false);
                            setNewFreeAccessPostsLimit("");
                            setNewFreeAccessVideosLimit("");
                            // Refresh list
                            const listRes = await apiClient.get("/admin/free-access-accounts", {
                              headers: { Authorization: `Bearer ${adminToken}` }
                            });
                            setFreeAccessAccounts(listRes.data.accounts || []);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Failed to add account");
                          } finally {
                            setAddingFreeAccess(false);
                          }
                        }}
                        disabled={addingFreeAccess || !newFreeAccessEmail}
                        className="btn-primary whitespace-nowrap"
                        data-testid="add-free-access-btn"
                      >
                        {addingFreeAccess ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Add
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Free Access Accounts List */}
                <div className="space-y-2">
                  {freeAccessAccounts.length === 0 ? (
                    <p className="text-center text-viraly-muted py-8">No free access accounts configured</p>
                  ) : (
                    freeAccessAccounts.map((account) => (
                      <div 
                        key={account.id} 
                        className={`flex items-center justify-between p-4 rounded-lg ${account.is_active ? 'bg-viraly-success/10 border border-viraly-success/20' : 'bg-white/5 opacity-60'}`}
                        data-testid={`free-access-account-${account.email}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${account.is_active ? 'bg-viraly-success/20' : 'bg-white/10'}`}>
                            <Gift className={`w-5 h-5 ${account.is_active ? 'text-viraly-success' : 'text-viraly-muted'}`} />
                          </div>
                          <div>
                            <p className="font-medium">{account.name || account.email}</p>
                            <p className="text-sm text-viraly-muted">{account.email}</p>
                            {account.reason && (
                              <p className="text-xs text-viraly-orange mt-1">{account.reason}</p>
                            )}
                            <div className="flex gap-3 mt-1">
                              {account.daily_posts_limit > 0 && (
                                <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{account.daily_posts_limit} posts/day</span>
                              )}
                              {account.daily_videos_limit > 0 && (
                                <span className="text-xs bg-white/10 px-2 py-0.5 rounded">{account.daily_videos_limit} videos/day</span>
                              )}
                              {account.daily_posts_limit === 0 && account.daily_videos_limit === 0 && (
                                <span className="text-xs bg-viraly-orange/20 text-viraly-orange px-2 py-0.5 rounded">Unlimited</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {account.invite_link && (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(account.invite_link);
                                toast.success("Invite link copied!");
                              }}
                              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                              title="Copy invite link"
                            >
                              <Copy className="w-4 h-4 text-viraly-muted" />
                            </button>
                          )}
                          <span className={`text-xs px-2 py-1 rounded ${account.is_active ? 'bg-viraly-success/20 text-viraly-success' : 'bg-white/10 text-viraly-muted'}`}>
                            {account.is_active ? (account.user_registered ? 'Active' : 'Pending') : 'Disabled'}
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-viraly-card border-white/10">
                              <DropdownMenuItem
                                className="hover:bg-white/10 cursor-pointer"
                                onClick={async () => {
                                  const adminToken = localStorage.getItem("viraly_admin_token");
                                  try {
                                    await apiClient.put(`/admin/free-access-accounts/${account.id}`, 
                                      { is_active: !account.is_active },
                                      { headers: { Authorization: `Bearer ${adminToken}` }}
                                    );
                                    toast.success(account.is_active ? "Account disabled - they'll need to pay now" : "Account enabled");
                                    const res = await apiClient.get("/admin/free-access-accounts", {
                                      headers: { Authorization: `Bearer ${adminToken}` }
                                    });
                                    setFreeAccessAccounts(res.data.accounts || []);
                                  } catch (err) {
                                    toast.error("Failed to update account");
                                  }
                                }}
                              >
                                {account.is_active ? (
                                  <><PauseCircle className="w-4 h-4 mr-2" /> Disable (Start Charging)</>
                                ) : (
                                  <><PlayCircle className="w-4 h-4 mr-2" /> Enable (Free Again)</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="hover:bg-red-500/20 text-red-400 cursor-pointer"
                                onClick={async () => {
                                  if (!confirm(`Remove ${account.email} from free access?`)) return;
                                  const adminToken = localStorage.getItem("viraly_admin_token");
                                  try {
                                    await apiClient.delete(`/admin/free-access-accounts/${account.id}`, {
                                      headers: { Authorization: `Bearer ${adminToken}` }
                                    });
                                    toast.success("Account removed from free access");
                                    const res = await apiClient.get("/admin/free-access-accounts", {
                                      headers: { Authorization: `Bearer ${adminToken}` }
                                    });
                                    setFreeAccessAccounts(res.data.accounts || []);
                                  } catch (err) {
                                    toast.error("Failed to remove account");
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <p className="text-xs text-viraly-muted mt-4 p-3 bg-white/5 rounded-lg">
                  <strong>How it works:</strong> Add an email before they register. If "Generate Invite Link" is checked, share the link with them. 
                  Once they register using that email (or link), they get free access. Disable anytime to convert them to paying customers.
                  Set daily limits to prevent abuse (0 = unlimited).
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Promotions Tab */}
        {activeTab === "promotions" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Gift className="w-8 h-8 text-viraly-orange" />
                  Promotions & Discounts
                </h1>
                <p className="text-viraly-muted mt-2">Manage promotional campaigns, coupons, and announcements</p>
              </div>
              <div className="flex gap-2">
                {promoSubTab === "promotions" && (
                  <Button className="btn-primary flex items-center gap-2" data-testid="create-promo-btn" onClick={() => setCreatePromoModal(true)}>
                    <Plus className="w-4 h-4" /> Create Promotion
                  </Button>
                )}
                {promoSubTab === "coupons" && (
                  <Button className="btn-primary flex items-center gap-2" data-testid="create-coupon-btn" onClick={() => setCreateCouponModal(true)}>
                    <Plus className="w-4 h-4" /> Create Coupon
                  </Button>
                )}
                {promoSubTab === "announcements" && (
                  <Button className="btn-primary flex items-center gap-2" data-testid="create-announcement-btn" onClick={() => setCreateAnnouncementModal(true)}>
                    <Plus className="w-4 h-4" /> Create Announcement
                  </Button>
                )}
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-2 mb-6 p-1 bg-white/5 rounded-xl w-fit">
              {[
                { id: "promotions", label: "Promotions" },
                { id: "suggestions", label: "AI Suggestions" },
                { id: "coupons", label: "Coupons" },
                { id: "announcements", label: "Announcements" },
                { id: "loyalty", label: "Loyalty" },
                { id: "referral", label: "Referral" },
                { id: "influencer", label: "Influencer" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setPromoSubTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    promoSubTab === tab.id ? "bg-viraly-orange text-white" : "text-viraly-muted hover:text-white"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Promotions Sub-tab */}
            {promoSubTab === "promotions" && (
              <>
                {/* Filters */}
                <div className="flex items-center gap-4 mb-6 p-4 rounded-xl bg-white/5">
                  <div className="flex items-center gap-2 text-viraly-muted">
                    <Filter className="w-4 h-4" />
                    <span className="text-sm font-medium">Filters:</span>
                  </div>
                  <select className="input-base px-3 py-2 text-sm rounded-lg" data-testid="filter-promo-status">
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="expired">Expired</option>
                    <option value="paused">Paused</option>
                  </select>
                  <select className="input-base px-3 py-2 text-sm rounded-lg" data-testid="filter-promo-type">
                    <option value="all">All Types</option>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>

            {/* Active Promotions */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {editablePromotions.map((promo) => (
                <div 
                  key={promo.id} 
                  className="card-base p-6 cursor-pointer hover:border-viraly-orange/50 transition-colors" 
                  data-testid={`promo-card-${promo.name}`}
                  onClick={() => setEditPromoModal(promo)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 rounded-lg bg-viraly-orange/20 text-viraly-orange font-mono font-bold">
                      {promo.name}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      promo.status === 'active' ? 'bg-viraly-success/20 text-viraly-success' :
                      promo.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                      promo.status === 'paused' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-white/10 text-viraly-muted'
                    }`}>{promo.status}</span>
                  </div>
                  <div className="text-3xl font-bold text-white mb-4">
                    {promo.type === 'percentage' ? `${promo.discount}%` : `$${promo.discount}`}
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-viraly-muted">Usage</span>
                      <span>{promo.uses}{promo.maxUses ? ` / ${promo.maxUses}` : ' (unlimited)'}</span>
                    </div>
                    {promo.expires && (
                      <div className="flex justify-between">
                        <span className="text-viraly-muted">Expires</span>
                        <span>{promo.expires}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10 flex gap-2">
                    <Button 
                      className="btn-secondary flex-1 text-sm py-2" 
                      data-testid={`edit-promo-${promo.name}`}
                      onClick={(e) => { e.stopPropagation(); setEditPromoModal(promo); }}
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button 
                      className="btn-secondary flex-1 text-sm py-2" 
                      data-testid={`pause-promo-${promo.name}`}
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        const updated = editablePromotions.map(p => 
                          p.id === promo.id ? {...p, status: p.status === 'paused' ? 'active' : 'paused'} : p
                        );
                        setEditablePromotions(updated);
                        toast.success(promo.status === 'paused' ? 'Promotion activated' : 'Promotion paused');
                      }}
                    >
                      {promo.status === 'paused' ? <PlayCircle className="w-4 h-4 mr-1" /> : <PauseCircle className="w-4 h-4 mr-1" />}
                      {promo.status === 'paused' ? 'Activate' : 'Pause'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
              </>
            )}

            {/* AI Suggestions Sub-tab */}
            {promoSubTab === "suggestions" && (
              <div className="space-y-6">
                <div className="card-base p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-6 h-6 text-viraly-orange" />
                    <h3 className="text-lg font-semibold">AI-Suggested Locale-Based Promotions</h3>
                  </div>
                  <p className="text-sm text-viraly-muted mb-6">
                    Based on upcoming regional events and holidays, here are suggested promotions you can create 
                    to target users in specific countries. Each promotion will only be shown to users registered 
                    from the specified locales.
                  </p>
                  
                  {suggestedPromotions.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                      <p className="text-viraly-muted">No upcoming events in the next 60 days</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {suggestedPromotions.map((suggestion, idx) => (
                        <div key={idx} className="card-base p-5 border border-white/10 hover:border-viraly-orange/50 transition-colors">
                          <div className="flex items-center justify-between mb-3">
                            <span className="px-3 py-1 rounded-lg bg-viraly-orange/20 text-viraly-orange font-bold">
                              {suggestion.event_name}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              suggestion.days_until <= 7 ? 'bg-red-500/20 text-red-400' :
                              suggestion.days_until <= 14 ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {suggestion.days_until === 0 ? 'Today!' : `${suggestion.days_until} days`}
                            </span>
                          </div>
                          
                          <div className="text-2xl font-bold text-white mb-2">
                            {suggestion.suggested_discount}% OFF
                          </div>
                          
                          <div className="space-y-2 text-sm mb-4">
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-viraly-muted" />
                              <span className="text-viraly-muted">
                                {suggestion.is_global ? 'Global (All countries)' : 
                                  `Locales: ${suggestion.target_locales?.join(', ')}`}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-viraly-muted" />
                              <span className="text-viraly-muted">
                                {suggestion.event_date}
                              </span>
                            </div>
                          </div>
                          
                          <p className="text-xs text-viraly-muted mb-4 line-clamp-2">
                            "{suggestion.suggested_message}"
                          </p>
                          
                          <Button 
                            className="w-full btn-primary"
                            onClick={() => createPromotionFromSuggestion(suggestion)}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Create This Promotion
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Coupons Sub-tab */}
            {promoSubTab === "coupons" && (
              <div className="space-y-6">
                {coupons.length === 0 ? (
                  <div className="card-base p-8 text-center">
                    <Tag className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                    <p className="text-viraly-muted">No coupons yet. Create your first coupon!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {coupons.map((coupon) => (
                      <div key={coupon.id} className="card-base p-4 hover:border-viraly-orange/50 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <span className="px-3 py-1 rounded-lg bg-purple-500/20 text-purple-400 font-mono font-bold text-sm">{coupon.code}</span>
                          <span className={`px-2 py-1 rounded text-xs ${coupon.is_active ? 'bg-viraly-success/20 text-viraly-success' : 'bg-white/10 text-viraly-muted'}`}>
                            {coupon.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="text-2xl font-bold text-white mb-2">
                          {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`} OFF
                        </div>
                        <div className="space-y-1 text-xs text-viraly-muted">
                          <p>Used: {coupon.current_uses}/{coupon.max_uses || '∞'}</p>
                          {coupon.min_purchase && <p>Min: ${coupon.min_purchase}</p>}
                          {coupon.expires_at && <p>Expires: {new Date(coupon.expires_at).toLocaleDateString()}</p>}
                        </div>
                        <div className="mt-3 pt-3 border-t border-white/10 flex gap-2">
                          <Button size="sm" variant="secondary" onClick={() => setEditCouponModal(coupon)} className="flex-1">
                            <Edit className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteCoupon(coupon.id)} className="flex-1">
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Announcements Sub-tab */}
            {promoSubTab === "announcements" && (
              <div className="space-y-6">
                {announcements.length === 0 ? (
                  <div className="card-base p-8 text-center">
                    <Bell className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                    <p className="text-viraly-muted">No announcements yet. Create your first announcement!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {announcements.map((ann) => (
                      <div key={ann.id} className={`card-base p-4 border-l-4 ${ann.priority === 'high' ? 'border-l-red-500' : ann.type === 'policy_update' ? 'border-l-yellow-500' : 'border-l-blue-500'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{ann.title}</h4>
                              <span className={`px-2 py-0.5 rounded text-xs ${ann.is_active ? 'bg-viraly-success/20 text-viraly-success' : 'bg-white/10 text-viraly-muted'}`}>
                                {ann.is_active ? 'Active' : 'Inactive'}
                              </span>
                              {ann.type === 'policy_update' && (
                                <span className="px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400">Policy Update</span>
                              )}
                            </div>
                            <p className="text-sm text-viraly-muted">{ann.message}</p>
                            <p className="text-xs text-viraly-muted mt-2">
                              Display: {ann.display_location} | Priority: {ann.priority} | Target: {ann.target_audience || 'all'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="secondary" onClick={() => setEditAnnouncementModal(ann)}>
                              <Edit className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Loyalty Sub-tab */}
            {promoSubTab === "loyalty" && (
              <>
            {/* Loyalty Tiers - Now Editable & Connected to Backend */}
            <div className="card-base p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold">Loyalty Tier Discounts</h3>
                <p className="text-viraly-muted text-sm">Click any tier to edit (saves to database)</p>
              </div>
              <p className="text-viraly-muted text-sm mb-4">Discounts applied based on rolling 12-month spending milestones</p>
              {loyaltyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                  <span className="ml-2 text-viraly-muted">Loading loyalty settings...</span>
                </div>
              ) : loyaltyTiers.length > 0 ? (
                <div className="grid grid-cols-5 gap-4">
                  {loyaltyTiers.map((tier) => (
                    <div 
                      key={tier.id} 
                      className={`p-4 rounded-xl bg-${tier.color}-500/10 border border-${tier.color}-500/20 text-center cursor-pointer hover:scale-105 transition-transform`}
                      onClick={() => setEditLoyaltyTierModal(tier)}
                    >
                      <p className={`font-bold text-${tier.color}-400 mb-1`}>{tier.tier}</p>
                      <p className="text-2xl font-bold">{tier.discount}%</p>
                      <p className="text-xs text-viraly-muted mt-1">After ${tier.threshold} spent</p>
                      <button className="mt-2 text-xs text-viraly-orange hover:text-white transition-colors">
                        <Edit className="w-3 h-3 inline mr-1" /> Edit
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-viraly-muted text-center py-4">No loyalty tiers configured</p>
              )}
            </div>
              </>
            )}

            {/* Referral Sub-tab */}
            {promoSubTab === "referral" && (
            <div className="card-base p-6">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Share2 className="w-5 h-5 text-viraly-orange" />
                Referral Program Settings
              </h3>
              <p className="text-sm text-viraly-muted mb-4">Configure rewards for referrers and discounts for referred users</p>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 rounded-xl bg-white/5">
                  <label className="text-sm text-viraly-muted mb-2 block">Referrer Reward (Free Posts)</label>
                  <div className="flex items-center gap-3">
                    <Input 
                      type="number" 
                      min="0"
                      max="100"
                      value={referralSettings.referrer_reward_posts}
                      onChange={(e) => setReferralSettings({...referralSettings, referrer_reward_posts: parseInt(e.target.value) || 0})}
                      className="input-base w-24 text-center text-xl font-bold"
                    />
                    <span className="text-viraly-muted">free posts</span>
                  </div>
                  <p className="text-xs text-viraly-muted mt-2">Given when referred user makes first purchase</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5">
                  <label className="text-sm text-viraly-muted mb-2 block">Referee Discount (%)</label>
                  <div className="flex items-center gap-3">
                    <Input 
                      type="number" 
                      min="0"
                      max="100"
                      value={referralSettings.referee_discount_percent}
                      onChange={(e) => setReferralSettings({...referralSettings, referee_discount_percent: parseInt(e.target.value) || 0})}
                      className="input-base w-24 text-center text-xl font-bold"
                    />
                    <span className="text-viraly-muted">% off first purchase</span>
                  </div>
                  <p className="text-xs text-viraly-muted mt-2">Applied to referee's first purchase</p>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button 
                  onClick={handleSaveReferralSettings}
                  className="btn-primary flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Save Referral Settings
                </Button>
              </div>
            </div>
            )}

            {/* Influencer Sub-tab */}
            {promoSubTab === "influencer" && (
              <>
            <div className="card-base p-6">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-viraly-orange" />
                Influencer Program Settings
              </h3>
              <p className="text-sm text-viraly-muted mb-4">
                Configure how customers become influencers and earn lifetime commissions.
                Influencers earn a % of all purchases made by their referees.
              </p>
              
              {/* Upgrade Requirements */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 rounded-xl bg-white/5">
                  <label className="text-xs text-viraly-muted mb-1 block">Min Referees Required</label>
                  <Input 
                    type="number" 
                    min="1"
                    value={influencerSettings.min_referees_for_upgrade}
                    onChange={(e) => setInfluencerSettings({...influencerSettings, min_referees_for_upgrade: parseInt(e.target.value) || 100})}
                    className="input-base text-center text-lg font-bold"
                  />
                  <p className="text-[10px] text-viraly-muted mt-1">People who register via their code and make a purchase</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5">
                  <label className="text-xs text-viraly-muted mb-1 block">Min Purchase Per Referee</label>
                  <div className="flex items-center gap-1">
                    <span className="text-viraly-muted">$</span>
                    <Input 
                      type="number" 
                      min="1"
                      step="0.01"
                      value={influencerSettings.min_purchase_per_referee}
                      onChange={(e) => setInfluencerSettings({...influencerSettings, min_purchase_per_referee: parseFloat(e.target.value) || 29})}
                      className="input-base text-center text-lg font-bold"
                    />
                  </div>
                  <p className="text-[10px] text-viraly-muted mt-1">Each referee must spend at least this amount</p>
                </div>
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                  <label className="text-xs text-green-400 mb-1 block">Commission Rate (%)</label>
                  <div className="flex items-center gap-1">
                    <Input 
                      type="number" 
                      min="1"
                      max="50"
                      value={influencerSettings.commission_percent}
                      onChange={(e) => setInfluencerSettings({...influencerSettings, commission_percent: parseFloat(e.target.value) || 10})}
                      className="input-base text-center text-lg font-bold"
                    />
                    <span className="text-viraly-muted">%</span>
                  </div>
                  <p className="text-[10px] text-viraly-muted mt-1">% of NET revenue influencer earns lifetime</p>
                </div>
                <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                  <label className="text-xs text-blue-400 mb-1 block">Payout Threshold</label>
                  <div className="flex items-center gap-1">
                    <span className="text-viraly-muted">$</span>
                    <Input 
                      type="number" 
                      min="10"
                      value={influencerSettings.payout_threshold}
                      onChange={(e) => setInfluencerSettings({...influencerSettings, payout_threshold: parseFloat(e.target.value) || 50})}
                      className="input-base text-center text-lg font-bold"
                    />
                  </div>
                  <p className="text-[10px] text-viraly-muted mt-1">Min earnings before payout request</p>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 mb-4">
                <p className="text-xs text-yellow-400">
                  <strong>Revenue Threshold:</strong> {influencerSettings.min_referees_for_upgrade} referees × ${influencerSettings.min_purchase_per_referee} = 
                  <strong> ${(influencerSettings.min_referees_for_upgrade * influencerSettings.min_purchase_per_referee).toLocaleString()}</strong> total revenue required for upgrade
                </p>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveInfluencerSettings} className="btn-primary flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Save Influencer Settings
                </Button>
              </div>
            </div>

            {/* Payout Requests */}
            <div className="card-base p-6 mt-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-viraly-orange" />
                  Payout Requests
                </h3>
                <div className="flex gap-4 text-sm">
                  <span className="text-yellow-400">Pending: ${payoutSummary.pending_total?.toFixed(2) || 0} ({payoutSummary.pending_count || 0})</span>
                  <span className="text-blue-400">Approved: ${payoutSummary.approved_total?.toFixed(2) || 0}</span>
                  <span className="text-green-400">Paid: ${payoutSummary.paid_total?.toFixed(2) || 0}</span>
                </div>
              </div>

              {payoutRequests.filter(p => p.status === "pending").length > 0 ? (
                <div className="space-y-3">
                  {payoutRequests.filter(p => p.status === "pending").map((payout) => (
                    <div key={payout.id} className="p-4 rounded-xl bg-white/5 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{payout.user_name}</p>
                        <p className="text-sm text-viraly-muted">{payout.user_email}</p>
                        <p className="text-xs text-viraly-muted mt-1">
                          Method: {payout.method === "etransfer" ? "Interac e-Transfer" : "PayPal"} • 
                          {payout.method === "etransfer" ? payout.payout_details?.etransfer_email : payout.payout_details?.paypal_email}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-400">${payout.amount.toFixed(2)}</p>
                        <div className="flex gap-2 mt-2">
                          <Button 
                            onClick={() => setSelectedPayout(payout)}
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700 text-xs"
                          >
                            Approve & Pay
                          </Button>
                          <Button 
                            onClick={() => handlePayoutAction(payout.id, "rejected", "Request rejected by admin")}
                            size="sm" 
                            variant="outline" 
                            className="text-red-400 border-red-400/30 text-xs"
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-viraly-muted py-8">No pending payout requests</p>
              )}
            </div>

            {/* Active Influencers */}
            <div className="card-base p-6 mt-8">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-viraly-orange" />
                Active Influencers ({influencers.length})
              </h3>
              
              {influencers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-xs text-viraly-muted">
                        <th className="pb-3">Influencer</th>
                        <th className="pb-3">Referees</th>
                        <th className="pb-3">Revenue</th>
                        <th className="pb-3">Earnings</th>
                        <th className="pb-3">Paid Out</th>
                        <th className="pb-3">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {influencers.map((inf) => (
                        <tr key={inf.id} className="border-b border-white/5">
                          <td className="py-3">
                            <p className="font-medium">{inf.name}</p>
                            <p className="text-xs text-viraly-muted">{inf.email}</p>
                          </td>
                          <td className="py-3">{inf.stats?.total_referees || 0}</td>
                          <td className="py-3">${(inf.stats?.total_revenue_from_referees || 0).toFixed(2)}</td>
                          <td className="py-3 text-green-400">${(inf.earnings?.total_earnings || 0).toFixed(2)}</td>
                          <td className="py-3">${(inf.earnings?.paid_out || 0).toFixed(2)}</td>
                          <td className="py-3 font-bold">${(inf.earnings?.pending_balance || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-viraly-muted py-8">No active influencers yet</p>
              )}
            </div>

            {/* Influencer Candidates */}
            <div className="card-base p-6 mt-8">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-viraly-orange" />
                Influencer Candidates (Close to Qualifying)
              </h3>
              
              {influencerCandidates.length > 0 ? (
                <div className="space-y-3">
                  {influencerCandidates.map((candidate) => (
                    <div key={candidate.id} className="p-4 rounded-xl bg-white/5 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{candidate.name}</p>
                        <p className="text-sm text-viraly-muted">{candidate.email}</p>
                        <div className="mt-2">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-viraly-muted">Progress:</span>
                            <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-viraly-gradient" 
                                style={{ width: `${candidate.eligibility?.progress_percent || 0}%` }}
                              />
                            </div>
                            <span className="text-viraly-orange">{(candidate.eligibility?.progress_percent || 0).toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm">
                          {candidate.eligibility?.current_referees_with_purchases || 0} / {candidate.eligibility?.required_referees || 10} referees
                        </p>
                        <p className="text-sm text-viraly-muted">
                          {candidate.eligibility?.current_referees_with_purchases >= 10 ? "✓ Eligible" : `${10 - (candidate.eligibility?.current_referees_with_purchases || 0)} more needed`}
                        </p>
                        <Button 
                          onClick={() => handleUpgradeToInfluencer(candidate.id)}
                          size="sm" 
                          className="mt-2 btn-primary text-xs"
                          disabled={!candidate.eligibility?.is_eligible}
                        >
                          {candidate.eligibility?.is_eligible ? "Upgrade Now" : "Not Yet Eligible"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-viraly-muted py-8">No candidates close to qualifying yet</p>
              )}
            </div>

            {/* Payout Approval Modal */}
            {selectedPayout && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="card-base p-6 max-w-md w-full">
                  <h3 className="text-lg font-bold mb-4">Approve Payout</h3>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-white/5">
                      <p className="font-medium">{selectedPayout.user_name}</p>
                      <p className="text-2xl font-bold text-green-400 mt-2">${selectedPayout.amount.toFixed(2)}</p>
                      <p className="text-sm text-viraly-muted mt-2">
                        Send to: {selectedPayout.method === "etransfer" ? selectedPayout.payout_details?.etransfer_email : selectedPayout.payout_details?.paypal_email}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Transaction Reference (optional)</label>
                      <Input 
                        placeholder="e.g., Interac confirmation #, PayPal transaction ID"
                        value={payoutTxRef}
                        onChange={(e) => setPayoutTxRef(e.target.value)}
                        className="input-base"
                      />
                      <p className="text-xs text-viraly-muted mt-1">Add after you complete the payment</p>
                    </div>
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Admin Notes (optional)</label>
                      <Input 
                        placeholder="Internal notes..."
                        value={payoutAdminNotes}
                        onChange={(e) => setPayoutAdminNotes(e.target.value)}
                        className="input-base"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setSelectedPayout(null)} className="flex-1">
                        Cancel
                      </Button>
                      <Button 
                        onClick={() => handlePayoutAction(selectedPayout.id, "approved", payoutAdminNotes)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                      >
                        Approve (Pay Later)
                      </Button>
                      <Button 
                        onClick={() => handlePayoutAction(selectedPayout.id, "paid", payoutAdminNotes, payoutTxRef)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        Mark as Paid
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </motion.div>
        )}

        {/* Team & Roles Tab */}
        {activeTab === "team" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <UserCog className="w-8 h-8 text-viraly-orange" />
                  Team & Roles
                </h1>
                <p className="text-viraly-muted mt-2">Manage admin team members and granular permissions</p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setCreatingRole(true)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  New Role
                </Button>
                <Button 
                  onClick={() => setCreatingTeamMember(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Team Member
                </Button>
              </div>
            </div>

            {rolesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : (
              <>
                {/* Team Members */}
                <div className="card-base overflow-hidden mb-8">
                  <div className="p-4 border-b border-white/10">
                    <h3 className="font-heading text-lg font-bold">Team Members ({teamMembersData.length})</h3>
                  </div>
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="text-left p-4 font-medium text-viraly-muted">Member</th>
                        <th className="text-left p-4 font-medium text-viraly-muted">Role</th>
                        <th className="text-left p-4 font-medium text-viraly-muted">Status</th>
                        <th className="text-left p-4 font-medium text-viraly-muted">Created</th>
                        <th className="text-left p-4 font-medium text-viraly-muted">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamMembersData.map((member) => (
                        <tr key={member.id} className="border-t border-white/5">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-viraly-gradient flex items-center justify-center">
                                <span className="font-bold text-white">{(member.name || member.email).charAt(0).toUpperCase()}</span>
                              </div>
                              <div>
                                <p className="font-medium">{member.name || "No Name"}</p>
                                <p className="text-sm text-viraly-muted">{member.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`px-3 py-1 rounded-full text-xs ${
                              member.role_id === 'super_admin' ? 'bg-red-500/20 text-red-400' :
                              member.role_id === 'content_moderator' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-viraly-orange/20 text-viraly-orange'
                            }`}>{member.role?.name || member.role_id}</span>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              member.is_active !== false ? 'bg-viraly-success/20 text-viraly-success' : 'bg-yellow-500/20 text-yellow-400'
                            }`}>{member.is_active !== false ? 'Active' : 'Suspended'}</span>
                          </td>
                          <td className="p-4 text-sm text-viraly-muted">
                            {member.created_at ? new Date(member.created_at).toLocaleDateString() : "N/A"}
                          </td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              <button 
                                className="p-2 hover:bg-white/10 rounded-lg" 
                                onClick={() => setEditingTeamMember(member)}
                                data-testid={`edit-team-member-${member.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {member.role_id !== 'super_admin' && (
                                <button 
                                  className="p-2 hover:bg-yellow-500/20 rounded-lg" 
                                  onClick={() => setSuspendTeamMemberModal(member)}
                                  data-testid={`suspend-team-member-${member.id}`}
                                >
                                  {member.is_active !== false ? (
                                    <PauseCircle className="w-4 h-4 text-yellow-400" />
                                  ) : (
                                    <PlayCircle className="w-4 h-4 text-green-400" />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Roles & Granular Permissions */}
                <div className="card-base p-6">
                  <h3 className="font-heading text-lg font-bold mb-4">Roles & Permissions</h3>
                  <div className="space-y-4">
                    {roles.map((role) => (
                      <div key={role.id} className="p-4 rounded-xl bg-white/5">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              {role.name}
                              {role.is_system && <span className="text-xs bg-white/10 px-2 py-0.5 rounded">System</span>}
                            </p>
                            <p className="text-sm text-viraly-muted">{role.description}</p>
                          </div>
                          {!role.is_system && (
                            <Button
                              size="sm"
                              className="btn-secondary"
                              onClick={() => setEditingRole(role)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(role.permissions || {}).map(([key, perms]) => (
                            <div key={key} className="px-2 py-1 rounded bg-viraly-orange/10 text-xs">
                              <span className="text-viraly-orange">{key.replace(/_/g, ' ')}</span>
                              <span className="text-viraly-muted ml-1">
                                ({Object.entries(perms).filter(([k, v]) => v === true).map(([k]) => k).join(', ')})
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Available Permissions Reference */}
                <div className="card-base p-6 mt-6">
                  <h3 className="font-heading text-lg font-bold mb-4">Available Permissions (22 Menu Items)</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(availablePermissions).map(([key, perm]) => (
                      <div key={key} className="p-3 rounded-lg bg-white/5 text-sm">
                        <p className="font-medium text-viraly-orange">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                        <p className="text-xs text-viraly-muted">{perm.description}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {Object.keys(perm).filter(k => k !== 'description').map(action => (
                            <span key={action} className="px-1.5 py-0.5 rounded bg-white/10 text-xs">{action}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Create Team Member Modal */}
            {creatingTeamMember && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-viraly-card rounded-2xl w-full max-w-md p-6">
                  <h3 className="font-heading text-xl font-bold mb-4">Add Team Member</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Email</label>
                      <Input
                        type="email"
                        value={newTeamMemberData.email}
                        onChange={(e) => setNewTeamMemberData(prev => ({ ...prev, email: e.target.value }))}
                        className="input-base"
                        placeholder="team@viraly.ca"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Name</label>
                      <Input
                        value={newTeamMemberData.name}
                        onChange={(e) => setNewTeamMemberData(prev => ({ ...prev, name: e.target.value }))}
                        className="input-base"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Role</label>
                      <select
                        value={newTeamMemberData.role_id}
                        onChange={(e) => setNewTeamMemberData(prev => ({ ...prev, role_id: e.target.value }))}
                        className="input-base w-full"
                      >
                        {roles.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Temporary Password</label>
                      <Input
                        type="password"
                        value={newTeamMemberData.password}
                        onChange={(e) => setNewTeamMemberData(prev => ({ ...prev, password: e.target.value }))}
                        className="input-base"
                        placeholder="ChangeMe123!"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => setCreatingTeamMember(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          const adminToken = localStorage.getItem("viraly_admin_token");
                          await apiClient.post("/admin/team-members", newTeamMemberData, {
                            headers: { Authorization: `Bearer ${adminToken}` }
                          });
                          toast.success("Team member added!");
                          setCreatingTeamMember(false);
                          setNewTeamMemberData({ email: "", name: "", role_id: "support_agent", password: "" });
                          fetchAdminData();
                        } catch (err) {
                          toast.error("Failed to add team member");
                        }
                      }}
                      className="flex-1 btn-primary"
                    >
                      Add Member
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Create Role Modal */}
            {creatingRole && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-viraly-card rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                  <h3 className="font-heading text-xl font-bold mb-4">Create New Role</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Role Name</label>
                      <Input
                        value={newRoleData.name}
                        onChange={(e) => setNewRoleData(prev => ({ ...prev, name: e.target.value }))}
                        className="input-base"
                        placeholder="e.g., Marketing Lead"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Description</label>
                      <Input
                        value={newRoleData.description}
                        onChange={(e) => setNewRoleData(prev => ({ ...prev, description: e.target.value }))}
                        className="input-base"
                        placeholder="Brief description of this role's responsibilities"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-viraly-muted mb-2 block">Permissions</label>
                      <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 rounded-lg bg-white/5">
                        {Object.entries(availablePermissions).map(([key, perm]) => (
                          <div key={key} className="p-2 rounded bg-white/5">
                            <p className="font-medium text-xs text-viraly-orange mb-1">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                            <div className="flex flex-wrap gap-1">
                              {Object.keys(perm).filter(k => k !== 'description').map(action => (
                                <label key={action} className="flex items-center gap-1 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={newRoleData.permissions?.[key]?.[action] || false}
                                    onChange={(e) => {
                                      setNewRoleData(prev => ({
                                        ...prev,
                                        permissions: {
                                          ...prev.permissions,
                                          [key]: {
                                            ...(prev.permissions?.[key] || {}),
                                            [action]: e.target.checked
                                          }
                                        }
                                      }));
                                    }}
                                    className="rounded"
                                  />
                                  {action}
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCreatingRole(false);
                        setNewRoleData({ name: "", description: "", permissions: {} });
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        if (!newRoleData.name.trim()) {
                          toast.error("Role name is required");
                          return;
                        }
                        try {
                          const adminToken = localStorage.getItem("viraly_admin_token");
                          await apiClient.post("/admin/roles", newRoleData, {
                            headers: { Authorization: `Bearer ${adminToken}` }
                          });
                          toast.success("Role created!");
                          setCreatingRole(false);
                          setNewRoleData({ name: "", description: "", permissions: {} });
                          fetchAdminData();
                        } catch (err) {
                          toast.error("Failed to create role");
                        }
                      }}
                      className="flex-1 btn-primary"
                    >
                      Create Role
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Edit Team Member Modal */}
            {editingTeamMember && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-viraly-card rounded-2xl w-full max-w-md p-6">
                  <h3 className="font-heading text-xl font-bold mb-4">Edit Team Member</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Email</label>
                      <Input
                        type="email"
                        value={editingTeamMember.email}
                        disabled
                        className="input-base bg-white/5"
                      />
                      <p className="text-xs text-viraly-muted mt-1">Email cannot be changed</p>
                    </div>
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Name</label>
                      <Input
                        value={editingTeamMember.name || ""}
                        onChange={(e) => setEditingTeamMember(prev => ({ ...prev, name: e.target.value }))}
                        className="input-base"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-viraly-muted mb-1 block">Role</label>
                      <select
                        value={editingTeamMember.role_id}
                        onChange={(e) => setEditingTeamMember(prev => ({ ...prev, role_id: e.target.value }))}
                        className="input-base w-full"
                        disabled={editingTeamMember.role_id === 'super_admin'}
                      >
                        {roles.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-viraly-muted">
                        <strong>Created by:</strong> {editingTeamMember.created_by || "System"}<br/>
                        <strong>Created:</strong> {editingTeamMember.created_at ? new Date(editingTeamMember.created_at).toLocaleString() : "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button
                      variant="outline"
                      onClick={() => setEditingTeamMember(null)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          const adminToken = localStorage.getItem("viraly_admin_token");
                          await apiClient.put(`/admin/team-members/${editingTeamMember.id}`, {
                            name: editingTeamMember.name,
                            role_id: editingTeamMember.role_id
                          }, {
                            headers: { Authorization: `Bearer ${adminToken}` }
                          });
                          toast.success("Team member updated!");
                          setEditingTeamMember(null);
                          fetchAdminData();
                        } catch (err) {
                          toast.error("Failed to update team member");
                        }
                      }}
                      className="flex-1 btn-primary"
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Suspend/Activate Team Member Modal */}
            {suspendTeamMemberModal && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-viraly-card rounded-2xl w-full max-w-md p-6">
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                      suspendTeamMemberModal.is_active !== false ? 'bg-yellow-500/20' : 'bg-green-500/20'
                    }`}>
                      {suspendTeamMemberModal.is_active !== false ? (
                        <PauseCircle className="w-8 h-8 text-yellow-400" />
                      ) : (
                        <PlayCircle className="w-8 h-8 text-green-400" />
                      )}
                    </div>
                    <h3 className="font-heading text-xl font-bold">
                      {suspendTeamMemberModal.is_active !== false ? 'Suspend' : 'Reactivate'} Team Member?
                    </h3>
                    <p className="text-viraly-muted mt-2">
                      {suspendTeamMemberModal.is_active !== false 
                        ? `This will suspend ${suspendTeamMemberModal.name || suspendTeamMemberModal.email}'s access. They won't be able to log in until reactivated.`
                        : `This will restore ${suspendTeamMemberModal.name || suspendTeamMemberModal.email}'s access to the admin panel.`
                      }
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-white/5 mb-6">
                    <p className="text-sm text-viraly-muted">
                      <strong>Note:</strong> All actions and data associated with this member will be preserved. 
                      Suspending does NOT delete any history or records.
                    </p>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setSuspendTeamMemberModal(null)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          const adminToken = localStorage.getItem("viraly_admin_token");
                          const newStatus = suspendTeamMemberModal.is_active === false;
                          await apiClient.put(`/admin/team-members/${suspendTeamMemberModal.id}`, {
                            is_active: newStatus
                          }, {
                            headers: { Authorization: `Bearer ${adminToken}` }
                          });
                          toast.success(newStatus ? "Team member reactivated!" : "Team member suspended!");
                          setSuspendTeamMemberModal(null);
                          fetchAdminData();
                        } catch (err) {
                          toast.error("Failed to update team member status");
                        }
                      }}
                      className={`flex-1 ${suspendTeamMemberModal.is_active !== false ? 'bg-yellow-500 hover:bg-yellow-600' : 'btn-primary'}`}
                    >
                      {suspendTeamMemberModal.is_active !== false ? 'Suspend Member' : 'Reactivate Member'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* API Balance Tab */}
        {activeTab === "api-balance" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Database className="w-8 h-8 text-viraly-orange" />
                  API Balance & Usage
                </h1>
                <p className="text-viraly-muted mt-2">Monitor AI service credits and usage across providers</p>
              </div>
              <Button 
                onClick={() => {
                  setApiBalanceLoading(true);
                  fetchAdminData().finally(() => setApiBalanceLoading(false));
                }}
                className="btn-secondary flex items-center gap-2"
                disabled={apiBalanceLoading}
              >
                <RefreshCw className={`w-4 h-4 ${apiBalanceLoading ? 'animate-spin' : ''}`} />
                Refresh Balances
              </Button>
            </div>

            {apiBalanceLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : apiBalances ? (
              <>
                {/* Provider Status Cards */}
                <div className="grid grid-cols-2 gap-6 mb-8">
                  {/* Emergent LLM */}
                  <div className="card-base p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          apiBalances.emergent_llm?.available ? 'bg-viraly-success/20' : 'bg-red-500/20'
                        }`}>
                          <Sparkles className={`w-6 h-6 ${apiBalances.emergent_llm?.available ? 'text-viraly-success' : 'text-red-400'}`} />
                        </div>
                        <div>
                          <h3 className="font-heading text-lg font-bold">Emergent LLM</h3>
                          <p className="text-sm text-viraly-muted">Text & Image Generation</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs ${
                        apiBalances.emergent_llm?.status === 'configured' || apiBalances.emergent_llm?.status === 'active'
                          ? 'bg-viraly-success/20 text-viraly-success' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {apiBalances.emergent_llm?.status === 'configured' ? '✓ Configured' : 
                         apiBalances.emergent_llm?.status === 'active' ? '✓ Active' : '✗ Not Configured'}
                      </span>
                    </div>
                    <p className="text-sm text-viraly-muted mb-4">{apiBalances.emergent_llm?.message}</p>
                    <a 
                      href={apiBalances.emergent_llm?.top_up_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-secondary text-sm px-4 py-2 inline-flex items-center gap-2"
                    >
                      <CreditCard className="w-4 h-4" />
                      Top Up Balance
                    </a>
                  </div>

                  {/* RunwayML */}
                  <div className="card-base p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          apiBalances.runway_ml?.available ? 'bg-blue-500/20' : 'bg-red-500/20'
                        }`}>
                          <Globe className={`w-6 h-6 ${apiBalances.runway_ml?.available ? 'text-blue-400' : 'text-red-400'}`} />
                        </div>
                        <div>
                          <h3 className="font-heading text-lg font-bold">RunwayML</h3>
                          <p className="text-sm text-viraly-muted">Video Generation (gen4_turbo)</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs ${
                        apiBalances.runway_ml?.status === 'active' || apiBalances.runway_ml?.status === 'configured'
                          ? 'bg-blue-500/20 text-blue-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {apiBalances.runway_ml?.status === 'active' ? '✓ Active' : 
                         apiBalances.runway_ml?.status === 'configured' ? '✓ Configured' : '✗ Not Configured'}
                      </span>
                    </div>
                    <p className="text-sm text-viraly-muted mb-4">{apiBalances.runway_ml?.message}</p>
                    <a 
                      href={apiBalances.runway_ml?.top_up_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-secondary text-sm px-4 py-2 inline-flex items-center gap-2"
                    >
                      <CreditCard className="w-4 h-4" />
                      Top Up Credits
                    </a>
                  </div>
                </div>

                {/* Usage Stats (30 days) */}
                <div className="card-base p-6 mb-8">
                  <h3 className="font-heading text-lg font-bold mb-6 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-viraly-orange" />
                    Usage Statistics (Last 30 Days)
                  </h3>
                  <div className="grid grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <p className="text-3xl font-bold text-viraly-orange">{apiBalances.usage_30d?.posts || 0}</p>
                      <p className="text-sm text-viraly-muted">Posts Generated</p>
                    </div>
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <p className="text-3xl font-bold text-blue-400">{apiBalances.usage_30d?.videos?.total_videos || 0}</p>
                      <p className="text-sm text-viraly-muted">Videos Generated</p>
                    </div>
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <p className="text-3xl font-bold text-viraly-success">{apiBalances.usage_30d?.videos?.completed || 0}</p>
                      <p className="text-sm text-viraly-muted">Videos Completed</p>
                    </div>
                    <div className="text-center p-4 bg-white/5 rounded-xl">
                      <p className="text-3xl font-bold text-yellow-400">{apiBalances.usage_30d?.videos?.processing || 0}</p>
                      <p className="text-sm text-viraly-muted">Videos Processing</p>
                    </div>
                  </div>
                </div>

                {/* Estimated Costs */}
                <div className="card-base p-6">
                  <h3 className="font-heading text-lg font-bold mb-6 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-viraly-orange" />
                    Estimated API Costs (Last 30 Days)
                  </h3>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="p-4 bg-white/5 rounded-xl">
                      <p className="text-sm text-viraly-muted mb-2">Text & Image (Emergent)</p>
                      <p className="text-2xl font-bold text-viraly-light">
                        ${(apiBalances.usage_30d?.estimated_cost?.posts || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-viraly-muted mt-1">~$0.044 per post</p>
                    </div>
                    <div className="p-4 bg-white/5 rounded-xl">
                      <p className="text-sm text-viraly-muted mb-2">Video (RunwayML)</p>
                      <p className="text-2xl font-bold text-blue-400">
                        ${(apiBalances.usage_30d?.estimated_cost?.videos || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-viraly-muted mt-1">~$0.50 per 10s video</p>
                    </div>
                    <div className="p-4 bg-viraly-orange/10 rounded-xl border border-viraly-orange/30">
                      <p className="text-sm text-viraly-muted mb-2">Total Estimated</p>
                      <p className="text-2xl font-bold text-viraly-orange">
                        ${((apiBalances.usage_30d?.estimated_cost?.posts || 0) + (apiBalances.usage_30d?.estimated_cost?.videos || 0)).toFixed(2)}
                      </p>
                      <p className="text-xs text-viraly-muted mt-1">Combined API spend</p>
                    </div>
                  </div>
                  <p className="text-xs text-viraly-muted mt-4">
                    Last checked: {apiBalances.last_checked ? new Date(apiBalances.last_checked).toLocaleString() : 'Never'}
                  </p>
                </div>
                
                {/* Cost Control: Actual vs Estimated */}
                {apiCostTracking && (
                  <div className="card-base p-6">
                    <h3 className="font-heading text-lg font-bold mb-6 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      Cost Control: Actual vs Estimated
                      <span className={`ml-auto px-3 py-1 rounded-full text-xs ${
                        apiCostTracking.cost_status === 'high' 
                          ? 'bg-red-500/20 text-red-400' 
                          : apiCostTracking.cost_status === 'low' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {apiCostTracking.cost_status === 'high' ? 'Over Budget' : 
                         apiCostTracking.cost_status === 'low' ? 'Under Budget' : 'On Track'}
                      </span>
                    </h3>
                    
                    {/* Summary Cards */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-white/5 rounded-xl">
                        <p className="text-sm text-viraly-muted mb-2">Estimated Cost</p>
                        <p className="text-2xl font-bold text-viraly-light">
                          ${apiCostTracking.total_estimated?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div className="p-4 bg-white/5 rounded-xl">
                        <p className="text-sm text-viraly-muted mb-2">Actual Cost</p>
                        <p className="text-2xl font-bold text-viraly-orange">
                          ${apiCostTracking.total_actual?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div className={`p-4 rounded-xl ${
                        apiCostTracking.total_delta > 0 
                          ? 'bg-red-500/10 border border-red-500/30' 
                          : 'bg-green-500/10 border border-green-500/30'
                      }`}>
                        <p className="text-sm text-viraly-muted mb-2">Delta</p>
                        <p className={`text-2xl font-bold ${apiCostTracking.total_delta > 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {apiCostTracking.total_delta > 0 ? '+' : ''}${apiCostTracking.total_delta?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div className={`p-4 rounded-xl ${
                        Math.abs(apiCostTracking.delta_percent) > 10 
                          ? 'bg-yellow-500/10 border border-yellow-500/30' 
                          : 'bg-blue-500/10'
                      }`}>
                        <p className="text-sm text-viraly-muted mb-2">Variance</p>
                        <p className={`text-2xl font-bold ${
                          apiCostTracking.delta_percent > 10 ? 'text-red-400' : 
                          apiCostTracking.delta_percent < -10 ? 'text-green-400' : 'text-blue-400'
                        }`}>
                          {apiCostTracking.delta_percent > 0 ? '+' : ''}{apiCostTracking.delta_percent?.toFixed(1) || 0}%
                        </p>
                      </div>
                    </div>
                    
                    {/* Operations Breakdown */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-viraly-muted">Cost Breakdown by Operation</h4>
                      {apiCostTracking.operations?.map((op, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium capitalize">
                              {op.type?.replace(/_/g, ' ')}
                            </span>
                            <span className="text-xs text-viraly-muted">
                              ({op.count} operations)
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-viraly-muted">
                              Est: ${op.estimated_cost?.toFixed(2)}
                            </span>
                            <span className="text-viraly-light font-medium">
                              Act: ${op.actual_cost?.toFixed(2)}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              op.delta > 0 ? 'bg-red-500/20 text-red-400' : 
                              op.delta < 0 ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-viraly-muted'
                            }`}>
                              {op.delta > 0 ? '+' : ''}{op.delta_percent?.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <p className="text-sm text-blue-400">
                        <strong>Cost Control Tip:</strong> Variance under 10% is considered normal. 
                        If actual costs consistently exceed estimates, consider reviewing API usage patterns 
                        or adjusting the pricing model.
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20 text-viraly-muted">
                <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No balance data available. Click refresh to load.</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Data & Compliance Tab */}
        {activeTab === "compliance" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <ShieldCheck className="w-8 h-8 text-viraly-orange" />
                  Data & Compliance
                </h1>
                <p className="text-viraly-muted mt-2">Privacy regulations, data management, and compliance settings</p>
              </div>
              <Button className="btn-secondary flex items-center gap-2">
                <Download className="w-4 h-4" />
                Export Compliance Report
              </Button>
            </div>

            {/* Compliance Status */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { name: "GDPR", region: "European Union", status: "compliant", icon: "🇪🇺" },
                { name: "PIPEDA", region: "Canada", status: "compliant", icon: "🇨🇦" },
                { name: "CCPA", region: "California", status: "compliant", icon: "🇺🇸" }
              ].map((reg) => (
                <div key={reg.name} className="card-base p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">{reg.icon}</span>
                    <div>
                      <p className="font-bold">{reg.name}</p>
                      <p className="text-xs text-viraly-muted">{reg.region}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs ${
                    reg.status === 'compliant' ? 'bg-viraly-success/20 text-viraly-success' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>{reg.status === 'compliant' ? '✓ Compliant' : '⏳ Pending'}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-6 mb-8">
              {/* Data Retention */}
              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-viraly-orange" />
                  Data Retention Policies
                </h3>
                <div className="space-y-3">
                  {[
                    { type: "User Data", retention: "Account lifetime + 30 days after deletion" },
                    { type: "Transaction Records", retention: "7 years (tax compliance)" },
                    { type: "Generated Content", retention: "Until user deletion request" },
                    { type: "Access Logs", retention: "90 days" },
                    { type: "Analytics Data", retention: "24 months (anonymized)" }
                  ].map((policy) => (
                    <div key={policy.type} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <p className="font-medium text-sm">{policy.type}</p>
                      <p className="text-sm text-viraly-muted">{policy.retention}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* User Rights Requests */}
              <div className="card-base p-6">
                <h3 className="font-heading text-lg font-bold mb-4">User Rights Requests</h3>
                <div className="space-y-3">
                  {[
                    { type: "Data Export Requests", count: 12, status: "3 pending" },
                    { type: "Deletion Requests", count: 5, status: "All completed" },
                    { type: "Access Requests", count: 8, status: "1 pending" },
                    { type: "Opt-out Requests", count: 23, status: "All completed" }
                  ].map((req) => (
                    <div key={req.type} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <div>
                        <p className="font-medium text-sm">{req.type}</p>
                        <p className="text-xs text-viraly-muted">{req.count} total this month</p>
                      </div>
                      <span className={`text-xs ${req.status.includes('pending') ? 'text-yellow-400' : 'text-viraly-success'}`}>
                        {req.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Security Settings */}
            <div className="card-base p-6 mb-8">
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-viraly-orange" />
                Security & Privacy Settings
              </h3>
              {complianceLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: "two_factor_admin", name: "Two-Factor Authentication", description: "Require 2FA for admin access" },
                      { key: "data_encryption", name: "Data Encryption at Rest", description: "AES-256 encryption for stored data" },
                      { key: "ssl_encryption", name: "SSL/TLS Encryption", description: "All data in transit encrypted" },
                      { key: "cookie_consent", name: "Cookie Consent Banner", description: "GDPR-compliant cookie notice" },
                      { key: "ip_logging", name: "IP Logging", description: "Log user IP addresses" }
                    ].map((setting) => (
                      <div key={setting.key} className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                        <div>
                          <p className="font-medium text-sm">{setting.name}</p>
                          <p className="text-xs text-viraly-muted">{setting.description}</p>
                        </div>
                        <button 
                          onClick={async () => {
                            const newValue = !complianceSettings[setting.key];
                            setComplianceSettings(prev => ({ ...prev, [setting.key]: newValue }));
                            try {
                              const adminToken = localStorage.getItem("viraly_admin_token");
                              await apiClient.put("/admin/compliance-settings", {
                                [setting.key]: newValue
                              }, {
                                headers: { Authorization: `Bearer ${adminToken}` }
                              });
                              toast.success(`${setting.name} ${newValue ? 'enabled' : 'disabled'}`);
                            } catch (err) {
                              setComplianceSettings(prev => ({ ...prev, [setting.key]: !newValue }));
                              toast.error("Failed to update setting");
                            }
                          }}
                          className={`w-12 h-6 rounded-full transition-colors ${complianceSettings[setting.key] ? 'bg-viraly-success' : 'bg-white/20'}`}
                          data-testid={`toggle-${setting.key}`}
                        >
                          <div className={`w-5 h-5 rounded-full bg-white transition-transform ${complianceSettings[setting.key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Auto-logout with configurable timeout */}
                  <div className="p-4 rounded-lg bg-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium text-sm">Auto-logout Inactive Sessions</p>
                        <p className="text-xs text-viraly-muted">Automatically log out users after inactivity</p>
                      </div>
                      <button 
                        onClick={async () => {
                          const newValue = !complianceSettings.auto_logout;
                          setComplianceSettings(prev => ({ ...prev, auto_logout: newValue }));
                          try {
                            const adminToken = localStorage.getItem("viraly_admin_token");
                            await apiClient.put("/admin/compliance-settings", {
                              auto_logout: newValue
                            }, {
                              headers: { Authorization: `Bearer ${adminToken}` }
                            });
                            toast.success(`Auto-logout ${newValue ? 'enabled' : 'disabled'}`);
                          } catch (err) {
                            setComplianceSettings(prev => ({ ...prev, auto_logout: !newValue }));
                            toast.error("Failed to update setting");
                          }
                        }}
                        className={`w-12 h-6 rounded-full transition-colors ${complianceSettings.auto_logout ? 'bg-viraly-success' : 'bg-white/20'}`}
                        data-testid="toggle-auto_logout"
                      >
                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${complianceSettings.auto_logout ? 'translate-x-6' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    
                    {complianceSettings.auto_logout && (
                      <div className="flex items-center gap-3 pt-3 border-t border-white/10">
                        <label className="text-sm text-viraly-muted">Timeout after:</label>
                        <select
                          value={complianceSettings.session_timeout_minutes || 60}
                          onChange={async (e) => {
                            const newValue = parseInt(e.target.value);
                            setComplianceSettings(prev => ({ ...prev, session_timeout_minutes: newValue }));
                            try {
                              const adminToken = localStorage.getItem("viraly_admin_token");
                              await apiClient.put("/admin/compliance-settings", {
                                session_timeout_minutes: newValue
                              }, {
                                headers: { Authorization: `Bearer ${adminToken}` }
                              });
                              toast.success(`Session timeout updated to ${newValue} minutes`);
                            } catch (err) {
                              toast.error("Failed to update timeout");
                            }
                          }}
                          className="input-base px-3 py-1.5 rounded-lg text-sm"
                          data-testid="session-timeout-select"
                        >
                          <option value={15}>15 minutes</option>
                          <option value={30}>30 minutes</option>
                          <option value={60}>1 hour</option>
                          <option value={120}>2 hours</option>
                          <option value={240}>4 hours</option>
                          <option value={480}>8 hours</option>
                        </select>
                        <span className="text-xs text-viraly-muted">of inactivity</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Audit Log - Paginated & Searchable */}
            <div className="card-base p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold">
                  Audit Log 
                  <span className="text-sm font-normal text-viraly-muted ml-2">({auditLogTotal.toLocaleString()} total events)</span>
                </h3>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Search logs..."
                    value={auditLogSearch}
                    onChange={(e) => setAuditLogSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setAuditLogPage(1);
                        fetchAdminData();
                      }
                    }}
                    className="input-base w-48 text-sm"
                  />
                  <Button 
                    className="btn-secondary text-sm"
                    onClick={() => { setAuditLogPage(1); fetchAdminData(); }}
                  >
                    <Search className="w-3 h-3 mr-1" />
                    Search
                  </Button>
                </div>
              </div>
              
              {auditLogsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-viraly-orange" />
                </div>
              ) : auditLogs.length > 0 ? (
                <>
                  <div className="space-y-2 max-h-80 overflow-y-auto mb-4">
                    {auditLogs.map((log, idx) => {
                      const logTime = log.timestamp ? new Date(log.timestamp) : new Date();
                      const timeAgo = getTimeAgo(logTime);
                      const gmtTime = logTime.toLocaleString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', hour12: true });
                      const estTime = logTime.toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: true });
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                          <div className="flex items-center gap-3">
                            <span className={`w-2 h-2 rounded-full ${
                              log.action?.includes('login') || log.action?.includes('security') ? 'bg-blue-400' :
                              log.action?.includes('delete') || log.action?.includes('suspend') ? 'bg-red-400' :
                              log.action?.includes('update') || log.action?.includes('edit') ? 'bg-yellow-400' :
                              log.action?.includes('create') ? 'bg-green-400' :
                              'bg-viraly-orange'
                            }`} />
                            <div>
                              <p className="text-sm">{log.action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                              <p className="text-xs text-viraly-muted">{log.user_id || log.admin_id || 'System'}</p>
                              {log.details && <p className="text-[10px] text-viraly-muted/70 truncate max-w-[300px]">{JSON.stringify(log.details)}</p>}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-viraly-muted">{timeAgo}</p>
                            <p className="text-[10px] text-viraly-muted/60">{gmtTime} GMT | {estTime} EST</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between border-t border-white/10 pt-4">
                    <p className="text-sm text-viraly-muted">
                      Page {auditLogPage} of {Math.ceil(auditLogTotal / 20)}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={auditLogPage <= 1}
                        onClick={() => { setAuditLogPage(p => p - 1); fetchAdminData(); }}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={auditLogPage >= Math.ceil(auditLogTotal / 20)}
                        onClick={() => { setAuditLogPage(p => p + 1); fetchAdminData(); }}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-viraly-muted text-center py-8">No audit logs found</p>
              )}
            </div>
          </motion.div>
        )}

        {/* B2B Overview Tab */}
        {activeTab === "b2b-overview" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Building className="w-8 h-8 text-viraly-orange" />
                  B2B Enterprise Overview
                </h1>
                <p className="text-viraly-muted mt-2">Partner, Enterprise, and Merchant management dashboard</p>
              </div>
              <div className="flex gap-2">
                <Button className="btn-primary flex items-center gap-2" onClick={() => setShowAddPartnerModal(true)}>
                  <Plus className="w-4 h-4" />
                  Add Partner
                </Button>
                <Button className="btn-secondary flex items-center gap-2" onClick={() => setShowAddEnterpriseModal(true)}>
                  <Plus className="w-4 h-4" />
                  Add Enterprise
                </Button>
              </div>
            </div>

            {b2bLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : (
              <>
                {/* B2B Stats Grid */}
                <div className="grid grid-cols-4 gap-4 mb-8">
                  <div className="card-base p-6 text-center">
                    <Handshake className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-viraly-light">{b2bStats.partners?.total || 0}</p>
                    <p className="text-viraly-muted text-sm">Total Partners</p>
                    <p className="text-xs text-green-400">{b2bStats.partners?.active || 0} active</p>
                  </div>
                  <div className="card-base p-6 text-center">
                    <Briefcase className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-viraly-light">{b2bStats.enterprises?.total || 0}</p>
                    <p className="text-viraly-muted text-sm">Enterprises</p>
                    <p className="text-xs text-green-400">{b2bStats.enterprises?.active || 0} active</p>
                  </div>
                  <div className="card-base p-6 text-center">
                    <Users className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-viraly-light">{b2bStats.merchants?.total || 0}</p>
                    <p className="text-viraly-muted text-sm">Merchants</p>
                    <p className="text-xs text-green-400">{b2bStats.merchants?.active || 0} active</p>
                  </div>
                  <div className="card-base p-6 text-center">
                    <MessageSquare className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-viraly-light">{b2bStats.inquiries?.pending || 0}</p>
                    <p className="text-viraly-muted text-sm">Pending Inquiries</p>
                    <button 
                      onClick={() => setActiveTab("b2b-inquiries")}
                      className="text-xs text-viraly-orange hover:underline"
                    >
                      View all →
                    </button>
                  </div>
                </div>

                {/* Enterprise Plans - Editable */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-viraly-light">Enterprise Plans (CAD)</h3>
                    <span className="text-xs text-viraly-muted">Click plan to edit pricing</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(b2bPlans).map(([key, plan]) => (
                      <div 
                        key={key} 
                        className="card-base p-6 cursor-pointer hover:border-viraly-orange/50 transition-all"
                        onClick={() => setEditingEnterprisePlan({ key, ...plan })}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xl font-bold text-viraly-light">{plan.name}</h4>
                          <Edit3 className="w-4 h-4 text-viraly-muted" />
                        </div>
                        <p className="text-2xl font-bold text-viraly-orange mt-2">
                          {plan.price_monthly_cad || plan.price_cad}
                          <span className="text-sm text-viraly-muted"> CAD/mo</span>
                        </p>
                        <div className="mt-4 space-y-2 text-sm text-viraly-muted">
                          <p>{(plan.monthly_post_credits || plan.monthly_posts)?.toLocaleString()} posts/month</p>
                          <p>{plan.monthly_video_credits || plan.monthly_videos} videos/month</p>
                          <p>{plan.max_merchants === -1 ? "Unlimited" : plan.max_merchants} merchants</p>
                          {plan.annual_discount_percent && (
                            <p className="text-green-400">{plan.annual_discount_percent}% annual discount</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Partner Tiers - Editable */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-viraly-light">Partner Commission Levels</h3>
                    <span className="text-xs text-viraly-muted">Fixed 12-month period from onboarding</span>
                  </div>
                  <div className="grid grid-cols-5 gap-3">
                    {Object.entries(partnerTiers || {}).sort((a, b) => (a[1].level || 0) - (b[1].level || 0)).map(([key, tier]) => (
                      <div 
                        key={key} 
                        className={`card-base p-3 cursor-pointer hover:border-purple-500/50 transition-all border-l-4 border-l-purple-500`}
                        onClick={() => setEditingPartnerTier({ key, ...tier })}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-viraly-light text-sm">{tier.name}</h4>
                          <Edit3 className="w-3 h-3 text-viraly-muted" />
                        </div>
                        <p className="text-xl font-bold text-viraly-orange">{(tier.commission_rate * 100).toFixed(1)}%</p>
                        <div className="mt-1 space-y-0.5 text-[10px] text-viraly-muted">
                          <p>{tier.min_active_enterprises}+ enterprises</p>
                          <p>{tier.min_revenue_12m_cad?.toLocaleString()}+ CAD</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Partner Payouts - Pending */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-viraly-light flex items-center gap-2">
                      <Banknote className="w-5 h-5 text-green-400" />
                      Partner Payouts
                    </h3>
                    <div className="flex gap-4 text-xs">
                      <span className="text-yellow-400">Pending: ${partnerPayoutSummary.pending_total?.toFixed(2) || '0.00'} ({partnerPayoutSummary.pending_count || 0})</span>
                      <span className="text-blue-400">Approved: ${partnerPayoutSummary.approved_total?.toFixed(2) || '0.00'}</span>
                      <span className="text-green-400">Paid: ${partnerPayoutSummary.paid_total?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                  
                  {partnerPayouts.filter(p => p.status === "pending" || p.status === "approved").length > 0 ? (
                    <div className="space-y-3">
                      {partnerPayouts.filter(p => p.status === "pending" || p.status === "approved").map((payout) => (
                        <div key={payout.id} className="card-base p-4 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-3 h-3 rounded-full ${payout.status === "pending" ? "bg-yellow-400" : "bg-blue-400"}`} />
                            <div>
                              <p className="font-bold text-viraly-light">{payout.partner_company}</p>
                              <p className="text-xs text-viraly-muted">{payout.paypal_email} • {payout.payout_method}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-green-400">${payout.amount_cad?.toFixed(2)} CAD</p>
                            <p className="text-xs text-viraly-muted">{new Date(payout.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex gap-2">
                            {payout.status === "pending" && (
                              <Button 
                                size="sm" 
                                className="bg-blue-500 hover:bg-blue-600"
                                onClick={async () => {
                                  try {
                                    await apiClient.put(`/b2b/partner-payouts/${payout.id}`, { action: "approve" }, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                                    toast.success("Payout approved");
                                    const res = await apiClient.get("/b2b/partner-payouts", { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                                    setPartnerPayouts(res.data.payouts || []);
                                    setPartnerPayoutSummary(res.data.summary || {});
                                  } catch { toast.error("Failed to approve"); }
                                }}
                              >
                                Approve
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => setSelectedPartnerPayout(payout)}
                            >
                              Mark as Paid
                            </Button>
                            {payout.status === "pending" && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={async () => {
                                  const notes = prompt("Rejection reason:");
                                  if (notes !== null) {
                                    try {
                                      await apiClient.put(`/b2b/partner-payouts/${payout.id}`, { action: "reject", admin_notes: notes }, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                                      toast.success("Payout rejected");
                                      const res = await apiClient.get("/b2b/partner-payouts", { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                                      setPartnerPayouts(res.data.payouts || []);
                                      setPartnerPayoutSummary(res.data.summary || {});
                                    } catch { toast.error("Failed to reject"); }
                                  }
                                }}
                              >
                                Reject
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="card-base p-6 text-center text-viraly-muted">
                      <Banknote className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No pending partner payouts</p>
                    </div>
                  )}
                </div>

                {/* Quick Links */}
                <div className="grid grid-cols-3 gap-4">
                  <button 
                    onClick={() => setActiveTab("b2b-partners")}
                    className="card-base p-6 hover:border-viraly-orange/50 transition-all text-left"
                  >
                    <Handshake className="w-6 h-6 text-blue-400 mb-2" />
                    <h4 className="font-bold text-viraly-light">Manage Partners</h4>
                    <p className="text-sm text-viraly-muted">View, approve, and manage partner accounts</p>
                  </button>
                  <button 
                    onClick={() => setActiveTab("b2b-enterprises")}
                    className="card-base p-6 hover:border-viraly-orange/50 transition-all text-left"
                  >
                    <Briefcase className="w-6 h-6 text-purple-400 mb-2" />
                    <h4 className="font-bold text-viraly-light">Manage Enterprises</h4>
                    <p className="text-sm text-viraly-muted">View enterprise clients and their API usage</p>
                  </button>
                  <button 
                    onClick={() => setActiveTab("b2b-inquiries")}
                    className="card-base p-6 hover:border-viraly-orange/50 transition-all text-left"
                  >
                    <MessageSquare className="w-6 h-6 text-yellow-400 mb-2" />
                    <h4 className="font-bold text-viraly-light">Review Inquiries</h4>
                    <p className="text-sm text-viraly-muted">Respond to partner and enterprise interest</p>
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}

        {/* B2B Partners Tab */}
        {activeTab === "b2b-partners" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Handshake className="w-8 h-8 text-blue-400" />
                  Partner Management
                </h1>
                <p className="text-viraly-muted mt-2">Manage channel partners and their referrals</p>
              </div>
              <Button className="btn-primary flex items-center gap-2" onClick={() => setShowAddPartnerModal(true)}>
                <Plus className="w-4 h-4" />
                Add Partner
              </Button>
            </div>

            {b2bLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : b2bPartners.length > 0 ? (
              <div className="card-base overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr className="text-left text-viraly-muted text-sm">
                      <th className="p-4">Company</th>
                      <th className="p-4">Contact</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Referrals</th>
                      <th className="p-4">Earnings</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b2bPartners.map(partner => (
                      <tr key={partner.id} className="border-t border-white/5 hover:bg-white/5">
                        <td className="p-4">
                          <p className="font-medium text-viraly-light">{partner.company_name}</p>
                          <p className="text-xs text-viraly-muted">{partner.referral_code}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-viraly-light">{partner.contact_name}</p>
                          <p className="text-xs text-viraly-muted">{partner.email}</p>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            partner.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            partner.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {partner.status}
                          </span>
                        </td>
                        <td className="p-4 text-viraly-light">{partner.total_referrals || 0}</td>
                        <td className="p-4 text-green-400">${(partner.total_earnings_cad || 0).toFixed(2)}</td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            {partner.status === 'pending' && (
                              <Button 
                                size="sm" 
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                onClick={async () => {
                                  try {
                                    await apiClient.put(`/api/b2b/partners/${partner.id}`, { status: 'approved' }, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                                    toast.success("Partner approved");
                                    fetchAdminData();
                                  } catch { toast.error("Failed to approve"); }
                                }}
                              >
                                Approve
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setSelectedB2bPartner(partner)}
                            >
                              View
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="card-base p-8 text-center">
                <Handshake className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                <p className="text-viraly-muted">No partners yet</p>
                <Button className="btn-primary mt-4" onClick={() => setShowAddPartnerModal(true)}>
                  Add First Partner
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {/* B2B Enterprises Tab */}
        {activeTab === "b2b-enterprises" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Briefcase className="w-8 h-8 text-purple-400" />
                  Enterprise Clients
                </h1>
                <p className="text-viraly-muted mt-2">Manage enterprise API clients and track onboarding progress</p>
              </div>
              <Button className="btn-primary flex items-center gap-2" onClick={() => setShowAddEnterpriseModal(true)} data-testid="add-enterprise-btn">
                <Plus className="w-4 h-4" />
                Add Enterprise
              </Button>
            </div>

            {/* Onboarding Pipeline View */}
            {b2bOnboardingSummary && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-viraly-light mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-viraly-orange" />
                  Onboarding Pipeline
                </h3>
                <div className="grid grid-cols-6 gap-3">
                  {b2bOnboardingSummary.status_flow?.map((stage, idx) => {
                    const stageData = b2bOnboardingSummary.summary?.[stage.id] || { count: 0, enterprises: [] };
                    const colorMap = {
                      gray: 'bg-gray-500/20 border-gray-500/50 text-gray-400',
                      blue: 'bg-blue-500/20 border-blue-500/50 text-blue-400',
                      purple: 'bg-purple-500/20 border-purple-500/50 text-purple-400',
                      yellow: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400',
                      orange: 'bg-orange-500/20 border-orange-500/50 text-orange-400',
                      green: 'bg-green-500/20 border-green-500/50 text-green-400'
                    };
                    const colors = colorMap[stage.color] || colorMap.gray;
                    
                    return (
                      <div 
                        key={stage.id} 
                        className={`card-base p-4 border ${colors} cursor-pointer hover:scale-105 transition-transform`}
                        onClick={() => {
                          if (stageData.enterprises?.length > 0) {
                            setSelectedOnboardingEnterprise(stageData.enterprises[0]);
                          }
                        }}
                        data-testid={`onboarding-stage-${stage.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-2xl font-bold">{stageData.count}</span>
                          {idx < 5 && <ChevronRight className="w-4 h-4 text-viraly-muted" />}
                        </div>
                        <p className="text-xs font-medium">{stage.label}</p>
                        {stageData.enterprises?.length > 0 && (
                          <p className="text-[10px] text-viraly-muted mt-1 truncate">
                            {stageData.enterprises[0].company_name}
                            {stageData.count > 1 && ` +${stageData.count - 1} more`}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-4 text-sm text-viraly-muted">
                  <span>Total: <strong className="text-viraly-light">{b2bOnboardingSummary.totals?.total || 0}</strong></span>
                  <span>Active: <strong className="text-green-400">{b2bOnboardingSummary.totals?.active || 0}</strong></span>
                  <span>In Progress: <strong className="text-yellow-400">{b2bOnboardingSummary.totals?.in_progress || 0}</strong></span>
                </div>
              </div>
            )}

            {b2bLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : b2bEnterprises.length > 0 ? (
              <div className="card-base overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr className="text-left text-viraly-muted text-sm">
                      <th className="p-4">Company</th>
                      <th className="p-4">Plan</th>
                      <th className="p-4">Onboarding</th>
                      <th className="p-4">Usage</th>
                      <th className="p-4">Merchants</th>
                      <th className="p-4">Via Partner</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b2bEnterprises.map(enterprise => {
                      const onboardingStatusColors = {
                        'pending_documents': 'bg-gray-500/20 text-gray-400',
                        'documents_sent': 'bg-blue-500/20 text-blue-400',
                        'documents_signed': 'bg-purple-500/20 text-purple-400',
                        'sandbox_testing': 'bg-yellow-500/20 text-yellow-400',
                        'production_ready': 'bg-orange-500/20 text-orange-400',
                        'active': 'bg-green-500/20 text-green-400'
                      };
                      const onboardingColor = onboardingStatusColors[enterprise.onboarding_status] || onboardingStatusColors.pending_documents;
                      
                      return (
                        <tr key={enterprise.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`enterprise-row-${enterprise.id}`}>
                          <td className="p-4">
                            <p className="font-medium text-viraly-light">{enterprise.company_name}</p>
                            <p className="text-xs text-viraly-muted">{enterprise.email}</p>
                          </td>
                          <td className="p-4">
                            <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-400 text-xs uppercase">
                              {enterprise.plan}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs ${onboardingColor}`}>
                              {(enterprise.onboarding_status || 'pending_documents').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="p-4 text-viraly-light text-sm">
                            {enterprise.posts_used_this_month || 0}/{enterprise.monthly_post_limit} posts
                          </td>
                          <td className="p-4 text-viraly-light">{enterprise.merchant_count || 0}</td>
                          <td className="p-4">
                            {enterprise.is_direct ? (
                              <span className="text-xs text-viraly-muted">Direct</span>
                            ) : (
                              <span className="text-xs text-blue-400">Partner</span>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setSelectedOnboardingEnterprise(enterprise)}
                                data-testid={`view-enterprise-${enterprise.id}`}
                              >
                                Manage
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="card-base p-8 text-center">
                <Briefcase className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                <p className="text-viraly-muted">No enterprises yet</p>
                <Button className="btn-primary mt-4" onClick={() => setShowAddEnterpriseModal(true)}>
                  Add First Enterprise
                </Button>
              </div>
            )}

            {/* Enterprise Onboarding Detail Modal */}
            <AnimatePresence>
              {selectedOnboardingEnterprise && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
                  onClick={() => setSelectedOnboardingEnterprise(null)}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-viraly-card border border-white/10 rounded-2xl p-6 w-full max-w-3xl mx-4 max-h-[85vh] overflow-y-auto"
                    data-testid="enterprise-onboarding-modal"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-viraly-light">{selectedOnboardingEnterprise.company_name}</h3>
                        <p className="text-sm text-viraly-muted">{selectedOnboardingEnterprise.email}</p>
                      </div>
                      <button
                        onClick={() => setSelectedOnboardingEnterprise(null)}
                        className="text-viraly-muted hover:text-white p-2"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Onboarding Progress Bar */}
                    <div className="mb-6">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-viraly-muted">Onboarding Progress</span>
                        <span className="text-sm text-viraly-orange">
                          {(selectedOnboardingEnterprise.onboarding_status || 'pending_documents').replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-viraly-orange to-orange-400 transition-all duration-500"
                          style={{ 
                            width: `${
                              selectedOnboardingEnterprise.onboarding_status === 'active' ? 100 :
                              selectedOnboardingEnterprise.onboarding_status === 'production_ready' ? 90 :
                              selectedOnboardingEnterprise.onboarding_status === 'sandbox_testing' ? 70 :
                              selectedOnboardingEnterprise.onboarding_status === 'documents_signed' ? 50 :
                              selectedOnboardingEnterprise.onboarding_status === 'documents_sent' ? 30 : 10
                            }%` 
                          }}
                        />
                      </div>
                    </div>

                    {/* Quick Info Grid - Onboarding relevant info only (Plan is chosen AFTER onboarding) */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-viraly-muted">Contact</p>
                        <p className="font-semibold text-viraly-light text-sm">{selectedOnboardingEnterprise.contact_name}</p>
                        <p className="text-xs text-viraly-muted">{selectedOnboardingEnterprise.phone || 'No phone'}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-viraly-muted">Portal URL</p>
                        <p className="font-semibold text-viraly-light text-sm truncate">{selectedOnboardingEnterprise.portal_url || '/enterprise'}</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-viraly-muted">Source</p>
                        <p className="font-semibold text-viraly-light">{selectedOnboardingEnterprise.is_direct ? 'Direct Client' : 'Via Partner'}</p>
                      </div>
                    </div>

                    {/* Documents Status - Auto-sent on enterprise creation, signed via Enterprise Portal */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-viraly-light mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Documents
                        <span className="text-[10px] text-viraly-muted font-normal ml-auto">
                          Auto-sent on creation • Signed via Portal
                        </span>
                      </h4>
                      <div className="space-y-2">
                        {(selectedOnboardingEnterprise.documents && selectedOnboardingEnterprise.documents.length > 0 
                          ? selectedOnboardingEnterprise.documents 
                          : [
                            { name: "Terms of Service", type: "terms_of_service", status: "pending" },
                            { name: "Service Level Agreement", type: "sla", status: "pending" },
                            { name: "Data Processing Agreement", type: "dpa", status: "pending" }
                          ]
                        ).map((doc) => (
                          <div key={doc.type} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              {doc.status === 'signed' ? (
                                <CheckCircle className="w-5 h-5 text-green-400" />
                              ) : doc.status === 'sent' ? (
                                <Clock className="w-5 h-5 text-blue-400 animate-pulse" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-500" />
                              )}
                              <div>
                                <p className="text-sm text-viraly-light">{doc.name}</p>
                                <p className="text-xs text-viraly-muted">
                                  {doc.status === 'signed' && doc.signed_at ? `Signed ${new Date(doc.signed_at).toLocaleDateString()}` :
                                   doc.status === 'sent' && doc.sent_at ? `Sent ${new Date(doc.sent_at).toLocaleDateString()} • Awaiting signature` :
                                   'Not yet sent'}
                                </p>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              doc.status === 'signed' ? 'bg-green-500/20 text-green-400' :
                              doc.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {doc.status === 'signed' ? 'Signed' : doc.status === 'sent' ? 'Awaiting' : 'Pending'}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Admin Override Actions - only show when needed */}
                      {selectedOnboardingEnterprise.onboarding_status === 'pending_documents' && (
                        <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                          <p className="text-xs text-yellow-400 mb-2">
                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                            Documents not yet sent? Use manual override:
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={onboardingActionLoading}
                            onClick={async () => {
                              setOnboardingActionLoading(true);
                              try {
                                await apiClient.post(`/b2b/enterprises/${selectedOnboardingEnterprise.id}/send-all-documents`, {}, { 
                                  headers: { Authorization: `Bearer ${localStorage.getItem('viraly_admin_token')}` } 
                                });
                                toast.success("Documents sent to enterprise email");
                                fetchAdminData();
                              } catch { toast.error("Failed to send documents"); }
                              setOnboardingActionLoading(false);
                            }}
                          >
                            <Mail className="w-3 h-3 mr-1" />
                            Resend Documents
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* API Keys */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-viraly-light mb-3 flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        API Keys
                      </h4>
                      <div className="space-y-3">
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-viraly-light flex items-center gap-2">
                                Sandbox API Key
                                <span className={`text-xs px-2 py-0.5 rounded ${selectedOnboardingEnterprise.sandbox_api_key_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                  {selectedOnboardingEnterprise.sandbox_api_key_active ? 'Active' : 'Inactive'}
                                </span>
                              </p>
                              <p className="text-xs text-viraly-muted font-mono mt-1">
                                {selectedOnboardingEnterprise.sandbox_api_key || 'vrl_test_***'}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(selectedOnboardingEnterprise.sandbox_api_key);
                                toast.success("Copied to clipboard");
                              }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-viraly-light flex items-center gap-2">
                                Production API Key
                                <span className={`text-xs px-2 py-0.5 rounded ${selectedOnboardingEnterprise.production_api_key_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                  {selectedOnboardingEnterprise.production_api_key_active ? 'Active' : 'Inactive'}
                                </span>
                              </p>
                              <p className="text-xs text-viraly-muted font-mono mt-1">
                                {selectedOnboardingEnterprise.production_api_key || 'vrl_live_***'}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(selectedOnboardingEnterprise.production_api_key);
                                toast.success("Copied to clipboard");
                              }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
                      {selectedOnboardingEnterprise.onboarding_status === 'documents_signed' && (
                        <Button
                          className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                          disabled={onboardingActionLoading}
                          onClick={async () => {
                            setOnboardingActionLoading(true);
                            try {
                              await apiClient.post(`/b2b/enterprises/${selectedOnboardingEnterprise.id}/activate-sandbox`, {}, { 
                                headers: { Authorization: `Bearer ${localStorage.getItem('viraly_admin_token')}` } 
                              });
                              toast.success("Sandbox activated!");
                              fetchAdminData();
                              setSelectedOnboardingEnterprise(null);
                            } catch (e) { toast.error(e.response?.data?.detail || "Failed to activate sandbox"); }
                            setOnboardingActionLoading(false);
                          }}
                          data-testid="activate-sandbox-btn"
                        >
                          <Zap className="w-4 h-4 mr-2" />
                          Activate Sandbox
                        </Button>
                      )}
                      {(selectedOnboardingEnterprise.onboarding_status === 'sandbox_testing' || selectedOnboardingEnterprise.onboarding_status === 'production_ready') && (
                        <Button
                          className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                          disabled={onboardingActionLoading}
                          onClick={async () => {
                            setOnboardingActionLoading(true);
                            try {
                              await apiClient.post(`/b2b/enterprises/${selectedOnboardingEnterprise.id}/activate-production`, {}, { 
                                headers: { Authorization: `Bearer ${localStorage.getItem('viraly_admin_token')}` } 
                              });
                              toast.success("Production API activated!");
                              fetchAdminData();
                              setSelectedOnboardingEnterprise(null);
                            } catch (e) { toast.error(e.response?.data?.detail || "Failed to activate production"); }
                            setOnboardingActionLoading(false);
                          }}
                          data-testid="activate-production-btn"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Activate Production
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        disabled={onboardingActionLoading}
                        onClick={async () => {
                          setOnboardingActionLoading(true);
                          try {
                            const res = await apiClient.post(`/b2b/enterprises/${selectedOnboardingEnterprise.id}/send-credentials`, {}, { 
                              headers: { Authorization: `Bearer ${localStorage.getItem('viraly_admin_token')}` } 
                            });
                            toast.success(`Credentials sent! Temp password: ${res.data.temp_password}`);
                          } catch { toast.error("Failed to send credentials"); }
                          setOnboardingActionLoading(false);
                        }}
                        data-testid="send-credentials-btn"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Send Credentials
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setSelectedB2bEnterprise(selectedOnboardingEnterprise)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Full Details
                      </Button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* B2B Inquiries Tab */}
        {activeTab === "b2b-inquiries" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <MessageSquare className="w-8 h-8 text-yellow-400" />
                  B2B Inquiries
                </h1>
                <p className="text-viraly-muted mt-2">Partner and enterprise interest inquiries from landing page</p>
              </div>
            </div>

            {b2bLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : b2bInquiries.length > 0 ? (
              <div className="space-y-4">
                {b2bInquiries.map(inquiry => (
                  <div key={inquiry.id} className="card-base p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-1 rounded text-xs uppercase ${
                            inquiry.type === 'partner' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                          }`}>
                            {inquiry.type}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            inquiry.status === 'new' ? 'bg-yellow-500/20 text-yellow-400' :
                            inquiry.status === 'contacted' ? 'bg-blue-500/20 text-blue-400' :
                            inquiry.status === 'converted' ? 'bg-green-500/20 text-green-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {inquiry.status}
                          </span>
                        </div>
                        <h4 className="font-bold text-viraly-light">{inquiry.company_name}</h4>
                        <p className="text-viraly-muted">{inquiry.contact_name} • {inquiry.email}</p>
                        {inquiry.message && (
                          <p className="text-sm text-viraly-muted mt-2 bg-white/5 p-3 rounded">{inquiry.message}</p>
                        )}
                        <p className="text-xs text-viraly-muted mt-2">{new Date(inquiry.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex gap-2">
                        {inquiry.status === 'new' && (
                          <Button 
                            size="sm"
                            onClick={async () => {
                              try {
                                await apiClient.put(`/api/b2b/inquiries/${inquiry.id}?status=contacted`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                                toast.success("Marked as contacted");
                                fetchAdminData();
                              } catch { toast.error("Failed to update"); }
                            }}
                          >
                            Mark Contacted
                          </Button>
                        )}
                        {(inquiry.status === 'new' || inquiry.status === 'contacted') && (
                          <Button 
                            size="sm"
                            className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            onClick={() => {
                              if (inquiry.type === 'partner') {
                                setNewPartnerData({ company_name: inquiry.company_name, contact_name: inquiry.contact_name, email: inquiry.email, phone: inquiry.phone || "", country: inquiry.country || "CA", territory: [], notes: inquiry.message || "" });
                                setShowAddPartnerModal(true);
                              } else {
                                setNewEnterpriseData({ company_name: inquiry.company_name, contact_name: inquiry.contact_name, email: inquiry.email, phone: inquiry.phone || "", country: inquiry.country || "CA", partner_id: "", plan: "starter", notes: inquiry.message || "" });
                                setShowAddEnterpriseModal(true);
                              }
                            }}
                          >
                            Convert
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card-base p-8 text-center">
                <MessageSquare className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                <p className="text-viraly-muted">No inquiries yet</p>
                <p className="text-xs text-viraly-muted mt-2">Inquiries from the landing page enterprise section will appear here</p>
              </div>
            )}
          </motion.div>
        )}

        {/* B2B Tech Support Tab */}
        {activeTab === "b2b-tech-support" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <LifeBuoy className="w-8 h-8 text-red-400" />
                  Tech Support Dashboard
                </h1>
                <p className="text-viraly-muted mt-2">Vira escalations and enterprise support monitoring (view-only until escalated)</p>
              </div>
              <Button onClick={fetchAdminData} variant="outline" size="sm">
                <RefreshCcw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {b2bLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-6">
                {/* Pending Escalations */}
                <div className="col-span-2 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-viraly-light flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      Pending Escalations
                    </h3>
                    <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
                      {b2bEscalations.filter(e => e.status === 'pending').length} pending
                    </span>
                  </div>
                  
                  {b2bEscalations.length > 0 ? (
                    <div className="space-y-3">
                      {b2bEscalations.map(escalation => (
                        <div key={escalation.id} className="card-base p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  escalation.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                  escalation.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                                  'bg-green-500/20 text-green-400'
                                }`}>
                                  {escalation.status}
                                </span>
                                <span className="text-xs text-viraly-muted">
                                  {new Date(escalation.created_at).toLocaleString()}
                                </span>
                              </div>
                              <h4 className="font-bold text-viraly-light">{escalation.enterprise_name}</h4>
                              <p className="text-sm text-viraly-muted mt-1">{escalation.reason}</p>
                            </div>
                            <Button size="sm" onClick={() => setSelectedEscalation(escalation)}>
                              View Details
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="card-base p-8 text-center">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                      <p className="text-viraly-muted">No pending escalations</p>
                      <p className="text-xs text-viraly-muted mt-2">All Vira conversations resolved without human intervention</p>
                    </div>
                  )}
                </div>
                
                {/* Recent Conversations (View Only) */}
                <div className="space-y-4">
                  <h3 className="font-bold text-viraly-light flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-purple-400" />
                    Recent Vira Conversations
                  </h3>
                  
                  {b2bConversations.length > 0 ? (
                    <div className="space-y-2 max-h-[500px] overflow-auto">
                      {b2bConversations.slice(0, 10).map(conv => (
                        <button
                          key={conv.id}
                          onClick={() => setSelectedConversation(conv)}
                          className="w-full card-base p-3 text-left hover:border-purple-500/50 transition-all"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-viraly-light">{conv.enterprise_name}</span>
                            {conv.escalated && (
                              <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">Escalated</span>
                            )}
                          </div>
                          <p className="text-xs text-viraly-muted">
                            {conv.messages?.length || 0} messages • {new Date(conv.last_message_at).toLocaleDateString()}
                          </p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="card-base p-4 text-center">
                      <p className="text-viraly-muted text-sm">No conversations yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* B2B API Logs Tab */}
        {activeTab === "b2b-api-logs" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Terminal className="w-8 h-8 text-green-400" />
                  Enterprise API Logs
                </h1>
                <p className="text-viraly-muted mt-2">Monitor enterprise API usage and debugging</p>
              </div>
              <Button onClick={fetchAdminData} variant="outline" size="sm">
                <RefreshCcw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {b2bLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : (
              <>
                {/* Stats */}
                <div className="grid grid-cols-5 gap-4 mb-8">
                  <div className="card-base p-4 text-center">
                    <p className="text-2xl font-bold text-yellow-400">{b2bApiLogStats.total?.sandbox || 0}</p>
                    <p className="text-xs text-viraly-muted">Sandbox Calls</p>
                  </div>
                  <div className="card-base p-4 text-center">
                    <p className="text-2xl font-bold text-green-400">{b2bApiLogStats.total?.production || 0}</p>
                    <p className="text-xs text-viraly-muted">Production Calls</p>
                  </div>
                  <div className="card-base p-4 text-center">
                    <p className="text-2xl font-bold text-red-400">{b2bApiLogStats.total?.errors || 0}</p>
                    <p className="text-xs text-viraly-muted">Errors</p>
                  </div>
                  <div className="card-base p-4 text-center">
                    <p className="text-2xl font-bold text-blue-400">{b2bApiLogStats.last_24h?.sandbox || 0}</p>
                    <p className="text-xs text-viraly-muted">Sandbox (24h)</p>
                  </div>
                  <div className="card-base p-4 text-center">
                    <p className="text-2xl font-bold text-purple-400">{b2bApiLogStats.last_24h?.production || 0}</p>
                    <p className="text-xs text-viraly-muted">Production (24h)</p>
                  </div>
                </div>
                
                {/* Logs Table */}
                {b2bApiLogs.length > 0 ? (
                  <div className="card-base overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr className="text-left text-viraly-muted text-sm">
                          <th className="p-4">Timestamp</th>
                          <th className="p-4">Enterprise</th>
                          <th className="p-4">Type</th>
                          <th className="p-4">Endpoint</th>
                          <th className="p-4">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b2bApiLogs.map(log => (
                          <tr key={log.id} className="border-t border-white/5 hover:bg-white/5">
                            <td className="p-4 text-viraly-muted text-sm font-mono">
                              {new Date(log.timestamp).toLocaleString()}
                            </td>
                            <td className="p-4 text-viraly-light">{log.enterprise_name || 'Unknown'}</td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded text-xs ${
                                log.api_key_type === 'sandbox' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'
                              }`}>
                                {log.api_key_type}
                              </span>
                            </td>
                            <td className="p-4 text-viraly-muted text-sm font-mono">{log.endpoint}</td>
                            <td className="p-4">
                              {log.success ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <span className="text-red-400 text-xs">{log.error || 'Error'}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="card-base p-8 text-center">
                    <Terminal className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
                    <p className="text-viraly-muted">No API logs yet</p>
                    <p className="text-xs text-viraly-muted mt-2">Enterprise API calls will be logged here</p>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* B2B Settings Tab */}
        {activeTab === "b2b-settings" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <Settings className="w-8 h-8 text-viraly-orange" />
                  B2B Settings
                </h1>
                <p className="text-viraly-muted mt-2">Configure B2B notification emails and enterprise settings</p>
              </div>
            </div>

            {b2bLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-viraly-orange" />
              </div>
            ) : (
              <div className="max-w-2xl space-y-6">
                {/* Notification Emails */}
                <div className="card-base p-6">
                  <h3 className="font-bold text-viraly-light mb-4 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-400" />
                    Inquiry Notification Emails
                  </h3>
                  <p className="text-sm text-viraly-muted mb-4">
                    Receive email notifications when new partner or enterprise inquiries are submitted
                  </p>
                  
                  {/* Current emails */}
                  <div className="space-y-2 mb-4">
                    {(b2bSettings.inquiry_notification_emails || [b2bSettings.inquiry_notification_email]).map((email, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <span className="text-viraly-light">{email}</span>
                        <button
                          onClick={async () => {
                            const newEmails = (b2bSettings.inquiry_notification_emails || []).filter(e => e !== email);
                            if (newEmails.length === 0) {
                              toast.error("Must have at least one notification email");
                              return;
                            }
                            try {
                              await apiClient.put("/b2b/settings", { inquiry_notification_emails: newEmails }, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                              setB2bSettings({ ...b2bSettings, inquiry_notification_emails: newEmails });
                              toast.success("Email removed");
                            } catch { toast.error("Failed to update"); }
                          }}
                          className="text-red-400 text-sm hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add new email */}
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={newB2bNotificationEmail}
                      onChange={(e) => setNewB2bNotificationEmail(e.target.value)}
                      placeholder="Add notification email..."
                      className="bg-white/5 border-white/10"
                    />
                    <Button
                      onClick={async () => {
                        if (!newB2bNotificationEmail || !newB2bNotificationEmail.includes('@')) {
                          toast.error("Enter a valid email");
                          return;
                        }
                        const newEmails = [...(b2bSettings.inquiry_notification_emails || []), newB2bNotificationEmail];
                        try {
                          await apiClient.put("/b2b/settings", { inquiry_notification_emails: newEmails }, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                          setB2bSettings({ ...b2bSettings, inquiry_notification_emails: newEmails });
                          setNewB2bNotificationEmail("");
                          toast.success("Email added");
                        } catch { toast.error("Failed to update"); }
                      }}
                    >
                      Add
                    </Button>
                  </div>
                </div>
                
                {/* Sandbox Settings */}
                <div className="card-base p-6">
                  <h3 className="font-bold text-viraly-light mb-4 flex items-center gap-2">
                    <Key className="w-5 h-5 text-yellow-400" />
                    Sandbox & API Settings
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-viraly-light">Sandbox Daily Limit</p>
                        <p className="text-xs text-viraly-muted">Max API calls per day in sandbox mode</p>
                      </div>
                      <span className="text-lg font-bold text-yellow-400">{b2bSettings.sandbox_daily_limit || 100}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-viraly-light">Calls for Production Activation</p>
                        <p className="text-xs text-viraly-muted">Successful sandbox calls needed to auto-enable production</p>
                      </div>
                      <span className="text-lg font-bold text-green-400">{b2bSettings.sandbox_calls_for_production || 10}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-viraly-light">Send Welcome Emails</p>
                        <p className="text-xs text-viraly-muted">Automatically send welcome emails on activation</p>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !b2bSettings.send_welcome_emails;
                          try {
                            await apiClient.put("/b2b/settings", { send_welcome_emails: newValue }, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                            setB2bSettings({ ...b2bSettings, send_welcome_emails: newValue });
                            toast.success("Setting updated");
                          } catch { toast.error("Failed to update"); }
                        }}
                        className={`w-12 h-6 rounded-full transition-all relative ${
                          b2bSettings.send_welcome_emails ? 'bg-green-500' : 'bg-white/20'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${
                          b2bSettings.send_welcome_emails ? 'left-7' : 'left-1'
                        }`} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Publish Retry Settings Tab */}
        {activeTab === "publish-retry" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                  <RefreshCcw className="w-8 h-8 text-viraly-orange" />
                  Publish Retry Settings
                </h1>
                <p className="text-viraly-muted mt-2">Configure automatic retry behavior for failed post publications</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleTriggerPublishRetry}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Trigger Retry Now
                </Button>
                <Button
                  onClick={handleSavePublishRetrySettings}
                  className="btn-primary"
                  disabled={publishRetrySettingsSaving}
                >
                  {publishRetrySettingsSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Save Settings
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Retry Configuration */}
              <div className="card-base p-6 space-y-6">
                <h3 className="font-bold text-viraly-light flex items-center gap-2">
                  <Settings className="w-5 h-5 text-viraly-orange" />
                  Retry Configuration
                </h3>

                {/* Max Retries */}
                <div>
                  <label className="block text-sm text-viraly-muted mb-2">Max Retries</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={publishRetrySettings.max_retries}
                      onChange={(e) => setPublishRetrySettings({...publishRetrySettings, max_retries: parseInt(e.target.value)})}
                      className="flex-1"
                    />
                    <span className="text-2xl font-bold text-viraly-orange w-12 text-center">{publishRetrySettings.max_retries}</span>
                  </div>
                  <p className="text-xs text-viraly-muted mt-1">Number of retry attempts before marking as needs attention</p>
                </div>

                {/* Retry Intervals */}
                <div>
                  <label className="block text-sm text-viraly-muted mb-2">Retry Intervals (minutes)</label>
                  <div className="flex gap-2">
                    {publishRetrySettings.retry_intervals_minutes.map((interval, idx) => (
                      <div key={idx} className="flex-1">
                        <Input
                          type="number"
                          min="1"
                          value={interval}
                          onChange={(e) => {
                            const newIntervals = [...publishRetrySettings.retry_intervals_minutes];
                            newIntervals[idx] = parseInt(e.target.value) || 1;
                            setPublishRetrySettings({...publishRetrySettings, retry_intervals_minutes: newIntervals});
                          }}
                          className="bg-white/5 border-white/10 text-center"
                        />
                        <p className="text-xs text-viraly-muted text-center mt-1">
                          {idx === 0 ? '1st retry' : idx === 1 ? '2nd retry' : '3rd+ retry'}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-viraly-muted mt-2">Wait times before each retry attempt (exponential backoff)</p>
                </div>

                {/* Notification Type */}
                <div>
                  <label className="block text-sm text-viraly-muted mb-2">Notification Type</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['push', 'email', 'both'].map(type => (
                      <button
                        key={type}
                        onClick={() => setPublishRetrySettings({...publishRetrySettings, notification_type: type})}
                        className={`p-3 rounded-lg border transition-all ${
                          publishRetrySettings.notification_type === type
                            ? 'border-viraly-orange bg-viraly-orange/20 text-viraly-orange'
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <span className="capitalize">{type}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-viraly-muted mt-2">How to notify users about publish status</p>
                </div>

                {/* TikTok Reminder Hours */}
                <div>
                  <label className="block text-sm text-viraly-muted mb-2">TikTok Approval Reminders (hours before)</label>
                  <div className="flex gap-2">
                    {publishRetrySettings.tiktok_reminder_hours.map((hours, idx) => (
                      <Input
                        key={idx}
                        type="number"
                        min="1"
                        value={hours}
                        onChange={(e) => {
                          const newHours = [...publishRetrySettings.tiktok_reminder_hours];
                          newHours[idx] = parseInt(e.target.value) || 1;
                          setPublishRetrySettings({...publishRetrySettings, tiktok_reminder_hours: newHours});
                        }}
                        className="bg-white/5 border-white/10 w-20 text-center"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-viraly-muted mt-2">Send reminders at these hours before TikTok post time</p>
                </div>

                {/* Auto Reschedule */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-viraly-light">Auto-Reschedule on Failure</p>
                    <p className="text-xs text-viraly-muted">Queue failed TikTok posts for next day</p>
                  </div>
                  <button
                    onClick={() => setPublishRetrySettings({...publishRetrySettings, auto_reschedule_on_failure: !publishRetrySettings.auto_reschedule_on_failure})}
                    className={`w-12 h-6 rounded-full transition-all relative ${
                      publishRetrySettings.auto_reschedule_on_failure ? 'bg-green-500' : 'bg-white/20'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${
                      publishRetrySettings.auto_reschedule_on_failure ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Failure Stats & Recent Failures */}
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="card-base p-4 text-center">
                    <p className="text-3xl font-bold text-red-400">{publishFailures.stats?.unresolved || 0}</p>
                    <p className="text-xs text-viraly-muted">Unresolved</p>
                  </div>
                  <div className="card-base p-4 text-center">
                    <p className="text-3xl font-bold text-green-400">{publishFailures.stats?.resolved || 0}</p>
                    <p className="text-xs text-viraly-muted">Resolved</p>
                  </div>
                  <div className="card-base p-4 text-center">
                    <p className="text-3xl font-bold text-viraly-orange">
                      {Object.keys(publishFailures.stats?.error_breakdown || {}).length}
                    </p>
                    <p className="text-xs text-viraly-muted">Error Types</p>
                  </div>
                </div>

                {/* Error Breakdown */}
                {Object.keys(publishFailures.stats?.error_breakdown || {}).length > 0 && (
                  <div className="card-base p-4">
                    <h4 className="font-bold text-viraly-light mb-3">Error Breakdown</h4>
                    <div className="space-y-2">
                      {Object.entries(publishFailures.stats?.error_breakdown || {}).map(([code, count]) => (
                        <div key={code} className="flex items-center justify-between text-sm">
                          <span className="text-viraly-muted">{code}</span>
                          <span className="font-bold text-red-400">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Failures */}
                <div className="card-base p-4">
                  <h4 className="font-bold text-viraly-light mb-3">Recent Failures</h4>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {(publishFailures.failures || []).length === 0 ? (
                      <p className="text-center text-viraly-muted py-4">No failures recorded</p>
                    ) : (
                      publishFailures.failures.map((failure) => (
                        <div key={failure.id} className={`p-3 rounded-lg border ${
                          failure.resolved ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-mono text-viraly-muted">
                              {failure.post_id?.slice(0, 8)}...
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              failure.resolved ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {failure.resolved ? 'Resolved' : failure.error_code || 'UNKNOWN'}
                            </span>
                          </div>
                          <p className="text-xs text-viraly-muted line-clamp-2">{failure.error_message}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-viraly-muted">
                              {new Date(failure.created_at).toLocaleString()}
                            </span>
                            {!failure.resolved && (
                              <button
                                onClick={() => handleResolvePublishFailure(failure.id, 'manual_resolve')}
                                className="text-xs text-blue-400 hover:underline"
                              >
                                Mark Resolved
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* User Detail Modal */}
        <AnimatePresence>
          {selectedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={closeUserDetail}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      selectedUser.status === 'suspended' || selectedUser.is_suspended ? 'bg-red-500/30' : 'bg-viraly-gradient'
                    }`}>
                      <span className={`font-bold text-2xl ${
                        selectedUser.status === 'suspended' || selectedUser.is_suspended ? 'text-red-400' : 'text-white'
                      }`}>
                        {selectedUser.name?.charAt(0) || "U"}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="font-heading text-2xl font-bold">{selectedUser.name}</h2>
                        <span className={`px-2 py-1 rounded text-xs ${
                          selectedUser.status === 'suspended' || selectedUser.is_suspended 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-viraly-success/20 text-viraly-success'
                        }`}>
                          {selectedUser.status === 'suspended' || selectedUser.is_suspended ? 'SUSPENDED' : 'Active'}
                        </span>
                      </div>
                      <p className="text-viraly-muted flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {selectedUser.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleImpersonateUser(selectedUser)}
                      disabled={impersonationLoading || selectedUser.status === 'suspended' || selectedUser.is_suspended}
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                      data-testid="view-as-user-btn"
                    >
                      {impersonationLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      View as User
                    </Button>
                    <Button
                      className={selectedUser.status === 'suspended' || selectedUser.is_suspended ? 'btn-primary' : 'bg-red-500 hover:bg-red-600'}
                      onClick={() => { closeUserDetail(); setSuspendUserModal(selectedUser); }}
                    >
                      {selectedUser.status === 'suspended' || selectedUser.is_suspended ? 'Reactivate' : 'Suspend'}
                    </Button>
                    <button
                      onClick={closeUserDetail}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      data-testid="close-user-detail"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                  {userDetailLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-2 border-viraly-orange border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : userDetail ? (
                    <div className="space-y-6">
                      {/* User Stats */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="card-base p-4 text-center bg-white/5">
                          <p className="text-viraly-muted text-sm">Total Posts</p>
                          <p className="text-2xl font-bold text-viraly-orange">{userDetail.stats?.total_posts || 0}</p>
                        </div>
                        <div className="card-base p-4 text-center bg-white/5">
                          <p className="text-viraly-muted text-sm">Published</p>
                          <p className="text-2xl font-bold text-viraly-success">{userDetail.stats?.published_posts || 0}</p>
                        </div>
                        <div className="card-base p-4 text-center bg-white/5">
                          <p className="text-viraly-muted text-sm">Total Spent</p>
                          <p className="text-2xl font-bold text-blue-400">${userDetail.stats?.total_spent?.toFixed(2) || "0.00"}</p>
                        </div>
                        <div className="card-base p-4 text-center bg-white/5">
                          <p className="text-viraly-muted text-sm">Referrals</p>
                          <p className="text-2xl font-bold text-purple-400">{userDetail.stats?.referred_users || 0}</p>
                        </div>
                      </div>

                      {/* User Info Cards */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Account Info */}
                        <div className="card-base p-4 bg-white/5">
                          <h3 className="font-bold mb-3 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-viraly-orange" />
                            Account Info
                          </h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-viraly-muted">Plan</span>
                              <span className="px-2 py-0.5 rounded-full bg-viraly-orange/20 text-viraly-orange capitalize">{userDetail.user?.plan || selectedUser.plan}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-viraly-muted">Posts Remaining</span>
                              <span>{userDetail.user?.posts_remaining || selectedUser.posts_remaining || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-viraly-muted">Auth Provider</span>
                              <span className="capitalize">{userDetail.user?.auth_provider || "email"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-viraly-muted">WebAuthn</span>
                              <span className="flex items-center gap-1">
                                {userDetail.user?.webauthn_enabled ? (
                                  <><Fingerprint className="w-4 h-4 text-viraly-success" /> Enabled</>
                                ) : (
                                  <span className="text-viraly-muted">Disabled</span>
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-viraly-muted">Joined</span>
                              <span>{new Date(userDetail.user?.created_at || selectedUser.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>

                        {/* Connected Accounts */}
                        <div className="card-base p-4 bg-white/5">
                          <h3 className="font-bold mb-3 flex items-center gap-2">
                            <Share2 className="w-4 h-4 text-viraly-orange" />
                            Connected Accounts
                          </h3>
                          {userDetail.social_accounts?.length > 0 ? (
                            <div className="space-y-2">
                              {userDetail.social_accounts.map((account, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                                  <div className="flex items-center gap-2">
                                    <span className="capitalize">{account.platform}</span>
                                    <span className="text-sm text-viraly-muted">{account.account_name}</span>
                                  </div>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${account.is_connected ? 'bg-viraly-success/20 text-viraly-success' : 'bg-red-500/20 text-red-400'}`}>
                                    {account.is_connected ? "Connected" : "Disconnected"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-viraly-muted text-sm">No social accounts connected</p>
                          )}
                        </div>
                      </div>

                      {/* User Profiles/Brands */}
                      <div className="card-base p-4 bg-white/5">
                        <h3 className="font-bold mb-3 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-viraly-orange" />
                          Profiles / Brands
                          <span className="text-xs text-viraly-muted ml-auto">
                            {userDetail.profiles?.length || 1} profile(s)
                          </span>
                        </h3>
                        {userDetail.profiles?.length > 0 ? (
                          <div className="grid grid-cols-2 gap-3">
                            {userDetail.profiles.map((profile, idx) => (
                              <div key={profile.id || idx} className="p-3 rounded-lg bg-white/5 border border-white/5">
                                <div className="flex items-center gap-3 mb-2">
                                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                                    {profile.logo_url ? (
                                      <img src={profile.logo_url} alt="" className="w-8 h-8 rounded object-cover" />
                                    ) : (
                                      <Building2 className="w-5 h-5 text-viraly-muted" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{profile.name}</p>
                                    <p className="text-xs text-viraly-muted">{profile.niche || "No niche set"}</p>
                                  </div>
                                  {profile.is_default && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-viraly-orange/20 text-viraly-orange ml-auto">Default</span>
                                  )}
                                </div>
                                <div className="text-xs text-viraly-muted grid grid-cols-2 gap-2">
                                  <span>Posts: {profile.posts_count || 0}</span>
                                  <span>Created: {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : "N/A"}</span>
                                </div>
                                {profile.social_accounts && Object.keys(profile.social_accounts).length > 0 && (
                                  <div className="mt-2 flex gap-1">
                                    {Object.entries(profile.social_accounts).map(([platform, data]) => (
                                      data.connected && (
                                        <span key={platform} className="text-xs px-2 py-0.5 rounded bg-white/10 capitalize">
                                          {platform}
                                        </span>
                                      )
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-viraly-muted text-sm">1 default profile (no additional profiles created)</p>
                        )}
                      </div>

                      {/* Recent Posts */}
                      <div className="card-base p-4 bg-white/5">
                        <h3 className="font-bold mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-viraly-orange" />
                          Recent Posts
                        </h3>
                        {userDetail.posts?.length > 0 ? (
                          <div className="space-y-2">
                            {userDetail.posts.slice(0, 5).map((post) => (
                              <div key={post.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                                <div className="flex items-center gap-3">
                                  <span className={`px-2 py-0.5 rounded text-xs capitalize ${
                                    post.platform === 'instagram' ? 'bg-pink-500/20 text-pink-400' :
                                    post.platform === 'facebook' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-purple-500/20 text-purple-400'
                                  }`}>{post.platform}</span>
                                  <span className="text-sm truncate max-w-[200px]">{post.caption}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    post.status === 'published' ? 'bg-viraly-success/20 text-viraly-success' :
                                    post.status === 'scheduled' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-white/10 text-viraly-muted'
                                  }`}>{post.status}</span>
                                  <span className="text-sm text-viraly-orange">Score: {post.viral_score}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-viraly-muted text-sm">No posts yet</p>
                        )}
                      </div>

                      {/* Transaction History */}
                      <div className="card-base p-4 bg-white/5">
                        <h3 className="font-bold mb-3 flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-viraly-orange" />
                          Transaction History
                        </h3>
                        {userDetail.transactions?.length > 0 ? (
                          <div className="space-y-2">
                            {userDetail.transactions.slice(0, 5).map((txn) => (
                              <div key={txn.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                                <div>
                                  <p className="font-medium">{txn.product}</p>
                                  <p className="text-xs text-viraly-muted">{new Date(txn.created_at).toLocaleDateString()}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-viraly-success">${txn.total_amount?.toFixed(2) || txn.subtotal?.toFixed(2)}</p>
                                  <p className={`text-xs ${txn.status === 'completed' ? 'text-viraly-success' : 'text-yellow-400'}`}>{txn.status}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-viraly-muted text-sm">No transactions yet</p>
                        )}
                      </div>

                      {/* Referrer Info */}
                      {userDetail.referrer && (
                        <div className="card-base p-4 bg-white/5">
                          <h3 className="font-bold mb-2">Referred By</h3>
                          <p className="text-sm">
                            <span className="text-viraly-orange">{userDetail.referrer.name}</span>
                            <span className="text-viraly-muted ml-2">({userDetail.referrer.email})</span>
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-center text-viraly-muted py-8">Failed to load user details</p>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Analytics Detail Modal */}
        <AnimatePresence>
          {analyticsDetail && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
              onClick={() => setAnalyticsDetail(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-viraly-card rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <h3 className="font-heading text-xl font-bold">
                    {analyticsDetail.type === 'top-content' && 'Top Performing Content'}
                    {analyticsDetail.type === 'revenue' && 'Revenue Breakdown'}
                    {analyticsDetail.type === 'engagement' && 'User Engagement Metrics'}
                    {analyticsDetail.type === 'platform' && 'Platform Performance'}
                    {analyticsDetail.type === 'overview' && 'Analytics Overview'}
                  </h3>
                  <button
                    onClick={() => setAnalyticsDetail(null)}
                    className="p-2 hover:bg-white/10 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                  {analyticsLoading ? (
                    <div className="text-center py-8">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto text-viraly-orange" />
                      <p className="text-viraly-muted mt-2">Loading analytics...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {analyticsDetail.type === 'top-content' && analyticsDetail.data?.top_content && (
                        <div className="space-y-3">
                          {analyticsDetail.data.top_content.map((post, idx) => (
                            <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-viraly-gradient flex items-center justify-center font-bold">
                                  #{idx + 1}
                                </div>
                                <div>
                                  <p className="font-medium truncate max-w-[300px]">{post.caption?.slice(0, 50)}...</p>
                                  <p className="text-xs text-viraly-muted">{post.user_name} • {post.platform}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-viraly-success">{post.viral_score || 0}</p>
                                <p className="text-xs text-viraly-muted">viral score</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {analyticsDetail.type === 'engagement' && analyticsDetail.data && (
                        <div className="grid grid-cols-2 gap-6">
                          <div className="card-base p-4 bg-white/5">
                            <h4 className="font-bold mb-3">User Tiers</h4>
                            {Object.entries(analyticsDetail.data.user_tiers || {}).map(([tier, count]) => (
                              <div key={tier} className="flex justify-between py-2 border-b border-white/5">
                                <span className="capitalize">{tier}</span>
                                <span className="font-bold">{count}</span>
                              </div>
                            ))}
                          </div>
                          <div className="card-base p-4 bg-white/5">
                            <h4 className="font-bold mb-3">Feature Usage</h4>
                            {Object.entries(analyticsDetail.data.feature_usage || {}).map(([feature, count]) => (
                              <div key={feature} className="flex justify-between py-2 border-b border-white/5">
                                <span className="capitalize">{feature.replace('_', ' ')}</span>
                                <span className="font-bold">{count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {analyticsDetail.type === 'revenue' && analyticsDetail.data && (
                        <div className="space-y-6">
                          <div className="card-base p-4 bg-white/5">
                            <h4 className="font-bold mb-3">Monthly Revenue (Last 6 Months)</h4>
                            <div className="space-y-2">
                              {analyticsDetail.data.monthly?.map((m, idx) => (
                                <div key={idx} className="flex justify-between py-2 border-b border-white/5">
                                  <span>{m.month}</span>
                                  <span className="font-bold text-viraly-success">${m.revenue.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="card-base p-4 bg-white/5">
                            <h4 className="font-bold mb-3">Revenue by Product</h4>
                            {Object.entries(analyticsDetail.data.by_product || {}).map(([product, data]) => (
                              <div key={product} className="flex justify-between py-2 border-b border-white/5">
                                <span>{product}</span>
                                <span className="font-bold text-viraly-orange">${data.revenue.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {(!analyticsDetail.data || Object.keys(analyticsDetail.data).length === 0) && (
                        <p className="text-center text-viraly-muted py-8">No data available yet</p>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Promotion Modal */}
        <AnimatePresence>
          {editPromoModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setEditPromoModal(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold">Edit Promotion: {editPromoModal.name}</h2>
                  <button onClick={() => setEditPromoModal(null)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Promo Code</label>
                    <Input
                      value={editPromoModal.name}
                      onChange={(e) => setEditPromoModal({...editPromoModal, name: e.target.value.toUpperCase()})}
                      className="input-base font-mono"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Discount Amount</label>
                      <Input
                        type="number"
                        value={editPromoModal.discount}
                        onChange={(e) => setEditPromoModal({...editPromoModal, discount: parseFloat(e.target.value)})}
                        className="input-base"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Type</label>
                      <select
                        value={editPromoModal.type}
                        onChange={(e) => setEditPromoModal({...editPromoModal, type: e.target.value})}
                        className="input-base w-full px-3 py-2 rounded-lg"
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount ($)</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Max Uses (blank = unlimited)</label>
                      <Input
                        type="number"
                        value={editPromoModal.maxUses || ""}
                        onChange={(e) => setEditPromoModal({...editPromoModal, maxUses: e.target.value ? parseInt(e.target.value) : null})}
                        className="input-base"
                        placeholder="Unlimited"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Expires (blank = never)</label>
                      <Input
                        type="date"
                        value={editPromoModal.expires || ""}
                        onChange={(e) => setEditPromoModal({...editPromoModal, expires: e.target.value || null})}
                        className="input-base"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Status</label>
                    <select
                      value={editPromoModal.status}
                      onChange={(e) => setEditPromoModal({...editPromoModal, status: e.target.value})}
                      className="input-base w-full px-3 py-2 rounded-lg"
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      className="btn-primary flex-1"
                      onClick={() => {
                        const updated = editablePromotions.map(p => p.id === editPromoModal.id ? editPromoModal : p);
                        setEditablePromotions(updated);
                        toast.success("Promotion updated successfully!");
                        setEditPromoModal(null);
                      }}
                    >
                      Save Changes
                    </Button>
                    <Button
                      className="btn-secondary text-red-400 hover:bg-red-500/20"
                      onClick={() => {
                        setEditablePromotions(editablePromotions.filter(p => p.id !== editPromoModal.id));
                        toast.success("Promotion deleted");
                        setEditPromoModal(null);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Email Template Modal */}
        <AnimatePresence>
          {editEmailTemplateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setEditEmailTemplateModal(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                    <Mail className="w-5 h-5 text-viraly-orange" />
                    Edit: {editEmailTemplateModal.name}
                  </h2>
                  <button onClick={() => setEditEmailTemplateModal(null)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Template Name</label>
                      <Input
                        value={editEmailTemplateModal.name}
                        onChange={(e) => setEditEmailTemplateModal({...editEmailTemplateModal, name: e.target.value})}
                        className="input-base"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Status</label>
                      <select
                        value={editEmailTemplateModal.status}
                        onChange={(e) => setEditEmailTemplateModal({...editEmailTemplateModal, status: e.target.value})}
                        className="input-base w-full px-3 py-2 rounded-lg"
                      >
                        <option value="active">Active</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Description</label>
                    <Input
                      value={editEmailTemplateModal.description}
                      onChange={(e) => setEditEmailTemplateModal({...editEmailTemplateModal, description: e.target.value})}
                      className="input-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Email Subject</label>
                    <Input
                      value={editEmailTemplateModal.subject}
                      onChange={(e) => setEditEmailTemplateModal({...editEmailTemplateModal, subject: e.target.value})}
                      className="input-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Email Body</label>
                    <Textarea
                      value={editEmailTemplateModal.body}
                      onChange={(e) => setEditEmailTemplateModal({...editEmailTemplateModal, body: e.target.value})}
                      className="input-base min-h-[200px] font-mono text-sm"
                      placeholder="Use {{name}}, {{email}}, {{plan}}, etc. for placeholders"
                    />
                  </div>
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm">
                    <p className="text-blue-400 font-medium mb-1">Available Variables:</p>
                    <p className="text-viraly-muted">{"{{name}}, {{email}}, {{plan}}, {{product}}, {{amount}}, {{days}}, {{link}}"}</p>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      className="btn-primary flex-1"
                      onClick={() => {
                        const updated = emailTemplates.map(t => t.id === editEmailTemplateModal.id ? editEmailTemplateModal : t);
                        setEmailTemplates(updated);
                        toast.success("Email template updated!");
                        setEditEmailTemplateModal(null);
                      }}
                    >
                      Save Template
                    </Button>
                    <Button className="btn-secondary" onClick={() => toast.info("Test email sent to admin@viraly.ca")}>
                      <Send className="w-4 h-4 mr-1" /> Test Send
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Create Email Template Modal */}
        <AnimatePresence>
          {createEmailTemplateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setCreateEmailTemplateModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                    <Mail className="w-5 h-5 text-viraly-orange" />
                    Create New Email Template
                  </h2>
                  <button onClick={() => setCreateEmailTemplateModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Template Name *</label>
                      <Input
                        value={newEmailTemplateData.name}
                        onChange={(e) => setNewEmailTemplateData({...newEmailTemplateData, name: e.target.value})}
                        className="input-base"
                        placeholder="e.g., Black Friday Promo"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Topic/Purpose *</label>
                      <Input
                        value={newEmailTemplateData.topic}
                        onChange={(e) => setNewEmailTemplateData({...newEmailTemplateData, topic: e.target.value})}
                        className="input-base"
                        placeholder="e.g., holiday promotion, feature announcement"
                      />
                    </div>
                  </div>
                  
                  {/* AI Suggestion Box */}
                  <div className="p-4 rounded-xl bg-viraly-orange/10 border border-viraly-orange/30">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-viraly-orange flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        AI Content Suggestion
                      </p>
                      <Button
                        size="sm"
                        className="btn-secondary text-xs"
                        disabled={!newEmailTemplateData.topic.trim() || generatingEmailContent}
                        onClick={async () => {
                          if (!newEmailTemplateData.topic.trim()) {
                            toast.error("Enter a topic first");
                            return;
                          }
                          setGeneratingEmailContent(true);
                          try {
                            const adminToken = localStorage.getItem("viraly_admin_token");
                            const res = await apiClient.post("/admin/communications/suggest-email", {
                              topic: newEmailTemplateData.topic,
                              template_name: newEmailTemplateData.name
                            }, {
                              headers: { Authorization: `Bearer ${adminToken}` }
                            });
                            setNewEmailTemplateData(prev => ({
                              ...prev,
                              subject: res.data.subject,
                              body: res.data.body
                            }));
                            toast.success("AI content generated!");
                          } catch (err) {
                            toast.error("Failed to generate content");
                          } finally {
                            setGeneratingEmailContent(false);
                          }
                        }}
                      >
                        {generatingEmailContent ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>Generate Content</>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-viraly-muted">
                      Type your topic above and click "Generate Content" to get AI-suggested subject line and body.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Email Subject *</label>
                    <Input
                      value={newEmailTemplateData.subject}
                      onChange={(e) => setNewEmailTemplateData({...newEmailTemplateData, subject: e.target.value})}
                      className="input-base"
                      placeholder="Enter subject line or use AI suggestion"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Email Body *</label>
                    <Textarea
                      value={newEmailTemplateData.body}
                      onChange={(e) => setNewEmailTemplateData({...newEmailTemplateData, body: e.target.value})}
                      className="input-base min-h-[200px] font-mono text-sm"
                      placeholder="Enter email body or use AI suggestion. Use {{name}}, {{email}}, etc. for personalization"
                    />
                  </div>
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm">
                    <p className="text-blue-400 font-medium mb-1">Available Variables:</p>
                    <p className="text-viraly-muted">{"{{name}}, {{email}}, {{plan}}, {{product}}, {{amount}}, {{days}}, {{link}}, {{company}}"}</p>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setCreateEmailTemplateModal(false);
                        setNewEmailTemplateData({ name: "", topic: "", subject: "", body: "", status: "active" });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="btn-primary flex-1"
                      disabled={!newEmailTemplateData.name.trim() || !newEmailTemplateData.subject.trim() || !newEmailTemplateData.body.trim()}
                      onClick={async () => {
                        try {
                          const adminToken = localStorage.getItem("viraly_admin_token");
                          const res = await apiClient.post("/admin/communications/templates", newEmailTemplateData, {
                            headers: { Authorization: `Bearer ${adminToken}` }
                          });
                          setEmailTemplates(prev => [...prev, res.data.template]);
                          toast.success("Email template created!");
                          setCreateEmailTemplateModal(false);
                          setNewEmailTemplateData({ name: "", topic: "", subject: "", body: "", status: "active" });
                        } catch (err) {
                          toast.error("Failed to create template");
                        }
                      }}
                    >
                      Create Template
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Loyalty Tier Modal */}
        <AnimatePresence>
          {editLoyaltyTierModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setEditLoyaltyTierModal(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-md overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold">Edit {editLoyaltyTierModal.tier} Tier</h2>
                  <button onClick={() => setEditLoyaltyTierModal(null)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Tier Name</label>
                    <Input
                      value={editLoyaltyTierModal.tier}
                      onChange={(e) => setEditLoyaltyTierModal({...editLoyaltyTierModal, tier: e.target.value})}
                      className="input-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Minimum Spend ($)</label>
                    <Input
                      type="number"
                      value={editLoyaltyTierModal.threshold}
                      onChange={(e) => setEditLoyaltyTierModal({...editLoyaltyTierModal, threshold: parseInt(e.target.value)})}
                      className="input-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Discount Percentage (%)</label>
                    <Input
                      type="number"
                      value={editLoyaltyTierModal.discount}
                      onChange={(e) => setEditLoyaltyTierModal({...editLoyaltyTierModal, discount: parseInt(e.target.value)})}
                      className="input-base"
                      min="0"
                      max="100"
                    />
                  </div>
                  <Button
                    className="btn-primary w-full"
                    onClick={() => handleSaveLoyaltyTier(editLoyaltyTierModal)}
                  >
                    Save Changes
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Platform Config Modal */}
        <AnimatePresence>
          {editPlatformConfig && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setEditPlatformConfig(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold capitalize">
                    Edit {editPlatformConfig.type.replace(/-/g, ' ')}
                  </h2>
                  <button onClick={() => setEditPlatformConfig(null)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                  <p className="text-sm text-viraly-muted mb-4">
                    Edit the values below. These costs are based on LLM/video provider pricing.
                  </p>
                  {editPlatformConfig.type === 'tax-rates' ? (
                    // Tax rates - nested structure
                    <div className="space-y-3">
                      {Object.entries(editPlatformConfig.data?.CA || {}).map(([prov, info]) => (
                        <div key={prov} className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
                          <span className="w-10 font-medium">{prov}</span>
                          <Input
                            type="number"
                            step="0.001"
                            value={(info.rate * 100).toFixed(3)}
                            onChange={(e) => {
                              const newRate = parseFloat(e.target.value) / 100;
                              setEditPlatformConfig({
                                ...editPlatformConfig,
                                data: {
                                  ...editPlatformConfig.data,
                                  CA: {
                                    ...editPlatformConfig.data.CA,
                                    [prov]: { ...info, rate: newRate }
                                  }
                                }
                              });
                            }}
                            className="input-base w-24"
                          />
                          <span className="text-sm text-viraly-muted">%</span>
                          <span className="text-xs text-viraly-muted ml-auto">{info.type}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Simple key-value pairs
                    <div className="space-y-3">
                      {Object.entries(editPlatformConfig.data || {}).map(([key, val]) => (
                        <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                          <span className="font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                          <Input
                            type="number"
                            value={val}
                            onChange={(e) => {
                              setEditPlatformConfig({
                                ...editPlatformConfig,
                                data: {
                                  ...editPlatformConfig.data,
                                  [key]: parseInt(e.target.value) || 0
                                }
                              });
                            }}
                            className="input-base w-24 text-center"
                            min="0"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="p-6 border-t border-white/10">
                  <Button
                    className="btn-primary w-full"
                    onClick={() => savePlatformConfig(editPlatformConfig.type, editPlatformConfig.data)}
                  >
                    Save Changes
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Revenue Detail Modal (Province Drill-Down) */}
        <AnimatePresence>
          {revenueDetailModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setRevenueDetailModal(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold flex items-center gap-3">
                    <span className="text-2xl">{revenueDetailModal.code === "INTL" ? "🌍" : "🇨🇦"}</span>
                    {revenueDetailModal.name} Revenue Details
                  </h2>
                  <button onClick={() => setRevenueDetailModal(null)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 space-y-6">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="card-base p-4 text-center bg-viraly-success/10">
                      <p className="text-viraly-muted text-xs mb-1">Total Revenue</p>
                      <p className="text-xl font-bold text-viraly-success">${revenueDetailModal.data?.total?.toLocaleString() || revenueDetailModal.apiData?.revenue?.toLocaleString()}</p>
                    </div>
                    <div className="card-base p-4 text-center bg-blue-500/10">
                      <p className="text-viraly-muted text-xs mb-1">This Month</p>
                      <p className="text-xl font-bold text-blue-400">${revenueDetailModal.data?.monthly?.toLocaleString() || 0}</p>
                    </div>
                    <div className="card-base p-4 text-center bg-purple-500/10">
                      <p className="text-viraly-muted text-xs mb-1">Today</p>
                      <p className="text-xl font-bold text-purple-400">${revenueDetailModal.data?.daily?.toLocaleString() || 0}</p>
                    </div>
                    <div className="card-base p-4 text-center bg-viraly-orange/10">
                      <p className="text-viraly-muted text-xs mb-1">YTD</p>
                      <p className="text-xl font-bold text-viraly-orange">${revenueDetailModal.data?.ytd?.toLocaleString() || 0}</p>
                    </div>
                  </div>

                  {/* Transaction Summary */}
                  <div className="card-base p-4 bg-white/5">
                    <h4 className="font-bold mb-3">Transaction Summary</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between py-2 border-b border-white/5">
                        <span className="text-viraly-muted">Total Transactions</span>
                        <span className="font-bold">{revenueDetailModal.data?.transactions || revenueDetailModal.apiData?.count}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/5">
                        <span className="text-viraly-muted">Tax Collected</span>
                        <span className="font-bold">${revenueDetailModal.apiData?.tax?.toFixed(2) || 0}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/5">
                        <span className="text-viraly-muted">Average Order Value</span>
                        <span className="font-bold">${((revenueDetailModal.apiData?.revenue || 0) / (revenueDetailModal.apiData?.count || 1)).toFixed(2)}</span>
                      </div>
                      {revenueDetailModal.code !== "INTL" && CANADIAN_TAX_RATES[revenueDetailModal.code] && (
                        <div className="flex justify-between py-2">
                          <span className="text-viraly-muted">Tax Rate</span>
                          <span className="font-bold">{CANADIAN_TAX_RATES[revenueDetailModal.code].rate}% ({CANADIAN_TAX_RATES[revenueDetailModal.code].type})</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-3">
                    <Button 
                      className="btn-secondary flex-1" 
                      onClick={() => {
                        setTransactionFilter({...transactionFilter, province: revenueDetailModal.code});
                        setActiveTab("transactions");
                        setRevenueDetailModal(null);
                      }}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Transactions
                    </Button>
                    <Button className="btn-secondary flex-1">
                      <Download className="w-4 h-4 mr-2" />
                      Export Report
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Create Promotion Modal */}
        <AnimatePresence>
          {createPromoModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setCreatePromoModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold">Create New Promotion</h2>
                  <button onClick={() => setCreatePromoModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const newPromo = {
                    id: Date.now().toString(),
                    name: formData.get("code").toUpperCase(),
                    discount: parseFloat(formData.get("discount")),
                    type: formData.get("type"),
                    maxUses: formData.get("maxUses") ? parseInt(formData.get("maxUses")) : null,
                    expires: formData.get("expires") || null,
                    status: "active",
                    uses: 0
                  };
                  setEditablePromotions([...editablePromotions, newPromo]);
                  toast.success("Promotion created!");
                  setCreatePromoModal(false);
                }} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Promo Code *</label>
                    <Input name="code" required className="input-base font-mono" placeholder="e.g., SUMMER25" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Discount *</label>
                      <Input name="discount" type="number" required className="input-base" placeholder="20" />
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Type *</label>
                      <select name="type" className="input-base w-full px-3 py-2 rounded-lg">
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed ($)</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Max Uses</label>
                      <Input name="maxUses" type="number" className="input-base" placeholder="Unlimited" />
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Expiry Date</label>
                      <Input name="expires" type="date" className="input-base" />
                    </div>
                  </div>
                  <Button type="submit" className="btn-primary w-full">Create Promotion</Button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Create Coupon Modal */}
        <AnimatePresence>
          {createCouponModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setCreateCouponModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold">Create New Coupon</h2>
                  <button onClick={() => setCreateCouponModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const couponData = {
                    code: formData.get("code").toUpperCase(),
                    discount_type: formData.get("discount_type"),
                    discount_value: parseFloat(formData.get("discount_value")),
                    max_uses: formData.get("max_uses") ? parseInt(formData.get("max_uses")) : null,
                    min_purchase: formData.get("min_purchase") ? parseFloat(formData.get("min_purchase")) : null,
                    expires_at: formData.get("expires_at") || null,
                    is_active: true
                  };
                  await handleCreateCoupon(couponData);
                }} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Coupon Code *</label>
                    <Input name="code" required className="input-base font-mono" placeholder="e.g., SAVE20" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Discount Value *</label>
                      <Input name="discount_value" type="number" required className="input-base" placeholder="20" />
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Discount Type *</label>
                      <select name="discount_type" className="input-base w-full px-3 py-2 rounded-lg">
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount ($)</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Max Uses</label>
                      <Input name="max_uses" type="number" className="input-base" placeholder="Unlimited" />
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Min Purchase ($)</label>
                      <Input name="min_purchase" type="number" className="input-base" placeholder="0" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Expiry Date</label>
                    <Input name="expires_at" type="datetime-local" className="input-base" />
                  </div>
                  <Button type="submit" className="btn-primary w-full">Create Coupon</Button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Create Announcement Modal */}
        <AnimatePresence>
          {createAnnouncementModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setCreateAnnouncementModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-viraly-card rounded-2xl w-full max-w-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold">Create Announcement</h2>
                  <button onClick={() => setCreateAnnouncementModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const announcementData = {
                    title: formData.get("title"),
                    message: formData.get("message"),
                    type: formData.get("type"),
                    priority: formData.get("priority"),
                    display_location: formData.get("display_location"),
                    target_audience: formData.get("target_audience") || "all",
                    is_active: true,
                    start_date: formData.get("start_date") || null,
                    end_date: formData.get("end_date") || null
                  };
                  await handleCreateAnnouncement(announcementData);
                }} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Title *</label>
                    <Input name="title" required className="input-base" placeholder="Announcement Title" />
                  </div>
                  <div>
                    <label className="block text-sm text-viraly-muted mb-2">Message *</label>
                    <textarea name="message" required className="input-base w-full px-3 py-2 rounded-lg h-24" placeholder="Your announcement message..." />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Type</label>
                      <select name="type" className="input-base w-full px-3 py-2 rounded-lg">
                        <option value="general">General</option>
                        <option value="feature">New Feature</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="policy_update">Policy Update</option>
                        <option value="promotion">Promotion</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Priority</label>
                      <select name="priority" className="input-base w-full px-3 py-2 rounded-lg">
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Display Location</label>
                      <select name="display_location" className="input-base w-full px-3 py-2 rounded-lg">
                        <option value="dashboard">Dashboard</option>
                        <option value="all">All Pages</option>
                        <option value="login">Login Page</option>
                        <option value="billing">Billing</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Target Audience</label>
                      <select name="target_audience" className="input-base w-full px-3 py-2 rounded-lg">
                        <option value="all">All Users</option>
                        <option value="free">Free Users</option>
                        <option value="paid">Paid Users</option>
                        <option value="pro">Pro Users</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">Start Date</label>
                      <Input name="start_date" type="datetime-local" className="input-base" />
                    </div>
                    <div>
                      <label className="block text-sm text-viraly-muted mb-2">End Date</label>
                      <Input name="end_date" type="datetime-local" className="input-base" />
                    </div>
                  </div>
                  <Button type="submit" className="btn-primary w-full">Create Announcement</Button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        </div> {/* Close p-8 wrapper */}
      </main>
        </>
      )}
      
      {/* Admin Help Modal */}
      <AdminHelpModal
        isOpen={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        section={helpSection}
        adminToken={localStorage.getItem("viraly_admin_token")}
      />
      
      {/* Suspend User Modal */}
      {suspendUserModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-viraly-card rounded-2xl w-full max-w-md p-6">
            <div className="text-center mb-6">
              <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                suspendUserModal.status !== 'suspended' ? 'bg-red-500/20' : 'bg-green-500/20'
              }`}>
                {suspendUserModal.status !== 'suspended' ? (
                  <PauseCircle className="w-8 h-8 text-red-400" />
                ) : (
                  <PlayCircle className="w-8 h-8 text-green-400" />
                )}
              </div>
              <h3 className="font-heading text-xl font-bold">
                {suspendUserModal.status !== 'suspended' ? 'Suspend' : 'Reactivate'} User?
              </h3>
              <p className="text-viraly-muted mt-2">
                {suspendUserModal.status !== 'suspended' 
                  ? `This will suspend ${suspendUserModal.name || suspendUserModal.email}'s account. They won't be able to log in or use the platform.`
                  : `This will restore ${suspendUserModal.name || suspendUserModal.email}'s account access.`
                }
              </p>
            </div>
            
            <div className="p-4 rounded-xl bg-white/5 mb-6">
              <p className="text-sm">
                <strong>User:</strong> {suspendUserModal.name}<br/>
                <strong>Email:</strong> {suspendUserModal.email}<br/>
                <strong>Plan:</strong> {suspendUserModal.plan || 'Free'}<br/>
                <strong>Posts:</strong> {suspendUserModal.posts_count || 0}
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setSuspendUserModal(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const adminToken = localStorage.getItem("viraly_admin_token");
                    const newStatus = suspendUserModal.status === 'suspended' ? 'active' : 'suspended';
                    await apiClient.put(`/admin/users/${suspendUserModal.id}/status`, {
                      status: newStatus
                    }, {
                      headers: { Authorization: `Bearer ${adminToken}` }
                    });
                    toast.success(newStatus === 'suspended' ? "User suspended!" : "User reactivated!");
                    setSuspendUserModal(null);
                    fetchAdminData();
                  } catch (err) {
                    toast.error("Failed to update user status");
                  }
                }}
                className={`flex-1 ${suspendUserModal.status !== 'suspended' ? 'bg-red-500 hover:bg-red-600' : 'btn-primary'}`}
              >
                {suspendUserModal.status !== 'suspended' ? 'Suspend User' : 'Reactivate User'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Change Plan Modal */}
      {changePlanModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-viraly-card rounded-2xl w-full max-w-md p-6">
            <h3 className="font-heading text-xl font-bold mb-4">Change User Plan</h3>
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-sm">
                  <strong>User:</strong> {changePlanModal.name}<br/>
                  <strong>Email:</strong> {changePlanModal.email}<br/>
                  <strong>Current Plan:</strong> <span className="text-viraly-orange capitalize">{changePlanModal.plan || 'Free'}</span>
                </p>
              </div>
              <div>
                <label className="text-sm text-viraly-muted mb-2 block">New Plan</label>
                <select
                  id="new-plan-select"
                  defaultValue={changePlanModal.plan || 'free'}
                  className="input-base w-full"
                >
                  <option value="free">Free</option>
                  <option value="starter">Starter ($29/mo)</option>
                  <option value="growth">Growth ($49/mo)</option>
                  <option value="pro">Pro ($99/mo)</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-viraly-muted mb-2 block">Reason (optional)</label>
                <Textarea
                  id="plan-change-reason"
                  placeholder="e.g., Customer support credit, promotional upgrade..."
                  className="input-base"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setChangePlanModal(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const adminToken = localStorage.getItem("viraly_admin_token");
                    const newPlan = document.getElementById('new-plan-select').value;
                    const reason = document.getElementById('plan-change-reason').value;
                    await apiClient.put(`/admin/users/${changePlanModal.id}/plan`, {
                      plan: newPlan,
                      reason: reason
                    }, {
                      headers: { Authorization: `Bearer ${adminToken}` }
                    });
                    toast.success(`Plan changed to ${newPlan}!`);
                    setChangePlanModal(null);
                    fetchAdminData();
                  } catch (err) {
                    toast.error("Failed to change plan");
                  }
                }}
                className="flex-1 btn-primary"
              >
                Change Plan
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Transaction Detail Modal - Corrected */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedTransaction(null)}>
          <div className="bg-viraly-card rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold">Transaction Details</h3>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => {
                    const details = `Transaction: ${selectedTransaction.id || selectedTransaction.transaction_id}
Status: ${selectedTransaction.status}
Customer: ${selectedTransaction.user_name || 'N/A'} (${selectedTransaction.user_email})
Product: ${selectedTransaction.product_type || selectedTransaction.type || 'Subscription'}
Subtotal: $${(selectedTransaction.subtotal || selectedTransaction.amount || 0).toFixed(2)}
Tax: $${(selectedTransaction.tax_amount || 0).toFixed(2)} (${selectedTransaction.tax_rate || 0}%)
Gateway Fee: $${(selectedTransaction.gateway_fee || 0).toFixed(2)} (Paid by Customer)
Total Charged: $${(selectedTransaction.total_amount || selectedTransaction.total || 0).toFixed(2)}
Payment: ${selectedTransaction.gateway || 'PayPal'}
Date: ${selectedTransaction.created_at ? new Date(selectedTransaction.created_at).toLocaleString() : 'N/A'}`;
                    navigator.clipboard.writeText(details);
                    toast.success("Transaction details copied!");
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg" title="Copy details"
                >
                  <Copy className="w-5 h-5" />
                </button>
                <button onClick={() => setSelectedTransaction(null)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Transaction ID & Status */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                <div>
                  <p className="text-xs text-viraly-muted">Transaction ID</p>
                  <p className="font-mono text-sm">{selectedTransaction.id || selectedTransaction.transaction_id}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm ${
                  selectedTransaction.status === 'completed' || selectedTransaction.status === 'succeeded' ? 'bg-green-500/20 text-green-400' :
                  selectedTransaction.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  selectedTransaction.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                  'bg-white/10'
                }`}>{selectedTransaction.status}</span>
              </div>
              
              {/* Customer Info */}
              <div className="p-4 rounded-xl bg-white/5">
                <h4 className="font-medium mb-3 text-viraly-orange">Customer Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><span className="text-viraly-muted">Name:</span> {selectedTransaction.user_name || 'N/A'}</p>
                  <p><span className="text-viraly-muted">Email:</span> {selectedTransaction.user_email || 'N/A'}</p>
                  <p><span className="text-viraly-muted">User ID:</span> <span className="font-mono text-xs">{selectedTransaction.user_id || 'N/A'}</span></p>
                  <p><span className="text-viraly-muted">Region:</span> {selectedTransaction.province ? `🇨🇦 ${selectedTransaction.province}` : '🌍 International'}</p>
                </div>
              </div>
              
              {/* Payment Breakdown - CORRECTED for Viraly */}
              <div className="p-4 rounded-xl bg-white/5">
                <h4 className="font-medium mb-3 text-viraly-orange">Payment Breakdown</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-viraly-muted">Base Price:</span>
                    <span>${(selectedTransaction.subtotal || selectedTransaction.amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-viraly-muted">Tax ({selectedTransaction.tax_rate || 0}% {selectedTransaction.province && CANADIAN_TAX_RATES[selectedTransaction.province]?.type}):</span>
                    <span>${(selectedTransaction.tax_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-blue-400">
                    <span>Gateway Fee (2.9% + $0.30 PayPal):</span>
                    <span>${(selectedTransaction.gateway_fee || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t border-white/10 pt-2 font-bold text-lg">
                    <span>Total Charged:</span>
                    <span className="text-viraly-orange">${(selectedTransaction.total_amount || selectedTransaction.total || 0).toFixed(2)}</span>
                  </div>
                </div>
                <div className="mt-3 p-3 rounded bg-green-500/10 border border-green-500/20">
                  <p className="text-sm text-green-400 font-medium">Viraly Receives: ${((selectedTransaction.subtotal || 0) + (selectedTransaction.tax_amount || 0)).toFixed(2)}</p>
                  <p className="text-xs text-viraly-muted mt-1">Customer pays the gateway fee. Tax is remitted to CRA.</p>
                </div>
              </div>
              
              {/* Payment Method */}
              <div className="p-4 rounded-xl bg-white/5">
                <h4 className="font-medium mb-3 text-viraly-orange">Payment Gateway</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><span className="text-viraly-muted">Gateway:</span> {selectedTransaction.gateway || 'PayPal'}</p>
                  <p><span className="text-viraly-muted">Method:</span> {selectedTransaction.payment_method || 'PayPal'}</p>
                  {selectedTransaction.paypal_order_id && (
                    <p className="col-span-2"><span className="text-viraly-muted">PayPal Order:</span> <span className="font-mono text-xs">{selectedTransaction.paypal_order_id}</span></p>
                  )}
                  {selectedTransaction.etransfer_reference && (
                    <p className="col-span-2"><span className="text-viraly-muted">E-Transfer Ref:</span> {selectedTransaction.etransfer_reference}</p>
                  )}
                </div>
              </div>
              
              {/* Timestamps */}
              <div className="p-4 rounded-xl bg-white/5">
                <h4 className="font-medium mb-3 text-viraly-orange">Timestamps</h4>
                {selectedTransaction.created_at && (() => {
                  const date = new Date(selectedTransaction.created_at);
                  return (
                    <div className="space-y-1 text-sm">
                      <p><span className="text-viraly-muted">Created:</span> {date.toLocaleString()} (Local)</p>
                      <p className="text-xs text-viraly-muted">
                        {date.toLocaleString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', hour12: true })} GMT | 
                        {date.toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: true })} EST
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Feature Toggle OFF Announcement Modal */}
      {featureToggleAnnouncement && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-viraly-card rounded-2xl w-full max-w-md p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-yellow-500/20 mx-auto mb-4 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-yellow-400" />
              </div>
              <h3 className="font-heading text-xl font-bold">Disable Feature?</h3>
              <p className="text-viraly-muted mt-2">
                You are about to disable <strong>{featureToggleAnnouncement.feature.name}</strong>. 
                Users will no longer have access to this feature.
              </p>
            </div>
            
            <div className="p-4 rounded-xl bg-white/5 mb-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="send-announcement-checkbox"
                  className="w-4 h-4 rounded"
                />
                <div>
                  <p className="font-medium text-sm">Create announcement for users</p>
                  <p className="text-xs text-viraly-muted">Notify users about this change via Promotions tab</p>
                </div>
              </label>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setFeatureToggleAnnouncement(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-yellow-500 hover:bg-yellow-600"
                onClick={async () => {
                  const { feature, newValue } = featureToggleAnnouncement;
                  const sendAnnouncement = document.getElementById('send-announcement-checkbox')?.checked;
                  
                  setFeatureToggles(prev => ({ ...prev, [feature.key]: newValue }));
                  try {
                    const adminToken = localStorage.getItem("viraly_admin_token");
                    await apiClient.put("/admin/feature-toggles", {
                      [feature.key]: newValue,
                      create_announcement: sendAnnouncement,
                      feature_name: feature.name
                    }, { headers: { Authorization: `Bearer ${adminToken}` }});
                    toast.success(`${feature.name} disabled${sendAnnouncement ? ' - Announcement created' : ''}`);
                  } catch (err) {
                    setFeatureToggles(prev => ({ ...prev, [feature.key]: !newValue }));
                    toast.error("Failed to update feature");
                  }
                  setFeatureToggleAnnouncement(null);
                }}
              >
                Disable Feature
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Partner Modal */}
      {showAddPartnerModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-viraly-card rounded-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Handshake className="w-6 h-6 text-blue-400" />
                Add New Partner
              </h3>
              <button onClick={() => setShowAddPartnerModal(false)} className="text-viraly-muted hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Company Name *</label>
                <Input
                  value={newPartnerData.company_name}
                  onChange={(e) => setNewPartnerData({ ...newPartnerData, company_name: e.target.value })}
                  placeholder="Partner Company Inc."
                />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Contact Name *</label>
                <Input
                  value={newPartnerData.contact_name}
                  onChange={(e) => setNewPartnerData({ ...newPartnerData, contact_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Email *</label>
                <Input
                  type="email"
                  value={newPartnerData.email}
                  onChange={(e) => setNewPartnerData({ ...newPartnerData, email: e.target.value })}
                  placeholder="partner@company.com"
                />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Phone</label>
                <Input
                  value={newPartnerData.phone}
                  onChange={(e) => setNewPartnerData({ ...newPartnerData, phone: e.target.value })}
                  placeholder="+1 123 456 7890"
                />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Country</label>
                <Input
                  value={newPartnerData.country}
                  onChange={(e) => setNewPartnerData({ ...newPartnerData, country: e.target.value })}
                  placeholder="CA"
                />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Notes</label>
                <Textarea
                  value={newPartnerData.notes}
                  onChange={(e) => setNewPartnerData({ ...newPartnerData, notes: e.target.value })}
                  placeholder="Additional notes about this partner..."
                  rows={3}
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setShowAddPartnerModal(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 btn-primary"
                onClick={async () => {
                  if (!newPartnerData.company_name || !newPartnerData.contact_name || !newPartnerData.email) {
                    toast.error("Please fill all required fields");
                    return;
                  }
                  try {
                    await apiClient.post("/b2b/partners", newPartnerData, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                    toast.success("Partner created successfully");
                    setShowAddPartnerModal(false);
                    setNewPartnerData({ company_name: "", contact_name: "", email: "", phone: "", country: "CA", territory: [], notes: "" });
                    fetchAdminData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to create partner");
                  }
                }}
              >
                Create Partner
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Add Enterprise Modal */}
      {showAddEnterpriseModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-viraly-card rounded-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Briefcase className="w-6 h-6 text-purple-400" />
                Add New Enterprise
              </h3>
              <button onClick={() => setShowAddEnterpriseModal(false)} className="text-viraly-muted hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Company Name *</label>
                <Input
                  value={newEnterpriseData.company_name}
                  onChange={(e) => setNewEnterpriseData({ ...newEnterpriseData, company_name: e.target.value })}
                  placeholder="Enterprise Corp."
                />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Contact Name *</label>
                <Input
                  value={newEnterpriseData.contact_name}
                  onChange={(e) => setNewEnterpriseData({ ...newEnterpriseData, contact_name: e.target.value })}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Email *</label>
                <Input
                  type="email"
                  value={newEnterpriseData.email}
                  onChange={(e) => setNewEnterpriseData({ ...newEnterpriseData, email: e.target.value })}
                  placeholder="contact@enterprise.com"
                />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Plan</label>
                <select
                  value={newEnterpriseData.plan}
                  onChange={(e) => setNewEnterpriseData({ ...newEnterpriseData, plan: e.target.value })}
                  className="w-full p-2 rounded-lg bg-white/5 border border-white/10 text-viraly-light"
                >
                  <option value="starter">Starter</option>
                  <option value="growth">Growth</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Referred by Partner (optional)</label>
                <select
                  value={newEnterpriseData.partner_id}
                  onChange={(e) => setNewEnterpriseData({ ...newEnterpriseData, partner_id: e.target.value })}
                  className="w-full p-2 rounded-lg bg-white/5 border border-white/10 text-viraly-light"
                >
                  <option value="">Direct Client (no partner)</option>
                  {b2bPartners.filter(p => p.status === 'approved').map(partner => (
                    <option key={partner.id} value={partner.id}>{partner.company_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Notes</label>
                <Textarea
                  value={newEnterpriseData.notes}
                  onChange={(e) => setNewEnterpriseData({ ...newEnterpriseData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setShowAddEnterpriseModal(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 btn-primary"
                onClick={async () => {
                  if (!newEnterpriseData.company_name || !newEnterpriseData.contact_name || !newEnterpriseData.email) {
                    toast.error("Please fill all required fields");
                    return;
                  }
                  try {
                    const payload = { ...newEnterpriseData };
                    if (!payload.partner_id) delete payload.partner_id;
                    await apiClient.post("/b2b/enterprises", payload, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                    toast.success("Enterprise created successfully");
                    setShowAddEnterpriseModal(false);
                    setNewEnterpriseData({ company_name: "", contact_name: "", email: "", phone: "", country: "CA", partner_id: "", plan: "starter", notes: "" });
                    fetchAdminData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to create enterprise");
                  }
                }}
              >
                Create Enterprise
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Vira Conversation Detail Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-viraly-card rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <MessageSquare className="w-6 h-6 text-purple-400" />
                  Vira Conversation
                </h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm text-viraly-light">{selectedConversation.enterprise_name}</span>
                  {selectedConversation.escalated && (
                    <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Escalated</span>
                  )}
                </div>
              </div>
              <button onClick={() => setSelectedConversation(null)} className="text-viraly-muted hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Timestamp Legend */}
            <div className="px-6 py-2 bg-white/5 text-xs text-viraly-muted flex items-center gap-4">
              <span>Timestamps shown in: Your local time</span>
              <span className="text-viraly-light">|</span>
              <span>EST: {new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' })} EST</span>
              <span className="text-viraly-light">|</span>
              <span>UTC: {new Date().toLocaleString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' })} UTC</span>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-auto p-6 space-y-4">
              {(selectedConversation.messages || []).map((msg, idx) => {
                const msgDate = new Date(msg.timestamp);
                const localTime = msgDate.toLocaleString();
                const estTime = msgDate.toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: true });
                
                return (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] ${
                      msg.role === 'user' 
                        ? 'bg-purple-500/20 text-viraly-light' 
                        : msg.role === 'system'
                        ? 'bg-yellow-500/20 text-yellow-300'
                        : 'bg-white/10 text-viraly-light'
                    } rounded-xl p-4`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-viraly-muted">
                          {msg.role === 'user' ? 'Enterprise Tech' : msg.role === 'system' ? 'System' : 'Vira AI'}
                        </span>
                        <span className="text-[10px] text-viraly-muted">
                          {localTime} (EST: {estTime})
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                );
              })}
              {(selectedConversation.messages || []).length === 0 && (
                <p className="text-center text-viraly-muted text-sm">No messages in this conversation</p>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-white/10 bg-white/5 flex items-center justify-between">
              <div className="text-xs text-viraly-muted">
                {selectedConversation.messages?.length || 0} messages • 
                Last activity: {new Date(selectedConversation.last_message_at).toLocaleString()}
              </div>
              <Button variant="outline" size="sm" onClick={() => setSelectedConversation(null)}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Edit Enterprise Plan Modal */}
      {editingEnterprisePlan && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-viraly-card rounded-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Briefcase className="w-6 h-6 text-purple-400" />
                Edit {editingEnterprisePlan.name} Plan
              </h3>
              <button onClick={() => setEditingEnterprisePlan(null)} className="text-viraly-muted hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              try {
                await apiClient.put(`/b2b/plans/${editingEnterprisePlan.key}`, {
                  plan_id: editingEnterprisePlan.key,
                  price_monthly_cad: parseFloat(formData.get('price_monthly_cad')),
                  monthly_post_credits: parseInt(formData.get('monthly_post_credits')),
                  monthly_video_credits: parseInt(formData.get('monthly_video_credits')),
                  max_merchants: parseInt(formData.get('max_merchants')),
                  annual_discount_percent: parseFloat(formData.get('annual_discount_percent'))
                }, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                toast.success(`${editingEnterprisePlan.name} plan updated`);
                setEditingEnterprisePlan(null);
                // Refresh plans
                const plansRes = await apiClient.get("/b2b/plans", { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                setB2bPlans(plansRes.data.plans || {});
              } catch (err) {
                toast.error("Failed to update plan");
              }
            }} className="space-y-4">
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Monthly Price (CAD)</label>
                <Input name="price_monthly_cad" type="number" defaultValue={editingEnterprisePlan.price_monthly_cad || editingEnterprisePlan.price_cad} className="input-base" />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Post Credits / Month</label>
                <Input name="monthly_post_credits" type="number" defaultValue={editingEnterprisePlan.monthly_post_credits || editingEnterprisePlan.monthly_posts} className="input-base" />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Video Credits / Month</label>
                <Input name="monthly_video_credits" type="number" defaultValue={editingEnterprisePlan.monthly_video_credits || editingEnterprisePlan.monthly_videos} className="input-base" />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Max Merchants (-1 for unlimited)</label>
                <Input name="max_merchants" type="number" defaultValue={editingEnterprisePlan.max_merchants} className="input-base" />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Annual Discount %</label>
                <Input name="annual_discount_percent" type="number" step="0.5" defaultValue={editingEnterprisePlan.annual_discount_percent || 0} className="input-base" />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="btn-primary flex-1">Save Changes</Button>
                <Button type="button" variant="outline" onClick={() => setEditingEnterprisePlan(null)}>Cancel</Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      
      {/* Edit Partner Tier Modal */}
      {editingPartnerTier && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-viraly-card rounded-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Handshake className="w-6 h-6 text-blue-400" />
                Edit {editingPartnerTier.name}
              </h3>
              <button onClick={() => setEditingPartnerTier(null)} className="text-viraly-muted hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              try {
                await apiClient.put(`/b2b/partner-tiers/${editingPartnerTier.key}`, {
                  tier_id: editingPartnerTier.key,
                  commission_rate: parseFloat(formData.get('commission_rate')) / 100,
                  min_active_enterprises: parseInt(formData.get('min_active_enterprises')),
                  min_revenue_12m_cad: parseFloat(formData.get('min_revenue_12m_cad'))
                }, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                toast.success(`${editingPartnerTier.name} updated`);
                setEditingPartnerTier(null);
                // Refresh tiers
                const tiersRes = await apiClient.get("/b2b/partner-tiers", { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                setPartnerTiers(tiersRes.data.tiers || {});
              } catch (err) {
                toast.error("Failed to update tier");
              }
            }} className="space-y-4">
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Commission Rate (%)</label>
                <Input name="commission_rate" type="number" step="0.5" defaultValue={(editingPartnerTier.commission_rate * 100).toFixed(1)} className="input-base" />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Min Active Enterprises</label>
                <Input name="min_active_enterprises" type="number" defaultValue={editingPartnerTier.min_active_enterprises} className="input-base" />
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Min 12-Month Revenue (CAD)</label>
                <Input name="min_revenue_12m_cad" type="number" defaultValue={editingPartnerTier.min_revenue_12m_cad} className="input-base" />
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-viraly-muted mb-2">Current Benefits:</p>
                <ul className="text-xs text-viraly-light space-y-1">
                  {editingPartnerTier.benefits?.map((b, i) => (
                    <li key={i}>• {b}</li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="btn-primary flex-1">Save Changes</Button>
                <Button type="button" variant="outline" onClick={() => setEditingPartnerTier(null)}>Cancel</Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
      
      {/* Mark Partner Payout as Paid Modal */}
      {selectedPartnerPayout && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-viraly-card rounded-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Banknote className="w-6 h-6 text-green-400" />
                Mark Payout as Paid
              </h3>
              <button onClick={() => setSelectedPartnerPayout(null)} className="text-viraly-muted hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
              <p className="text-lg font-bold text-green-400">${selectedPartnerPayout.amount_cad?.toFixed(2)} CAD</p>
              <p className="text-sm text-viraly-light">{selectedPartnerPayout.partner_company}</p>
              <p className="text-xs text-viraly-muted">{selectedPartnerPayout.payout_method}: {selectedPartnerPayout.paypal_email}</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-viraly-muted block mb-1">PayPal Transaction ID *</label>
                <Input 
                  value={partnerPayoutTxRef} 
                  onChange={(e) => setPartnerPayoutTxRef(e.target.value)}
                  placeholder="e.g., 1AB23456CD789012E"
                  className="input-base" 
                />
                <p className="text-xs text-viraly-muted mt-1">Enter the transaction ID after manually sending via PayPal</p>
              </div>
              <div>
                <label className="text-sm text-viraly-muted block mb-1">Admin Notes (optional)</label>
                <Input 
                  value={partnerPayoutNotes} 
                  onChange={(e) => setPartnerPayoutNotes(e.target.value)}
                  placeholder="Any notes about this payout"
                  className="input-base" 
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button 
                  className="btn-primary flex-1"
                  disabled={!partnerPayoutTxRef}
                  onClick={async () => {
                    try {
                      await apiClient.put(`/b2b/partner-payouts/${selectedPartnerPayout.id}`, {
                        action: "mark_paid",
                        transaction_reference: partnerPayoutTxRef,
                        admin_notes: partnerPayoutNotes
                      }, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                      toast.success("Payout marked as paid!");
                      setSelectedPartnerPayout(null);
                      setPartnerPayoutTxRef("");
                      setPartnerPayoutNotes("");
                      // Refresh
                      const res = await apiClient.get("/b2b/partner-payouts", { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                      setPartnerPayouts(res.data.payouts || []);
                      setPartnerPayoutSummary(res.data.summary || {});
                    } catch (err) {
                      toast.error("Failed to mark as paid");
                    }
                  }}
                >
                  Confirm Payment
                </Button>
                <Button type="button" variant="outline" onClick={() => setSelectedPartnerPayout(null)}>Cancel</Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Enterprise Detail Modal */}
      {selectedB2bEnterprise && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-viraly-card border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-white/10 sticky top-0 bg-viraly-card z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{selectedB2bEnterprise.company_name}</h2>
                    <p className="text-sm text-viraly-muted">{selectedB2bEnterprise.email}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedB2bEnterprise(null)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status & Onboarding Progress */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card-base p-4">
                  <p className="text-sm text-viraly-muted mb-1">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    selectedB2bEnterprise.status === 'active' ? 'bg-green-500/20 text-green-400' :
                    selectedB2bEnterprise.status === 'sandbox' ? 'bg-yellow-500/20 text-yellow-400' :
                    selectedB2bEnterprise.status === 'pending' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {selectedB2bEnterprise.status?.toUpperCase() || 'INQUIRY'}
                  </span>
                </div>
                <div className="card-base p-4">
                  <p className="text-sm text-viraly-muted mb-1">Plan</p>
                  <p className="font-bold text-purple-400">{selectedB2bEnterprise.plan || 'Not Set'}</p>
                </div>
                <div className="card-base p-4">
                  <p className="text-sm text-viraly-muted mb-1">Merchants</p>
                  <p className="font-bold text-blue-400">{selectedB2bEnterprise.merchant_count || 0}</p>
                </div>
                <div className="card-base p-4">
                  <p className="text-sm text-viraly-muted mb-1">Partner</p>
                  <p className="font-bold text-orange-400">{selectedB2bEnterprise.partner_code || 'Direct'}</p>
                </div>
              </div>
              
              {/* Contact Information */}
              <div className="card-base p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" /> Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-viraly-muted">Contact Name</p>
                    <p className="font-medium">{selectedB2bEnterprise.contact_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-viraly-muted">Phone</p>
                    <p className="font-medium">{selectedB2bEnterprise.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-viraly-muted">Industry</p>
                    <p className="font-medium">{selectedB2bEnterprise.industry || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-viraly-muted">Expected Merchants</p>
                    <p className="font-medium">{selectedB2bEnterprise.expected_merchants || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              {/* API Keys */}
              <div className="card-base p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4" /> API Credentials
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <p className="text-sm text-viraly-muted">Sandbox API Key</p>
                      <code className="text-xs text-yellow-400">{selectedB2bEnterprise.sandbox_api_key || 'Not generated'}</code>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs ${selectedB2bEnterprise.sandbox_api_key_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {selectedB2bEnterprise.sandbox_api_key_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <p className="text-sm text-viraly-muted">Production API Key</p>
                      <code className="text-xs text-green-400">{selectedB2bEnterprise.production_api_key || 'Not generated'}</code>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs ${selectedB2bEnterprise.production_api_key_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {selectedB2bEnterprise.production_api_key_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Onboarding Documents */}
              <div className="card-base p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Onboarding Documents
                </h3>
                <div className="space-y-2">
                  {selectedB2bEnterprise.documents?.length > 0 ? (
                    selectedB2bEnterprise.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white/5 rounded-lg text-sm">
                        <span>{doc.name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          doc.status === 'signed' ? 'bg-green-500/20 text-green-400' :
                          doc.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>{doc.status}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-viraly-muted">No documents uploaded yet</p>
                  )}
                </div>
              </div>
              
              {/* Login URL */}
              <div className="card-base p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Link2 className="w-4 h-4" /> Enterprise Portal Access
                </h3>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-white/5 rounded text-sm text-viraly-muted">
                    {window.location.origin}/enterprise/{selectedB2bEnterprise.slug || selectedB2bEnterprise.id}
                  </code>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/enterprise/${selectedB2bEnterprise.slug || selectedB2bEnterprise.id}`);
                      toast.success("URL copied!");
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3 pt-4">
                {selectedB2bEnterprise.status === 'pending' && (
                  <Button 
                    className="btn-primary"
                    onClick={async () => {
                      try {
                        const adminToken = localStorage.getItem("viraly_admin_token");
                        await apiClient.put(`/b2b/enterprises/${selectedB2bEnterprise.id}/activate`, {}, {
                          headers: { Authorization: `Bearer ${adminToken}` }
                        });
                        toast.success("Enterprise activated!");
                        setSelectedB2bEnterprise(null);
                        // Refresh enterprises
                        const res = await apiClient.get("/b2b/enterprises", { headers: { Authorization: `Bearer ${adminToken}` } });
                        setB2bEnterprises(res.data.enterprises || res.data || []);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to activate");
                      }
                    }}
                  >
                    <Check className="w-4 h-4 mr-2" /> Activate Enterprise
                  </Button>
                )}
                {!selectedB2bEnterprise.production_api_key_active && selectedB2bEnterprise.status === 'sandbox' && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={async () => {
                      try {
                        const adminToken = localStorage.getItem("viraly_admin_token");
                        await apiClient.post(`/b2b/enterprises/${selectedB2bEnterprise.id}/approve-production`, {}, {
                          headers: { Authorization: `Bearer ${adminToken}` }
                        });
                        toast.success("Production API key approved!");
                        setSelectedB2bEnterprise(null);
                        // Refresh
                        const res = await apiClient.get("/b2b/enterprises", { headers: { Authorization: `Bearer ${adminToken}` } });
                        setB2bEnterprises(res.data.enterprises || res.data || []);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to approve");
                      }
                    }}
                  >
                    <Key className="w-4 h-4 mr-2" /> Approve Production Key
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => window.open(`/enterprise/${selectedB2bEnterprise.slug || selectedB2bEnterprise.id}`, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Open Portal
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
